import React from "react";
import ReactFlow, {
  Background,
  ControlButton,
  Connection,
  Controls,
  Edge,
  EdgeChange,
  Handle,
  Node,
  NodeChange,
  NodeProps,
  Position,
  ReactFlowInstance,
  ReactFlowProvider,
  SelectionMode,
  applyEdgeChanges,
  applyNodeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import { notifyWorkspaceChanged, subscribeToWorkspaceChanged } from "../workspaceSync";
import { getCurrentPollScore, getPollCompletion } from "../pollMetrics";
import AgentVisualization, { AgentVisualizationData } from "../components/AgentVisualization";
import { SyncHealthPanel } from "./SyncControlCenter";
import { waitForSyncRun, syncRunMessage } from "../syncRuns";
import { ApiActualEvidence, getKpiReportingDate, isCurrentQueueSnapshot, verifiedSnapshotHistory } from "../kpiReporting";

const askLensesQueryPath =
  process.env.REACT_APP_ASK_LENSES_AGENT_VERSION?.trim().toLowerCase() === "v2"
    ? "agent-v2"
    : "agent-v3";

type Status = "green" | "green_strong" | "yellow" | "red" | "neutral";
type Campus = string;
type Frequency = "weekly" | "monthly" | "bimonthly" | "quarterly" | "yearly" | "periodic";
type WeekStartsOn = "saturday" | "sunday" | "monday";
type Scope = "campus" | "global";
type PerformanceDirection = "higher_is_better" | "lower_is_better";
type Lens = string;
type MetricHistory = Record<string, number | undefined>;
type LensPositions = Record<string, { x: number; y: number }>;
type RestrictedFields = Record<string, string[]>;
type ScopeType = "organization" | "campus" | "ministry" | "department" | "service";
type FloatingPanelKey = "lens" | "reference" | "kpi";
type PanelSize = { width: number; height: number };
type EnterpriseSummary = {
  id: number;
  name: string;
  slug: string;
  website_url?: string;
  week_starts_on?: WeekStartsOn | null;
  updated_at?: string;
};
type AppUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  enterprise_id?: number | null;
  is_active: boolean;
};

const kpiHandleStyle: React.CSSProperties = {
  width: 14,
  height: 14,
  background: "transparent",
  border: "none",
  opacity: 0,
};

type SignedInUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  enterprise_id?: number | null;
  is_active?: boolean;
};
type TaskEmailResult = {
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  errors?: string[];
};
type WorkspaceAgentAction = {
  type: string;
  label: string;
  confirmation: string;
  target_count: number;
  target_ids: string[];
  params?: Record<string, unknown>;
};
type WorkspaceAgentResponse = {
  message: string;
  intent: string;
  count?: number | null;
  conversation_id?: number | null;
  tool_calls?: { name: string; arguments?: Record<string, unknown>; result?: Record<string, unknown> }[];
  links?: { label: string; url: string }[];
  action?: WorkspaceAgentAction | null;
  updated_workspace?: Record<string, unknown> | null;
  visualization?: AgentVisualizationData | null;
  freshness_warnings?: string[];
};
type WorkspaceAgentMessage = {
  role: "user" | "assistant";
  content: string;
};
type WorkspaceAgentOperation = "answer" | "action";
type DashboardConfig = {
  trackedKpiIds: string[];
  trackedKpiLabels?: string[];
};
type KpiContextMenuState = {
  nodeId: string;
  x: number;
  y: number;
};
type MapContextMenuState = {
  x: number;
  y: number;
  flowPosition: { x: number; y: number };
};
type WorkspaceCapabilities = {
  enterprise_id?: number | null;
  can_view_workspace: boolean;
  can_manage_workspace: boolean;
  can_manage_lens: boolean;
  can_create_kpi: boolean;
  can_edit_kpi: boolean;
  can_delete_kpi: boolean;
  can_manage_kpi_relationships: boolean;
};
type QuickBooksStatus = {
  configured: boolean;
  connected: boolean;
  enterprise_id: number;
  realm_id?: string | null;
  connected_at?: string | null;
  updated_at?: string | null;
  token_expires_at?: string | null;
};
type PushpayChmsStatus = {
  configured: boolean;
  connected: boolean;
  enterprise_id: number;
  authorization_url?: string | null;
  client_id_preview?: string | null;
  redirect_uri?: string | null;
  subdomain?: string | null;
  scope_preview?: string | null;
  granted_scope?: string | null;
  connected_at?: string | null;
  updated_at?: string | null;
  token_expires_at?: string | null;
};
type PushpayAttendanceMetric =
  | "total_attendance"
  | "average_attendance"
  | "meeting_count"
  | "visitor_count"
  | "reported_rate"
  | "queue_count";
type PushpayKpiResource = "campus_weekend_attendance" | "event_attendance" | "group_attendance" | "process_queue_count" | "schedule_volunteer_checkins" | "ccb_unique_givers";
type QuickBooksKpiResource = "quickbooks_giving" | "quickbooks_expenses" | "quickbooks_account_balance";
type KpiApiResource = PushpayKpiResource | QuickBooksKpiResource;
type PushpayCampusAttendancePreset = "south" | "sonrise_north" | "queen_creek" | "beth_emanuel";
type CcbProcessQueueStatus = "" | "waiting" | "in-process" | "done" | "not-started";
type KpiApiConfig = {
  provider: "pushpay_chms" | "quickbooks";
  resource: KpiApiResource;
  groupSearch: string;
  eventSearch: string;
  eventId: string;
  calendarSearch: string;
  processSearch: string;
  queueSearch: string;
  queueStatus: CcbProcessQueueStatus;
  processId?: string;
  queueId?: string;
  primaryMetric?: string;
  secondaryStatuses?: string[];
  scheduleSearch?: string;
  minimumGiftAmount?: number;
  campusAttendancePreset: PushpayCampusAttendancePreset;
  metric: PushpayAttendanceMetric;
  accountIds?: string[];
  locationName?: string;
  dimensionName?: string;
  dimensionType?: "location" | "class";
  accountingMethod?: "" | "Cash" | "Accrual";
};
type PushpayDgroupAttendancePeriod = {
  period_key: string;
  start_date: string;
  end_date: string;
  value: number;
  total_attendance: number;
  occurrence_count: number;
  met_count: number;
  not_reported_count: number;
  visitor_count: number;
};
type PushpayDgroupAttendancePreview = {
  start_date: string;
  end_date: string;
  period: "weekly" | "monthly" | "bimonthly" | "quarterly" | "yearly";
  metric: PushpayAttendanceMetric;
  group_search: string;
  group_count: number;
  record_count: number;
  groups: { id?: string | number | null; name?: string | null }[];
  periods: PushpayDgroupAttendancePeriod[];
};
type QuickBooksAccount = {
  id: string;
  name: string;
  fully_qualified_name?: string | null;
  account_type?: string | null;
  account_sub_type?: string | null;
  classification?: string | null;
  active: boolean;
};
type QuickBooksDimension = {
  id: string;
  name: string;
  fully_qualified_name?: string | null;
  active: boolean;
};
type QuickBooksDimensions = {
  enterprise_id: number;
  classes: QuickBooksDimension[];
  locations: QuickBooksDimension[];
};
type QuickBooksSupportSession = {
  enterprise_id: number;
  token: string;
  expires_at: string;
};
const buildQuickBooksSupportInstructions = (session: QuickBooksSupportSession) =>
  [
    "QuickBooks finance support token",
    `token=${session.token}`,
    `expires_at=${session.expires_at}`,
    "",
    "Example query:",
    `curl "https://lenses-backend.onrender.com/support/quickbooks/report?support_token=${encodeURIComponent(
      session.token
    )}&report_type=profit_and_loss&start_date=2026-03-01&end_date=2026-03-31&summarize_column_by=Departments"`,
  ].join("\n");

const accessTagOptions = [
  { key: "financial", label: "Financial" },
  { key: "confidential", label: "Confidential" },
  { key: "giving", label: "Giving" },
  { key: "payroll", label: "Payroll" },
  { key: "budget", label: "Budget" },
];

const restrictedKpiFieldOptions = [
  { key: "status", label: "KPI color/status" },
  { key: "actualHistory", label: "Actual values" },
  { key: "targetHistory", label: "Target values" },
  { key: "definition", label: "Definition" },
  { key: "measurement", label: "How it is measured" },
  { key: "updateNotes", label: "Update notes" },
  { key: "owner", label: "Owner" },
  { key: "dataSource", label: "Data source" },
  { key: "pollConfig", label: "Poll configuration" },
  { key: "initiatives", label: "Initiatives" },
  { key: "tasks", label: "Tasks" },
];
type QuickBooksProfitAndLossLocation = {
  name: string;
  income: number;
  expenses: number;
  net_income: number;
};
type QuickBooksProfitAndLossByLocation = {
  enterprise_id: number;
  start_date: string;
  end_date: string;
  locations: QuickBooksProfitAndLossLocation[];
};
type QuickBooksProfitAndLossClass = {
  name: string;
  income: number;
  expenses: number;
  net_income: number;
};
type QuickBooksProfitAndLossByClass = {
  enterprise_id: number;
  start_date: string;
  end_date: string;
  classes: QuickBooksProfitAndLossClass[];
};
type QuickBooksGivingDimensionAnswer = {
  enterprise_id: number;
  start_date: string;
  end_date: string;
  dimension_type: "location" | "class";
  requested_dimension: string;
  matched_dimension?: string | null;
  accounting_method?: string | null;
  total: number;
  account_ids: string[];
  matched_account_ids: string[];
  available_dimensions: string[];
};
type QuickBooksGivingPeriod = {
  period_key: string;
  start_date: string;
  end_date: string;
  total: number;
};
type QuickBooksGivingPreview = {
  enterprise_id: number;
  account_ids: string[];
  matched_account_ids: string[];
  period: "weekly" | "monthly";
  start_date: string;
  end_date: string;
  periods: QuickBooksGivingPeriod[];
};
type CcbGivingPeriod = {
  period_key: string;
  start_date: string;
  end_date: string;
  total: number;
  transaction_count: number;
  campus_totals: Record<string, number>;
  campus_transaction_counts: Record<string, number>;
};
type CcbGivingPreview = {
  start_date: string;
  end_date: string;
  period: "weekly" | "monthly";
  date_basis: "batch" | "transaction";
  campus_name?: string | null;
  fund_names: string[];
  periods: CcbGivingPeriod[];
};
type QuickBooksExpensePeriod = {
  period_key: string;
  start_date: string;
  end_date: string;
  total: number;
};
type QuickBooksExpensePreview = {
  enterprise_id: number;
  account_ids: string[];
  matched_account_ids: string[];
  period: "weekly" | "monthly";
  start_date: string;
  end_date: string;
  periods: QuickBooksExpensePeriod[];
};
type QuickBooksExpenseBudgetPeriod = {
  period_key: string;
  start_date: string;
  end_date: string;
  total: number;
};
type QuickBooksExpenseBudgetPreview = {
  enterprise_id: number;
  account_ids: string[];
  matched_account_ids: string[];
  budget_count: number;
  budget_detail_count: number;
  period: "weekly" | "monthly";
  start_date: string;
  end_date: string;
  periods: QuickBooksExpenseBudgetPeriod[];
};
type QuickBooksAccountBalancePeriod = {
  period_key: string;
  start_date: string;
  end_date: string;
  total: number;
};
type QuickBooksAccountBalancePreview = {
  enterprise_id: number;
  account_ids: string[];
  matched_account_ids: string[];
  period: "weekly" | "monthly";
  start_date: string;
  end_date: string;
  periods: QuickBooksAccountBalancePeriod[];
};
type QuickBooksBudgetDiagnosticMonth = {
  month: string;
  total_budget: number;
  expense_budget: number;
  total_lines: number;
  matched_expense_lines: number;
};
type QuickBooksBudgetDiagnosticAccount = {
  account_id: string;
  account_name: string;
  total: number;
  line_count: number;
};
type QuickBooksBudgetDiagnostics = {
  enterprise_id: number;
  status: string;
  message: string;
  budget_count: number;
  budget_detail_count: number;
  expense_account_count: number;
  matched_expense_account_count: number;
  start_date: string;
  end_date: string;
  months: QuickBooksBudgetDiagnosticMonth[];
  top_accounts: QuickBooksBudgetDiagnosticAccount[];
};
type LensConfig = {
  name: string;
  scopeType: ScopeType;
  scopeId: string;
  description: string;
};
type LensConfigs = Record<Lens, LensConfig>;
type WorkStatus = "not-started" | "in-progress" | "blocked" | "done";
type WorkPriority = "low" | "normal" | "high" | "critical";
type WorkEffort = "light" | "medium" | "heavy";
type ValueType = "count" | "currency" | "percentage" | "rating_1_10";
type MetricPersistence = "period_only" | "carry_forward";
type AggregationMode = "current" | "mtd" | "qtd" | "ytd";
type ComparisonMode = "target" | "previous_period" | "same_period_last_year";
type KpiCalculationMode =
  | "direct"
  | "calculated_values"
  | "formula_percentage"
  | "influenced_by_children";

const defaultWorkspaceCapabilities: WorkspaceCapabilities = {
  enterprise_id: null,
  can_view_workspace: false,
  can_manage_workspace: false,
  can_manage_lens: false,
  can_create_kpi: false,
  can_edit_kpi: false,
  can_delete_kpi: false,
  can_manage_kpi_relationships: false,
};

type TaskItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  priority?: WorkPriority;
  effort?: WorkEffort;
  notes: string;
};

type InitiativeItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  priority?: WorkPriority;
  effort?: WorkEffort;
  summary: string;
  tasks: TaskItem[];
};

type PollQuestionType = "number" | "rating" | "yes_no" | "text";
type PollResponseValue = number | boolean | string;

type PollResponse = {
  id: string;
  submittedAt: string;
  respondentLabel?: string;
  respondentGroup?: string;
  value: PollResponseValue;
  comment?: string;
};

type PollInvite = {
  id: string;
  token: string;
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  sentAt?: string;
  completedAt?: string;
};

type PollRecipient = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  groups: string[];
  name?: string;
};

type PollConfig = {
  id: string;
  campaignRootKpiId?: string;
  linkedKpiIds: string[];
  title: string;
  description: string;
  targetGroup?: string;
  targetGroups: string[];
  questionPrompt: string;
  questionType: PollQuestionType;
  isAnonymous: boolean;
  status: "draft" | "published";
  publishedAt?: string;
  responses: PollResponse[];
  invites: PollInvite[];
};
type PollEditorMode = "view" | "edit";

type AiStrategyTaskRecommendation = {
  title: string;
  owner_role?: string;
  priority?: WorkPriority;
  effort?: WorkEffort;
};

type AiStrategyInitiativeRecommendation = {
  title: string;
  summary?: string;
  owner_role?: string;
  priority?: WorkPriority;
  effort?: WorkEffort;
  tasks?: AiStrategyTaskRecommendation[];
};

type AiStrategyKpiRecommendation = {
  label: string;
  definition?: string;
  measurement?: string;
  owner_role?: string;
  value_type?: ValueType;
  calculation_mode?: KpiCalculationMode;
  data_source?: string;
  frequency?: Frequency;
  children?: AiStrategyKpiRecommendation[];
  initiatives?: AiStrategyInitiativeRecommendation[];
};

type AiStrategyLensRecommendation = {
  name: string;
  scope_type?: ScopeType;
  scope_id?: string;
  description?: string;
  rationale?: string;
  kpis?: AiStrategyKpiRecommendation[];
};

type AiStrategyRecommendationResponse = {
  summary: string;
  lenses: AiStrategyLensRecommendation[];
  model: string;
};

type AiAddKind = "lens" | "kpi" | "initiative" | "kpiTask" | "initiativeTask";

type AiAddDraft = {
  kind: AiAddKind;
  kpiId?: string;
  initiativeId?: string;
  lens?: AiStrategyLensRecommendation;
  kpi?: AiStrategyKpiRecommendation;
  initiative?: AiStrategyInitiativeRecommendation;
  task?: AiStrategyTaskRecommendation;
  summary?: string;
};

type DetailFieldKey =
  | "frequency"
  | "definition"
  | "measurement"
  | "owner"
  | "dataOwner"
  | "dataSource"
  | "valueType"
  | "updateNotes"
  | "eventDate"
  | "formulaNumeratorKpiId"
  | "formulaDenominatorKpiId";

type KpiNodeData = {
  label: string;
  target?: number;
  targetHistory: MetricHistory;
  actual?: number;
  actualHistory: MetricHistory;
  apiActualEvidence?: ApiActualEvidence | null;
  unit?: string;
  frequency: Frequency;
  scope: Scope;
  performanceDirection: PerformanceDirection;
  supportsCumulative: boolean;
  metricPersistence: MetricPersistence;
  calculationMode: KpiCalculationMode;
  eventDate: string;
  definition: string;
  measurement: string;
  owner: string;
  dataOwner: string;
  dataSource: string;
  apiConfig?: KpiApiConfig | null;
  valueType: ValueType;
  formulaNumeratorKpiId?: string;
  formulaDenominatorKpiId?: string;
  updateNotes: string;
  pollConfig?: PollConfig | null;
  restrictedFields: RestrictedFields;
  initiatives: InitiativeItem[];
  tasks: TaskItem[];
  lenses: Lens[];
  lensLabels: string[];
  positionsByLens: LensPositions;
  collapsedInLenses: Lens[];
  status?: Status;
  isRollup: boolean;
  metricsEditable: boolean;
  metricNote?: string;
  statusNote?: string;
  reportingPeriodLabel?: string;
  colorActual?: number;
  metricsRestricted?: boolean;
  comparisonMode: ComparisonMode;
  rollupWarning?: string;
  childCount: number;
  childrenCollapsed: boolean;
  isInitiativeRailOpen: boolean;
  onOpenDetails: (id: string) => void;
  onSelectNode: (id: string, additive: boolean) => void;
  onToggleInitiativeRail: (id: string) => void;
  onLabelChange: (id: string, value: string) => void;
  onMetricChange: (
    id: string,
    field: "target" | "actual" | "unit",
    value: string
  ) => void;
  onConfigChange: (
    id: string,
    field:
      | "scope"
      | "performanceDirection"
      | "supportsCumulative"
      | "metricPersistence"
      | "calculationMode",
    value: Scope | PerformanceDirection | boolean | MetricPersistence | KpiCalculationMode
  ) => void;
  onDetailChange: (id: string, field: DetailFieldKey, value: string) => void;
  onApiConfigChange: (id: string, config: KpiApiConfig | null) => void;
  onRestrictedFieldsChange: (id: string, restrictedFields: RestrictedFields) => void;
  onToggleRollup: (id: string, checked: boolean) => void;
  onAddChild: (id: string) => void;
  onToggleChildren: (id: string) => void;
};

const noopLabelChange = (_id: string, _value: string) => {};
const noopMetricChange = (
  _id: string,
  _field: "target" | "actual" | "unit",
  _value: string
) => {};
const noopConfigChange = (
  _id: string,
  _field:
    | "scope"
    | "performanceDirection"
    | "supportsCumulative"
    | "metricPersistence"
    | "calculationMode",
  _value: Scope | PerformanceDirection | boolean | MetricPersistence | KpiCalculationMode
) => {};
const noopDetailChange = (_id: string, _field: DetailFieldKey, _value: string) => {};
const noopApiConfigChange = (_id: string, _config: KpiApiConfig | null) => {};
const noopRestrictedFieldsChange = (_id: string, _restrictedFields: RestrictedFields) => {};
const noopToggleRollup = (_id: string, _checked: boolean) => {};
const noopAddChild = (_id: string) => {};
const noopToggleChildren = (_id: string) => {};
const noopOpenDetails = (_id: string) => {};
const noopSelectNode = (_id: string, _additive: boolean) => {};
const noopToggleInitiativeRail = (_id: string) => {};

const initialOwners: string[] = [];
const initialPollAudienceGroups: string[] = [
  "Staff",
  "Senior Staff",
  "Executive Team",
  "D-Group Leaders",
  "Volunteers",
];
const initialPollRecipients: PollRecipient[] = [];
const performanceDirectionOptions: PerformanceDirection[] = [
  "higher_is_better",
  "lower_is_better",
];
const dataSourceOptions = ["Manual", "API", "Poll"] as const;
const valueTypeOptions: ValueType[] = ["count", "currency", "percentage", "rating_1_10"];
const defaultPushpayGroupAttendanceApiConfig: KpiApiConfig = {
  provider: "pushpay_chms",
  resource: "group_attendance",
  groupSearch: "",
  eventSearch: "",
  eventId: "",
  calendarSearch: "",
  processSearch: "",
  queueSearch: "",
  queueStatus: "",
  scheduleSearch: "",
  campusAttendancePreset: "sonrise_north",
  metric: "total_attendance",
};
const defaultPushpayEventAttendanceApiConfig: KpiApiConfig = {
  provider: "pushpay_chms",
  resource: "event_attendance",
  groupSearch: "",
  eventSearch: "9:00am Service",
  eventId: "",
  calendarSearch: "North",
  processSearch: "",
  queueSearch: "",
  queueStatus: "",
  campusAttendancePreset: "sonrise_north",
  metric: "total_attendance",
};
const defaultPushpayCampusAttendanceApiConfig: KpiApiConfig = {
  provider: "pushpay_chms",
  resource: "campus_weekend_attendance",
  groupSearch: "",
  eventSearch: "",
  eventId: "",
  calendarSearch: "",
  processSearch: "",
  queueSearch: "",
  queueStatus: "",
  campusAttendancePreset: "sonrise_north",
  metric: "total_attendance",
};
const defaultCcbProcessQueueApiConfig: KpiApiConfig = {
  provider: "pushpay_chms",
  resource: "process_queue_count",
  groupSearch: "",
  eventSearch: "",
  eventId: "",
  calendarSearch: "",
  processSearch: "",
  queueSearch: "",
  queueStatus: "done",
  campusAttendancePreset: "sonrise_north",
  metric: "queue_count",
};
const defaultQuickBooksGivingApiConfig: KpiApiConfig = {
  ...defaultPushpayGroupAttendanceApiConfig,
  provider: "quickbooks",
  resource: "quickbooks_giving",
  metric: "total_attendance",
  accountIds: [],
  locationName: "",
  dimensionName: "",
  dimensionType: "location",
  accountingMethod: "",
};
const defaultQuickBooksExpensesApiConfig: KpiApiConfig = {
  ...defaultQuickBooksGivingApiConfig,
  resource: "quickbooks_expenses",
};
const defaultQuickBooksAccountBalanceApiConfig: KpiApiConfig = {
  ...defaultQuickBooksGivingApiConfig,
  resource: "quickbooks_account_balance",
};
const pushpayResourceOptions: { value: PushpayKpiResource; label: string; description: string }[] = [
  {
    value: "campus_weekend_attendance",
    label: "Campus weekend attendance",
    description: "Weekend service attendance by campus, including main services plus related children and nursery events.",
  },
  {
    value: "event_attendance",
    label: "Event attendance",
    description: "Head counts from Events > Calendar attendance, filterable by event id, service name, and campus/calendar.",
  },
  {
    value: "group_attendance",
    label: "Group attendance",
    description: "Attendance occurrences for any Pushpay group search, such as Sonrise, Kesher, Discipleship, or all groups.",
  },
  {
    value: "process_queue_count",
    label: "CCB process queue count",
    description: "Current people/items in People > Processes queues, filterable by process, queue, and queue status.",
  },
  {
    value: "schedule_volunteer_checkins",
    label: "Schedules & Plans volunteer check-ins",
    description: "Volunteers who checked in to schedules across campus-assigned Schedules & Plans categories.",
  },
  {
    value: "ccb_unique_givers",
    label: "CCB unique giving units",
    description: "Distinct CCB givers whose eligible giving total is above $100 in the reporting period.",
  },
];
const quickBooksResourceOptions: { value: QuickBooksKpiResource; label: string; description: string }[] = [
  {
    value: "quickbooks_giving",
    label: "QuickBooks giving",
    description: "Giving income from QuickBooks P&L reports, optionally matched to a location/department.",
  },
  {
    value: "quickbooks_expenses",
    label: "QuickBooks expenses",
    description: "Operating expenses from QuickBooks P&L reports, optionally matched to a location/department.",
  },
  {
    value: "quickbooks_account_balance",
    label: "QuickBooks account balance",
    description: "Balance sheet totals from selected or auto-detected savings accounts.",
  },
];
const pushpayAttendanceMetricOptions: { value: PushpayAttendanceMetric; label: string }[] = [
  { value: "total_attendance", label: "Total attendance" },
  { value: "average_attendance", label: "Average attendance" },
  { value: "meeting_count", label: "Meeting count" },
  { value: "visitor_count", label: "Visitor count" },
  { value: "reported_rate", label: "Reported rate" },
];
const ccbProcessQueueStatusOptions: { value: CcbProcessQueueStatus; label: string }[] = [
  { value: "", label: "Any status" },
  { value: "waiting", label: "Waiting" },
  { value: "in-process", label: "In process" },
  { value: "done", label: "Done" },
  { value: "not-started", label: "Not started" },
];
const pushpayCampusAttendanceOptions: { value: PushpayCampusAttendancePreset; label: string }[] = [
  { value: "sonrise_north", label: "Bethel SonRise - North Campus" },
  { value: "south", label: "Bethel Chandler - South Campus" },
  { value: "queen_creek", label: "Bethel Queen Creek" },
  { value: "beth_emanuel", label: "Beth Emanuel" },
];
const campusAttendanceKpiLabelPresets: Record<string, PushpayCampusAttendancePreset> = {
  "Bethel South Attendance": "south",
  "Bethel SonRise Attendance": "sonrise_north",
  "Bethel QC Attendance": "queen_creek",
  "Beth Emanuel Attendance": "beth_emanuel",
};
const volunteerScheduleKpiLabelSearches: Record<string, string> = {
  "Bethel South Volunteers": "south campus|south",
  "Bethel SonRise Volunteers": "north campus|sonrise|north",
  "Bethel QC Volunteers": "queen creek|qc",
  "Beth Emanuel Volunteers": "beth emanuel|emanuel",
};
const getVolunteerScheduleConfigForLabel = (label: string): KpiApiConfig | null => {
  const scheduleSearch = volunteerScheduleKpiLabelSearches[label.trim()];
  return scheduleSearch
    ? { ...defaultPushpayGroupAttendanceApiConfig, resource: "schedule_volunteer_checkins", scheduleSearch }
    : null;
};
const getCcbUniqueGiversConfigForLabel = (label: string): KpiApiConfig | null =>
  label.trim() === "Total Unique Giving Units"
    ? { ...defaultPushpayGroupAttendanceApiConfig, resource: "ccb_unique_givers", minimumGiftAmount: 100 }
    : null;
const normalizeKpiApiPresetLabel = (label: string): string =>
  label.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
const getCampusAttendanceConfigForLabel = (label: string): KpiApiConfig | null => {
  const preset = campusAttendanceKpiLabelPresets[label.trim()];
  if (!preset) return null;
  return {
    ...defaultPushpayCampusAttendanceApiConfig,
    campusAttendancePreset: preset,
  };
};
const getProcessQueueConfigForLabel = (label: string): KpiApiConfig | null => {
  const normalizedLabel = normalizeKpiApiPresetLabel(label);
  const isLeadershipMeetingKpi =
    normalizedLabel.includes("leadership meetings") ||
    normalizedLabel.includes("leader meetings") ||
    normalizedLabel.includes("leader meeting") ||
    (normalizedLabel.includes("leadership") && normalizedLabel.includes("meeting"));
  if (!isLeadershipMeetingKpi) return null;
  return {
    ...defaultCcbProcessQueueApiConfig,
    processSearch: "Next Steps",
    queueSearch: "Schedule Leader Meeting",
    queueStatus: "done",
  };
};
const getFinanceDimensionNameForLabel = (label: string) => {
  const normalizedLabel = normalizeKpiApiPresetLabel(label).replace(/\s+/g, "");
  if (normalizedLabel.includes("sonrise")) return "SonRise";
  if (normalizedLabel.includes("queencreek") || normalizedLabel.includes("bethelqc") || normalizedLabel.includes("qcgiving")) {
    return "Queen Creek";
  }
  if (normalizedLabel.includes("emanuel")) return "Beth Emanuel";
  if (normalizedLabel.includes("south") || normalizedLabel.includes("chandler")) return "South";
  return "";
};
const getQuickBooksFinanceConfigForLabel = (label: string): KpiApiConfig | null => {
  const normalizedLabel = normalizeKpiApiPresetLabel(label).replace(/\s+/g, "");
  const dimensionName = getFinanceDimensionNameForLabel(label);
  const baseConfig = {
    accountIds: [],
    dimensionType: "location" as const,
    dimensionName,
    locationName: dimensionName,
    accountingMethod: "" as const,
  };
  if (normalizedLabel.includes("saving") || normalizedLabel.includes("reserve")) {
    return {
      ...defaultQuickBooksAccountBalanceApiConfig,
      ...baseConfig,
    };
  }
  if (
    normalizedLabel.includes("giving") ||
    normalizedLabel.includes("tithe") ||
    normalizedLabel.includes("offering")
  ) {
    return {
      ...defaultQuickBooksGivingApiConfig,
      ...baseConfig,
    };
  }
  if (
    normalizedLabel.includes("expense") ||
    normalizedLabel.includes("expenditure") ||
    normalizedLabel.includes("spending")
  ) {
    return {
      ...defaultQuickBooksExpensesApiConfig,
      ...baseConfig,
    };
  }
  return null;
};
const getKpiApiConfigForLabel = (label: string): KpiApiConfig | null =>
  getCampusAttendanceConfigForLabel(label) ?? getVolunteerScheduleConfigForLabel(label) ?? getCcbUniqueGiversConfigForLabel(label) ?? getProcessQueueConfigForLabel(label) ?? getQuickBooksFinanceConfigForLabel(label);

const normalizeKpiApiConfig = (value: unknown): KpiApiConfig | null => {
  if (!value || typeof value !== "object") return null;
  const raw = value as {
    provider?: unknown;
    resource?: unknown;
    groupSearch?: unknown;
    eventSearch?: unknown;
    eventId?: unknown;
    calendarSearch?: unknown;
    processSearch?: unknown;
    queueSearch?: unknown;
    queueStatus?: unknown;
    processId?: unknown;
    queueId?: unknown;
    primaryMetric?: unknown;
    secondaryStatuses?: unknown;
    scheduleSearch?: unknown;
    minimumGiftAmount?: unknown;
    campusAttendancePreset?: unknown;
    metric?: unknown;
    accountIds?: unknown;
    account_ids?: unknown;
    locationName?: unknown;
    location_name?: unknown;
    dimensionName?: unknown;
    dimension_name?: unknown;
    dimensionType?: unknown;
    dimension_type?: unknown;
    accountingMethod?: unknown;
    accounting_method?: unknown;
  };
  if (raw.provider !== "pushpay_chms" && raw.provider !== "quickbooks") return null;
  const rawResource = typeof raw.resource === "string" ? raw.resource : "";
  if (raw.provider === "quickbooks") {
    if (
      rawResource !== "quickbooks_giving" &&
      rawResource !== "quickbooks_expenses" &&
      rawResource !== "quickbooks_account_balance"
    ) {
      return null;
    }
    const defaultConfig =
      rawResource === "quickbooks_expenses"
        ? defaultQuickBooksExpensesApiConfig
        : rawResource === "quickbooks_account_balance"
          ? defaultQuickBooksAccountBalanceApiConfig
          : defaultQuickBooksGivingApiConfig;
    const rawAccountIds = raw.accountIds ?? raw.account_ids;
    const accountIds = Array.isArray(rawAccountIds)
      ? rawAccountIds.map((item) => String(item).trim()).filter(Boolean)
      : typeof rawAccountIds === "string"
        ? rawAccountIds.split(",").map((item) => item.trim()).filter(Boolean)
        : [];
    const rawDimensionType = raw.dimensionType ?? raw.dimension_type;
    const dimensionType = rawDimensionType === "class" ? "class" : "location";
    const rawAccountingMethod = raw.accountingMethod ?? raw.accounting_method;
    const accountingMethod =
      rawAccountingMethod === "Cash" || rawAccountingMethod === "Accrual" ? rawAccountingMethod : "";
    return {
      ...defaultConfig,
      provider: "quickbooks",
      resource: rawResource as QuickBooksKpiResource,
      accountIds,
      locationName: typeof (raw.locationName ?? raw.location_name) === "string" ? String(raw.locationName ?? raw.location_name) : "",
      dimensionName: typeof (raw.dimensionName ?? raw.dimension_name) === "string" ? String(raw.dimensionName ?? raw.dimension_name) : "",
      dimensionType,
      accountingMethod,
    };
  }
  if (
    rawResource !== "group_attendance" &&
    rawResource !== "dgroup_attendance" &&
    rawResource !== "event_attendance" &&
    rawResource !== "campus_weekend_attendance" &&
    rawResource !== "process_queue_count"
    && rawResource !== "schedule_volunteer_checkins"
    && rawResource !== "ccb_unique_givers"
  ) {
    return null;
  }
  const metric: PushpayAttendanceMetric = pushpayAttendanceMetricOptions.some((option) => option.value === raw.metric)
    ? (raw.metric as PushpayAttendanceMetric)
    : "total_attendance";
  const resource =
    rawResource === "campus_weekend_attendance"
      ? "campus_weekend_attendance"
      : rawResource === "event_attendance"
        ? "event_attendance"
        : rawResource === "process_queue_count"
          ? "process_queue_count"
          : rawResource === "schedule_volunteer_checkins"
            ? "schedule_volunteer_checkins"
          : rawResource === "ccb_unique_givers"
            ? "ccb_unique_givers"
          : "group_attendance";
  const campusAttendancePreset = pushpayCampusAttendanceOptions.some((option) => option.value === raw.campusAttendancePreset)
    ? (raw.campusAttendancePreset as PushpayCampusAttendancePreset)
    : "sonrise_north";
  const queueStatus = ccbProcessQueueStatusOptions.some((option) => option.value === raw.queueStatus)
    ? (raw.queueStatus as CcbProcessQueueStatus)
    : "";
  return {
    provider: "pushpay_chms",
    resource,
    groupSearch: typeof raw.groupSearch === "string" ? raw.groupSearch : "",
    eventSearch: typeof raw.eventSearch === "string" ? raw.eventSearch : "",
    eventId: typeof raw.eventId === "string" ? raw.eventId : "",
    calendarSearch: typeof raw.calendarSearch === "string" ? raw.calendarSearch : "",
    processSearch: typeof raw.processSearch === "string" ? raw.processSearch : "",
    queueSearch: typeof raw.queueSearch === "string" ? raw.queueSearch : "",
    queueStatus,
    processId: typeof raw.processId === "string" ? raw.processId : undefined,
    queueId: typeof raw.queueId === "string" ? raw.queueId : undefined,
    primaryMetric: typeof raw.primaryMetric === "string" ? raw.primaryMetric : undefined,
    secondaryStatuses: Array.isArray(raw.secondaryStatuses)
      ? raw.secondaryStatuses.map((item) => String(item)).filter(Boolean)
      : undefined,
    scheduleSearch: typeof raw.scheduleSearch === "string" ? raw.scheduleSearch : "",
    minimumGiftAmount: typeof raw.minimumGiftAmount === "number" ? raw.minimumGiftAmount : undefined,
    campusAttendancePreset,
    metric: resource === "process_queue_count" ? "queue_count" : metric,
  };
};

const getEffectiveKpiApiConfig = (node: Node<KpiNodeData>): KpiApiConfig | null =>
  getVolunteerScheduleConfigForLabel(node.data.label) ??
  getCcbUniqueGiversConfigForLabel(node.data.label) ??
  normalizeKpiApiConfig(node.data.apiConfig) ??
  getKpiApiConfigForLabel(node.data.label);

const kpiApiSourceRequiresPushpayConnection = (config: KpiApiConfig | null): boolean =>
  !config ||
  (config.provider === "pushpay_chms" &&
    (config.resource === "group_attendance" || config.resource === "schedule_volunteer_checkins"));

const kpiApiSourceRequiresQuickBooksConnection = (config: KpiApiConfig | null): boolean =>
  Boolean(config && config.provider === "quickbooks");

const isKpiApiSourceAvailable = (
  config: KpiApiConfig | null,
  isPushpayConnected: boolean | undefined,
  isQuickBooksConnected: boolean | undefined = true
): boolean =>
  (!kpiApiSourceRequiresPushpayConnection(config) || Boolean(isPushpayConnected)) &&
  (!kpiApiSourceRequiresQuickBooksConnection(config) || Boolean(isQuickBooksConnected));

const getKpiApiSourceLabel = (config: KpiApiConfig): string =>
  config.provider === "quickbooks"
    ? "QuickBooks"
    : config.resource === "group_attendance" || config.resource === "schedule_volunteer_checkins"
      ? "Pushpay"
      : "CCB";

const getNormalizedValueType = (dataSource: string | undefined, valueType: unknown): ValueType => {
  if (dataSource === "Poll") {
    return "rating_1_10";
  }

  return typeof valueType === "string" && valueTypeOptions.includes(valueType as ValueType)
    ? (valueType as ValueType)
    : "count";
};
const frequencyOptions: Frequency[] = ["weekly", "monthly", "bimonthly", "quarterly", "yearly", "periodic"];
const aggregationModeOptions: AggregationMode[] = ["current", "mtd", "qtd", "ytd"];
const comparisonModeOptions: ComparisonMode[] = ["target", "previous_period", "same_period_last_year"];
const calculationModeOptions: KpiCalculationMode[] = [
  "direct",
  "calculated_values",
  "formula_percentage",
  "influenced_by_children",
];
const initialLenses: Lens[] = ["org-chart-org", "discipleship-org", "operations-org"];
const defaultCampus: Campus = "global";
const defaultWeekStartsOn: WeekStartsOn = "monday";
const metricInputReadOnlyStyle = {
  color: "#6b7280",
  background: "#f3f4f6",
  cursor: "not-allowed",
};
const normalizeWeekStartsOn = (value: unknown): WeekStartsOn =>
  value === "saturday" || value === "sunday" || value === "monday" ? value : defaultWeekStartsOn;
const getReportingWeekStart = (date: Date, weekStartsOn: WeekStartsOn) => {
  const nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = nextDate.getDay();
  const targetDay = weekStartsOn === "saturday" ? 6 : weekStartsOn === "sunday" ? 0 : 1;
  const offset = (day - targetDay + 7) % 7;
  nextDate.setDate(nextDate.getDate() - offset);
  return nextDate;
};
const getInitialReferenceDate = (weekStartsOn: WeekStartsOn = defaultWeekStartsOn) =>
  getReportingWeekStart(new Date(), weekStartsOn);
const initialReferenceDate = getInitialReferenceDate();
const defaultLens = initialLenses[0];
const brandLogoUrl = `${process.env.PUBLIC_URL}/favicon.png?v=2`;
const initialLensConfigs: LensConfigs = {
  "org-chart-org": {
    name: "Org Chart",
    scopeType: "organization",
    scopeId: "entire-organization",
    description:
      "Tracks KPIs tied to organizational leadership, ministry oversight, and team accountability.",
  },
  "discipleship-org": {
    name: "Discipleship",
    scopeType: "organization",
    scopeId: "entire-organization",
    description:
      "Focuses on spiritual formation, ministry environments, and growth pathways across the church.",
  },
  "operations-org": {
    name: "Operations",
    scopeType: "organization",
    scopeId: "entire-organization",
    description:
      "Covers execution, finance, systems, and operational health needed to support ministry outcomes.",
  },
};
const workStatusOptions: WorkStatus[] = ["not-started", "in-progress", "blocked", "done"];
const workPriorityOptions: WorkPriority[] = ["low", "normal", "high", "critical"];
const workEffortOptions: WorkEffort[] = ["light", "medium", "heavy"];
const defaultEnterpriseName = "Default Enterprise";
const activeEnterpriseStorageKey = "lenses.activeEnterpriseId";
const enterprisesCacheKey = "lenses.enterprisesCache.v1";
const workspaceCacheVersion = "postgres-v2";
const getEnterpriseCacheIdentity = (enterprise: EnterpriseSummary | null | undefined) => {
  const rawIdentity = enterprise?.slug || enterprise?.name || "unknown";
  return rawIdentity
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";
};
const getWorkspaceEnterpriseIdentity = (
  enterpriseId: number | null,
  enterprise: EnterpriseSummary | null | undefined
) => ({
  id: enterpriseId,
  slug: enterprise?.slug ?? "",
  name: enterprise?.name ?? "",
});
const getWorkspaceCacheKey = (enterpriseId: number, enterprise: EnterpriseSummary | null | undefined) =>
  `lenses.workspaceCache.${workspaceCacheVersion}.${enterpriseId}.${getEnterpriseCacheIdentity(enterprise)}`;
const getWorkspacePendingSaveKey = (enterpriseId: number, enterprise: EnterpriseSummary | null | undefined) =>
  `lenses.workspacePendingSave.${workspaceCacheVersion}.${enterpriseId}.${getEnterpriseCacheIdentity(enterprise)}`;
const getWorkspacePersistenceFingerprint = (workspace: Record<string, unknown>) => {
  const {
    activeLens: _activeLens,
    referenceDate: _referenceDate,
    aggregationMode: _aggregationMode,
    comparisonMode: _comparisonMode,
    campus: _campus,
    isLensLibraryCollapsed: _isLensLibraryCollapsed,
    isReferenceLibraryCollapsed: _isReferenceLibraryCollapsed,
    isLibraryCollapsed: _isLibraryCollapsed,
    panelOffsets: _panelOffsets,
    panelSizes: _panelSizes,
    ...persistentWorkspace
  } = workspace;
  return JSON.stringify(persistentWorkspace);
};

const readStoredActiveEnterpriseId = () => {
  if (typeof window === "undefined") return null;
  const parsedEnterpriseId = Number(window.localStorage.getItem(activeEnterpriseStorageKey));
  return Number.isFinite(parsedEnterpriseId) && parsedEnterpriseId > 0 ? parsedEnterpriseId : null;
};

const readCachedEnterpriseSummaries = () => {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(enterprisesCacheKey) ?? "[]");
    return Array.isArray(parsed) ? (parsed as EnterpriseSummary[]) : [];
  } catch {
    window.localStorage.removeItem(enterprisesCacheKey);
    return [];
  }
};

const KPI_NODE_WIDTH = 220;
const KPI_NODE_HEIGHT = 560;
const KPI_HORIZONTAL_GAP = 280;
const KPI_VERTICAL_GAP = 230;
const KPI_NODE_MARGIN = 32;
const KPI_VERTICAL_GUTTER = 96;
const KPI_LAYER_HORIZONTAL_GAP = 300;
const KPI_LAYER_VERTICAL_GAP = KPI_NODE_HEIGHT + 96;
const UNDO_HISTORY_LIMIT = 12;

const normalizeAiScopeType = (value: string | undefined): ScopeType =>
  value === "campus" ||
  value === "ministry" ||
  value === "department" ||
  value === "service" ||
  value === "organization"
    ? value
    : "organization";
const normalizeAiFrequency = (value: string | undefined): Frequency =>
  frequencyOptions.includes(value as Frequency) ? (value as Frequency) : "weekly";
const normalizeAiValueType = (value: string | undefined): ValueType =>
  valueTypeOptions.includes(value as ValueType) ? (value as ValueType) : "count";
const normalizeAiCalculationMode = (value: string | undefined): KpiCalculationMode =>
  calculationModeOptions.includes(value as KpiCalculationMode)
    ? (value as KpiCalculationMode)
    : "direct";
const normalizeAiPriority = (value: string | undefined): WorkPriority =>
  workPriorityOptions.includes(value as WorkPriority) ? (value as WorkPriority) : "normal";
const normalizeAiEffort = (value: string | undefined): WorkEffort =>
  workEffortOptions.includes(value as WorkEffort) ? (value as WorkEffort) : "medium";

const createTaskFromAiDraft = (task: AiStrategyTaskRecommendation): TaskItem => ({
  id: uuidv4(),
  title: task.title?.trim() || "New task",
  owner: task.owner_role?.trim() || "Assign an owner",
  dueDate: "",
  status: "not-started",
  priority: normalizeAiPriority(task.priority),
  effort: normalizeAiEffort(task.effort),
  notes: "",
});

const createInitiativeFromAiDraft = (
  initiative: AiStrategyInitiativeRecommendation
): InitiativeItem => ({
  id: uuidv4(),
  title: initiative.title?.trim() || "New initiative",
  owner: initiative.owner_role?.trim() || "Assign an owner",
  dueDate: "",
  status: "not-started",
  priority: normalizeAiPriority(initiative.priority),
  effort: normalizeAiEffort(initiative.effort),
  summary: initiative.summary?.trim() || "",
  tasks: (initiative.tasks ?? []).slice(0, 8).map(createTaskFromAiDraft),
});

const cloneNodesForUndo = (nodes: Node<KpiNodeData>[]) =>
  nodes.map((node) => ({
    ...node,
    position: { ...node.position },
    data: {
      ...node.data,
      targetHistory: { ...node.data.targetHistory },
      actualHistory: { ...node.data.actualHistory },
      initiatives: node.data.initiatives.map((initiative) => ({
        ...initiative,
        tasks: initiative.tasks.map((task) => ({ ...task })),
      })),
      tasks: node.data.tasks.map((task) => ({ ...task })),
      lenses: [...node.data.lenses],
      lensLabels: [...node.data.lensLabels],
      positionsByLens: Object.fromEntries(
        Object.entries(node.data.positionsByLens).map(([lens, position]) => [
          lens,
          { ...position },
        ])
      ),
      collapsedInLenses: [...node.data.collapsedInLenses],
      pollConfig: node.data.pollConfig
        ? {
            ...node.data.pollConfig,
            linkedKpiIds: [...node.data.pollConfig.linkedKpiIds],
            targetGroups: [...node.data.pollConfig.targetGroups],
            responses: node.data.pollConfig.responses.map((response) => ({ ...response })),
            invites: node.data.pollConfig.invites.map((invite) => ({ ...invite })),
          }
        : node.data.pollConfig,
    },
  }));

const cloneEdgesForUndo = (edges: Edge[]) =>
  edges.map((edge) => ({
    ...edge,
    data: edge.data
      ? {
          ...edge.data,
          lenses: Array.isArray(edge.data.lenses) ? [...edge.data.lenses] : edge.data.lenses,
        }
      : edge.data,
    style: edge.style ? { ...edge.style } : edge.style,
    labelStyle: edge.labelStyle ? { ...edge.labelStyle } : edge.labelStyle,
    labelBgStyle: edge.labelBgStyle ? { ...edge.labelBgStyle } : edge.labelBgStyle,
    markerEnd: edge.markerEnd,
  }));

const statusColor = (status?: Status) => {
  switch (status) {
    case "green_strong":
      return "#15803d";
    case "green":
      return "#22c55e";
    case "yellow":
      return "#eab308";
    case "red":
      return "#ef4444";
    default:
      return "#9ca3af";
  }
};
const interpolateHexColor = (startHex: string, endHex: string, amount: number) => {
  const clampedAmount = Math.min(1, Math.max(0, amount));
  const start = Number.parseInt(startHex.slice(1), 16);
  const end = Number.parseInt(endHex.slice(1), 16);
  const startRgb = [(start >> 16) & 255, (start >> 8) & 255, start & 255];
  const endRgb = [(end >> 16) & 255, (end >> 8) & 255, end & 255];
  const nextRgb = startRgb.map((channel, index) =>
    Math.round(channel + (endRgb[index] - channel) * clampedAmount)
  );
  return `#${nextRgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
};
const hexToRgba = (hex: string, alpha: number) => {
  const clean = hex.replace("#", "");
  const value =
    clean.length === 3
      ? clean
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : clean;
  const parsed = Number.parseInt(value, 16);
  if (Number.isNaN(parsed)) {
    return `rgba(156,163,175,${alpha})`;
  }
  const r = (parsed >> 16) & 255;
  const g = (parsed >> 8) & 255;
  const b = parsed & 255;
  return `rgba(${r},${g},${b},${alpha})`;
};
const ratingColor = (value: number | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const rating = Math.min(10, Math.max(1, value));
  const stops = [
    "#450a0a",
    "#7f1d1d",
    "#dc2626",
    "#ea580c",
    "#f97316",
    "#fb923c",
    "#facc15",
    "#86efac",
    "#22c55e",
    "#166534",
  ];
  const lowerRating = Math.floor(rating);
  const upperRating = Math.ceil(rating);
  const lowerColor = stops[lowerRating - 1];
  const upperColor = stops[upperRating - 1];
  return lowerRating === upperRating
    ? lowerColor
    : interpolateHexColor(lowerColor, upperColor, rating - lowerRating);
};
const kpiDisplayColor = (
  valueType: ValueType,
  actual: number | undefined,
  colorActual: number | undefined,
  status?: Status
) =>
  typeof colorActual === "number"
    ? ratingColor(colorActual) ?? statusColor(status)
    : valueType === "rating_1_10"
      ? ratingColor(actual) ?? statusColor(status)
      : statusColor(status);

const getDirectScore = (
  target?: number,
  actual?: number,
  performanceDirection: PerformanceDirection = "higher_is_better"
): number | null => {
  if (
    typeof target !== "number" ||
    typeof actual !== "number" ||
    Number.isNaN(target) ||
    Number.isNaN(actual) ||
    target < 0
  ) {
    return null;
  }

  if (target === 0) {
    if (actual === 0) return 1;
    return performanceDirection === "higher_is_better" ? Number.POSITIVE_INFINITY : 0;
  }

  if (performanceDirection === "lower_is_better") {
    if (actual === 0) return Number.POSITIVE_INFINITY;
    return target / actual;
  }

  return actual / target;
};

const getStatusFromScore = (score: number | null): Status => {
  if (score === null) return "neutral";
  if (score >= 1.2) return "green_strong";
  if (score >= 1) return "green";
  if (score >= 0.8) return "yellow";
  return "red";
};

const isMonthlyFinancialPeriodInProgress = (
  referenceDate: Date,
  frequency: Frequency,
  valueType: ValueType
) => {
  if (frequency !== "monthly" || valueType !== "currency") return false;
  const periodRange = getPeriodRange("monthly", getPeriodKey(referenceDate, "monthly"));
  if (!periodRange) return false;
  const today = getStartOfDay(new Date());
  return today >= periodRange.start && today <= periodRange.end;
};

const isFinancialPeriodInProgressNote = (statusNote?: string) =>
  Boolean(statusNote?.split("|").some((part) => part.trim() === "Month in progress"));

const getScoreFromRatingActual = (value: number | undefined): number | null => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  if (value >= 9) return 1.2;
  if (value >= 7.5) return 1;
  if (value >= 6.5) return 0.8;
  return 0;
};

const normalizeInfluence = (edge: Edge): number | null => {
  const raw = edge.data?.influence;
  if (typeof raw !== "number" || Number.isNaN(raw) || raw <= 0) return null;
  return raw / 100;
};

const rectanglesOverlap = (
  left: { x: number; y: number; width: number; height: number },
  right: { x: number; y: number; width: number; height: number }
) =>
  left.x < right.x + right.width &&
  left.x + left.width > right.x &&
  left.y < right.y + right.height &&
  left.y + left.height > right.y;

const padTwo = (value: number) => String(value).padStart(2, "0");

const getIsoWeekParts = (date: Date) => {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const week = Math.ceil(
    (((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7
  );

  return { year: utcDate.getUTCFullYear(), week };
};

const getReportingWeekParts = (date: Date, weekStartsOn: WeekStartsOn) => {
  const weekStart = getReportingWeekStart(date, weekStartsOn);
  const weekNumberDate = new Date(weekStart);
  if (weekStartsOn === "saturday") {
    weekNumberDate.setDate(weekNumberDate.getDate() + 2);
  } else if (weekStartsOn === "sunday") {
    weekNumberDate.setDate(weekNumberDate.getDate() + 1);
  }
  return getIsoWeekParts(weekNumberDate);
};

const getDateFromIsoWeek = (year: number, week: number) => {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay() || 7;
  if (dayOfWeek <= 4) {
    simple.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  } else {
    simple.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  }
  return new Date(simple.getUTCFullYear(), simple.getUTCMonth(), simple.getUTCDate());
};

const getPeriodKey = (date: Date, frequency: Frequency) => {
  if (frequency === "weekly") {
    const iso = getIsoWeekParts(date);
    return `${iso.year}-W${padTwo(iso.week)}`;
  }

  if (frequency === "monthly") {
    return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}`;
  }

  if (frequency === "bimonthly") {
    return `${date.getFullYear()}-B${Math.floor(date.getMonth() / 2) + 1}`;
  }

  if (frequency === "quarterly") {
    return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
  }

  if (frequency === "periodic") {
    return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;
  }

  return `${date.getFullYear()}`;
};

const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getStartOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const getStartOfQuarter = (date: Date) =>
  new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);

const getStartOfYear = (date: Date) => new Date(date.getFullYear(), 0, 1);

const formatDateInputValue = (date: Date) =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

const getDefaultQuickBooksStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 83);
  return formatDateInputValue(date);
};

const QUICKBOOKS_GIVING_KEYWORDS = [
  "tithe",
  "tithes",
  "offering",
  "offerings",
  "giving",
  "contribution",
  "contributions",
  "donation",
  "donations",
  "general fund",
];

const formatQuickBooksAccountLine = (account: QuickBooksAccount) =>
  [
    account.fully_qualified_name || account.name,
    account.account_type ? `type=${account.account_type}` : null,
    account.account_sub_type ? `subtype=${account.account_sub_type}` : null,
    account.classification ? `classification=${account.classification}` : null,
    account.active ? "active" : "inactive",
    `id=${account.id}`,
  ]
    .filter(Boolean)
    .join(" | ");

const formatQuickBooksDimensionLine = (dimension: QuickBooksDimension) =>
  [
    dimension.fully_qualified_name || dimension.name,
    dimension.active ? "active" : "inactive",
    `id=${dimension.id}`,
  ]
    .filter(Boolean)
    .join(" | ");

const formatQuickBooksPnLLocationLine = (location: QuickBooksProfitAndLossLocation) =>
  [
    location.name,
    `income=${formatMetricValue(location.income, "currency")}`,
    `expenses=${formatMetricValue(location.expenses, "currency")}`,
    `net=${formatMetricValue(location.net_income, "currency")}`,
  ].join(" | ");

const formatQuickBooksPnLClassLine = (quickBooksClass: QuickBooksProfitAndLossClass) =>
  [
    quickBooksClass.name,
    `income=${formatMetricValue(quickBooksClass.income, "currency")}`,
    `expenses=${formatMetricValue(quickBooksClass.expenses, "currency")}`,
    `net=${formatMetricValue(quickBooksClass.net_income, "currency")}`,
  ].join(" | ");

const isLikelyQuickBooksGivingAccount = (account: QuickBooksAccount) => {
  const searchable = `${account.name} ${account.fully_qualified_name || ""} ${account.account_type || ""} ${
    account.account_sub_type || ""
  }`.toLowerCase();
  const isIncome =
    account.account_type === "Income" ||
    account.account_type === "Other Income" ||
    account.classification === "Revenue";
  return isIncome && QUICKBOOKS_GIVING_KEYWORDS.some((keyword) => searchable.includes(keyword));
};

const isQuickBooksExpenseAccount = (account: QuickBooksAccount) =>
  account.classification === "Expense" ||
  account.account_type === "Expense" ||
  account.account_type === "Other Expense" ||
  account.account_type === "Cost of Goods Sold";

const isQuickBooksIncomeAccount = (account: QuickBooksAccount) =>
  account.classification === "Revenue" ||
  account.account_type === "Income" ||
  account.account_type === "Other Income";

const isQuickBooksBankAccount = (account: QuickBooksAccount) =>
  account.account_type === "Bank" ||
  account.account_sub_type === "Checking" ||
  account.account_sub_type === "Savings";

const isQuickBooksSavingsAccount = (account: QuickBooksAccount) => {
  const searchable = `${account.name} ${account.fully_qualified_name || ""} ${account.account_type || ""} ${
    account.account_sub_type || ""
  }`.toLowerCase();
  return (
    account.account_sub_type === "Savings" ||
    account.account_sub_type === "MoneyMarket" ||
    (account.account_type === "Bank" && searchable.includes("saving")) ||
    (account.account_type === "Bank" && searchable.includes("money market")) ||
    (account.account_type === "Bank" && /\bmma\b/.test(searchable))
  );
};

const countQuickBooksAccountsBy = (
  accounts: QuickBooksAccount[],
  getKey: (account: QuickBooksAccount) => string | null | undefined
) => {
  const counts = new Map<string, number>();
  accounts.forEach((account) => {
    const key = getKey(account)?.trim() || "Unspecified";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([key, count]) => `${key}: ${count}`);
};

const formatQuickBooksAccountSection = (
  title: string,
  accounts: QuickBooksAccount[],
  emptyText = "None found"
) => [
  "",
  `${title} (${accounts.length})`,
  ...(accounts.length > 0 ? accounts.map(formatQuickBooksAccountLine) : [emptyText]),
];

const normalizeFinanceMatchText = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "");

const getCcbGivingCampusGroupForKpiLabel = (label: string) => {
  const normalized = normalizeFinanceMatchText(label);
  if (["bethelsouthgiving", "southgiving"].includes(normalized)) return "south";
  if (["bethelsonrisegiving", "sonrisegiving"].includes(normalized)) return "sonrise";
  if (["bethelqcgiving", "queencreekgiving", "qcgiving"].includes(normalized)) return "qc";
  if (
    [
      "bethemanueltitheofferings",
      "bethemanuelgiving",
      "bethelemanueltitheofferings",
      "bethelemanuelgiving",
    ].includes(normalized) ||
    (normalized.includes("emanuel") &&
      (normalized.includes("giving") || normalized.includes("titheofferings")))
  ) {
    return "beth-emanuel";
  }
  return null;
};

const isCcbCampusInGivingGroup = (campusName: string, group: string) => {
  const normalized = campusName.toLowerCase();
  if (group === "south") return normalized.includes("south") || normalized.includes("chandler");
  if (group === "sonrise") return normalized.includes("sonrise");
  if (group === "qc") return normalized.includes("queen creek") || normalized.includes(" qc");
  if (group === "beth-emanuel") return normalized.includes("emanuel");
  return false;
};

const getAggregationStart = (date: Date, aggregationMode: AggregationMode) => {
  switch (aggregationMode) {
    case "mtd":
      return getStartOfMonth(date);
    case "qtd":
      return getStartOfQuarter(date);
    case "ytd":
      return getStartOfYear(date);
    default:
      return null;
  }
};

const getPeriodRange = (frequency: Frequency, periodKey: string) => {
  if (frequency === "weekly") {
    const match = /^(\d{4})-W(\d{2})$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, weekString] = match;
    const year = Number(yearString);
    const week = Number(weekString);
    const start = getDateFromIsoWeek(year, week);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
  }

  if (frequency === "monthly") {
    const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, monthString] = match;
    const year = Number(yearString);
    const month = Number(monthString) - 1;
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { start, end };
  }

  if (frequency === "bimonthly") {
    const match = /^(\d{4})-B([1-6])$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, periodString] = match;
    const year = Number(yearString);
    const period = Number(periodString) - 1;
    const startMonth = period * 2;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 2, 0);
    return { start, end };
  }

  if (frequency === "quarterly") {
    const match = /^(\d{4})-Q([1-4])$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, quarterString] = match;
    const year = Number(yearString);
    const quarter = Number(quarterString) - 1;
    const startMonth = quarter * 3;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 3, 0);
    return { start, end };
  }

  if (frequency === "yearly") {
    const year = Number(periodKey);
    if (Number.isNaN(year)) return null;
    return {
      start: new Date(year, 0, 1),
      end: new Date(year, 11, 31),
    };
  }

  const parsed = new Date(`${periodKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return { start: parsed, end: parsed };
};

const metricHistoryFrequencies: Frequency[] = ["weekly", "monthly", "bimonthly", "quarterly", "yearly", "periodic"];

const getMetricHistoryEntryRange = (historyKey: string) => {
  const [periodKey, scopeKey] = historyKey.split("::");
  if (!periodKey || !scopeKey) return null;
  for (const frequency of metricHistoryFrequencies) {
    const range = getPeriodRange(frequency, periodKey);
    if (range) return range;
  }
  return null;
};

const clearMetricHistoryRange = (
  history: MetricHistory,
  startDate: string,
  endDate: string
) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { ...history };
  }

  const nextHistory = { ...history };
  Object.keys(nextHistory).forEach((key) => {
    const range = getMetricHistoryEntryRange(key);
    if (!range) return;
    if (range.start <= end && range.end >= start) {
      delete nextHistory[key];
    }
  });
  return nextHistory;
};

const aggregateMetricValues = (values: number[], valueType: ValueType) => {
  if (values.length === 0) return undefined;
  if (valueType === "count" || valueType === "currency") {
    return values.reduce((sum, value) => sum + value, 0);
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const calculateChildValue = (values: Array<number | undefined>, valueType: ValueType) =>
  aggregateMetricValues(
    values.filter((value): value is number => typeof value === "number"),
    valueType
  );

const formatAggregationLabel = (aggregationMode: AggregationMode) => {
  switch (aggregationMode) {
    case "mtd":
      return "MTD";
    case "qtd":
      return "QTD";
    case "ytd":
      return "YTD";
    default:
      return "Current Period";
  }
};

const formatComparisonLabel = (comparisonMode: ComparisonMode) => {
  switch (comparisonMode) {
    case "previous_period":
      return "Previous Period";
    case "same_period_last_year":
      return "Same Period Last Year";
    default:
      return "Target";
  }
};

const formatComparisonMetricLabel = (comparisonMode: ComparisonMode) => {
  switch (comparisonMode) {
    case "previous_period":
      return "Previous";
    case "same_period_last_year":
      return "Previous Year";
    default:
      return "Target";
  }
};

const formatMetricValue = (value: number | undefined, valueType: ValueType) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  if (valueType === "percentage") return `${Number(value.toFixed(2))}%`;
  if (valueType === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (valueType === "rating_1_10") return String(Number(value.toFixed(2)));
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
};

const ApiConnectionStatusBadge = ({
  connected,
  configured,
}: {
  connected: boolean | undefined;
  configured: boolean | undefined;
}) => {
  const label = connected ? "Connected" : configured === false ? "Not set up" : "Not connected";
  const color = connected ? "#22c55e" : "#ef4444";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        border: `1px solid ${connected ? "rgba(34,197,94,0.42)" : "rgba(239,68,68,0.42)"}`,
        borderRadius: 999,
        padding: "5px 9px",
        fontSize: 12,
        fontWeight: 900,
        color: connected ? "#bbf7d0" : "#fecaca",
        background: connected ? "rgba(22,101,52,0.24)" : "rgba(127,29,29,0.26)",
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 9,
          height: 9,
          borderRadius: 999,
          background: color,
          boxShadow: `0 0 0 3px ${connected ? "rgba(34,197,94,0.14)" : "rgba(239,68,68,0.14)"}`,
        }}
      />
      {label}
    </span>
  );
};

const getMetricInputStep = (valueType: ValueType) =>
  valueType === "count" || valueType === "rating_1_10" ? 1 : 0.01;

const getMetricInputPrefix = (valueType: ValueType) => (valueType === "currency" ? "$" : "");

const getMetricInputSuffix = (valueType: ValueType) => (valueType === "percentage" ? "%" : "");

const formatMetricInputValue = (value: number | undefined, valueType: ValueType) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "";
  if (valueType === "percentage" || valueType === "rating_1_10") {
    return String(Number(value.toFixed(2)));
  }
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
};

const parseMetricInputValue = (value: string) => {
  const cleaned = value.replace(/[$,%\s]/g, "").replace(/,/g, "");
  if (!cleaned) return undefined;
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getMetricDisplayFontSize = (text: string) => {
  if (text.length >= 14) return 10;
  if (text.length >= 12) return 11;
  if (text.length >= 10) return 12;
  if (text.length >= 8) return 13;
  if (text.length >= 7) return 15;
  return 17;
};

const getMetricPairFontSize = (leftText: string, rightText: string) =>
  getMetricDisplayFontSize(leftText.length >= rightText.length ? leftText : rightText);

const formatCalculationModeLabel = (mode: KpiCalculationMode) => {
  switch (mode) {
    case "calculated_values":
      return "Calculated from child values";
    case "formula_percentage":
      return "Calculated percentage from KPIs";
    case "influenced_by_children":
      return "Influenced by child performance";
    default:
      return "Direct KPI";
  }
};

const normalizeCalculationMode = (
  value: unknown,
  fallbackIsRollup = false
): KpiCalculationMode => {
  if (
    value === "calculated_values" ||
    value === "formula_percentage" ||
    value === "influenced_by_children" ||
    value === "direct"
  ) {
    return value;
  }
  return fallbackIsRollup ? "influenced_by_children" : "direct";
};

const calculateFormulaPercentage = (
  numerator: number | undefined,
  denominator: number | undefined
) => {
  if (typeof numerator !== "number" || typeof denominator !== "number") return undefined;
  if (Number.isNaN(numerator) || Number.isNaN(denominator) || denominator <= 0) return undefined;
  return (numerator / denominator) * 100;
};

const shiftComparisonDate = (
  referenceDate: Date,
  frequency: Frequency,
  comparisonMode: ComparisonMode
) => {
  const nextDate = new Date(referenceDate);
  if (comparisonMode === "previous_period") {
    switch (frequency) {
      case "weekly":
        nextDate.setDate(nextDate.getDate() - 7);
        return nextDate;
      case "monthly":
        nextDate.setMonth(nextDate.getMonth() - 1);
        return nextDate;
      case "bimonthly":
        nextDate.setMonth(nextDate.getMonth() - 2);
        return nextDate;
      case "quarterly":
        nextDate.setMonth(nextDate.getMonth() - 3);
        return nextDate;
      case "yearly":
        nextDate.setFullYear(nextDate.getFullYear() - 1);
        return nextDate;
      case "periodic":
        nextDate.setDate(nextDate.getDate() - 1);
        return nextDate;
      default:
        return nextDate;
    }
  }

  if (comparisonMode === "same_period_last_year") {
    nextDate.setFullYear(nextDate.getFullYear() - 1);
  }

  return nextDate;
};

const shiftPeriodAnchorDate = (anchorDate: Date, frequency: Frequency, offset: number) => {
  const nextDate = new Date(anchorDate);
  switch (frequency) {
    case "weekly":
      nextDate.setDate(nextDate.getDate() + offset * 7);
      break;
    case "monthly":
      nextDate.setMonth(nextDate.getMonth() + offset);
      break;
    case "bimonthly":
      nextDate.setMonth(nextDate.getMonth() + offset * 2);
      break;
    case "quarterly":
      nextDate.setMonth(nextDate.getMonth() + offset * 3);
      break;
    case "yearly":
      nextDate.setFullYear(nextDate.getFullYear() + offset);
      break;
    case "periodic":
      nextDate.setDate(nextDate.getDate() + offset);
      break;
    default:
      break;
  }
  return nextDate;
};

const shiftPeriodKeyLastYear = (periodKey: string, frequency: Frequency) => {
  if (frequency === "weekly") {
    const match = /^(\d{4})-W(\d{2})$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-W${match[2]}` : periodKey;
  }
  if (frequency === "monthly") {
    const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-${match[2]}` : periodKey;
  }
  if (frequency === "bimonthly") {
    const match = /^(\d{4})-B([1-6])$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-B${match[2]}` : periodKey;
  }
  if (frequency === "quarterly") {
    const match = /^(\d{4})-Q([1-4])$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-Q${match[2]}` : periodKey;
  }
  if (frequency === "yearly") {
    return String(Number(periodKey) - 1);
  }
  const parsed = new Date(`${periodKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return periodKey;
  parsed.setFullYear(parsed.getFullYear() - 1);
  return parsed.toISOString().slice(0, 10);
};

const getPeriodAnchorDate = (date: Date, frequency: Frequency) => {
  const periodKey = getPeriodKey(date, frequency);
  const range = getPeriodRange(frequency, periodKey);
  return range?.start ?? getStartOfDay(date);
};

const formatKpiReportingPeriod = (date: Date, frequency: Frequency) => {
  if (frequency === "weekly") {
    return `Week ending ${date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  }
  if (frequency === "monthly") {
    return `${date.toLocaleDateString(undefined, { month: "long", year: "numeric" })} · Last completed month`;
  }
  if (frequency === "quarterly") {
    return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()} · Last completed quarter`;
  }
  if (frequency === "bimonthly") {
    return `Bimonthly period ${Math.floor(date.getMonth() / 2) + 1}, ${date.getFullYear()}`;
  }
  if (frequency === "yearly") return String(date.getFullYear());
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const getAggregationWindowPeriodKeys = (
  referenceDate: Date,
  frequency: Frequency,
  aggregationMode: AggregationMode
) => {
  if (aggregationMode === "current") {
    return [getPeriodKey(referenceDate, frequency)];
  }

  const aggregationStart = getAggregationStart(referenceDate, aggregationMode);
  if (!aggregationStart) return [getPeriodKey(referenceDate, frequency)];

  const keys: string[] = [];
  let cursor = getPeriodAnchorDate(referenceDate, frequency);

  while (cursor >= aggregationStart) {
    keys.unshift(getPeriodKey(cursor, frequency));
    cursor = shiftPeriodAnchorDate(cursor, frequency, -1);
  }

  return Array.from(new Set(keys.filter(Boolean)));
};

const resolveMetricPersistence = (
  frequency: Frequency,
  _metricPersistence: MetricPersistence
): MetricPersistence => {
  if (frequency === "periodic") return "carry_forward";
  return "period_only";
};

const getScopeKey = (_scope: Scope, _campus: Campus) => "global";

const getMetricKey = (
  date: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus
) => `${getPeriodKey(date, frequency)}::${getScopeKey(scope, campus)}`;

const derivePollActualHistory = (
  pollConfig: PollConfig | null | undefined,
  frequency: Frequency,
  scope: Scope,
  campus: Campus
): MetricHistory => {
  if (!pollConfig) return {};

  const valuesByKey = new Map<string, number[]>();

  pollConfig.responses.forEach((response) => {
    const numericValue =
      typeof response.value === "number" ? response.value : Number(response.value);
    if (Number.isNaN(numericValue)) return;

    const submittedAt = new Date(response.submittedAt);
    if (Number.isNaN(submittedAt.getTime())) return;

    const key = getMetricKey(submittedAt, frequency, scope, campus);
    valuesByKey.set(key, [...(valuesByKey.get(key) ?? []), numericValue]);
  });

  return Object.fromEntries(
    Array.from(valuesByKey.entries()).map(([key, values]) => [
      key,
      values.reduce((sum, value) => sum + value, 0) / values.length,
    ])
  );
};

const isDateInFuture = (referenceDate: Date, eventDate: string) => {
  if (!eventDate) return false;
  const parsedEventDate = new Date(`${eventDate}T00:00:00`);
  if (Number.isNaN(parsedEventDate.getTime())) return false;
  return referenceDate < parsedEventDate;
};

const getDefaultScopeId = (scopeType: ScopeType) => {
  switch (scopeType) {
    case "organization":
      return "entire-organization";
    case "ministry":
      return "general-ministry";
    case "department":
      return "general-department";
    case "service":
      return "general-service";
    default:
      return "";
  }
};

const getValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  _scope: Scope,
  _campus: Campus,
  valueType: ValueType,
  aggregationMode: AggregationMode = "current",
  persistenceMode: MetricPersistence = "period_only"
): number | undefined => {
  const effectivePersistence = resolveMetricPersistence(frequency, persistenceMode);
  const periodKey = getPeriodKey(referenceDate, frequency);
  const filteredEntries = Object.entries(history).filter(([key, value]) => {
    const [entryPeriodKey, entryScopeKey] = key.split("::");
    return Boolean(entryPeriodKey && entryScopeKey && entryPeriodKey <= periodKey && typeof value === "number");
  });

  const getPeriodValue = (targetPeriodKey: string) => {
    const samePeriodEntries = Object.entries(history).filter(([key, value]) => {
      const [entryPeriodKey, entryScopeKey] = key.split("::");
      return entryPeriodKey === targetPeriodKey && Boolean(entryScopeKey) && typeof value === "number";
    });
    const globalEntry = samePeriodEntries.find(([key]) => key.endsWith("::global"));
    if (typeof globalEntry?.[1] === "number") return globalEntry[1];
    return aggregateMetricValues(
      samePeriodEntries.map(([, value]) => value).filter((value): value is number => typeof value === "number"),
      valueType
    );
  };

  const getLatestValue = () => {
    const latestPeriodKey = filteredEntries
      .map(([key]) => key.split("::")[0])
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right))
      .at(-1);
    return latestPeriodKey ? getPeriodValue(latestPeriodKey) : undefined;
  };

  if (aggregationMode !== "current") {
    const windowValues = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode)
      .map((entryPeriodKey) => getPeriodValue(entryPeriodKey))
      .filter((value): value is number => typeof value === "number");

    const aggregatedValue = aggregateMetricValues(windowValues, valueType);
    if (typeof aggregatedValue === "number") {
      return aggregatedValue;
    }

    if (effectivePersistence === "carry_forward") {
      return getLatestValue();
    }

    return undefined;
  }

  if (effectivePersistence === "carry_forward") {
    return getLatestValue();
  }

  return getPeriodValue(periodKey);
};

const getComparisonValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus,
  valueType: ValueType,
  aggregationMode: AggregationMode,
  comparisonMode: ComparisonMode
) => {
  if (comparisonMode === "previous_period" && aggregationMode !== "current") {
    const currentKeys = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode);
    if (currentKeys.length > 0) {
      const firstRange = getPeriodRange(frequency, currentKeys[0]);
      if (firstRange) {
        const previousValues = currentKeys
          .map((_, index) =>
            getValueForContext(
              history,
              shiftPeriodAnchorDate(firstRange.start, frequency, index - currentKeys.length),
              frequency,
              scope,
              campus,
              valueType,
              "current",
              "period_only"
            )
          )
          .filter((value): value is number => typeof value === "number");
        const aggregatedValue = aggregateMetricValues(previousValues, valueType);
        if (typeof aggregatedValue === "number") {
          return aggregatedValue;
        }
      }
    }
  }

  if (comparisonMode === "same_period_last_year") {
    const currentKeys = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode);
    const comparisonKeys = currentKeys.map((periodKey) =>
      shiftPeriodKeyLastYear(periodKey, frequency)
    );
    const comparisonValues = comparisonKeys
      .map((periodKey) =>
        getValueForContext(
          history,
          getPeriodRange(frequency, periodKey)?.start ?? comparisonDate,
          frequency,
          scope,
          campus,
          valueType,
          "current",
          "period_only"
        )
      )
      .filter((value): value is number => typeof value === "number");
    const aggregatedValue = aggregateMetricValues(comparisonValues, valueType);
    if (typeof aggregatedValue === "number") {
      return aggregatedValue;
    }
  }

  const comparisonDate = shiftComparisonDate(referenceDate, frequency, comparisonMode);
  return getValueForContext(
    history,
    comparisonDate,
    frequency,
    scope,
    campus,
    valueType,
    aggregationMode,
    "period_only"
  );
};

const setValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  _scope: Scope,
  _campus: Campus,
  value: number | undefined
): MetricHistory => {
  const key = getMetricKey(referenceDate, frequency, "global", defaultCampus);
  return {
    ...history,
    [key]: value,
  };
};

const shiftReferenceDate = (date: Date, direction: "previous" | "next") => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + (direction === "next" ? 7 : -7));
  return nextDate;
};

const formatReferenceDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

function InlineHelp({
  text,
}: {
  text: string;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <span
      className="nodrag nopan"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        className="nodrag nopan"
        aria-label={text}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: "1px solid #9ca3af",
          background: "#fff",
          color: "#4b5563",
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "help",
          padding: 0,
        }}
      >
        i
      </button>
      {isOpen ? (
        <span
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            width: 220,
            padding: "10px 12px",
            borderRadius: 10,
            background: "#111827",
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.45,
            boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
            zIndex: 40,
            pointerEvents: "none",
          }}
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}

function AskLensesBrand({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`ask-lenses-brand${compact ? " is-compact" : ""}`}>
      <span className="ask-lenses-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M12 2.8a7.7 7.7 0 0 0-6.4 12l-1 4.4 4.3-1.3A7.7 7.7 0 1 0 12 2.8Z" />
          <path d="m12 6.5.85 2.25 2.25.85-2.25.85L12 12.7l-.85-2.25-2.25-.85 2.25-.85L12 6.5Z" />
          <path d="m16.6 12.4.45 1.2 1.2.45-1.2.45-.45 1.2-.45-1.2-1.2-.45 1.2-.45.45-1.2Z" />
        </svg>
      </span>
      <span className="ask-lenses-wordmark">
        <span>Ask</span>
        <span>Lenses</span>
      </span>
    </span>
  );
}

const toTitleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const formatEnumLabel = (value: string) =>
  value
    .split("_")
    .map((part) => toTitleCase(part))
    .join(" ");
const formatValueTypeLabel = (value: ValueType) =>
  value === "rating_1_10" ? "Rating (1-10)" : formatEnumLabel(value);
const formatDataSourceLabel = (value: string) => toTitleCase(value);
const formatWorkStatusLabel = (status: WorkStatus) => formatEnumLabel(status.replace("-", "_"));
const formatWorkPriorityLabel = (priority: WorkPriority | undefined) =>
  formatEnumLabel(priority ?? "normal");
const formatWorkEffortLabel = (effort: WorkEffort | undefined) => formatEnumLabel(effort ?? "medium");
const formatWorkDueDate = (value: string) => {
  if (!value) return "No due date";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "No due date";
  return `Due ${parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};
const workStatusChipStyle = (status: WorkStatus): React.CSSProperties => {
  const palette: Record<WorkStatus, { background: string; color: string; border: string }> = {
    "not-started": { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
    "in-progress": { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    blocked: { background: "#fff1f2", color: "#be123c", border: "#fecdd3" },
    done: { background: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  };
  const colors = palette[status];
  return {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 22,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    padding: "2px 8px",
    background: colors.background,
    color: colors.color,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
  };
};
const workPriorityChipStyle = (priority: WorkPriority | undefined): React.CSSProperties => {
  const palette: Record<WorkPriority, { background: string; color: string; border: string }> = {
    low: { background: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
    normal: { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
    high: { background: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
    critical: { background: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  };
  const colors = palette[priority ?? "normal"];
  return {
    ...workStatusChipStyle("not-started"),
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.color,
  };
};
const workEffortChipStyle = (effort: WorkEffort | undefined): React.CSSProperties => {
  const palette: Record<WorkEffort, { background: string; color: string; border: string }> = {
    light: { background: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
    medium: { background: "#f8fafc", color: "#475569", border: "#e2e8f0" },
    heavy: { background: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
  };
  const colors = palette[effort ?? "medium"];
  return {
    ...workStatusChipStyle("not-started"),
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.color,
  };
};
const matchesSearch = (value: string, search: string) =>
  value.toLowerCase().includes(search.trim().toLowerCase());
const clampRatingValue = (value: number) => Math.min(10, Math.max(1, value));

const buildPollQuestionPrompt = (kpi: {
  label?: string;
  definition?: string;
  measurement?: string;
}) => {
  const label = kpi.label?.trim() || "this KPI";
  const definition = kpi.definition?.trim();
  const definitionLine = definition
    ? `${label} is defined as: ${definition}.`
    : `${label} is the KPI being measured.`;

  return `${definitionLine}\n\nPlease rate from 1-10 how well we are doing in this area, and leave a comment with any evidence or examples that shaped your rating.`;
};

const getPollPublishContext = (_scope?: Scope, _campus?: Campus) => {
  return {
    canPublish: true,
    tone: "info",
    summary: "This poll will run enterprise-wide.",
    detail: "Responses will update this KPI's enterprise-wide result.",
  };
};

const createTask = (overrides: Partial<TaskItem> = {}): TaskItem => ({
  id: uuidv4(),
  title: "New task",
  owner: "",
  dueDate: "",
  status: "not-started",
  priority: "normal",
  effort: "medium",
  notes: "",
  ...overrides,
});

const cloneTask = (task: TaskItem): TaskItem => ({
  ...task,
  id: uuidv4(),
});

const createInitiative = (
  overrides: Partial<Omit<InitiativeItem, "tasks">> & { tasks?: TaskItem[] } = {}
): InitiativeItem => ({
  id: uuidv4(),
  title: "New initiative",
  owner: "",
  dueDate: "",
  status: "not-started",
  priority: "normal",
  effort: "medium",
  summary: "",
  tasks: [],
  ...overrides,
});

const cloneInitiative = (initiative: InitiativeItem): InitiativeItem => ({
  ...initiative,
  id: uuidv4(),
  tasks: initiative.tasks.map(cloneTask),
});

const clonePollConfig = (pollConfig: PollConfig | null | undefined): PollConfig | null => {
  if (!pollConfig) return null;

  return {
    ...pollConfig,
    id: uuidv4(),
    campaignRootKpiId: undefined,
    linkedKpiIds: [],
    status: "draft",
    publishedAt: undefined,
    responses: [],
    invites: [],
  };
};

const createPollConfig = (kpi?: {
  label?: string;
  definition?: string;
  measurement?: string;
}): PollConfig => ({
  id: uuidv4(),
  campaignRootKpiId: undefined,
  linkedKpiIds: [],
  title: "New KPI Poll",
  description: "",
  targetGroups: [],
  questionPrompt: buildPollQuestionPrompt(kpi ?? {}),
  questionType: "rating",
  isAnonymous: true,
  status: "draft",
  responses: [],
  invites: [],
});

const normalizePollConfig = (
  pollConfig?: (Partial<PollConfig> & { status?: string }) | null
): PollConfig | null => {
  if (!pollConfig) return null;
  const rawStatus = (pollConfig as { status?: string }).status;

  return {
    ...pollConfig,
    id: pollConfig.id ?? uuidv4(),
    campaignRootKpiId:
      typeof pollConfig.campaignRootKpiId === "string" && pollConfig.campaignRootKpiId.trim().length > 0
        ? pollConfig.campaignRootKpiId
        : undefined,
    linkedKpiIds: Array.isArray(pollConfig.linkedKpiIds)
      ? Array.from(
          new Set(
            pollConfig.linkedKpiIds.filter(
              (linkedKpiId): linkedKpiId is string =>
                typeof linkedKpiId === "string" && linkedKpiId.trim().length > 0
            )
          )
        )
      : [],
    title: pollConfig.title ?? "New KPI Poll",
    description: pollConfig.description ?? "",
    questionPrompt: pollConfig.questionPrompt ?? "How would you rate this KPI on a 1-10 scale?",
    questionType: "rating",
    isAnonymous: pollConfig.isAnonymous ?? true,
    responses: Array.isArray(pollConfig.responses)
      ? pollConfig.responses.map((response) => ({
          ...response,
          comment: typeof response.comment === "string" ? response.comment : "",
        }))
      : [],
    invites: Array.isArray(pollConfig.invites) ? pollConfig.invites : [],
    targetGroups:
      Array.isArray(pollConfig.targetGroups) && pollConfig.targetGroups.length > 0
        ? pollConfig.targetGroups
        : pollConfig.targetGroup
          ? [pollConfig.targetGroup]
          : [],
    status: rawStatus === "published" || rawStatus === "active" ? "published" : "draft",
  };
};

const formatPollResponseValue = (value: PollResponseValue) => {
  return String(value);
};

const getPollCampaignKpiIds = (masterKpiId: string, pollConfig?: PollConfig | null) =>
  Array.from(new Set([masterKpiId, ...((pollConfig?.linkedKpiIds ?? []).filter(Boolean))]));

const getPollCampaignSetup = (
  nodes: Node<KpiNodeData>[],
  masterKpiId: string,
  pollConfig: PollConfig | null | undefined,
  referenceDate: Date,
  campus: Campus
) => {
  const campaignKpis = getPollCampaignKpiIds(masterKpiId, pollConfig)
    .map((kpiId) => nodes.find((node) => node.id === kpiId))
    .filter((node): node is Node<KpiNodeData> => Boolean(node));

  const autoConvertKpis = campaignKpis.filter((node) => node.data.dataSource !== "Poll");
  const missingTargetKpis = campaignKpis.filter((node) => {
    const targetValue = getValueForContext(
      node.data.targetHistory ?? {},
      referenceDate,
      node.data.frequency,
      node.data.scope,
      campus,
      node.data.valueType,
      "current",
      node.data.metricPersistence
    );
    return typeof targetValue !== "number";
  });

  const frequencyGroups = campaignKpis.reduce<Record<Frequency, string[]>>((groups, node) => {
    const frequency = node.data.frequency;
    const label = node.data.label || "Untitled KPI";
    if (!groups[frequency]) {
      groups[frequency] = [];
    }
    groups[frequency].push(label);
    return groups;
  }, {} as Record<Frequency, string[]>);

  const mismatchedFrequencies = Object.keys(frequencyGroups).length > 1;

  return {
    campaignKpis,
    autoConvertKpis,
    missingTargetKpis,
    frequencyGroups,
    mismatchedFrequencies,
  };
};

const summarizePollComments = (comments: string[]) => {
  const cleanedComments = comments
    .map((comment) => comment.trim())
    .filter((comment) => comment.length > 0);

  if (cleanedComments.length === 0) {
    return null;
  }

  const splitIntoClauses = (comment: string) =>
    comment
      .replace(/\s+(but|however|although|though|while)\s+/gi, ". ")
      .split(/[.!?;]+/)
      .map((part) => part.trim())
      .filter((part) => part.length >= 12);

  const positiveSignals = [
    "clear",
    "helpful",
    "strong",
    "good",
    "great",
    "love",
    "encouraging",
    "effective",
    "better",
    "improved",
  ];
  const concernSignals = [
    "confusing",
    "unclear",
    "hard",
    "difficult",
    "frustrating",
    "slow",
    "missing",
    "need",
    "lack",
    "weak",
    "poor",
    "performance",
    "performative",
    "staged",
    "stagey",
    "spotlight",
  ];
  const themePatterns = [
    { label: "clarity", words: ["clear", "clarity", "understand", "understanding", "confusing", "unclear"] },
    { label: "communication", words: ["communication", "communicate", "updates", "follow-up", "follow up"] },
    { label: "support", words: ["support", "care", "help", "encouragement", "pastor"] },
    { label: "consistency", words: ["consistent", "consistency", "regular", "follow through", "routine"] },
    { label: "engagement", words: ["engaged", "engagement", "participation", "connected", "involved"] },
    { label: "timeliness", words: ["timely", "late", "slow", "responsive", "response"] },
    { label: "measurement", words: ["measure", "measured", "metrics", "score", "rating"] },
  ];

  const positiveClauses: string[] = [];
  const concernClauses: string[] = [];
  const themeCounts = new Map<string, number>();

  cleanedComments.forEach((comment) => {
    splitIntoClauses(comment).forEach((clause) => {
      const normalized = clause.toLowerCase();
      const hasPositive = positiveSignals.some((word) => normalized.includes(word));
      const hasConcern = concernSignals.some((word) => normalized.includes(word));

      if (hasPositive && positiveClauses.length < 3) {
        positiveClauses.push(clause);
      }
      if (hasConcern && concernClauses.length < 3) {
        concernClauses.push(clause);
      }

      themePatterns.forEach((theme) => {
        if (theme.words.some((word) => normalized.includes(word))) {
          themeCounts.set(theme.label, (themeCounts.get(theme.label) ?? 0) + 1);
        }
      });
    });
  });

  const topThemes = Array.from(themeCounts.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .map(([label]) => label);

  const themeProfiles: Record<string, { title: string; detail: string }> = {
    clarity: {
      title: "Clarity is coming through",
      detail: "People are reacting to whether expectations, direction, and communication feel easy to understand.",
    },
    communication: {
      title: "Communication is shaping trust",
      detail: "Updates, follow-through, and how information is shared are clearly affecting how people experience this KPI.",
    },
    support: {
      title: "Support is being felt",
      detail: "Comments suggest people notice encouragement, care, and whether help is meaningfully present.",
    },
    consistency: {
      title: "Consistency is becoming part of the story",
      detail: "People are paying attention to whether the experience feels steady, repeatable, and dependable over time.",
    },
    engagement: {
      title: "Engagement is strong",
      detail: "People are noticing participation, involvement, and responsiveness rather than passivity.",
    },
    timeliness: {
      title: "Responsiveness matters",
      detail: "The pace of follow-up and whether people experience things as timely is part of the feedback pattern.",
    },
    measurement: {
      title: "People are reacting to how this is measured",
      detail: "The comments are not only about the outcome, but also about what is being noticed, scored, or counted.",
    },
  };

  const formatSentence = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const sentence = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
  };

  const shortSummaryLead =
    positiveClauses.length > concernClauses.length
      ? concernClauses.length > 0
        ? "Comments are mostly positive, with a few concerns to watch."
        : "Comments are mostly positive."
      : concernClauses.length > positiveClauses.length
        ? positiveClauses.length > 0
          ? "Comments are mixed, but concerns are coming through clearly."
          : "Comments are mostly concerned."
        : positiveClauses.length > 0 || concernClauses.length > 0
          ? "Comments are mixed."
          : "Comments are varied.";

  const shortSummary =
    topThemes.length > 0
      ? `${shortSummaryLead} The main theme is ${topThemes[0]}.`
      : shortSummaryLead;

  const coreThemeSections =
    topThemes.length > 0
      ? topThemes.map((theme, index) => {
          const profile = themeProfiles[theme] ?? {
            title: `${theme.charAt(0).toUpperCase() + theme.slice(1)} is showing up in the feedback`,
            detail: "This theme is appearing often enough to shape the overall read of the comments.",
          };
          const example = positiveClauses[index] ?? concernClauses[index] ?? null;
          return [
            `${index + 1}. ${profile.title}`,
            `- ${profile.detail}`,
            example ? `- Example signal: ${formatSentence(example)}` : null,
          ]
            .filter(Boolean)
            .join("\n");
        })
      : [
          [
            "1. The feedback is varied",
            "- There is not one dominant theme yet, so the comments are better read as directional than definitive.",
            `- Example signal: ${formatSentence(cleanedComments[0])}`,
          ].join("\n"),
        ];

  const keyTensionSection =
    positiveClauses.length > 0 && concernClauses.length > 0
      ? [
          "Key Tension",
          `People are seeing real value here, but there is also a caution around how ${topThemes[0] ?? "the experience"} is landing.`,
          `- Positive side: ${formatSentence(positiveClauses[0])}`,
          `- Risk side: ${formatSentence(concernClauses[0])}`,
        ].join("\n")
      : positiveClauses.length > 1
        ? [
            "Key Tension",
            "There is not a major negative thread yet, but there is still a need to protect what people are responding well to.",
            `- Reinforce: ${formatSentence(positiveClauses[0])}`,
            `- Also preserve: ${formatSentence(positiveClauses[1])}`,
          ].join("\n")
        : [
            "Key Tension",
            "No single tension dominates the feedback yet.",
          ].join("\n");

  const strategicInsightSection =
    concernClauses.length > 0
      ? [
          "Strategic Insight",
          "This looks more like refinement than overhaul.",
          "- Keep reinforcing what people already value.",
          `- Address this concern early: ${formatSentence(concernClauses[0])}`,
        ].join("\n")
      : [
          "Strategic Insight",
          "This reads more like momentum to build on than a problem to solve.",
          positiveClauses.length > 0
            ? `- Keep leaning into: ${formatSentence(positiveClauses[0])}`
            : "- Keep reinforcing the signals that are already landing well.",
        ].join("\n");

  const bottomLineSection =
    positiveClauses.length > concernClauses.length
      ? concernClauses.length > 0
        ? "Bottom Line\nThe feedback is largely encouraging, with one or two concerns worth shepherding before they grow."
        : "Bottom Line\nThe feedback is clearly encouraging and points to something healthy worth strengthening."
      : concernClauses.length > positiveClauses.length
        ? "Bottom Line\nThe feedback is mixed and caution is rising enough that it should be addressed directly."
        : "Bottom Line\nThe feedback is mixed, but it gives a useful read on where to tune rather than where to rebuild.";

  const longSummary = [
    "Core Themes",
    ...coreThemeSections.flatMap((section, index) => (index > 0 ? ["", section] : [section])),
    "",
    keyTensionSection,
    "",
    strategicInsightSection,
    "",
    bottomLineSection,
  ].join("\n");

  return {
    shortSummary,
    longSummary,
  };
};

const getPollRecipientLabel = (recipient: PollRecipient) => {
  const fullName = [recipient.firstName?.trim(), recipient.lastName?.trim()]
    .filter(Boolean)
    .join(" ");
  return fullName || recipient.name?.trim() || "Unnamed recipient";
};

const createToken = () =>
  `${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const stripNodeDataForPersistence = (data: KpiNodeData) => ({
  label: data.label,
  targetHistory: data.targetHistory,
  actualHistory: data.actualHistory,
  apiActualEvidence: data.apiActualEvidence ?? null,
  unit: data.unit ?? "",
  frequency: data.frequency,
  scope: data.scope,
  performanceDirection: data.performanceDirection,
  supportsCumulative: data.supportsCumulative,
  metricPersistence: data.metricPersistence,
  calculationMode: data.calculationMode,
  eventDate: data.eventDate,
  definition: data.definition,
  measurement: data.measurement,
  owner: data.owner,
  dataOwner: data.dataOwner,
  dataSource: data.dataSource,
  apiConfig: data.apiConfig ?? null,
  valueType: data.valueType,
  formulaNumeratorKpiId: data.formulaNumeratorKpiId ?? "",
  formulaDenominatorKpiId: data.formulaDenominatorKpiId ?? "",
  updateNotes: data.updateNotes,
  pollConfig: data.pollConfig ?? null,
  restrictedFields: data.restrictedFields ?? {},
  initiatives: data.initiatives,
  tasks: data.tasks,
  lenses: data.lenses,
  lensLabels: data.lensLabels,
  positionsByLens: data.positionsByLens,
  collapsedInLenses: data.collapsedInLenses,
  isRollup: data.isRollup,
});

const hydrateNodeDataFromPersistence = (raw: Partial<KpiNodeData>): KpiNodeData => {
  const calculationMode = normalizeCalculationMode(raw.calculationMode, raw.isRollup ?? false);

  return {
  label: raw.label ?? "New KPI",
  target: undefined,
  targetHistory: raw.targetHistory ?? {},
  actual: undefined,
  actualHistory: raw.actualHistory ?? {},
  apiActualEvidence: raw.apiActualEvidence ?? null,
  frequency: raw.frequency ?? "weekly",
  scope: "global",
  performanceDirection: raw.performanceDirection ?? "higher_is_better",
  supportsCumulative: raw.supportsCumulative ?? false,
  metricPersistence:
    raw.metricPersistence === "carry_forward" ||
    raw.metricPersistence === "period_only"
      ? raw.metricPersistence
      : raw.frequency === "periodic" || raw.dataSource === "Poll" || Boolean(raw.eventDate)
        ? "carry_forward"
        : "period_only",
  calculationMode,
  eventDate: raw.eventDate ?? "",
  definition: raw.definition ?? "Describe what this KPI is intended to measure.",
  measurement: raw.measurement ?? "Describe exactly how this KPI is counted or calculated.",
  owner: raw.owner ?? "Assign an owner",
  dataOwner: raw.dataOwner ?? "",
  unit: raw.dataSource === "Poll" ? "" : raw.unit ?? "",
  dataSource:
    typeof raw.dataSource === "string" && dataSourceOptions.includes(raw.dataSource as (typeof dataSourceOptions)[number])
      ? raw.dataSource
      : "Manual",
  apiConfig: normalizeKpiApiConfig(raw.apiConfig),
  valueType: getNormalizedValueType(raw.dataSource, raw.valueType),
  formulaNumeratorKpiId: raw.formulaNumeratorKpiId ?? "",
  formulaDenominatorKpiId: raw.formulaDenominatorKpiId ?? "",
  updateNotes: raw.updateNotes ?? "Add any timing, validation, or reporting notes here.",
  pollConfig: normalizePollConfig(raw.pollConfig),
  restrictedFields: raw.restrictedFields ?? {},
  initiatives: raw.initiatives ?? [],
  tasks: raw.tasks ?? [],
  lenses: raw.lenses ?? [],
  lensLabels: raw.lensLabels ?? [],
  positionsByLens: raw.positionsByLens ?? {},
  collapsedInLenses: raw.collapsedInLenses ?? [],
  status: raw.status ?? "neutral",
  metricsRestricted: raw.metricsRestricted ?? false,
  isRollup: calculationMode !== "direct",
  metricsEditable: true,
  metricNote: undefined,
  statusNote: undefined,
  colorActual: raw.colorActual,
  comparisonMode: "target",
  rollupWarning: undefined,
  childCount: 0,
  childrenCollapsed: false,
  isInitiativeRailOpen: false,
  onOpenDetails: noopOpenDetails,
  onSelectNode: noopSelectNode,
  onToggleInitiativeRail: noopToggleInitiativeRail,
  onLabelChange: noopLabelChange,
  onMetricChange: noopMetricChange,
  onConfigChange: noopConfigChange,
  onDetailChange: noopDetailChange,
  onApiConfigChange: noopApiConfigChange,
  onRestrictedFieldsChange: noopRestrictedFieldsChange,
  onToggleRollup: noopToggleRollup,
  onAddChild: noopAddChild,
  onToggleChildren: noopToggleChildren,
  };
};

const stopCanvasDrag = (e: React.MouseEvent | React.PointerEvent) => {
  e.stopPropagation();
};

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target.isContentEditable
  );
};

function DetailField({
  label,
  value,
  onChange,
  multiline = false,
  readOnly = false,
  onBlur,
  onKeyDown,
  listId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  readOnly?: boolean;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  listId?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {multiline ? (
        <textarea
          className="nodrag nopan"
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            minHeight: 96,
            resize: "vertical",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 14,
            color: "#111827",
            lineHeight: 1.5,
            background: readOnly ? "#f9fafb" : "#fff",
          }}
        />
      ) : (
        <input
          className="nodrag nopan"
          value={value}
          list={listId}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 14,
            color: "#111827",
            lineHeight: 1.5,
            background: readOnly ? "#f9fafb" : "#fff",
          }}
        />
      )}
    </div>
  );
}

function WorkItemField({
  value,
  onChange,
  placeholder,
  listId,
  variant = "default",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  listId?: string;
  variant?: "default" | "title" | "taskTitle" | "meta" | "body";
}) {
  const isTitle = variant === "title";
  const isTaskTitle = variant === "taskTitle";
  const isMeta = variant === "meta";
  const isBody = variant === "body";
  return (
    <input
      className="nodrag nopan"
      value={value}
      list={listId}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onMouseDown={stopCanvasDrag}
      onPointerDown={stopCanvasDrag}
      style={{
        width: isMeta ? 120 : "100%",
        boxSizing: "border-box",
        border: isTitle || isTaskTitle || isMeta ? "1px solid transparent" : "1px solid #e5e7eb",
        borderBottom: isMeta ? "1px solid #e5e7eb" : undefined,
        borderRadius: isTitle || isTaskTitle ? 8 : 10,
        padding: isTitle ? "4px 0" : isTaskTitle ? "4px 0" : isMeta ? "3px 0" : "8px 10px",
        fontSize: isTitle ? 18 : 12,
        fontWeight: isTitle ? 800 : isTaskTitle || isMeta ? 700 : 500,
        color: isMeta ? "#4b5563" : "#111827",
        background: isTitle || isTaskTitle || isMeta ? "transparent" : isBody ? "#f8fafc" : "#f9fafb",
        lineHeight: 1.35,
      }}
    />
  );
}

function StringListEditor({
  title,
  items,
  onChange,
}: {
  title: string;
  items: string[];
  onChange: (nextItems: string[]) => void;
}) {
  const normalizeItems = (nextItems: string[]) =>
    nextItems.map((item) => item.trim()).filter((item) => item.length > 0);

  return (
    <div
      style={{
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: "#111827",
          marginBottom: 10,
        }}
      >
        {title}
      </div>

      <div
        style={{
          display: "grid",
          gap: 8,
        }}
      >
        {items.map((item, index) => (
          <div
            key={`${title}-${index}`}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
            }}
          >
            <input
              className="nodrag nopan"
              value={item}
              onChange={(e) =>
                onChange(items.map((current, currentIndex) =>
                  currentIndex === index ? e.target.value : current
                ))
              }
              onBlur={() => onChange(normalizeItems(items))}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
                color: "#111827",
                background: "#fff",
              }}
            />
            <button
              type="button"
              className="nodrag nopan"
              onClick={() => onChange(items.filter((_, currentIndex) => currentIndex !== index))}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "1px solid #dc2626",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
                fontWeight: 700,
                background: "#fff5f5",
                color: "#b91c1c",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="nodrag nopan ask-lenses-trigger"
        onClick={() => onChange([...items, ""])}
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          marginTop: 10,
          border: "1px solid #d1d5db",
          borderRadius: 10,
          padding: "8px 10px",
          fontSize: 13,
          fontWeight: 700,
          background: "#fff",
          color: "#111827",
          cursor: "pointer",
        }}
      >
        + Add
      </button>
    </div>
  );
}

function PollRecipientsEditor({
  recipients,
  pollAudienceGroups,
  onChange,
  onClose,
}: {
  recipients: PollRecipient[];
  pollAudienceGroups: string[];
  onChange: (nextRecipients: PollRecipient[]) => void;
  onClose: () => void;
}) {
  const getRecipientLabel = (recipient: PollRecipient) => {
    const firstName = recipient.firstName.trim();
    const lastName = recipient.lastName.trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    return fullName || recipient.name?.trim() || "Unnamed recipient";
  };

  const normalizeRecipient = (recipient: PollRecipient): PollRecipient => ({
    ...recipient,
    firstName: recipient.firstName.trim(),
    lastName: recipient.lastName.trim(),
    email: recipient.email.trim(),
    groups: Array.from(
      new Set(recipient.groups.map((group) => group.trim()).filter((group) => group.length > 0))
    ),
  });

  const updateRecipient = (
    recipientId: string,
    updater: (recipient: PollRecipient) => PollRecipient
  ) => {
    onChange(
      recipients.map((recipient) =>
        recipient.id === recipientId ? normalizeRecipient(updater(recipient)) : recipient
      )
    );
  };

  const toggleGroup = (recipientId: string, groupName: string) => {
    updateRecipient(recipientId, (recipient) => ({
      ...recipient,
      groups: recipient.groups.includes(groupName)
        ? recipient.groups.filter((group) => group !== groupName)
        : [...recipient.groups, groupName],
    }));
  };

  return (
    <div
      className="nodrag nopan"
      onClick={onClose}
      onMouseDown={stopCanvasDrag}
      onPointerDown={stopCanvasDrag}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 24, 39, 0.48)",
        display: "grid",
        placeItems: "center",
        padding: 24,
        zIndex: 1200,
      }}
    >
      <div>
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(1120px, 100%)",
            maxHeight: "min(88vh, 960px)",
            overflow: "auto",
            background: "#fff",
            border: "2px solid #111827",
            borderRadius: 20,
            padding: 22,
            display: "grid",
            gap: 16,
            boxShadow: "0 30px 70px rgba(0,0,0,0.24)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              flexWrap: "wrap",
              position: "sticky",
              top: -22,
              margin: "-22px -22px 16px",
              padding: "22px 22px 16px",
              background: "rgba(255,255,255,0.96)",
              borderBottom: "1px solid #e5e7eb",
              zIndex: 2,
              backdropFilter: "blur(6px)",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#111827",
                  marginBottom: 4,
                }}
              >
                Poll Recipients
              </div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Add people here, assign them to one or more groups, and they will be available for
                poll invitations.
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6, fontWeight: 700 }}>
                Changes save automatically.
              </div>
            </div>
            <button
              type="button"
              className="nodrag nopan"
              onClick={onClose}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 12,
                padding: "10px 14px",
                fontSize: 14,
                fontWeight: 700,
                background: "#fff",
                color: "#111827",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            {recipients.map((recipient) => (
          <div
            key={recipient.id}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 16,
              background: "#fff",
              padding: 16,
              display: "grid",
              gap: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                {getRecipientLabel(recipient)}
              </div>
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => onChange(recipients.filter((item) => item.id !== recipient.id))}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #dc2626",
                  borderRadius: 10,
                  padding: "8px 10px",
                  fontSize: 13,
                  fontWeight: 700,
                  background: "#fff5f5",
                  color: "#b91c1c",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1.2fr",
                gap: 12,
                alignItems: "start",
              }}
            >
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  First Name
                </span>
                <input
                  className="nodrag nopan"
                  value={recipient.firstName}
                  placeholder="First name"
                  onChange={(e) =>
                    updateRecipient(recipient.id, (current) => ({
                      ...current,
                      firstName: e.target.value,
                    }))
                  }
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontSize: 16,
                    color: "#111827",
                    background: "#fff",
                  }}
                />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Last Name
                </span>
                <input
                  className="nodrag nopan"
                  value={recipient.lastName}
                  placeholder="Last name"
                  onChange={(e) =>
                    updateRecipient(recipient.id, (current) => ({
                      ...current,
                      lastName: e.target.value,
                    }))
                  }
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontSize: 16,
                    color: "#111827",
                    background: "#fff",
                  }}
                />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Email
                </span>
                <input
                  className="nodrag nopan"
                  value={recipient.email}
                  placeholder="Email address"
                  onChange={(e) =>
                    updateRecipient(recipient.id, (current) => ({
                      ...current,
                      email: e.target.value,
                    }))
                  }
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontSize: 16,
                    color: "#111827",
                    background: "#fff",
                  }}
                />
              </label>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Groups</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 8,
                }}
              >
                {pollAudienceGroups.map((group) => {
                  const isSelected = recipient.groups.includes(group);
                  return (
                    <label
                      key={`${recipient.id}-${group}`}
                      className="nodrag nopan"
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        border: isSelected ? "2px solid #2563eb" : "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 10px",
                        background: isSelected ? "#eff6ff" : "#fff",
                        fontSize: 13,
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleGroup(recipient.id, group)}
                      />
                      {group}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
          </div>

          <button
            type="button"
            className="nodrag nopan"
            onClick={() =>
              onChange([
                ...recipients,
                { id: uuidv4(), firstName: "", lastName: "", email: "", groups: [] },
              ])
            }
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 12,
              padding: "10px 12px",
              fontSize: 14,
              fontWeight: 700,
              background: "#fff",
              color: "#111827",
              cursor: "pointer",
              justifySelf: "start",
            }}
          >
            + Add Recipient
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiNode({ id, data, selected }: NodeProps<KpiNodeData>) {
  const metricsDisabled = data.calculationMode !== "direct" || !data.metricsEditable;
  const quickActualEditable = !metricsDisabled && data.dataSource !== "Poll";
  const cardColor = kpiDisplayColor(data.valueType, data.actual, data.colorActual, data.status);
  const cardSurface = "#080d14";
  const cardPanel = "#111827";
  const cardPanelSoft = "rgba(31,41,55,0.76)";
  const cardBorder = "rgba(148,163,184,0.24)";
  const cardText = "#f8fafc";
  const cardMuted = "#9ca3af";
  const cardSubtle = "#cbd5e1";
  const targetDisplayValue = formatMetricValue(data.target, data.valueType);
  const actualDisplayValue = formatMetricValue(data.actual, data.valueType);
  const actualInputDisplayValue = formatMetricInputValue(data.actual, data.valueType);
  const pollCompletion =
    data.dataSource === "Poll" ? getPollCompletion(data.pollConfig?.invites) : null;
  const metricValueFontSize = getMetricPairFontSize(targetDisplayValue, actualDisplayValue);
  const isCurrencyKpi = data.valueType === "currency";
  const [isWorkDrawerOpen, setIsWorkDrawerOpen] = React.useState(false);
  const totalWorkItems = data.tasks.length + data.initiatives.length;
  const hasWorkItems = totalWorkItems > 0;
  const visibleKpiTasks = data.tasks.slice(0, 3);
  const visibleInitiatives = data.initiatives.slice(0, 3);
  const activeInitiatives = data.initiatives.filter((initiative) => initiative.status !== "done");
  const activeKpiTasks = data.tasks.filter((task) => task.status !== "done");
  const activeRailItemsCount = activeInitiatives.length + activeKpiTasks.length;
  const maxRailItems = 4;
  const railInitiatives = data.initiatives.slice(0, data.tasks.length > 0 ? 2 : maxRailItems);
  const railKpiTasks = data.tasks.slice(0, maxRailItems - railInitiatives.length);
  const visibleRailItemsCount = railInitiatives.length + railKpiTasks.length;
  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        "button,input,select,textarea,label,a,[role='button'],[data-kpi-interactive='true']"
      )
    ) {
      return;
    }

    event.stopPropagation();
    data.onSelectNode(id, event.shiftKey);
  };

  return (
    <div
      className={`map-kpi-card${selected ? " is-selected" : ""}`}
      onClick={handleCardClick}
      style={{
        background: hexToRgba(cardColor, 0.075),
        border: `1px solid ${hexToRgba(cardColor, 0.3)}`,
        borderRadius: 16,
        padding: 4,
        width: isCurrencyKpi ? 264 : 236,
        boxSizing: "border-box",
        position: "relative",
        boxShadow: selected
          ? "0 0 0 1px rgba(96,165,250,0.9), 0 0 0 4px rgba(37,99,235,0.16), 0 20px 44px rgba(0,0,0,0.34)"
          : undefined,
        transform: selected ? "translateY(-1px)" : "none",
      }}
    >
      <span
        className="map-kpi-status-accent"
        aria-hidden="true"
        style={{ background: hexToRgba(cardColor, 0.72) }}
      />
      {hasWorkItems ? (
        <>
          <button
            type="button"
            className="nodrag nopan"
            data-kpi-interactive="true"
            aria-label={`${data.isInitiativeRailOpen ? "Hide" : "Show"} work items for ${data.label}`}
            onClick={(event) => {
              event.stopPropagation();
              data.onToggleInitiativeRail(id);
            }}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              position: "absolute",
              top: 18,
              right: -24,
              zIndex: 3,
              width: 30,
              minHeight: 78,
              border: "1px solid rgba(251,146,60,0.5)",
              borderRadius: "0 12px 12px 0",
              background: data.isInitiativeRailOpen
                ? "linear-gradient(180deg, rgba(251,146,60,0.34), rgba(15,23,42,0.96))"
                : "linear-gradient(180deg, rgba(251,146,60,0.22), rgba(15,23,42,0.9))",
              color: "#fed7aa",
              boxShadow: "0 0 24px rgba(251,146,60,0.22)",
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              padding: 0,
            }}
          >
            <span
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {totalWorkItems} work
            </span>
          </button>
          <div
            className="nodrag nopan"
            data-kpi-interactive="true"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              position: "absolute",
              left: "calc(100% + 18px)",
              top: 8,
              width: 260,
              maxHeight: 360,
              zIndex: 2,
              opacity: data.isInitiativeRailOpen ? 1 : 0,
              transform: data.isInitiativeRailOpen ? "translateX(0)" : "translateX(-8px)",
              pointerEvents: data.isInitiativeRailOpen ? "auto" : "none",
              transition: "opacity 260ms cubic-bezier(0.22, 1, 0.36, 1), transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 42,
                left: -18,
                width: 18,
                height: 1,
                background: "linear-gradient(90deg, rgba(251,146,60,0), rgba(251,146,60,0.8))",
                boxShadow: "0 0 12px rgba(251,146,60,0.38)",
              }}
            />
            <div
              style={{
                border: "1px solid rgba(251,146,60,0.38)",
                borderRadius: 14,
                background:
                  "linear-gradient(180deg, rgba(15,23,42,0.97), rgba(8,13,20,0.95))",
                boxShadow: "0 18px 34px rgba(0,0,0,0.34), 0 0 34px rgba(251,146,60,0.12)",
                padding: 10,
                display: "grid",
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 900,
                      color: "#fb923c",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    Work Rail
                  </div>
                  <div style={{ color: cardText, fontSize: 13, fontWeight: 850, marginTop: 2 }}>
                    {activeRailItemsCount} active item
                    {activeRailItemsCount === 1 ? "" : "s"}
                  </div>
                </div>
                <button
                  type="button"
                  className="nodrag nopan"
                  onClick={(event) => {
                    event.stopPropagation();
                    data.onOpenDetails(id);
                  }}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid rgba(251,146,60,0.42)",
                    borderRadius: 8,
                    background: "rgba(251,146,60,0.12)",
                    color: "#fed7aa",
                    fontSize: 10,
                    fontWeight: 900,
                    padding: "6px 7px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Manage
                </button>
              </div>

              <div style={{ display: "grid", gap: 7 }}>
                {railInitiatives.length > 0 ? (
                  <div style={{ display: "grid", gap: 7 }}>
                    <div
                      style={{
                        color: cardMuted,
                        fontSize: 10,
                        fontWeight: 900,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      Initiatives
                    </div>
                    {railInitiatives.map((initiative) => (
                      <div
                        key={initiative.id}
                        style={{
                          border: "1px solid rgba(148,163,184,0.22)",
                          borderLeft: "3px solid #fb923c",
                          borderRadius: 10,
                          background: "rgba(17,24,39,0.84)",
                          padding: "8px 9px",
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            color: cardText,
                            fontSize: 12,
                            fontWeight: 850,
                            lineHeight: 1.25,
                          }}
                        >
                          {initiative.title}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            alignItems: "center",
                            color: cardMuted,
                            fontSize: 10,
                            fontWeight: 750,
                          }}
                        >
                          <span>{initiative.owner || "No owner"}</span>
                          <span>-</span>
                          <span>{toTitleCase(initiative.status)}</span>
                          <span>-</span>
                          <span>
                            {initiative.tasks.length} task{initiative.tasks.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        {initiative.dueDate ? (
                          <div style={{ color: "#fed7aa", fontSize: 10, fontWeight: 800 }}>
                            Due {initiative.dueDate}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                {railKpiTasks.length > 0 ? (
                  <div style={{ display: "grid", gap: 7 }}>
                    <div
                      style={{
                        color: cardMuted,
                        fontSize: 10,
                        fontWeight: 900,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      KPI Tasks
                    </div>
                    {railKpiTasks.map((task) => (
                      <div
                        key={task.id}
                        style={{
                          border: "1px solid rgba(148,163,184,0.22)",
                          borderLeft: "3px solid #60a5fa",
                          borderRadius: 10,
                          background: "rgba(17,24,39,0.84)",
                          padding: "8px 9px",
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            color: cardText,
                            fontSize: 12,
                            fontWeight: 850,
                            lineHeight: 1.25,
                          }}
                        >
                          {task.title}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            alignItems: "center",
                            color: cardMuted,
                            fontSize: 10,
                            fontWeight: 750,
                          }}
                        >
                          <span>{task.owner || "No owner"}</span>
                          <span>-</span>
                          <span>{toTitleCase(task.status)}</span>
                        </div>
                        {task.dueDate ? (
                          <div style={{ color: "#bfdbfe", fontSize: 10, fontWeight: 800 }}>
                            Due {task.dueDate}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              {totalWorkItems > visibleRailItemsCount ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  onClick={(event) => {
                    event.stopPropagation();
                    data.onOpenDetails(id);
                  }}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid rgba(148,163,184,0.24)",
                    borderRadius: 9,
                    background: "rgba(8,13,20,0.76)",
                    color: cardSubtle,
                    fontSize: 11,
                    fontWeight: 850,
                    padding: "7px 8px",
                    cursor: "pointer",
                  }}
                >
                  View {totalWorkItems - visibleRailItemsCount} more
                </button>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
      <div
        className="map-kpi-card-surface"
        style={{
          background: `linear-gradient(180deg, ${hexToRgba(cardColor, 0.075)}, rgba(17,24,39,0.98) 32%, ${cardSurface})`,
          borderRadius: 12,
          padding: 18,
          border: `1px solid ${selected ? "rgba(96,165,250,0.58)" : cardBorder}`,
          boxSizing: "border-box",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.045)",
        }}
      >
        {selected ? (
          <div
            style={{
              marginBottom: 8,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#93c5fd",
            }}
          >
            Selected
          </div>
        ) : null}
        <div
          className="map-kpi-title"
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: 15,
            fontWeight: 760,
            color: cardText,
            marginBottom: 8,
            lineHeight: 1.3,
          }}
        >
          {data.label}
        </div>

        <div
          className="map-kpi-period"
          style={{
            fontSize: 11,
            color: cardMuted,
            lineHeight: 1.45,
            marginBottom: 8,
            fontWeight: 700,
          }}
        >
          {data.reportingPeriodLabel || toTitleCase(data.frequency)}
        </div>

        {pollCompletion ? (
          <div
            style={{
              border: `1px solid ${cardBorder}`,
              borderRadius: 9,
              background: "rgba(37,99,235,0.14)",
              padding: "8px 9px",
              marginBottom: 8,
              display: "grid",
              gap: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                fontSize: 10,
                fontWeight: 850,
                color: cardSubtle,
                textTransform: "uppercase",
              }}
            >
              <span>Poll completion</span>
              <span>{pollCompletion.percentage}%</span>
            </div>
            <div
              role="progressbar"
              aria-label={`Poll ${pollCompletion.percentage}% complete`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pollCompletion.percentage}
              style={{
                height: 6,
                borderRadius: 999,
                background: "rgba(148,163,184,0.24)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${pollCompletion.percentage}%`,
                  height: "100%",
                  borderRadius: 999,
                  background: "#60a5fa",
                  transition: "width 180ms ease",
                }}
              />
            </div>
            <div style={{ fontSize: 10, color: cardMuted, fontWeight: 750 }}>
              {pollCompletion.completed} of {pollCompletion.total} invite
              {pollCompletion.total === 1 ? "" : "s"} completed
            </div>
          </div>
        ) : null}

        <div
          className="map-kpi-owner"
          style={{
            border: `1px solid ${cardBorder}`,
            borderRadius: 9,
            background: "rgba(15,23,42,0.86)",
            padding: "8px 9px",
            marginBottom: 8,
            display: "grid",
            gap: 3,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 850,
              color: cardMuted,
              textTransform: "uppercase",
            }}
          >
            Owner
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: cardSubtle,
              lineHeight: 1.25,
              overflowWrap: "anywhere",
            }}
          >
            {data.owner || "Unassigned"}
          </div>
        </div>

        <label
          className="map-kpi-calculation"
          style={{
            display: "grid",
            gap: 4,
            marginBottom: 8,
            fontSize: 12,
            fontWeight: 600,
            color: cardSubtle,
            minWidth: 0,
          }}
        >
          <span>KPI calculation</span>
          <select
            className="nodrag nopan"
            value={data.calculationMode}
            onChange={(e) =>
              data.onConfigChange(id, "calculationMode", e.target.value as KpiCalculationMode)
            }
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              border: `1px solid ${cardBorder}`,
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              color: cardText,
              background: "rgba(8,13,20,0.96)",
            }}
          >
            {calculationModeOptions.map((option) => (
              <option key={option} value={option}>
                {formatCalculationModeLabel(option)}
              </option>
            ))}
          </select>
        </label>

        {data.metricNote ? (
          <div
            style={{
              fontSize: 12,
              color: cardMuted,
              marginBottom: 10,
              lineHeight: 1.4,
            }}
          >
            {data.metricNote}
          </div>
        ) : null}

        {data.statusNote ? (
          <div
            style={{
              fontSize: 12,
              color: cardSubtle,
              marginBottom: 8,
              lineHeight: 1.4,
              fontWeight: 700,
            }}
          >
            {data.statusNote}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 8,
            marginBottom: 8,
            opacity: metricsDisabled ? 0.5 : 1,
          }}
        >
          <div
            className="map-kpi-metric"
            style={{
              display: "grid",
              gap: 5,
              minWidth: 0,
              border: `1px solid ${cardBorder}`,
              borderRadius: 9,
              background: "rgba(8,13,20,0.72)",
              padding: "8px 9px",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 850,
                color: cardMuted,
                textTransform: "uppercase",
              }}
            >
              {formatComparisonMetricLabel(data.comparisonMode)}
            </div>
            <div
              className="map-kpi-value"
              style={{
                width: "100%",
                boxSizing: "border-box",
                fontSize: metricValueFontSize,
                fontWeight: 850,
                color: cardText,
                minHeight: 34,
                display: "flex",
                alignItems: "center",
                lineHeight: 1.15,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "clip",
                letterSpacing: 0,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {targetDisplayValue}
            </div>
          </div>
          <div
            className="map-kpi-metric"
            style={{
              display: "grid",
              gap: 5,
              minWidth: 0,
              border: `1px solid ${cardBorder}`,
              borderRadius: 9,
              background: "rgba(8,13,20,0.72)",
              padding: "8px 9px",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 850,
                color: cardMuted,
                textTransform: "uppercase",
              }}
            >
              Actual
            </div>
            {quickActualEditable ? (
              <div
                style={{
                  position: "relative",
                  minWidth: 0,
                  minHeight: 34,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {getMetricInputPrefix(data.valueType) ? (
                  <span
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 11,
                      fontWeight: 800,
                      color: cardMuted,
                      pointerEvents: "none",
                    }}
                  >
                    {getMetricInputPrefix(data.valueType)}
                  </span>
                ) : null}
                <input
                  aria-label={`Actual value for ${data.label}`}
                  className="nodrag nopan map-kpi-value"
                  type="text"
                  inputMode={data.valueType === "currency" || data.valueType === "percentage" ? "decimal" : "numeric"}
                  min={data.valueType === "rating_1_10" ? 1 : undefined}
                  max={data.valueType === "rating_1_10" ? 10 : undefined}
                  value={actualInputDisplayValue}
                  onChange={(e) => data.onMetricChange(id, "actual", e.target.value)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "none",
                    borderRadius: 0,
                    padding: `0 ${getMetricInputSuffix(data.valueType) ? 13 : 0}px 0 ${
                      getMetricInputPrefix(data.valueType) ? 11 : 0
                    }px`,
                    fontSize: metricValueFontSize,
                    fontWeight: 850,
                    background: "transparent",
                    color: cardText,
                    minHeight: 34,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    overflow: "visible",
                    textOverflow: "clip",
                    letterSpacing: 0,
                    fontVariantNumeric: "tabular-nums",
                    outline: "none",
                  }}
                />
                {getMetricInputSuffix(data.valueType) ? (
                  <span
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 11,
                      fontWeight: 800,
                      color: cardMuted,
                      pointerEvents: "none",
                    }}
                  >
                    {getMetricInputSuffix(data.valueType)}
                  </span>
                ) : null}
              </div>
            ) : (
              <div
                className="map-kpi-value"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  fontSize: metricValueFontSize,
                  fontWeight: 850,
                  color: cardText,
                  minHeight: 34,
                  display: "flex",
                  alignItems: "center",
                  lineHeight: 1.15,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "clip",
                  letterSpacing: 0,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {actualDisplayValue}
              </div>
            )}
          </div>
        </div>

        {data.calculationMode !== "direct" ? (
          <div
            style={{
              fontSize: 12,
              color: data.rollupWarning ? "#fca5a5" : cardMuted,
              marginBottom: 8,
              lineHeight: 1.4,
              fontWeight: data.rollupWarning ? 700 : 500,
            }}
          >
            {data.rollupWarning ||
              (data.calculationMode === "calculated_values"
                ? "Target and actual are calculated from child KPIs"
                : "Performance is influenced by child KPIs")}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 8,
            padding: "7px 8px",
            borderRadius: 8,
            background: hasWorkItems ? "rgba(37,99,235,0.16)" : cardPanelSoft,
            border: `1px solid ${hasWorkItems ? "rgba(96,165,250,0.38)" : cardBorder}`,
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: cardSubtle,
              }}
            >
              {data.initiatives.length} initiative{data.initiatives.length === 1 ? "" : "s"}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: cardSubtle,
              }}
            >
              {data.tasks.length} KPI task{data.tasks.length === 1 ? "" : "s"}
            </span>
          </div>
          <button
            type="button"
            className="nodrag nopan map-expand-control"
            onClick={() => setIsWorkDrawerOpen((current) => !current)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              border: "none",
              background: "transparent",
              color: hasWorkItems ? "#93c5fd" : cardMuted,
              fontSize: 11,
              fontWeight: 800,
              cursor: "pointer",
              padding: 0,
              whiteSpace: "nowrap",
            }}
          >
            {isWorkDrawerOpen ? "Hide" : hasWorkItems ? "Show" : "View"}
          </button>
        </div>

        <div
          style={{
            maxHeight: isWorkDrawerOpen ? 220 : 0,
            overflow: "hidden",
            opacity: isWorkDrawerOpen ? 1 : 0,
            transform: isWorkDrawerOpen ? "translateY(0)" : "translateY(-4px)",
            transition: "max-height 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease, transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
            marginBottom: isWorkDrawerOpen ? 8 : 0,
          }}
        >
          <div
            style={{
              border: `1px solid ${cardBorder}`,
              borderRadius: 10,
              background: "rgba(8,13,20,0.86)",
              padding: 8,
              display: "grid",
              gap: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "stretch",
              }}
            >
              <div
                style={{
                  borderRadius: 8,
                  background: cardPanel,
                  border: `1px solid ${cardBorder}`,
                  padding: "7px 8px",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: cardMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 2,
                  }}
                >
                  Initiatives
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: cardText }}>
                  {data.initiatives.length}
                </div>
                <div style={{ fontSize: 11, color: cardMuted }}>
                  {activeInitiatives.length} active
                </div>
              </div>
              <div
                style={{
                  borderRadius: 8,
                  background: cardPanel,
                  border: `1px solid ${cardBorder}`,
                  padding: "7px 8px",
                  width: 92,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: cardMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 2,
                  }}
                >
                  Tasks
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: cardText }}>{data.tasks.length}</div>
                <div style={{ fontSize: 11, color: cardMuted }}>
                  direct
                </div>
              </div>
            </div>

            {hasWorkItems ? (
              <div style={{ display: "grid", gap: 6 }}>
                {visibleInitiatives.length > 0 ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: cardMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Initiatives
                    </div>
                    {visibleInitiatives.map((initiative) => (
                      <div
                        key={initiative.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          padding: "6px 8px",
                          borderRadius: 8,
                          background: cardPanel,
                          border: `1px solid ${cardBorder}`,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: cardText,
                            lineHeight: 1.3,
                          }}
                        >
                          {initiative.title}
                        </div>
                        <div style={{ fontSize: 10, color: cardMuted }}>
                            {initiative.owner || "No owner"}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: initiative.status === "done" ? "#86efac" : "#facc15",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {toTitleCase(initiative.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {visibleKpiTasks.length > 0 ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: cardMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      KPI Tasks
                    </div>
                    {visibleKpiTasks.map((task) => (
                      <div
                        key={task.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          padding: "6px 8px",
                          borderRadius: 8,
                          background: cardPanel,
                          border: `1px solid ${cardBorder}`,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: cardText,
                              lineHeight: 1.3,
                            }}
                          >
                            {task.title}
                          </div>
                          <div style={{ fontSize: 10, color: cardMuted }}>
                            {task.owner || "No owner"}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: task.status === "done" ? "#86efac" : "#facc15",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {toTitleCase(task.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {visibleInitiatives.length === 0 && visibleKpiTasks.length === 0 ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: cardMuted,
                    }}
                  >
                    No initiatives or direct KPI tasks are assigned yet.
                  </div>
                ) : null}
              </div>
            ) : (
              <div
                style={{
                  fontSize: 11,
                  color: cardMuted,
                }}
              >
                No initiatives or tasks are assigned to this KPI yet.
              </div>
            )}

            <button
              type="button"
              className="nodrag nopan"
              onClick={() => data.onOpenDetails(id)}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "1px solid rgba(96,165,250,0.4)",
                borderRadius: 8,
                padding: "7px 8px",
                fontSize: 11,
                fontWeight: 800,
                background: "rgba(37,99,235,0.14)",
                color: "#93c5fd",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Open Work Details
            </button>
          </div>
        </div>

        <button
          className="nodrag nopan"
          onClick={() => data.onAddChild(id)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            background: "rgba(148,163,184,0.16)",
            border: `1px solid ${cardBorder}`,
            borderRadius: 8,
            padding: "6px 8px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            width: "100%",
            color: cardText,
          }}
        >
          + KPI
        </button>

        {data.childCount > 0 ? (
          <button
            className="nodrag nopan map-expand-control"
            onClick={() => data.onToggleChildren(id)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              background: "rgba(8,13,20,0.86)",
              border: `1px solid ${cardBorder}`,
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              width: "100%",
              marginTop: 6,
              color: cardSubtle,
            }}
          >
            {data.childrenCollapsed ? "Show" : "Hide"} {data.childCount}{" "}
            {data.childCount === 1 ? "Child" : "Children"}
          </button>
        ) : null}
      </div>

      <Handle type="target" position={Position.Bottom} style={kpiHandleStyle} />
      <Handle type="source" position={Position.Top} style={kpiHandleStyle} />
    </div>
  );
}

const nodeTypes = {
  kpi: KpiNode,
};

function makeEdge(
  id: string,
  source: string,
  target: string,
  influence?: number,
  lenses: Lens[] = []
): Edge {
  const normalizedLenses = Array.from(new Set(lenses.filter(Boolean)));
  return {
    id,
    source,
    target,
    type: "default",
    label: typeof influence === "number" ? `${influence}%` : "Set %",
    data: { influence, lenses: normalizedLenses },
    interactionWidth: 40,
    style: { stroke: "#e2e8f0", strokeWidth: 2.5, opacity: 0.92 },
    labelStyle: { fill: "#ffffff", fontWeight: 700, fontSize: 16 },
    labelBgStyle: { fill: "#000000", fillOpacity: 1 },
    labelBgPadding: [6, 4],
    labelBgBorderRadius: 4,
  };
}

function sanitizePersistedEdge(edge: Edge): Edge {
  return makeEdge(
    edge.id,
    edge.source,
    edge.target,
    typeof edge.data?.influence === "number" ? edge.data.influence : undefined,
    Array.isArray(edge.data?.lenses)
      ? edge.data.lenses.filter((lens: unknown): lens is Lens => typeof lens === "string")
      : []
  );
}

const getEdgeLensMembership = (edge: Edge): Lens[] =>
  Array.isArray(edge.data?.lenses)
    ? edge.data.lenses.filter((lens: unknown): lens is Lens => typeof lens === "string")
    : [];

const edgeBelongsToLens = (
  edge: Edge,
  lens: Lens,
  nodeMap: Map<string, Node<KpiNodeData>>
) => {
  const edgeLenses = getEdgeLensMembership(edge);
  if (edgeLenses.length > 0) {
    return edgeLenses.includes(lens);
  }

  return Boolean(
    nodeMap.get(edge.source)?.data.lenses.includes(lens) &&
      nodeMap.get(edge.target)?.data.lenses.includes(lens)
  );
};

const pushUniqueMapValue = <T,>(map: Map<string, T[]>, key: string, value: T) => {
  if (!map.has(key)) {
    map.set(key, []);
  }
  const values = map.get(key)!;
  if (!values.includes(value)) {
    values.push(value);
  }
};

const isValidFlowPosition = (position: unknown): position is { x: number; y: number } =>
  Boolean(
    position &&
      typeof position === "object" &&
      typeof (position as { x?: unknown }).x === "number" &&
      Number.isFinite((position as { x: number }).x) &&
      typeof (position as { y?: unknown }).y === "number" &&
      Number.isFinite((position as { y: number }).y)
  );

const getSafeNodePositionForLens = (node: Node<KpiNodeData>, lens: Lens) => {
  const lensPosition = node.data.positionsByLens[lens];
  if (isValidFlowPosition(lensPosition)) return lensPosition;
  if (isValidFlowPosition(node.position)) return node.position;
  return { x: 0, y: 0 };
};

const isSupportedCcbGivingPeriod = (frequency: Frequency): frequency is "weekly" | "monthly" =>
  frequency === "weekly" || frequency === "monthly";

const setEdgeLensMembership = (edge: Edge, lens: Lens, include: boolean) => {
  const nextLenses = include
    ? Array.from(new Set([...getEdgeLensMembership(edge), lens]))
    : getEdgeLensMembership(edge).filter((item) => item !== lens);

  return makeEdge(
    edge.id,
    edge.source,
    edge.target,
    typeof edge.data?.influence === "number" ? edge.data.influence : undefined,
    nextLenses
  );
};

export default function StrategyMapEditor({
  signedInUser = null,
  onSignOut,
}: {
  signedInUser?: SignedInUser | null;
  onSignOut?: () => void;
}) {
  const allKpisFilterValue = "__all__";
  const unattachedKpisFilterValue = "__unattached__";
  const [searchParams, setSearchParams] = useSearchParams();

  const [availableLenses, setAvailableLenses] = React.useState<Lens[]>(initialLenses);
  const [lensConfigs, setLensConfigs] = React.useState<LensConfigs>(initialLensConfigs);
  const [activeLens, setActiveLens] = React.useState<Lens>(defaultLens);
  const [referenceDate, setReferenceDate] = React.useState(initialReferenceDate);
  const [weekStartsOn, setWeekStartsOn] = React.useState<WeekStartsOn>(defaultWeekStartsOn);
  const [aggregationMode, setAggregationMode] = React.useState<AggregationMode>("current");
  const [comparisonMode, setComparisonMode] = React.useState<ComparisonMode>("target");
  const [campus, setCampus] = React.useState<Campus>(defaultCampus);
  const [ownerList, setOwnerList] = React.useState<string[]>(initialOwners);
  const [workspaceUsers, setWorkspaceUsers] = React.useState<AppUser[]>([]);
  const [currentUser, setCurrentUser] = React.useState<AppUser | null>(() =>
    signedInUser
      ? {
          ...signedInUser,
          is_active: signedInUser.is_active ?? true,
        }
      : null
  );
  const [hasLoadedWorkspaceUsers, setHasLoadedWorkspaceUsers] = React.useState(false);
  const [pollAudienceGroups, setPollAudienceGroups] =
    React.useState<string[]>(initialPollAudienceGroups);
  const [pollRecipients, setPollRecipients] = React.useState<PollRecipient[]>(initialPollRecipients);
  const [isPollRecipientsOpen, setIsPollRecipientsOpen] = React.useState(false);
  const [enterprises, setEnterprises] = React.useState<EnterpriseSummary[]>(readCachedEnterpriseSummaries);
  const [activeEnterpriseId, setActiveEnterpriseId] = React.useState<number | null>(() => {
    const queryEnterpriseId = Number(searchParams.get("enterpriseId"));
    return Number.isFinite(queryEnterpriseId) && queryEnterpriseId > 0
      ? queryEnterpriseId
      : readStoredActiveEnterpriseId();
  });
  const [workspaceCapabilities, setWorkspaceCapabilities] =
    React.useState<WorkspaceCapabilities>(defaultWorkspaceCapabilities);
  const [quickBooksStatus, setQuickBooksStatus] = React.useState<QuickBooksStatus | null>(null);
  const [quickBooksMessage, setQuickBooksMessage] = React.useState<string | null>(null);
  const [isQuickBooksLoading, setIsQuickBooksLoading] = React.useState(false);
  const [pushpayStatus, setPushpayStatus] = React.useState<PushpayChmsStatus | null>(null);
  const [pushpayMessage, setPushpayMessage] = React.useState<string | null>(null);
  const [isPushpayLoading, setIsPushpayLoading] = React.useState(false);
  const [kpiApiMessage, setKpiApiMessage] = React.useState<string | null>(null);
  const [isKpiApiLoading, setIsKpiApiLoading] = React.useState(false);
  const [kpiApiStartDate, setKpiApiStartDate] = React.useState(getDefaultQuickBooksStartDate);
  const [kpiApiEndDate, setKpiApiEndDate] = React.useState(() => formatDateInputValue(new Date()));
  const [quickBooksSupportSession, setQuickBooksSupportSession] =
    React.useState<QuickBooksSupportSession | null>(null);
  const quickBooksSupportInstructions = React.useMemo(
    () => (quickBooksSupportSession ? buildQuickBooksSupportInstructions(quickBooksSupportSession) : ""),
    [quickBooksSupportSession]
  );
  const displayedWeekStartDate = React.useMemo(
    () => getReportingWeekStart(referenceDate, weekStartsOn),
    [referenceDate, weekStartsOn]
  );
  const displayedWeekParts = React.useMemo(
    () => getReportingWeekParts(referenceDate, weekStartsOn),
    [referenceDate, weekStartsOn]
  );
  const showQuickBooksDiagnostics = quickBooksSupportSession?.token === "__show_quickbooks_diagnostics__";
  const [quickBooksAccounts, setQuickBooksAccounts] = React.useState<QuickBooksAccount[]>([]);
  const [quickBooksDimensions, setQuickBooksDimensions] = React.useState<QuickBooksDimensions | null>(null);
  const [quickBooksProfitAndLossByLocation, setQuickBooksProfitAndLossByLocation] =
    React.useState<QuickBooksProfitAndLossByLocation | null>(null);
  const [quickBooksProfitAndLossByClass, setQuickBooksProfitAndLossByClass] =
    React.useState<QuickBooksProfitAndLossByClass | null>(null);
  const [quickBooksAccountQuery, setQuickBooksAccountQuery] = React.useState("tithe giving offering");
  const [quickBooksCopyMessage, setQuickBooksCopyMessage] = React.useState<string | null>(null);
  const [quickBooksSelectedAccountIds, setQuickBooksSelectedAccountIds] = React.useState<string[]>([]);
  const [quickBooksStartDate, setQuickBooksStartDate] = React.useState(getDefaultQuickBooksStartDate);
  const [quickBooksEndDate, setQuickBooksEndDate] = React.useState(() => formatDateInputValue(new Date()));
  const [quickBooksPeriod, setQuickBooksPeriod] = React.useState<"weekly" | "monthly">("monthly");
  const [quickBooksTargetKpiId, setQuickBooksTargetKpiId] = React.useState("");
  const [quickBooksExpenseLocationName, setQuickBooksExpenseLocationName] = React.useState("");
  const [quickBooksGivingPreview, setQuickBooksGivingPreview] =
    React.useState<QuickBooksGivingPreview | null>(null);
  const [ccbGivingPreview, setCcbGivingPreview] = React.useState<CcbGivingPreview | null>(null);
  const [ccbGivingCampusName, setCcbGivingCampusName] = React.useState("");
  const [quickBooksExpensePreview, setQuickBooksExpensePreview] =
    React.useState<QuickBooksExpensePreview | null>(null);
  const [quickBooksExpenseBudgetPreview, setQuickBooksExpenseBudgetPreview] =
    React.useState<QuickBooksExpenseBudgetPreview | null>(null);
  const [quickBooksAccountBalancePreview, setQuickBooksAccountBalancePreview] =
    React.useState<QuickBooksAccountBalancePreview | null>(null);
  const [quickBooksFinanceDimensionType, setQuickBooksFinanceDimensionType] =
    React.useState<"location" | "class">("location");
  const [quickBooksFinanceDimensionName, setQuickBooksFinanceDimensionName] =
    React.useState("Bethel SonRise");
  const [quickBooksFinanceAccountingMethod, setQuickBooksFinanceAccountingMethod] =
    React.useState<"" | "Cash" | "Accrual">("");
  const [quickBooksGivingDimensionAnswer, setQuickBooksGivingDimensionAnswer] =
    React.useState<QuickBooksGivingDimensionAnswer | null>(null);
  const [isEnterpriseLoading, setIsEnterpriseLoading] = React.useState(true);
  const [isWorkspaceLoading, setIsWorkspaceLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [activeKpiId, setActiveKpiId] = React.useState<string | null>(null);
  const [activeInitiativeRailNodeId, setActiveInitiativeRailNodeId] = React.useState<string | null>(null);
  const [pollEditorKpiId, setPollEditorKpiId] = React.useState<string | null>(null);
  const [pollEditorMode, setPollEditorMode] = React.useState<PollEditorMode>("view");
  const [isPollSummaryExpanded, setIsPollSummaryExpanded] = React.useState(false);
  const [pollCommentsCopyMessage, setPollCommentsCopyMessage] = React.useState<string | null>(null);
  const [isPollEmailComposerOpen, setIsPollEmailComposerOpen] = React.useState(false);
  const [pollEmailSubject, setPollEmailSubject] = React.useState("");
  const [pollEmailGreeting, setPollEmailGreeting] = React.useState("");
  const [pollEmailBody, setPollEmailBody] = React.useState("");
  const [pollEmailClosing, setPollEmailClosing] = React.useState("");
  const [pollEmailStatusMessage, setPollEmailStatusMessage] = React.useState<string | null>(null);
  const [isSendingPollEmails, setIsSendingPollEmails] = React.useState(false);
  const [kpiWorkEmailStatusMessage, setKpiWorkEmailStatusMessage] = React.useState<string | null>(null);
  const [isSendingKpiWorkUpdateEmails, setIsSendingKpiWorkUpdateEmails] = React.useState(false);
  const [isKpiInfoOpen, setIsKpiInfoOpen] = React.useState(false);
  const [isRestrictedFieldsOpen, setIsRestrictedFieldsOpen] = React.useState(false);
  const [isKpiWorkOpen, setIsKpiWorkOpen] = React.useState(false);
  const [kpiContextMenu, setKpiContextMenu] = React.useState<KpiContextMenuState | null>(null);
  const [mapContextMenu, setMapContextMenu] = React.useState<MapContextMenuState | null>(null);
  const [childKpiInfluenceDraft, setChildKpiInfluenceDraft] = React.useState<{
    parentId: string;
    parentLabel: string;
    value: string;
  } | null>(null);
  const [activeLensDetailId, setActiveLensDetailId] = React.useState<Lens | null>(null);

  const userOwnerOptions = React.useMemo(() => {
    const seen = new Set<string>();
    return workspaceUsers
      .filter(
        (user) =>
          user.is_active &&
          (activeEnterpriseId === null ||
            user.enterprise_id == null ||
            user.enterprise_id === activeEnterpriseId)
      )
      .map((user) => user.name.trim() || user.email.trim())
      .filter((owner) => {
        if (!owner || seen.has(owner.toLowerCase())) {
          return false;
        }
        seen.add(owner.toLowerCase());
        return true;
      })
      .sort((left, right) => left.localeCompare(right));
  }, [activeEnterpriseId, workspaceUsers]);
  const userOwnerSet = React.useMemo(
    () => new Set(userOwnerOptions.map((owner) => owner.toLowerCase())),
    [userOwnerOptions]
  );
  const ownerEmailByKey = React.useMemo(() => {
    const lookup = new Map<string, string>();
    workspaceUsers.forEach((user) => {
      const email = user.email.trim();
      if (!email) return;
      const name = user.name.trim();
      if (name) {
        lookup.set(name.toLowerCase(), email);
      }
      lookup.set(email.toLowerCase(), email);
    });
    return lookup;
  }, [workspaceUsers]);
  const currentUserHasLegacyAdminRole = React.useMemo(() => {
    const role = currentUser?.role?.trim().toLowerCase();
    return role === "owner" || role === "admin" || role === "super_admin";
  }, [currentUser]);
  const canManageWorkspace =
    workspaceCapabilities.can_manage_workspace || currentUserHasLegacyAdminRole;
  const canManageLenses =
    workspaceCapabilities.can_manage_lens || canManageWorkspace;
  const canCreateKpis =
    workspaceCapabilities.can_create_kpi || canManageWorkspace;
  const canEditKpis =
    workspaceCapabilities.can_edit_kpi || canManageWorkspace;
  const canDeleteKpis =
    workspaceCapabilities.can_delete_kpi || canManageWorkspace;
  const canManageKpiRelationships =
    workspaceCapabilities.can_manage_kpi_relationships || canManageWorkspace;
  const currentUserOwnerKeys = React.useMemo(() => {
    const keys = [currentUser?.name, currentUser?.email]
      .map((value) => value?.trim().toLowerCase())
      .filter((value): value is string => Boolean(value));
    return new Set(keys);
  }, [currentUser]);
  const [lensNameDrafts, setLensNameDrafts] = React.useState<Record<string, string>>(() =>
    Object.fromEntries(initialLenses.map((lens) => [lens, initialLensConfigs[lens].name]))
  );
  const [isCreateLensOpen, setIsCreateLensOpen] = React.useState(false);
  const [newLensForm, setNewLensForm] = React.useState<{
    name: string;
    scopeType: ScopeType;
    scopeId: string;
    description: string;
  }>({
    name: "",
    scopeType: "organization",
    scopeId: "",
    description: "",
  });
  const [kpiLibraryFilter, setKpiLibraryFilter] = React.useState(allKpisFilterValue);
  const [kpiLibrarySearch, setKpiLibrarySearch] = React.useState("");
  const [pollKpiSearch, setPollKpiSearch] = React.useState("");
  const [isLensLibraryCollapsed, setIsLensLibraryCollapsed] = React.useState(true);
  const [isReferenceLibraryCollapsed, setIsReferenceLibraryCollapsed] = React.useState(true);
  const [isLibraryCollapsed, setIsLibraryCollapsed] = React.useState(true);
  const [isLensesSectionCollapsed, setIsLensesSectionCollapsed] = React.useState(true);
  const [isKpiSectionCollapsed, setIsKpiSectionCollapsed] = React.useState(true);
  const [isReferenceListsSectionCollapsed, setIsReferenceListsSectionCollapsed] =
    React.useState(true);
  const [isAiStrategySectionCollapsed, setIsAiStrategySectionCollapsed] =
    React.useState(true);
  const [isApiSettingsCollapsed, setIsApiSettingsCollapsed] = React.useState(true);
  const [aiStrategyPrompt, setAiStrategyPrompt] = React.useState(
    "Recommend strategic lenses, KPI trees, initiatives, and starter tasks for this enterprise."
  );
  const [aiStrategyDraft, setAiStrategyDraft] =
    React.useState<AiStrategyRecommendationResponse | null>(null);
  const [selectedAiLensIndexes, setSelectedAiLensIndexes] = React.useState<number[]>([]);
  const [isGeneratingStrategyDraft, setIsGeneratingStrategyDraft] = React.useState(false);
  const [aiStrategyMessage, setAiStrategyMessage] = React.useState<string | null>(null);
  const [aiAddDraft, setAiAddDraft] = React.useState<AiAddDraft | null>(null);
  const [aiAddLoadingKind, setAiAddLoadingKind] = React.useState<AiAddKind | null>(null);
  const [aiAddMessage, setAiAddMessage] = React.useState<string | null>(null);
  const [aiAddMessageTarget, setAiAddMessageTarget] = React.useState<{
    kind: AiAddKind;
    kpiId?: string;
    initiativeId?: string;
  } | null>(null);
  const [isWorkspaceAgentOpen, setIsWorkspaceAgentOpen] = React.useState(false);
  const [workspaceAgentPrompt, setWorkspaceAgentPrompt] = React.useState("");
  const [workspaceAgentResponse, setWorkspaceAgentResponse] =
    React.useState<WorkspaceAgentResponse | null>(null);
  const [workspaceAgentHistory, setWorkspaceAgentHistory] = React.useState<WorkspaceAgentMessage[]>([]);
  const [workspaceAgentConversationId, setWorkspaceAgentConversationId] = React.useState<number | null>(null);
  const [isWorkspaceAgentLoading, setIsWorkspaceAgentLoading] = React.useState(false);
  const [workspaceAgentOperation, setWorkspaceAgentOperation] =
    React.useState<WorkspaceAgentOperation>("answer");
  const [workspaceAgentElapsedSeconds, setWorkspaceAgentElapsedSeconds] = React.useState(0);
  const [isEnterpriseListCollapsed, setIsEnterpriseListCollapsed] = React.useState(true);
  const [isOwnerListCollapsed, setIsOwnerListCollapsed] = React.useState(true);
  const [isPollAudienceGroupsCollapsed, setIsPollAudienceGroupsCollapsed] =
    React.useState(true);
  const [isPollRecipientsCollapsed, setIsPollRecipientsCollapsed] = React.useState(true);
  const [panelOffsets, setPanelOffsets] = React.useState<
    Record<FloatingPanelKey, { x: number; y: number }>
  >({
    lens: { x: 0, y: 0 },
    reference: { x: 0, y: 0 },
    kpi: { x: 0, y: 0 },
  });
  const [panelSizes, setPanelSizes] = React.useState<Record<FloatingPanelKey, PanelSize>>({
    lens: { width: 380, height: 720 },
    reference: { width: 280, height: 720 },
    kpi: { width: 320, height: 720 },
  });
  const [draggingPanel, setDraggingPanel] = React.useState<FloatingPanelKey | null>(null);
  const [resizingPanel, setResizingPanel] = React.useState<FloatingPanelKey | null>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    React.useState<ReactFlowInstance | null>(null);
  const [libraryPointerDrag, setLibraryPointerDrag] = React.useState<{
    nodeId: string;
    label: string;
    x: number;
    y: number;
  } | null>(null);
  const panelDragRef = React.useRef<{
    panel: FloatingPanelKey;
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const panelResizeRef = React.useRef<{
    panel: FloatingPanelKey;
    pointerId: number;
    startX: number;
    startY: number;
    originWidth: number;
    originHeight: number;
  } | null>(null);
  const libraryPointerDragRef = React.useRef<{
    nodeId: string;
    label: string;
    pointerId: number;
    startX: number;
    startY: number;
    x: number;
    y: number;
    isDragging: boolean;
  } | null>(null);
  const hasLoadedEnterpriseRef = React.useRef(false);
  const extraWorkspaceFieldsRef = React.useRef<Record<string, unknown>>({});
  const applyWorkspaceRef = React.useRef<
    (workspace?: Record<string, unknown>, options?: { resetUndoHistory?: boolean; preserveView?: boolean }) => void
  >(() => {});
  const serializeWorkspaceRef = React.useRef<() => Record<string, unknown>>(() => ({}));
  const skipNextWorkspaceAutosaveRef = React.useRef(false);
  const undoHistoryRef = React.useRef<string[]>([]);
  const lastWorkspaceSnapshotRef = React.useRef<string | null>(null);
  const lastWorkspacePersistenceFingerprintRef = React.useRef<string | null>(null);
  const workspaceUpdatedAtRef = React.useRef<string | null>(null);
  const workspaceSavePromiseRef = React.useRef<Promise<void>>(Promise.resolve());
  const localWorkspaceMutationVersionRef = React.useRef(0);
  const immediateUndoSnapshotRef = React.useRef<string | null>(null);
  const immediateUndoStateRef = React.useRef<{
    nodes: Node<KpiNodeData>[];
    edges: Edge[];
  } | null>(null);

  const defaultNodes = React.useMemo<Node<KpiNodeData>[]>(() => [], []);

  const defaultEdges = React.useMemo<Edge[]>(() => [], []);

  const [allNodes, setAllNodes] = React.useState<Node<KpiNodeData>[]>(defaultNodes);
  const [allEdges, setAllEdges] = React.useState<Edge[]>(defaultEdges);
  const actualInputRef = React.useRef<HTMLInputElement | null>(null);
  const quickBooksImportKpis = React.useMemo(
    () =>
      allNodes
        .filter(
          (node) =>
            node.data.calculationMode === "direct"
        )
        .slice()
        .sort((left, right) => left.data.label.localeCompare(right.data.label)),
    [allNodes]
  );
  const quickBooksLikelyGivingAccounts = React.useMemo(
    () => quickBooksAccounts.filter(isLikelyQuickBooksGivingAccount),
    [quickBooksAccounts]
  );
  const quickBooksExpenseAccounts = React.useMemo(
    () => quickBooksAccounts.filter(isQuickBooksExpenseAccount),
    [quickBooksAccounts]
  );
  const quickBooksSavingsAccounts = React.useMemo(
    () => quickBooksAccounts.filter(isQuickBooksSavingsAccount),
    [quickBooksAccounts]
  );
  const quickBooksExpenseLocations = quickBooksDimensions?.locations ?? [];
  const quickBooksBalanceAccountIds = React.useMemo(
    () =>
      quickBooksSelectedAccountIds.length > 0
        ? quickBooksSelectedAccountIds
        : quickBooksSavingsAccounts.map((account) => account.id),
    [quickBooksSavingsAccounts, quickBooksSelectedAccountIds]
  );
  const quickBooksFilteredAccounts = React.useMemo(() => {
    const terms = quickBooksAccountQuery
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    if (terms.length === 0) {
      return quickBooksAccounts;
    }
    return quickBooksAccounts.filter((account) => {
      const searchable = [
        account.name,
        account.fully_qualified_name,
        account.account_type,
        account.account_sub_type,
        account.classification,
        account.active ? "active" : "inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.some((term) => searchable.includes(term));
    });
  }, [quickBooksAccountQuery, quickBooksAccounts]);

  const normalizeKpiLabel = React.useCallback((value: string) => value.trim().toLowerCase(), []);

  const getUniqueKpiLabel = React.useCallback(
    (baseLabel: string, excludeId?: string) => {
      const trimmedBase = baseLabel.trim() || "New KPI";
      const existingLabels = new Set(
        allNodes
          .filter((node) => node.id !== excludeId)
          .map((node) => normalizeKpiLabel(node.data.label))
      );

      if (!existingLabels.has(normalizeKpiLabel(trimmedBase))) {
        return trimmedBase;
      }

      let index = 2;
      let candidate = `${trimmedBase} ${index}`;
      while (existingLabels.has(normalizeKpiLabel(candidate))) {
        index += 1;
        candidate = `${trimmedBase} ${index}`;
      }

      return candidate;
    },
    [allNodes, normalizeKpiLabel]
  );

  const syncNodesToContext = React.useCallback(
    (
      currentNodes: Node<KpiNodeData>[],
      nextReferenceDate: Date,
      nextCampus: Campus,
      nextLens: Lens
    ) =>
      currentNodes.map((node) => {
        const apiConfig = getEffectiveKpiApiConfig(node);
        const currentSnapshot = isCurrentQueueSnapshot(apiConfig);
        const actualHistory = verifiedSnapshotHistory(node.data.actualHistory, node.data.apiActualEvidence, apiConfig, node.data.frequency);
        const kpiReportingDate = getKpiReportingDate(nextReferenceDate, node.data.frequency, apiConfig);
        const visibleInLens = node.data.lenses.includes(nextLens);
        const activeAggregationMode = currentSnapshot ? "current" : aggregationMode;
        const activeComparisonMode = comparisonMode;
        const eventUpcoming = isDateInFuture(nextReferenceDate, node.data.eventDate);
        const financialPeriodInProgress = isMonthlyFinancialPeriodInProgress(
          kpiReportingDate,
          node.data.frequency,
          node.data.valueType
        );
        const comparisonValue =
          activeComparisonMode === "target"
            ? getValueForContext(
                node.data.targetHistory,
                kpiReportingDate,
                node.data.frequency,
                node.data.scope,
                nextCampus,
                node.data.valueType,
                activeAggregationMode,
                node.data.metricPersistence
              )
            : getComparisonValueForContext(
                actualHistory,
                kpiReportingDate,
                node.data.frequency,
                node.data.scope,
                nextCampus,
                node.data.valueType,
                activeAggregationMode,
                activeComparisonMode
              );
        const metricsEditable = node.data.calculationMode === "direct" && visibleInLens;
        const metricNote = !visibleInLens
          ? `Available in: ${node.data.lenses.join(", ")}`
          : undefined;

        const pollScore =
          node.data.dataSource === "Poll"
            ? getCurrentPollScore(node.data.pollConfig?.responses)
            : undefined;

        return {
          ...node,
          position: node.data.positionsByLens[nextLens] ?? node.position,
          data: {
            ...node.data,
            target: comparisonValue,
            actual:
              node.data.dataSource === "Poll"
                ? pollScore
                : getValueForContext(
                    actualHistory,
                    kpiReportingDate,
                    node.data.frequency,
                    node.data.scope,
                    nextCampus,
                    node.data.valueType,
                    activeAggregationMode,
                    node.data.metricPersistence
                  ),
            metricsEditable,
            metricNote,
            reportingPeriodLabel: currentSnapshot
              ? `${kpiReportingDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })} · Current snapshot${node.data.apiActualEvidence?.observed_at ? ` · As of ${new Date(node.data.apiActualEvidence.observed_at).toLocaleDateString()}` : " · Awaiting verified sync"}`
              : formatKpiReportingPeriod(kpiReportingDate, node.data.frequency),
            comparisonMode: activeComparisonMode,
            statusNote: eventUpcoming
              ? "Upcoming"
              : [
                  financialPeriodInProgress ? "Month in progress" : null,
                  activeAggregationMode !== "current"
                    ? `${formatAggregationLabel(activeAggregationMode)} view`
                    : null,
                  activeComparisonMode !== "target"
                    ? `Compared to ${formatComparisonLabel(activeComparisonMode).toLowerCase()}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" | ") || undefined,
          },
        };
      }),
    [aggregationMode, comparisonMode]
  );

  const recalculateNodes = React.useCallback(
    (currentNodes: Node<KpiNodeData>[], currentEdges: Edge[]) => {
      const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));
      const incomingMap = new Map<string, Edge[]>();

      currentEdges.forEach((edge) => {
        const sourceNode = nodeMap.get(edge.source);
        const targetNode = nodeMap.get(edge.target);
        if (
          !sourceNode?.data.lenses.includes(activeLens) ||
          !targetNode?.data.lenses.includes(activeLens)
        ) {
          return;
        }
        if (!incomingMap.has(edge.target)) incomingMap.set(edge.target, []);
        const incomingEdges = incomingMap.get(edge.target)!;
        if (!incomingEdges.some((incomingEdge) => incomingEdge.source === edge.source)) {
          incomingEdges.push(edge);
        }
      });

      const computeScore = (
        nodeId: string,
        visiting = new Set<string>()
      ): { score: number | null; warning?: string; target?: number; actual?: number; colorActual?: number } => {
        if (visiting.has(nodeId)) {
          return { score: null, warning: "Circular dependency detected" };
        }

        const node = nodeMap.get(nodeId);
        if (!node) return { score: null, warning: "Missing node" };

        const data = node.data;
        const incomingEdges = incomingMap.get(nodeId) || [];
        const calculationMode = data.calculationMode;

        if (data.metricsRestricted) {
          return {
            score: null,
            target: data.target,
            actual: data.actual,
            colorActual: data.colorActual,
          };
        }

        if (calculationMode === "direct") {
          if (data.statusNote === "Upcoming" || isFinancialPeriodInProgressNote(data.statusNote)) {
            return {
              score: null,
              target: data.target,
              actual: data.actual,
              colorActual: data.valueType === "rating_1_10" ? data.actual : undefined,
            };
          }
          return {
            score: getDirectScore(
              data.target,
              data.actual,
              data.performanceDirection
            ),
            target: data.target,
            actual: data.actual,
            colorActual: data.valueType === "rating_1_10" ? data.actual : undefined,
          };
        }

        if (calculationMode === "formula_percentage") {
          if (!data.formulaNumeratorKpiId || !data.formulaDenominatorKpiId) {
            return {
              score: null,
              warning: "Select numerator and denominator KPIs for this percentage formula",
            };
          }

          const nextVisiting = new Set(visiting);
          nextVisiting.add(nodeId);
          const numeratorResult = computeScore(data.formulaNumeratorKpiId, nextVisiting);
          const denominatorResult = computeScore(data.formulaDenominatorKpiId, nextVisiting);

          if (numeratorResult.warning || denominatorResult.warning) {
            return {
              score: null,
              warning: "Numerator or denominator KPI needs attention before this percentage can calculate",
            };
          }

          const actual = calculateFormulaPercentage(
            numeratorResult.actual,
            denominatorResult.actual
          );

          if (typeof denominatorResult.actual === "number" && denominatorResult.actual <= 0) {
            return {
              score: null,
              warning: "Actual denominator must be greater than 0",
            };
          }

          if (typeof data.target !== "number") {
            return {
              score: null,
              actual,
              warning: "Set a target for this percentage KPI",
            };
          }

          if (typeof actual !== "number") {
            return {
              score: null,
              target: data.target,
              actual,
              warning: "Waiting on numerator and denominator actual values",
            };
          }

          return {
            score: getDirectScore(data.target, actual, data.performanceDirection),
            target: data.target,
            actual,
            colorActual: data.valueType === "rating_1_10" ? actual : undefined,
          };
        }

        if (incomingEdges.length === 0) {
          return { score: null, warning: "Calculated KPI has no child KPIs" };
        }

        if (calculationMode === "calculated_values") {
          const childNodes = incomingEdges
            .map((edge) => nodeMap.get(edge.source))
            .filter((childNode): childNode is Node<KpiNodeData> => Boolean(childNode));
          const uniqueChildValueTypes = Array.from(
            new Set(childNodes.map((childNode) => childNode.data.valueType))
          );
          if (uniqueChildValueTypes.length > 1) {
            return {
              score: null,
              warning: `Child KPIs use mixed value types (${uniqueChildValueTypes
                .map((valueType) => formatValueTypeLabel(valueType))
                .join(", ")}). Calculated parents need matching child value types.`,
            };
          }
          const rollupValueType = uniqueChildValueTypes[0] ?? data.valueType;
          const childResults = incomingEdges
            .map((edge) => {
              const nextVisiting = new Set(visiting);
              nextVisiting.add(nodeId);
              return computeScore(edge.source, nextVisiting);
            });
          const target = calculateChildValue(
            childResults.map((result) => result.target),
            rollupValueType
          );
          const actual = calculateChildValue(
            childResults.map((result) => result.actual),
            rollupValueType
          );
          const hasMissingTarget = childResults.some((result) => typeof result.target !== "number");
          const hasMissingActual = childResults.some((result) => typeof result.actual !== "number");

          if (
            hasMissingTarget ||
            hasMissingActual ||
            typeof target !== "number" ||
            typeof actual !== "number"
          ) {
            return {
              score: null,
              target,
              actual,
              warning:
                hasMissingTarget && hasMissingActual
                  ? "Waiting on child KPI targets and actuals"
                  : hasMissingTarget
                    ? "Waiting on child KPI targets"
                    : "Waiting on child KPI actuals",
            };
          }

          if (isFinancialPeriodInProgressNote(data.statusNote)) {
            return {
              score: null,
              target,
              actual,
              colorActual: data.valueType === "rating_1_10" ? actual : undefined,
            };
          }

          return {
            score: getDirectScore(target, actual, data.performanceDirection),
            target,
            actual,
            colorActual: data.valueType === "rating_1_10" ? actual : undefined,
          };
        }

        let configuredWeightTotal = 0;
        let weightedSum = 0;
        let validWeightTotal = 0;
        let weightedColorActualSum = 0;
        let validColorActualWeightTotal = 0;
        let missingWeight = false;
        let missingChildScore = false;
        let hasUpcomingChildren = false;
        let hasChildWarning = false;

        for (const edge of incomingEdges) {
          const childNode = nodeMap.get(edge.source);
          if (childNode?.data.statusNote === "Upcoming") {
            hasUpcomingChildren = true;
            continue;
          }

          const influence = normalizeInfluence(edge);

          if (influence === null) {
            missingWeight = true;
            continue;
          }

          configuredWeightTotal += influence;

          const nextVisiting = new Set(visiting);
          nextVisiting.add(nodeId);
          const childResult = computeScore(edge.source, nextVisiting);

          if (childResult.warning) {
            hasChildWarning = true;
          }

          const childScore = childResult.score ?? getScoreFromRatingActual(childResult.colorActual);

          if (childScore !== null) {
            weightedSum += childScore * influence;
            validWeightTotal += influence;
            if (typeof childResult.colorActual === "number") {
              weightedColorActualSum += childResult.colorActual * influence;
              validColorActualWeightTotal += influence;
            }
          } else {
            missingChildScore = true;
          }
        }

        if (missingWeight) {
          return {
            score: null,
            warning: "One or more child relationships are missing influence %",
          };
        }

        if (!hasUpcomingChildren && Math.abs(configuredWeightTotal - 1) > 0.001) {
          return {
            score: null,
            warning: `Influence must total 100% (currently ${Math.round(
              configuredWeightTotal * 100
            )}%)`,
          };
        }

        if (hasChildWarning) {
          return {
            score: null,
            warning: "One or more child KPIs need attention before this rollup can score",
          };
        }

        if (missingChildScore) {
          return {
            score: null,
            warning: "One or more child KPIs are still neutral",
          };
        }

        if (validWeightTotal === 0) {
          return {
            score: null,
            warning: hasUpcomingChildren
              ? "Upcoming"
              : "Child KPIs have no valid weighted performance",
          };
        }

        const score = weightedSum / validWeightTotal;
        return {
          score,
          colorActual:
            validColorActualWeightTotal > 0
              ? weightedColorActualSum / validColorActualWeightTotal
              : undefined,
        };
      };

      return currentNodes.map((node) => {
        const result = computeScore(node.id);
        const preserveRestrictedPerformance = Boolean(node.data.metricsRestricted);
        return {
          ...node,
          data: {
            ...node.data,
            target:
              node.data.calculationMode === "calculated_values"
                ? result.target
                : node.data.target,
            actual:
              node.data.calculationMode === "calculated_values" ||
              node.data.calculationMode === "formula_percentage"
                ? result.actual
                : node.data.actual,
            colorActual: preserveRestrictedPerformance ? node.data.colorActual : result.colorActual,
            status: preserveRestrictedPerformance ? node.data.status : getStatusFromScore(result.score),
            rollupWarning:
              preserveRestrictedPerformance || node.data.calculationMode === "direct"
                ? undefined
                : result.warning,
          },
        };
      });
    },
    [activeLens]
  );

  const applyContextAndRecalculate: (
    nextNodes: Node<KpiNodeData>[],
    nextEdges: Edge[],
    nextReferenceDate?: Date,
    nextCampus?: Campus,
    nextLens?: Lens
  ) => Node<KpiNodeData>[] = React.useCallback(
    (
      nextNodes: Node<KpiNodeData>[],
      nextEdges: Edge[],
      nextReferenceDate = referenceDate,
      nextCampus = campus,
      nextLens = activeLens
    ): Node<KpiNodeData>[] =>
      recalculateNodes(
        syncNodesToContext(nextNodes, nextReferenceDate, nextCampus, nextLens),
        nextEdges
      ),
    [activeLens, campus, recalculateNodes, referenceDate, syncNodesToContext]
  );

  const ownedKpiBranchIds = React.useMemo(() => {
    if (currentUserOwnerKeys.size === 0) {
      return new Set<string>();
    }

    const ownedIds = new Set(
      allNodes
        .filter((node) =>
          [node.data.owner, node.data.dataOwner].some((owner) =>
            currentUserOwnerKeys.has((owner ?? "").trim().toLowerCase())
          )
        )
        .map((node) => node.id)
    );
    const branchIds = new Set(ownedIds);
    const queue = Array.from(ownedIds);
    const nodeById = new Map(allNodes.map((node) => [node.id, node]));
    const getNodeY = (nodeId: string) => {
      const node = nodeById.get(nodeId);
      return node?.data.positionsByLens[activeLens]?.y ?? node?.position.y ?? 0;
    };

    while (queue.length > 0) {
      const parentId = queue.shift()!;
      allEdges.forEach((edge) => {
        const downstreamId =
          edge.target === parentId
            ? edge.source
            : edge.source === parentId && getNodeY(edge.target) >= getNodeY(parentId)
              ? edge.target
              : null;
        if (!downstreamId || branchIds.has(downstreamId)) {
          return;
        }
        branchIds.add(downstreamId);
        queue.push(downstreamId);
      });
    }

    return branchIds;
  }, [activeLens, allEdges, allNodes, currentUserOwnerKeys]);

  const isOwnedKpiOrDescendant = React.useCallback(
    (nodeId: string) => {
      if (currentUserOwnerKeys.size === 0) {
        return false;
      }

      const nodeById = new Map(allNodes.map((node) => [node.id, node]));
      const isOwned = (id: string) =>
        [nodeById.get(id)?.data.owner, nodeById.get(id)?.data.dataOwner].some((owner) =>
          currentUserOwnerKeys.has((owner ?? "").trim().toLowerCase())
        );
      const getNodeY = (id: string) => {
        const node = nodeById.get(id);
        return node?.data.positionsByLens[activeLens]?.y ?? node?.position.y ?? 0;
      };
      const visited = new Set<string>();
      const queue = [nodeId];

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) {
          continue;
        }
        visited.add(currentId);

        if (isOwned(currentId)) {
          return true;
        }

        allEdges.forEach((edge) => {
          const parentId =
            edge.source === currentId
              ? edge.target
              : edge.target === currentId && getNodeY(edge.source) <= getNodeY(currentId)
                ? edge.source
                : null;
          if (parentId && !visited.has(parentId)) {
            queue.push(parentId);
          }
        });
      }

      return false;
    },
    [activeLens, allEdges, allNodes, currentUserOwnerKeys]
  );

  const canEditKpiNode = React.useCallback(
    (id: string) =>
      canEditKpis || ownedKpiBranchIds.has(id) || isOwnedKpiOrDescendant(id),
    [canEditKpis, isOwnedKpiOrDescendant, ownedKpiBranchIds]
  );

  const canDeleteKpiNode = React.useCallback(
    (id: string) => canDeleteKpis || ownedKpiBranchIds.has(id) || isOwnedKpiOrDescendant(id),
    [canDeleteKpis, isOwnedKpiOrDescendant, ownedKpiBranchIds]
  );

  const canManageRelationshipFor = React.useCallback(
    (sourceId?: string | null, targetId?: string | null) =>
      canManageKpiRelationships ||
      (sourceId ? ownedKpiBranchIds.has(sourceId) : false) ||
      (targetId ? ownedKpiBranchIds.has(targetId) : false),
    [canManageKpiRelationships, ownedKpiBranchIds]
  );

  const canPersistWorkspace = canManageWorkspace || ownedKpiBranchIds.size > 0;

  const updateNodeById = React.useCallback(
    (id: string, updater: (node: Node<KpiNodeData>) => Node<KpiNodeData>) => {
      if (!canEditKpiNode(id) && !currentUserHasLegacyAdminRole) {
        setLoadError("You can only edit KPIs you own or KPIs under one you own.");
        return;
      }
      setAllNodes((previous) =>
        applyContextAndRecalculate(
          previous.map((node) => (node.id === id ? updater(node) : node)),
          allEdges
        )
      );
    },
    [allEdges, applyContextAndRecalculate, canEditKpiNode, currentUserHasLegacyAdminRole]
  );

  const onLabelChange = React.useCallback(
    (id: string, value: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          label: value,
        },
      }));
    },
    [updateNodeById]
  );

  const onLabelCommit = React.useCallback(
    (id: string) => {
      const currentNode = allNodes.find((node) => node.id === id);
      if (!currentNode) return;

      const trimmedValue = currentNode.data.label.trim();
      if (!trimmedValue) {
        const fallbackLabel = getUniqueKpiLabel("New KPI", id);
        updateNodeById(id, (node) => ({
          ...node,
          data: {
            ...node.data,
            label: fallbackLabel,
          },
        }));
        return;
      }

      const normalizedValue = normalizeKpiLabel(trimmedValue);
      const conflictingNode = allNodes.find(
        (node) => node.id !== id && normalizeKpiLabel(node.data.label) === normalizedValue
      );

      if (!conflictingNode) {
        if (trimmedValue !== currentNode.data.label) {
          updateNodeById(id, (node) => ({
            ...node,
            data: {
              ...node.data,
              label: trimmedValue,
            },
          }));
        }
        return;
      }

      const uniqueLabel = getUniqueKpiLabel(trimmedValue, id);
      window.alert(
        `A KPI named "${conflictingNode.data.label}" already exists, so this one was renamed to "${uniqueLabel}" to keep KPI names unique.`
      );
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          label: uniqueLabel,
        },
      }));
    },
    [allNodes, getUniqueKpiLabel, normalizeKpiLabel, updateNodeById]
  );

  const onMetricChange = React.useCallback(
    (id: string, field: "target" | "actual" | "unit", value: string) => {
      updateNodeById(id, (node) => {
        if (field === "unit") {
          return {
            ...node,
            data: {
              ...node.data,
              unit: value,
            },
          };
        }

        let nextValue = parseMetricInputValue(value);
        if (
          typeof nextValue === "number" &&
          !Number.isNaN(nextValue) &&
          node.data.valueType === "rating_1_10"
        ) {
          nextValue = clampRatingValue(nextValue);
        }
        const historyField = field === "target" ? "targetHistory" : "actualHistory";
        const kpiReportingDate = getKpiReportingDate(referenceDate, node.data.frequency, getEffectiveKpiApiConfig(node));

        return {
          ...node,
          data: {
            ...node.data,
            [field]: nextValue,
            [historyField]: setValueForContext(
              node.data[historyField],
              kpiReportingDate,
              node.data.frequency,
              node.data.scope,
              campus,
              nextValue
            ),
          },
        };
      });
    },
    [campus, referenceDate, updateNodeById]
  );

  const onConfigChange = React.useCallback(
    (
      id: string,
      field:
        | "scope"
        | "performanceDirection"
        | "supportsCumulative"
        | "metricPersistence"
        | "calculationMode",
      value: Scope | PerformanceDirection | boolean | MetricPersistence | KpiCalculationMode
    ) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
          ...(field === "calculationMode"
            ? {
                isRollup: (value as KpiCalculationMode) !== "direct",
                ...((value as KpiCalculationMode) === "formula_percentage"
                  ? { valueType: "percentage" as ValueType }
                  : {}),
              }
            : {}),
        },
      }));
    },
    [updateNodeById]
  );

  const onDetailChange = React.useCallback(
    (id: string, field: DetailFieldKey, value: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
          ...(field === "dataSource" && value === "Poll" ? { valueType: "rating_1_10", apiConfig: null } : {}),
          ...(field === "dataSource" && value === "Manual" ? { apiConfig: null } : {}),
          ...(field === "dataSource" && value === "API"
            ? {
                apiConfig: node.data.apiConfig ?? getKpiApiConfigForLabel(node.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                valueType: "count" as ValueType,
              }
            : {}),
          ...(field === "valueType" && node.data.calculationMode === "formula_percentage"
            ? { valueType: "percentage" as ValueType }
            : {}),
        },
      }));
    },
    [updateNodeById]
  );

  const onApiConfigChange = React.useCallback(
    (id: string, config: KpiApiConfig | null) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          apiConfig: config,
          dataSource: config ? "API" : node.data.dataSource,
          ...(config?.metric === "reported_rate" ? { valueType: "percentage" as ValueType } : {}),
          ...(config && config.metric !== "reported_rate" ? { valueType: "count" as ValueType } : {}),
        },
      }));
    },
    [updateNodeById]
  );

  const onRestrictedFieldsChange = React.useCallback(
    (id: string, restrictedFields: RestrictedFields) => {
      if (!canManageWorkspace) {
        return;
      }
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          restrictedFields,
        },
      }));
    },
    [canManageWorkspace, updateNodeById]
  );

  const onToggleRollup = React.useCallback(
    (id: string, checked: boolean) => {
      const calculationMode: KpiCalculationMode = checked ? "influenced_by_children" : "direct";
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          isRollup: checked,
          calculationMode,
        },
      }));
    },
    [updateNodeById]
  );

  const onAddKpiTask = React.useCallback(
    (id: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: [...node.data.tasks, createTask()],
        },
      }));
    },
    [updateNodeById]
  );

  const onCreatePoll = React.useCallback(
    (id: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          pollConfig:
            node.data.pollConfig ??
            createPollConfig({
              label: node.data.label,
              definition: node.data.definition,
              measurement: node.data.measurement,
            }),
        },
      }));
    },
    [updateNodeById]
  );

  const onPollConfigChange = React.useCallback(
    (id: string, field: keyof Omit<PollConfig, "id">, value: string | boolean) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          pollConfig: {
            ...(node.data.pollConfig ?? createPollConfig()),
            [field]: value,
          },
        },
      }));
    },
    [updateNodeById]
  );

  const onTogglePollAudienceGroup = React.useCallback(
    (id: string, groupName: string) => {
      updateNodeById(id, (node) => {
        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const currentGroups =
          currentPoll.targetGroups.length > 0
            ? currentPoll.targetGroups
            : currentPoll.targetGroup
              ? [currentPoll.targetGroup]
              : [];
        const nextGroups = currentGroups.includes(groupName)
          ? currentGroups.filter((group) => group !== groupName)
          : [...currentGroups, groupName];

        return {
          ...node,
          data: {
            ...node.data,
            pollConfig: {
              ...currentPoll,
              targetGroup: undefined,
              targetGroups: nextGroups,
            },
          },
        };
      });
    },
    [updateNodeById]
  );

  const onTogglePollLinkedKpi = React.useCallback(
    (id: string, linkedKpiId: string) => {
      updateNodeById(id, (node) => {
        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const currentLinkedKpiIds = currentPoll.linkedKpiIds ?? [];
        const nextLinkedKpiIds = currentLinkedKpiIds.includes(linkedKpiId)
          ? currentLinkedKpiIds.filter((currentId) => currentId !== linkedKpiId)
          : [...currentLinkedKpiIds, linkedKpiId];

        return {
          ...node,
          data: {
            ...node.data,
            pollConfig: {
              ...currentPoll,
              linkedKpiIds: nextLinkedKpiIds,
            },
          },
        };
      });
    },
    [updateNodeById]
  );

  const onPublishPoll = React.useCallback(
    (id: string) => {
      setAllNodes((previous) => {
        const node = previous.find((currentNode) => currentNode.id === id);
        if (!node) {
          return previous;
        }

        const publishContext = getPollPublishContext(node.data.scope, campus);
        if (!publishContext.canPublish) {
          return previous;
        }

        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const campaignKpiIds = getPollCampaignKpiIds(id, currentPoll);
        const selectedGroups =
          currentPoll.targetGroups.length > 0
            ? currentPoll.targetGroups
            : currentPoll.targetGroup
              ? [currentPoll.targetGroup]
              : [];
        const matchedRecipients = pollRecipients.filter((recipient) =>
          recipient.groups.some((group) => selectedGroups.includes(group))
        );
        const existingInvites = currentPoll.invites ?? [];
        const recipientInvites = matchedRecipients.map((recipient) => {
          const existingInvite = existingInvites.find(
            (invite) => invite.recipientId === recipient.id || invite.recipientEmail === recipient.email
          );

          return {
            id: existingInvite?.id ?? uuidv4(),
            token: existingInvite?.token ?? createToken(),
            recipientId: recipient.id,
            recipientName: getPollRecipientLabel(recipient),
            recipientEmail: recipient.email,
            sentAt: existingInvite?.sentAt ?? new Date().toISOString(),
            completedAt: existingInvite?.completedAt,
          };
        });
        const generalInvite =
          matchedRecipients.length === 0 && selectedGroups.length > 0
            ? (() => {
                const existingInvite = existingInvites.find(
                  (invite) => invite.recipientId === "__general__"
                );

                return {
                  id: existingInvite?.id ?? uuidv4(),
                  token: existingInvite?.token ?? createToken(),
                  recipientId: "__general__",
                  recipientName: `General share link for ${selectedGroups.join(", ")}`,
                  recipientEmail: "",
                  sentAt: existingInvite?.sentAt ?? new Date().toISOString(),
                  completedAt: existingInvite?.completedAt,
                };
              })()
            : null;
        const nextInvites = generalInvite ? [generalInvite] : recipientInvites;
        const publishedAt = new Date().toISOString();

        return previous.map((currentNode) => {
          if (!campaignKpiIds.includes(currentNode.id)) {
            return currentNode;
          }

          const existingPoll =
            currentNode.data.pollConfig ??
            createPollConfig({
              label: currentNode.data.label,
              definition: currentNode.data.definition,
              measurement: currentNode.data.measurement,
            });

          return {
            ...currentNode,
            data: {
              ...currentNode.data,
              unit: "",
              dataSource: "Poll",
              valueType: "rating_1_10",
              pollConfig: {
                ...existingPoll,
                campaignRootKpiId: id,
                linkedKpiIds: campaignKpiIds.filter((campaignKpiId) => campaignKpiId !== currentNode.id),
                title: currentPoll.title,
                description: currentPoll.description,
                targetGroup: undefined,
                targetGroups: selectedGroups,
                isAnonymous: currentPoll.isAnonymous,
                status: "published",
                publishedAt,
                invites: nextInvites,
                questionPrompt:
                  currentNode.id === id
                    ? currentPoll.questionPrompt
                    : buildPollQuestionPrompt({
                        label: currentNode.data.label,
                        definition: currentNode.data.definition,
                        measurement: currentNode.data.measurement,
                      }),
              },
            },
          };
        });
      });
    },
    [campus, pollRecipients]
  );

  const onRerunPoll = React.useCallback(
    (id: string) => {
      const confirmed = window.confirm(
        "Re-run this poll? This will clear prior responses and generate fresh invite links for the next round."
      );
      if (!confirmed) {
        return;
      }

      setAllNodes((previous) => {
        const node = previous.find((currentNode) => currentNode.id === id);
        if (!node) {
          return previous;
        }

        const publishContext = getPollPublishContext(node.data.scope, campus);
        if (!publishContext.canPublish) {
          return previous;
        }

        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const campaignKpiIds = getPollCampaignKpiIds(id, currentPoll);
        const selectedGroups =
          currentPoll.targetGroups.length > 0
            ? currentPoll.targetGroups
            : currentPoll.targetGroup
              ? [currentPoll.targetGroup]
              : [];
        const matchedRecipients = pollRecipients.filter((recipient) =>
          recipient.groups.some((group) => selectedGroups.includes(group))
        );
        const nextInvites =
          matchedRecipients.length === 0 && selectedGroups.length > 0
            ? [
                {
                  id: uuidv4(),
                  token: createToken(),
                  recipientId: "__general__",
                  recipientName: `General share link for ${selectedGroups.join(", ")}`,
                  recipientEmail: "",
                  sentAt: new Date().toISOString(),
                  completedAt: undefined,
                },
              ]
            : matchedRecipients.map((recipient) => ({
                id: uuidv4(),
                token: createToken(),
                recipientId: recipient.id,
                recipientName: getPollRecipientLabel(recipient),
                recipientEmail: recipient.email,
                sentAt: new Date().toISOString(),
                completedAt: undefined,
              }));
        const publishedAt = new Date().toISOString();

        return applyContextAndRecalculate(
          previous.map((currentNode) => {
            if (!campaignKpiIds.includes(currentNode.id)) {
              return currentNode;
            }

            const existingPoll =
              currentNode.data.pollConfig ??
              createPollConfig({
                label: currentNode.data.label,
                definition: currentNode.data.definition,
                measurement: currentNode.data.measurement,
              });

            return {
              ...currentNode,
              data: {
                ...currentNode.data,
                unit: "",
                dataSource: "Poll",
                valueType: "rating_1_10",
                actualHistory: {},
                pollConfig: {
                  ...existingPoll,
                  campaignRootKpiId: id,
                  linkedKpiIds: campaignKpiIds.filter((campaignKpiId) => campaignKpiId !== currentNode.id),
                  title: currentPoll.title,
                  description: currentPoll.description,
                  targetGroup: undefined,
                  targetGroups: selectedGroups,
                  isAnonymous: currentPoll.isAnonymous,
                  status: "published",
                  publishedAt,
                  responses: [],
                  invites: nextInvites,
                  questionPrompt:
                    currentNode.id === id
                      ? currentPoll.questionPrompt
                      : buildPollQuestionPrompt({
                          label: currentNode.data.label,
                          definition: currentNode.data.definition,
                          measurement: currentNode.data.measurement,
                        }),
                },
              },
            };
          }),
          allEdges
        );
      });
    },
    [allEdges, applyContextAndRecalculate, campus, pollRecipients]
  );

  const onTaskChange = React.useCallback(
    (id: string, taskId: string, field: keyof Omit<TaskItem, "id">, value: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: node.data.tasks.map((task) =>
            task.id === taskId ? { ...task, [field]: value } : task
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onDeleteKpiTask = React.useCallback(
    (id: string, taskId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: node.data.tasks.filter((task) => task.id !== taskId),
        },
      }));
    },
    [updateNodeById]
  );

  const onAddInitiative = React.useCallback(
    (id: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: [...node.data.initiatives, createInitiative()],
        },
      }));
    },
    [updateNodeById]
  );

  const onInitiativeChange = React.useCallback(
    (
      id: string,
      initiativeId: string,
      field: keyof Omit<InitiativeItem, "id" | "tasks">,
      value: string
    ) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? { ...initiative, [field]: value }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onDeleteInitiative = React.useCallback(
    (id: string, initiativeId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.filter(
            (initiative) => initiative.id !== initiativeId
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onAddInitiativeTask = React.useCallback(
    (id: string, initiativeId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? {
                  ...initiative,
                  tasks: [...initiative.tasks, createTask()],
                }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onInitiativeTaskChange = React.useCallback(
    (
      id: string,
      initiativeId: string,
      taskId: string,
      field: keyof Omit<TaskItem, "id">,
      value: string
    ) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? {
                  ...initiative,
                  tasks: initiative.tasks.map((task) =>
                    task.id === taskId ? { ...task, [field]: value } : task
                  ),
                }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onDeleteInitiativeTask = React.useCallback(
    (id: string, initiativeId: string, taskId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? {
                  ...initiative,
                  tasks: initiative.tasks.filter((task) => task.id !== taskId),
                }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const getVisibleNodeIdsForLens = React.useCallback(
    (nodes: Node<KpiNodeData>[], lens: Lens) => {
      const lensNodes = nodes.filter((node) => node.data.lenses.includes(lens));
      const lensEdges = allEdges.filter((edge) => {
        const sourceNode = lensNodes.find((node) => node.id === edge.source);
        const targetNode = lensNodes.find((node) => node.id === edge.target);
        return Boolean(sourceNode && targetNode);
      });

      const childIdsByParent = new Map<string, string[]>();
      const parentIdsByChild = new Map<string, string[]>();

      lensEdges.forEach((edge) => {
        pushUniqueMapValue(childIdsByParent, edge.target, edge.source);
        pushUniqueMapValue(parentIdsByChild, edge.source, edge.target);
      });

      const topLevelNodeIds = lensNodes
        .filter((node) => !parentIdsByChild.has(node.id))
        .map((node) => node.id);

      const visibleNodeIds = new Set<string>();
      const queue =
        topLevelNodeIds.length > 0
          ? [...topLevelNodeIds]
          : lensNodes.map((node) => node.id);

      while (queue.length > 0) {
        const nodeId = queue.shift()!;
        if (visibleNodeIds.has(nodeId)) continue;
        visibleNodeIds.add(nodeId);

        const node = lensNodes.find((item) => item.id === nodeId);
        if (!node || node.data.collapsedInLenses.includes(lens)) continue;

        (childIdsByParent.get(nodeId) ?? []).forEach((childId) => {
          if (!visibleNodeIds.has(childId)) {
            queue.push(childId);
          }
        });
      }

      return visibleNodeIds;
    },
    [allEdges]
  );

  const autoLayoutChildren = React.useCallback(
    (nodes: Node<KpiNodeData>[], parentIds: string[]) => {
      if (parentIds.length === 0) return nodes;

      const nextNodes = nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          positionsByLens: { ...node.data.positionsByLens },
          collapsedInLenses: [...node.data.collapsedInLenses],
        },
      }));
      const nodeMap = new Map(nextNodes.map((node) => [node.id, node]));
      const visibleNodeIds = getVisibleNodeIdsForLens(nextNodes, activeLens);
      const activeLensEdges = allEdges.filter(
        (edge) =>
          nodeMap.get(edge.source)?.data.lenses.includes(activeLens) &&
          nodeMap.get(edge.target)?.data.lenses.includes(activeLens)
      );
      const childIdsByParent = new Map<string, string[]>();
      activeLensEdges.forEach((edge) => {
        pushUniqueMapValue(childIdsByParent, edge.target, edge.source);
      });

      const subtreeIds = new Set<string>();
      const collectSubtree = (parentId: string) => {
        (childIdsByParent.get(parentId) ?? []).forEach((childId) => {
          if (subtreeIds.has(childId)) return;
          subtreeIds.add(childId);
          collectSubtree(childId);
        });
      };
      parentIds.forEach(collectSubtree);

      const occupiedRects = nextNodes
        .filter(
          (node) =>
            node.data.lenses.includes(activeLens) &&
            visibleNodeIds.has(node.id) &&
            !subtreeIds.has(node.id)
        )
        .map((node) => {
          const position = node.data.positionsByLens[activeLens] ?? node.position;
          return {
            x: position.x - KPI_NODE_MARGIN,
            y: position.y - KPI_NODE_MARGIN,
            width: KPI_NODE_WIDTH + KPI_NODE_MARGIN * 2,
            height: KPI_NODE_HEIGHT + KPI_NODE_MARGIN * 2,
          };
        });

      const layoutBranch = (parentId: string, path = new Set<string>()) => {
        if (path.has(parentId)) return;
        const parentNode = nodeMap.get(parentId);
        if (!parentNode) return;
        const nextPath = new Set(path);
        nextPath.add(parentId);

        const childIds = (childIdsByParent.get(parentId) ?? []).filter((childId) =>
          nodeMap.get(childId)?.data.lenses.includes(activeLens)
        );
        if (childIds.length === 0) return;

        const parentPosition = parentNode.data.positionsByLens[activeLens] ?? parentNode.position;
        const sortedChildIds = [...childIds].sort((leftId, rightId) => {
          const left = nodeMap.get(leftId);
          const right = nodeMap.get(rightId);
          const leftX = left?.data.positionsByLens[activeLens]?.x ?? left?.position.x ?? 0;
          const rightX = right?.data.positionsByLens[activeLens]?.x ?? right?.position.x ?? 0;
          return leftX - rightX;
        });
        const preferredXPositions = sortedChildIds.map(
          (_childId, index) =>
            parentPosition.x +
            (index - (sortedChildIds.length - 1) / 2) * KPI_HORIZONTAL_GAP
        );

        const minimumChildY = parentPosition.y + KPI_NODE_HEIGHT + KPI_VERTICAL_GUTTER;
        let rowY = minimumChildY;
        for (let depthOffset = 0; depthOffset < 10; depthOffset += 1) {
          const candidateY = minimumChildY + depthOffset * KPI_VERTICAL_GAP;
          const candidateRects = preferredXPositions.map((x) => ({
            x: x - KPI_NODE_MARGIN,
            y: candidateY - KPI_NODE_MARGIN,
            width: KPI_NODE_WIDTH + KPI_NODE_MARGIN * 2,
            height: KPI_NODE_HEIGHT + KPI_NODE_MARGIN * 2,
          }));

          const hasCollision = candidateRects.some((candidateRect) =>
            occupiedRects.some((rect) => rectanglesOverlap(rect, candidateRect))
          );

          if (!hasCollision) {
            rowY = candidateY;
            candidateRects.forEach((rect) => occupiedRects.push(rect));
            break;
          }
        }

        sortedChildIds.forEach((childId, index) => {
          const childNode = nodeMap.get(childId);
          if (!childNode) return;

          const nextPosition = {
            x: preferredXPositions[index],
            y: rowY,
          };

          childNode.position = nextPosition;
          childNode.data.positionsByLens[activeLens] = nextPosition;

          if (!childNode.data.collapsedInLenses.includes(activeLens)) {
            layoutBranch(childId, nextPath);
          }
        });
      };

      parentIds.forEach((parentId) => {
        if (nodeMap.get(parentId)?.data.lenses.includes(activeLens)) {
          layoutBranch(parentId);
        }
      });

      return nextNodes;
    },
    [activeLens, allEdges, getVisibleNodeIdsForLens]
  );

  const layoutVisibleLensGraph = React.useCallback(
    (
      nodes: Node<KpiNodeData>[],
      lens: Lens = activeLens,
      edges: Edge[] = allEdges,
      options?: {
        compact?: boolean;
      }
    ): Node<KpiNodeData>[] => {
      const compact = options?.compact ?? false;
      const nextNodes = nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          positionsByLens: { ...node.data.positionsByLens },
          collapsedInLenses: [...node.data.collapsedInLenses],
        },
      }));

      const lensNodes = nextNodes.filter((node) => node.data.lenses.includes(lens));
      if (lensNodes.length === 0) return nextNodes;

      const lensNodeMap = new Map(lensNodes.map((node) => [node.id, node]));
      const lensEdges = edges.filter(
        (edge) => lensNodeMap.has(edge.source) && lensNodeMap.has(edge.target)
      );

      const childIdsByParent = new Map<string, string[]>();
      const parentIdsByChild = new Map<string, string[]>();

      lensEdges.forEach((edge) => {
        pushUniqueMapValue(childIdsByParent, edge.target, edge.source);
        pushUniqueMapValue(parentIdsByChild, edge.source, edge.target);
      });

      const topLevelNodeIds = lensNodes
        .filter((node) => !parentIdsByChild.has(node.id))
        .map((node) => node.id);

      const visibleNodeIds = new Set<string>();
      const queue =
        topLevelNodeIds.length > 0
          ? [...topLevelNodeIds]
          : lensNodes.map((node) => node.id);

      while (queue.length > 0) {
        const nodeId = queue.shift()!;
        if (visibleNodeIds.has(nodeId)) continue;
        visibleNodeIds.add(nodeId);

        const node = lensNodeMap.get(nodeId);
        if (!node || node.data.collapsedInLenses.includes(lens)) continue;

        (childIdsByParent.get(nodeId) ?? []).forEach((childId) => {
          if (!visibleNodeIds.has(childId)) {
            queue.push(childId);
          }
        });
      }

      const visibleRoots =
        topLevelNodeIds.filter((nodeId) => visibleNodeIds.has(nodeId)).length > 0
          ? topLevelNodeIds.filter((nodeId) => visibleNodeIds.has(nodeId))
          : Array.from(visibleNodeIds);

      const depthByNodeId = new Map<string, number>();
      const assignDepth = (nodeId: string, depth: number, path = new Set<string>()) => {
        if (path.has(nodeId)) return;
        const currentDepth = depthByNodeId.get(nodeId);
        if (currentDepth !== undefined && currentDepth >= depth) return;
        depthByNodeId.set(nodeId, depth);
        const nextPath = new Set(path);
        nextPath.add(nodeId);

        (childIdsByParent.get(nodeId) ?? []).forEach((childId) => {
          if (visibleNodeIds.has(childId)) {
            assignDepth(childId, depth + 1, nextPath);
          }
        });
      };
      visibleRoots.forEach((nodeId) => assignDepth(nodeId, 0));

      const rowNodeIds = new Map<number, string[]>();
      visibleNodeIds.forEach((nodeId) => {
        const depth = depthByNodeId.get(nodeId) ?? 0;
        if (!rowNodeIds.has(depth)) {
          rowNodeIds.set(depth, []);
        }
        rowNodeIds.get(depth)!.push(nodeId);
      });

      const placeRow = (nodeIds: string[], targetXs: number[]) => {
        const ordered = nodeIds
          .map((nodeId, index) => ({ nodeId, targetX: targetXs[index] }))
          .sort((left, right) => left.targetX - right.targetX);

        const placed: Array<{ nodeId: string; targetX: number; x: number }> = [];
        ordered.forEach((item, index) => {
          const previousX = index === 0 ? item.targetX : placed[index - 1].x;
          placed.push({
            ...item,
            x: index === 0 ? item.targetX : Math.max(item.targetX, previousX + KPI_LAYER_HORIZONTAL_GAP),
          });
        });

        const desiredCenter =
          ordered.reduce((sum, item) => sum + item.targetX, 0) / Math.max(ordered.length, 1);
        const actualCenter =
          placed.length === 1 ? placed[0].x : (placed[0].x + placed[placed.length - 1].x) / 2;
        const shift = desiredCenter - actualCenter;

        return placed.map((item) => ({ nodeId: item.nodeId, x: item.x + shift }));
      };

      const rootCurrentYValues = visibleRoots
        .map((nodeId) => lensNodeMap.get(nodeId)?.data.positionsByLens[lens]?.y)
        .filter((value): value is number => typeof value === "number");
      const startY =
        rootCurrentYValues.length > 0
          ? Math.min(...rootCurrentYValues)
          : 120;

      const xByNodeId = new Map<string, number>();
      const maxDepth = Math.max(...Array.from(rowNodeIds.keys()), 0);
      const getCurrentLensX = (nodeId: string) =>
        lensNodeMap.get(nodeId)?.data.positionsByLens[lens]?.x ??
        lensNodeMap.get(nodeId)?.position.x ??
        0;

      for (let depth = 0; depth <= maxDepth; depth += 1) {
        const rowIds = rowNodeIds.get(depth) ?? [];
        const nodeIds = compact
          ? [...rowIds].sort((leftId, rightId) => getCurrentLensX(leftId) - getCurrentLensX(rightId))
          : rowIds;
        if (nodeIds.length === 0) continue;

        const targetXs =
          compact
            ? depth === 0
              ? nodeIds.map(
                  (_nodeId, index) =>
                    (index - (nodeIds.length - 1) / 2) * KPI_LAYER_HORIZONTAL_GAP
                )
              : nodeIds.map((nodeId) => {
                  const parentIds = (parentIdsByChild.get(nodeId) ?? []).filter((parentId) =>
                    visibleNodeIds.has(parentId)
                  );
                  const parentXs = parentIds
                    .map((parentId) => xByNodeId.get(parentId))
                    .filter((value): value is number => typeof value === "number");

                  if (parentXs.length === 0) {
                    return getCurrentLensX(nodeId);
                  }

                  const parentCenterX = parentXs.reduce((sum, value) => sum + value, 0) / parentXs.length;
                  const siblingIds = Array.from(
                    new Set(
                      parentIds.flatMap((parentId) =>
                        (childIdsByParent.get(parentId) ?? []).filter((childId) => visibleNodeIds.has(childId))
                      )
                    )
                  ).sort((leftId, rightId) => getCurrentLensX(leftId) - getCurrentLensX(rightId));
                  const siblingIndex = Math.max(siblingIds.indexOf(nodeId), 0);

                  return (
                    parentCenterX +
                    (siblingIndex - (siblingIds.length - 1) / 2) * KPI_LAYER_HORIZONTAL_GAP
                  );
                })
            : depth === 0
              ? nodeIds.map((nodeId) => getCurrentLensX(nodeId))
            : nodeIds.map((nodeId) => {
                const parentIds = (parentIdsByChild.get(nodeId) ?? []).filter((parentId) =>
                  visibleNodeIds.has(parentId)
                );
                if (parentIds.length === 0) {
                  return (
                    lensNodeMap.get(nodeId)?.data.positionsByLens[lens]?.x ??
                    lensNodeMap.get(nodeId)?.position.x ??
                    0
                  );
                }

                const parentXs = parentIds
                  .map((parentId) => xByNodeId.get(parentId))
                  .filter((value): value is number => typeof value === "number");

                if (parentXs.length === 0) {
                  return (
                    lensNodeMap.get(nodeId)?.data.positionsByLens[lens]?.x ??
                    lensNodeMap.get(nodeId)?.position.x ??
                    0
                  );
                }

                return parentXs.reduce((sum, value) => sum + value, 0) / parentXs.length;
              });

        const placedRow =
          compact && depth > 0
            ? nodeIds.map((nodeId, index) => ({ nodeId, x: targetXs[index] }))
            : placeRow(nodeIds, targetXs);
        const rowY = startY + depth * KPI_LAYER_VERTICAL_GAP;

        placedRow.forEach(({ nodeId, x }) => {
          xByNodeId.set(nodeId, x);
          const node = lensNodeMap.get(nodeId);
          if (!node) return;

          const nextPosition = { x, y: rowY };
          node.position = nextPosition;
          node.data.positionsByLens[lens] = nextPosition;
        });
      }

      return nextNodes;
    },
    [activeLens, allEdges]
  );

  const onNodesChange = React.useCallback(
    (changes: NodeChange[]) => {
      if (!canManageWorkspace) {
        return;
      }
      setAllNodes((previous) => {
        const visible = previous
          .filter((node) => node.data.lenses.includes(activeLens))
          .map((node) => ({
            ...node,
            position: node.data.positionsByLens[activeLens] ?? node.position,
          }));

        const updatedVisible = applyNodeChanges(changes, visible);
        const updatedMap = new Map(updatedVisible.map((node) => [node.id, node]));
        const movedParentIds = changes.reduce<string[]>((ids, change) => {
          if (
            change.type === "position" &&
            "id" in change &&
            "dragging" in change &&
            change.dragging === false
          ) {
            ids.push(change.id);
          }
          return ids;
        }, []);

        const nextNodes = previous.map((node) => {
          if (!node.data.lenses.includes(activeLens)) return node;
          const updated = updatedMap.get(node.id);
          if (!updated) return node;

          return {
            ...node,
            ...updated,
            position: updated.position ?? node.position,
            data: {
              ...node.data,
              ...updated.data,
              positionsByLens: {
                ...node.data.positionsByLens,
                [activeLens]: updated.position ?? node.position,
              },
            },
          };
        });

        return autoLayoutChildren(nextNodes, movedParentIds);
      });
    },
    [activeLens, autoLayoutChildren, canManageWorkspace]
  );

  const onEdgesChange = React.useCallback((changes: EdgeChange[]) => {
    if (
      changes.some((change) => {
        if (!("id" in change)) return false;
        const edge = allEdges.find((item) => item.id === change.id);
        return !canManageRelationshipFor(edge?.source, edge?.target);
      })
    ) {
      return;
    }
    setAllEdges((previous) => applyEdgeChanges(changes, previous));
  }, [allEdges, canManageRelationshipFor]);

  const createNodeData: (
    overrides: Partial<KpiNodeData>,
    position: { x: number; y: number }
  ) => KpiNodeData = React.useCallback(
    (
      overrides: Partial<KpiNodeData>,
      position: { x: number; y: number }
    ): KpiNodeData => {
      const nextLabel = getUniqueKpiLabel(overrides.label ?? "New KPI");
      return {
      target: undefined,
      targetHistory: {},
      actual: undefined,
      actualHistory: {},
      unit: "",
      frequency: "weekly",
      scope: "global",
      performanceDirection: "higher_is_better",
      supportsCumulative: false,
      metricPersistence: "period_only",
      calculationMode: "direct",
      eventDate: "",
      definition: "Describe what this KPI is intended to measure.",
      measurement: "Describe exactly how this KPI is counted or calculated.",
      owner: "Assign an owner",
      dataOwner: "",
      dataSource: "Manual",
      apiConfig: null,
      valueType: "count",
      formulaNumeratorKpiId: "",
      formulaDenominatorKpiId: "",
      updateNotes: "Add any timing, validation, or reporting notes here.",
      pollConfig: null,
      restrictedFields: {},
      initiatives: [],
      tasks: [],
      lenses: [activeLens],
      lensLabels: [activeLens],
      positionsByLens: { [activeLens]: position },
      collapsedInLenses: [],
      status: "neutral",
      metricsRestricted: false,
      isRollup: false,
      metricsEditable: true,
      metricNote: undefined,
      statusNote: undefined,
      comparisonMode,
      rollupWarning: undefined,
    childCount: 0,
    childrenCollapsed: false,
    onOpenDetails: noopOpenDetails,
    onSelectNode: noopSelectNode,
    onLabelChange: noopLabelChange,
      onMetricChange: noopMetricChange,
      onConfigChange: noopConfigChange,
      onDetailChange: noopDetailChange,
      onApiConfigChange: noopApiConfigChange,
      onRestrictedFieldsChange: noopRestrictedFieldsChange,
      onToggleRollup: noopToggleRollup,
      onAddChild: noopAddChild,
      onToggleChildren: noopToggleChildren,
      ...overrides,
      label: nextLabel,
      isInitiativeRailOpen: false,
      onToggleInitiativeRail: noopToggleInitiativeRail,
    };
    },
    [activeLens, comparisonMode, getUniqueKpiLabel]
  );

  const getChildBranchForLens = React.useCallback(
    (
      rootNodeId: string,
      sourceLens: Lens,
      nodes: Node<KpiNodeData>[],
      edges: Edge[]
    ) => {
      const nodeMap = new Map(nodes.map((node) => [node.id, node]));
      const branchNodeIds = new Set<string>();
      const branchEdges: Edge[] = [];
      const queue = [rootNodeId];

      while (queue.length > 0) {
        const parentId = queue.shift()!;
        edges.forEach((edge) => {
          if (edge.target !== parentId || !edgeBelongsToLens(edge, sourceLens, nodeMap)) {
            return;
          }

          branchEdges.push(edge);
          if (!branchNodeIds.has(edge.source)) {
            branchNodeIds.add(edge.source);
            queue.push(edge.source);
          }
        });
      }

      return {
        childNodes: Array.from(branchNodeIds)
          .map((nodeId) => nodeMap.get(nodeId))
          .filter((node): node is Node<KpiNodeData> => Boolean(node)),
        branchEdges,
      };
    },
    []
  );

  const chooseLensToImportChildrenFrom = React.useCallback(
    (node: Node<KpiNodeData>) => {
      const options = node.data.lenses
        .filter((lens) => lens !== activeLens)
        .filter((lens) => getChildBranchForLens(node.id, lens, allNodes, allEdges).branchEdges.length > 0);

      if (options.length === 0) return null;

      const wantsImport = window.confirm(
        `Include child KPIs for ${node.data.label} from another lens?`
      );
      if (!wantsImport) return null;

      if (options.length === 1) {
        return options[0];
      }

      const promptValue = window.prompt(
        `Choose a lens to copy child relationships from:\n${options
          .map((lens, index) => `${index + 1}. ${lensConfigs[lens]?.name ?? lens}`)
          .join("\n")}`,
        "1"
      );
      if (promptValue === null) return null;

      const numericChoice = Number(promptValue.trim());
      if (!Number.isNaN(numericChoice) && numericChoice >= 1 && numericChoice <= options.length) {
        return options[numericChoice - 1];
      }

      return (
        options.find(
          (lens) =>
            (lensConfigs[lens]?.name ?? lens).toLowerCase() === promptValue.trim().toLowerCase()
        ) ?? null
      );
    },
    [activeLens, allEdges, allNodes, getChildBranchForLens, lensConfigs]
  );

  const importChildBranchIntoLens = React.useCallback(
    (
      rootNodeId: string,
      sourceLens: Lens,
      targetLens: Lens,
      anchorPosition: { x: number; y: number },
      nodes: Node<KpiNodeData>[],
      edges: Edge[]
    ) => {
      const nodeMap = new Map(nodes.map((node) => [node.id, node]));
      const rootNode = nodeMap.get(rootNodeId);
      if (!rootNode) return { nodes, edges };

      const { childNodes, branchEdges } = getChildBranchForLens(rootNodeId, sourceLens, nodes, edges);
      if (childNodes.length === 0 && branchEdges.length === 0) {
        return { nodes, edges };
      }

      const sourceRootPosition = rootNode.data.positionsByLens[sourceLens] ?? rootNode.position;
      const offsetX = anchorPosition.x - sourceRootPosition.x;
      const offsetY = anchorPosition.y - sourceRootPosition.y;

      const nextNodes = nodes.map((node) => {
        const branchNode = childNodes.find((item) => item.id === node.id);
        if (!branchNode) return node;

        const sourcePosition = branchNode.data.positionsByLens[sourceLens] ?? branchNode.position;
        const importedPosition = {
          x: sourcePosition.x + offsetX,
          y: sourcePosition.y + offsetY,
        };
        const nextLenses = branchNode.data.lenses.includes(targetLens)
          ? branchNode.data.lenses
          : [...branchNode.data.lenses, targetLens];

        return {
          ...node,
          data: {
            ...node.data,
            lenses: nextLenses,
            positionsByLens: {
              ...node.data.positionsByLens,
              [targetLens]: importedPosition,
            },
          },
          position: importedPosition,
        };
      });

      const nextEdges = edges.map((edge) =>
        branchEdges.some((item) => item.id === edge.id)
          ? setEdgeLensMembership(edge, targetLens, true)
          : edge
      );

      return { nodes: nextNodes, edges: nextEdges };
    },
    [getChildBranchForLens]
  );

  const addChildKpi = React.useCallback(
    (parentId: string, influence?: number) => {
      const parent = allNodes.find((node) => node.id === parentId);
      if (!parent) return;

      const childId = uuidv4();
      const nodeMap = new Map(allNodes.map((node) => [node.id, node]));
      const siblings = allEdges.filter(
        (edge) => edge.target === parentId && edgeBelongsToLens(edge, activeLens, nodeMap)
      ).length;
      const parentPosition = getSafeNodePositionForLens(parent, activeLens);
      const position = {
        x: parentPosition.x - 250 + siblings * 300,
        y: parentPosition.y + 260,
      };

      const newNode: Node<KpiNodeData> = {
        id: childId,
        type: "kpi",
        position,
        data: createNodeData(
          {
            frequency: parent.data.frequency,
            scope: parent.data.scope,
            owner: parent.data.owner,
          },
          position
        ),
      };

      const newEdge = makeEdge(
        `e-${childId}-${parentId}-${uuidv4()}`,
        childId,
        parentId,
        influence,
        [activeLens]
      );

      const nextEdges = [...allEdges, newEdge];
      setAllEdges(nextEdges);
      setAllNodes((previous) =>
        layoutVisibleLensGraph(
          applyContextAndRecalculate([...previous, newNode], nextEdges),
          activeLens,
          nextEdges
        )
      );
      setActiveKpiId(childId);
    },
    [
      activeLens,
      allEdges,
      allNodes,
      applyContextAndRecalculate,
      createNodeData,
      layoutVisibleLensGraph,
    ]
  );

  const onAddChild = React.useCallback(
    (parentId: string) => {
      if (!canCreateKpis && !canEditKpiNode(parentId)) {
        setLoadError("You can only add child KPIs under KPIs you own.");
        return;
      }
      const parent = allNodes.find((node) => node.id === parentId);
      if (!parent) return;

      const usesInfluence =
        parent.data.calculationMode !== "calculated_values" &&
        parent.data.calculationMode !== "formula_percentage";
      if (usesInfluence) {
        setChildKpiInfluenceDraft({
          parentId,
          parentLabel: parent.data.label || "this KPI",
          value: "20",
        });
        return;
      }

      addChildKpi(parentId);
    },
    [addChildKpi, allNodes, canCreateKpis, canEditKpiNode]
  );

  const submitChildKpiInfluence = React.useCallback(() => {
    if (!childKpiInfluenceDraft) return;
    const trimmed = childKpiInfluenceDraft.value.trim();
    const parsedInfluence = trimmed === "" ? undefined : Number(trimmed);
    if (
      typeof parsedInfluence === "number" &&
      (!Number.isFinite(parsedInfluence) || parsedInfluence < 0)
    ) {
      setLoadError("Enter a valid influence percentage.");
      return;
    }
    addChildKpi(childKpiInfluenceDraft.parentId, parsedInfluence);
    setChildKpiInfluenceDraft(null);
  }, [addChildKpi, childKpiInfluenceDraft]);

  const addRootKpiAtPosition = React.useCallback((position: { x: number; y: number }) => {
    if (!canCreateKpis) {
      setLoadError("You need workspace manager or admin access to add KPIs.");
      return;
    }
    const rootId = uuidv4();
    const newNode: Node<KpiNodeData> = {
      id: rootId,
      type: "kpi",
      position,
      data: createNodeData({}, position),
    };

    setAllNodes((previous) => applyContextAndRecalculate([...previous, newNode], allEdges));
    setActiveKpiId(rootId);
  }, [allEdges, applyContextAndRecalculate, canCreateKpis, createNodeData]);

  const onAddRootKpi = React.useCallback(() => {
    const lensNodes = allNodes.filter((node) => node.data.lenses.includes(activeLens));
    const lensNodeIds = new Set(lensNodes.map((node) => node.id));
    const lensEdges = allEdges.filter(
      (edge) => lensNodeIds.has(edge.source) && lensNodeIds.has(edge.target)
    );
    const rootCount = lensNodes.filter(
      (node) => !lensEdges.some((edge) => edge.target === node.id)
    ).length;
    const viewportCenter = reactFlowInstance
      ? reactFlowInstance.screenToFlowPosition({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        })
      : null;
    const position = {
      x:
        viewportCenter?.x ??
        180 + rootCount * 340,
      y:
        viewportCenter?.y ??
        120,
    };

    addRootKpiAtPosition(position);
  }, [
    activeLens,
    allEdges,
    allNodes,
    addRootKpiAtPosition,
    reactFlowInstance,
  ]);

  const onReferenceDateStep = React.useCallback(
    (direction: "previous" | "next") => {
      const nextReferenceDate = shiftReferenceDate(
        getReportingWeekStart(referenceDate, weekStartsOn),
        direction
      );
      setReferenceDate(nextReferenceDate);
      setAllNodes((previous) =>
        applyContextAndRecalculate(previous, allEdges, nextReferenceDate, campus)
      );
    },
    [allEdges, applyContextAndRecalculate, campus, referenceDate, weekStartsOn]
  );

  const onAggregationModeChange = React.useCallback(
    (nextValue: AggregationMode) => {
      setAggregationMode(nextValue);
      setAllNodes((previous) =>
        applyContextAndRecalculate(previous, allEdges, referenceDate, campus, activeLens)
      );
    },
    [activeLens, allEdges, applyContextAndRecalculate, campus, referenceDate]
  );

  const onComparisonModeChange = React.useCallback(
    (nextValue: ComparisonMode) => {
      setComparisonMode(nextValue);
      setAllNodes((previous) =>
        applyContextAndRecalculate(previous, allEdges, referenceDate, campus, activeLens)
      );
    },
    [activeLens, allEdges, applyContextAndRecalculate, campus, referenceDate]
  );

  const onLensChange = React.useCallback(
    (nextLens: Lens) => {
      setActiveLens(nextLens);
      setActiveKpiId(null);
      setAllNodes((previous) =>
        layoutVisibleLensGraph(
          applyContextAndRecalculate(previous, allEdges, referenceDate, campus, nextLens),
          nextLens,
          allEdges,
          { compact: true }
        )
      );

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          reactFlowInstance?.fitView({
            padding: 0.2,
            duration: 350,
          });
        });
      });
    },
    [
      allEdges,
      applyContextAndRecalculate,
      campus,
      layoutVisibleLensGraph,
      reactFlowInstance,
      referenceDate,
    ]
  );

  const onAddLens = React.useCallback(() => {
    if (!canManageLenses) {
      setLoadError("You need workspace manager or admin access to add lenses.");
      return;
    }
    setNewLensForm({
      name: "",
      scopeType: "organization",
      scopeId: "",
      description: "",
    });
    setIsCreateLensOpen(true);
  }, [canManageLenses]);

  const onCreateLens = React.useCallback(() => {
    if (!canManageLenses) {
      setLoadError("You need workspace manager or admin access to create lenses.");
      return;
    }
    const trimmedName = newLensForm.name.trim();
    const trimmedScopeId =
      newLensForm.scopeId.trim() || getDefaultScopeId(newLensForm.scopeType);
    if (!trimmedScopeId) return;

    const lensId = uuidv4();
    const lensName = trimmedName || trimmedScopeId;

    setAvailableLenses((previous) => [...previous, lensId]);
    setLensConfigs((previous) => ({
      ...previous,
      [lensId]: {
        name: lensName,
        scopeType: newLensForm.scopeType,
        scopeId: trimmedScopeId,
        description:
          newLensForm.description.trim() || "Describe the purpose and boundaries of this lens.",
      },
    }));
    setLensNameDrafts((previous) => ({
      ...previous,
      [lensId]: lensName,
    }));
    setIsCreateLensOpen(false);
    onLensChange(lensId);
    setActiveLensDetailId(lensId);
  }, [canManageLenses, newLensForm, onLensChange]);

  const onLensConfigChange = React.useCallback(
    (lens: Lens, field: keyof Omit<LensConfig, "name">, value: string) => {
      if (!canManageLenses) {
        setLoadError("You need workspace manager or admin access to edit lenses.");
        return;
      }
      setLensConfigs((previous) => ({
        ...previous,
        [lens]: {
          ...previous[lens],
          [field]: value,
        },
      }));
    },
    [canManageLenses]
  );

  const onLensDraftChange = React.useCallback((lens: Lens, value: string) => {
    if (!canManageLenses) {
      return;
    }
    setLensNameDrafts((previous) => ({
      ...previous,
      [lens]: value,
    }));
  }, [canManageLenses]);

  const onRenameLens = React.useCallback(
    (currentLens: Lens, nextLensValue: string) => {
      if (!canManageLenses) {
        setLoadError("You need workspace manager or admin access to rename lenses.");
        return;
      }
      const trimmed = nextLensValue.trim();
      const currentName = lensConfigs[currentLens]?.name ?? currentLens;
      if (!trimmed || trimmed === currentName) {
        setLensNameDrafts((previous) => ({
          ...previous,
          [currentLens]: currentName,
        }));
        return;
      }
      if (
        Object.entries(lensConfigs).some(
          ([lensId, config]) => lensId !== currentLens && config.name === trimmed
        )
      ) {
        setLensNameDrafts((previous) => ({
          ...previous,
          [currentLens]: currentName,
        }));
        return;
      }
      setLensConfigs((previous) => ({
        ...previous,
        [currentLens]: {
          ...previous[currentLens],
          name: trimmed,
        },
      }));
      setLensNameDrafts((previous) => ({
        ...previous,
        [currentLens]: trimmed,
      }));
    },
    [
      canManageLenses,
      lensConfigs,
    ]
  );

  const onDeleteLens = React.useCallback(
    (lensToDelete: Lens) => {
      if (!canManageLenses) {
        setLoadError("You need workspace manager or admin access to delete lenses.");
        return;
      }
      if (availableLenses.length <= 1) return;

      const lensName = lensConfigs[lensToDelete]?.name ?? lensToDelete;
      const confirmed = window.confirm(
        `Are you sure you want to delete "${lensName}"?\n\nKPIs that only belong to this lens will be removed from the workspace. KPIs that also belong to other lenses will stay there.`
      );
      if (!confirmed) return;

      const nextAvailableLenses = availableLenses.filter((lens) => lens !== lensToDelete);
      const nextActiveLens =
        activeLens === lensToDelete ? nextAvailableLenses[0] : activeLens;

      setAvailableLenses(nextAvailableLenses);
      setLensConfigs((previous) => {
        const nextConfigs = { ...previous };
        delete nextConfigs[lensToDelete];
        return nextConfigs;
      });
      setLensNameDrafts((previous) => {
        const nextDrafts = { ...previous };
        delete nextDrafts[lensToDelete];
        return nextDrafts;
      });
      if (activeLens === lensToDelete) {
        setActiveLens(nextActiveLens);
        setActiveKpiId(null);
      }
      if (activeLensDetailId === lensToDelete) {
        setActiveLensDetailId(null);
      }

      let nextEdgesSnapshot = allEdges;
      setAllNodes((previous) => {
        const nextNodes = previous
          .map((node) => {
            if (!node.data.lenses.includes(lensToDelete)) return node;

            const nextPositionsByLens = { ...node.data.positionsByLens };
            delete nextPositionsByLens[lensToDelete];
            const nextLenses = node.data.lenses.filter((lens) => lens !== lensToDelete);

            if (nextLenses.length === 0) return null;

            return {
              ...node,
              data: {
                ...node.data,
                lenses: nextLenses,
                positionsByLens: nextPositionsByLens,
              },
            };
          })
          .filter((node): node is Node<KpiNodeData> => node !== null);

        const remainingNodeIds = new Set(nextNodes.map((node) => node.id));
        nextEdgesSnapshot = allEdges.filter(
          (edge) => remainingNodeIds.has(edge.source) && remainingNodeIds.has(edge.target)
        );
        setAllEdges(nextEdgesSnapshot);

        return applyContextAndRecalculate(
          nextNodes,
          nextEdgesSnapshot,
          referenceDate,
          campus,
          nextActiveLens
        );
      });
    },
    [
      activeLens,
      activeLensDetailId,
      allEdges,
      applyContextAndRecalculate,
      availableLenses,
      campus,
      canManageLenses,
      lensConfigs,
      referenceDate,
    ]
  );

  const onEdgeClick = React.useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      if (!canManageRelationshipFor(edge.source, edge.target)) {
        setLoadError("You can only edit KPI relationships inside a branch you own.");
        return;
      }
      const targetNode = allNodes.find((node) => node.id === edge.target);
      const sourceNode = allNodes.find((node) => node.id === edge.source);
      const disconnectEdge = () => {
        const explicitLenses = getEdgeLensMembership(edge);
        const legacySharedLenses =
          explicitLenses.length > 0
            ? explicitLenses
            : (sourceNode?.data.lenses ?? []).filter((lens) =>
                targetNode?.data.lenses.includes(lens)
              );
        const nextLenses = legacySharedLenses.filter((lens) => lens !== activeLens);
        const nextEdges =
          nextLenses.length === 0
            ? allEdges.filter((current) => current.id !== edge.id)
            : allEdges.map((current) =>
                current.id === edge.id
                  ? makeEdge(
                      current.id,
                      current.source,
                      current.target,
                      typeof current.data?.influence === "number"
                        ? current.data.influence
                        : undefined,
                      nextLenses
                    )
                  : current
              );

        localWorkspaceMutationVersionRef.current += 1;
        setAllEdges(nextEdges);
        setAllNodes((previous) =>
          layoutVisibleLensGraph(
            applyContextAndRecalculate(previous, nextEdges),
            activeLens,
            nextEdges
          )
        );
      };

      const currentInfluence = edge.data?.influence;
      const influenceInput =
        targetNode?.data.calculationMode === "calculated_values" ||
        targetNode?.data.calculationMode === "formula_percentage"
          ? window.prompt(
              'This KPI is calculated from child values, so influence % is not used.\n\nType "disconnect" to remove this relationship from this map.',
              ""
            )
          : window.prompt(
              'Update influence %.\n\nType "disconnect" to remove this relationship from this map.',
              typeof currentInfluence === "number" ? String(currentInfluence) : ""
            );
      if (influenceInput === null) return;

      const trimmed = influenceInput.trim();
      if (trimmed.toLowerCase() === "disconnect") {
        disconnectEdge();
        return;
      }
      if (
        targetNode?.data.calculationMode === "calculated_values" ||
        targetNode?.data.calculationMode === "formula_percentage"
      ) return;

      const influence = trimmed === "" ? undefined : Number(trimmed);
      const nextEdges = allEdges.map((current) =>
        current.id === edge.id
          ? makeEdge(
              current.id,
              current.source,
              current.target,
              influence,
              getEdgeLensMembership(current)
            )
          : current
      );
      localWorkspaceMutationVersionRef.current += 1;
      setAllEdges(nextEdges);
      setAllNodes((previous) => applyContextAndRecalculate(previous, nextEdges));
    },
    [
      activeLens,
      allEdges,
      allNodes,
      applyContextAndRecalculate,
      canManageRelationshipFor,
      layoutVisibleLensGraph,
    ]
  );

  const onConnect = React.useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      if (!canManageRelationshipFor(connection.source, connection.target)) {
        setLoadError("You can only edit KPI relationships inside a branch you own.");
        return;
      }
      const targetNode = allNodes.find((node) => node.id === connection.target);
      const usesInfluence =
        targetNode?.data.calculationMode !== "calculated_values" &&
        targetNode?.data.calculationMode !== "formula_percentage";

      const influenceInput = usesInfluence ? window.prompt("Influence %?", "20") : null;
      if (usesInfluence && influenceInput === null) return;

      const trimmed = influenceInput?.trim() ?? "";
      const influence = usesInfluence && trimmed !== "" ? Number(trimmed) : undefined;
      const nextEdge = makeEdge(
        `e-${connection.source}-${connection.target}-${uuidv4()}`,
        connection.source,
        connection.target,
        influence,
        [activeLens]
      );
      const nextEdges = [...allEdges, nextEdge];

      localWorkspaceMutationVersionRef.current += 1;
      setAllEdges(nextEdges);
      setAllNodes((previous) =>
        layoutVisibleLensGraph(
          applyContextAndRecalculate(previous, nextEdges),
          activeLens,
          nextEdges
        )
      );
    },
    [
      activeLens,
      allEdges,
      allNodes,
      applyContextAndRecalculate,
      canManageRelationshipFor,
      layoutVisibleLensGraph,
    ]
  );

  const onNodeDoubleClick = React.useCallback(
    (_event: React.MouseEvent, node: Node<KpiNodeData>) => {
      setKpiContextMenu(null);
      setMapContextMenu(null);
      setActiveKpiId(node.id);
    },
    []
  );

  const onPaneClick = React.useCallback(() => {
    setKpiContextMenu(null);
    setMapContextMenu(null);
    setActiveInitiativeRailNodeId(null);
    setAllNodes((previous) =>
      previous.map((node) =>
        node.selected
          ? {
              ...node,
              selected: false,
            }
          : node
      )
    );
  }, []);

  const onSelectNode = React.useCallback(
    (nodeId: string, additive: boolean) => {
      setKpiContextMenu(null);
      setMapContextMenu(null);
      setAllNodes((previous) =>
        previous.map((node) => {
          if (!node.data.lenses.includes(activeLens)) return node;

          if (additive) {
            if (node.id !== nodeId) return node;
            return {
              ...node,
              selected: !node.selected,
            };
          }

          return {
            ...node,
            selected: node.id === nodeId,
          };
        })
      );
    },
    [activeLens]
  );

  const onToggleInitiativeRail = React.useCallback((id: string) => {
    setActiveInitiativeRailNodeId((current) => (current === id ? null : id));
  }, []);

  const onOpenDetails = React.useCallback((id: string) => {
    setKpiContextMenu(null);
    setMapContextMenu(null);
    setActiveKpiId(id);
  }, []);

  const onNodeContextMenu = React.useCallback(
    (event: React.MouseEvent, node: Node<KpiNodeData>) => {
      event.preventDefault();
      event.stopPropagation();
      setMapContextMenu(null);
      const menuWidth = 280;
      const menuMaxHeight = Math.min(520, window.innerHeight - 24);
      const menuPadding = 12;
      setAllNodes((previous) =>
        previous.map((item) =>
          item.data.lenses.includes(activeLens)
            ? {
                ...item,
                selected: item.id === node.id,
              }
            : item
        )
      );
      setKpiContextMenu({
        nodeId: node.id,
        x: Math.min(
          Math.max(event.clientX, menuPadding),
          window.innerWidth - menuWidth - menuPadding
        ),
        y: Math.min(
          Math.max(event.clientY, menuPadding),
          window.innerHeight - menuMaxHeight - menuPadding
        ),
      });
    },
    [activeLens]
  );

  const onPaneContextMenu = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const menuWidth = 240;
      const menuMaxHeight = 180;
      const menuPadding = 12;
      const flowPosition = reactFlowInstance
        ? reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          })
        : { x: 180, y: 120 };

      setKpiContextMenu(null);
      setMapContextMenu({
        flowPosition,
        x: Math.min(
          Math.max(event.clientX, menuPadding),
          window.innerWidth - menuWidth - menuPadding
        ),
        y: Math.min(
          Math.max(event.clientY, menuPadding),
          window.innerHeight - menuMaxHeight - menuPadding
        ),
      });
    },
    [reactFlowInstance]
  );

  const kpiDragDataType = "application/x-lens-kpi-id";

  const onCanvasDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
  };

  const addLibraryKpiToActiveLens = React.useCallback(
    (nodeId: string, screenPosition: { x: number; y: number }) => {
      if (!canManageWorkspace) {
        setLoadError("You need workspace manager or admin access to change the strategy map.");
        return;
      }
      if (!nodeId) return;

      const dropPosition = reactFlowInstance
        ? reactFlowInstance.screenToFlowPosition(screenPosition)
        : { x: 220, y: 180 };
      const draggedNode = allNodes.find((node) => node.id === nodeId);
      if (!draggedNode) return;

      let nextNodes = allNodes.map((node) => {
        if (node.id !== nodeId) return node;

        const nextLenses = node.data.lenses.includes(activeLens)
          ? node.data.lenses
          : [...node.data.lenses, activeLens];

        return {
          ...node,
          position: dropPosition,
          data: {
            ...node.data,
            lenses: nextLenses,
            positionsByLens: {
              ...node.data.positionsByLens,
              [activeLens]: dropPosition,
            },
          },
        };
      });
      let nextEdges = allEdges;

      if (!draggedNode.data.lenses.includes(activeLens)) {
        const sourceLens = chooseLensToImportChildrenFrom(draggedNode);
        if (sourceLens) {
          const imported = importChildBranchIntoLens(
            nodeId,
            sourceLens,
            activeLens,
            dropPosition,
            nextNodes,
            nextEdges
          );
          nextNodes = imported.nodes;
          nextEdges = imported.edges;
        }
      }

      setAllEdges(nextEdges);
      setAllNodes((previous) =>
        layoutVisibleLensGraph(
          applyContextAndRecalculate(
            nextNodes.map((node) => {
              const current = previous.find((item) => item.id === node.id);
              return current ? { ...current, ...node, data: { ...current.data, ...node.data } } : node;
            }),
            nextEdges
          ),
          activeLens,
          nextEdges
        )
      );
    },
    [
      activeLens,
      allEdges,
      allNodes,
      applyContextAndRecalculate,
      canManageWorkspace,
      chooseLensToImportChildrenFrom,
      importChildBranchIntoLens,
      layoutVisibleLensGraph,
      reactFlowInstance,
    ]
  );

  const onCanvasDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canManageWorkspace) {
      setLoadError("You need workspace manager or admin access to change the strategy map.");
      return;
    }
    const nodeId =
      event.dataTransfer.getData(kpiDragDataType) || event.dataTransfer.getData("text/plain");
    if (!nodeId) return;

    addLibraryKpiToActiveLens(nodeId, {
      x: event.clientX,
      y: event.clientY,
    });
  };

  const onLibraryPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>, node: Node<KpiNodeData>, inLens: boolean) => {
      if (inLens || event.button !== 0) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest("button, input, select, textarea, a")
      ) {
        return;
      }

      stopCanvasDrag(event);
      event.preventDefault();
      libraryPointerDragRef.current = {
        nodeId: node.id,
        label: node.data.label || "Untitled KPI",
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        isDragging: false,
      };
    },
    []
  );

  React.useEffect(() => {
    const isOverReactFlowCanvas = (x: number, y: number) => {
      const flowElement = document.querySelector(".react-flow");
      if (!(flowElement instanceof HTMLElement)) return false;
      const rect = flowElement.getBoundingClientRect();
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    };

    const onPointerMove = (event: PointerEvent) => {
      const dragState = libraryPointerDragRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      dragState.x = event.clientX;
      dragState.y = event.clientY;
      const movedEnough =
        Math.hypot(event.clientX - dragState.startX, event.clientY - dragState.startY) > 5;
      if (!dragState.isDragging && movedEnough) {
        dragState.isDragging = true;
      }
      if (dragState.isDragging) {
        event.preventDefault();
        setLibraryPointerDrag({
          nodeId: dragState.nodeId,
          label: dragState.label,
          x: event.clientX,
          y: event.clientY,
        });
      }
    };

    const endDrag = (event: PointerEvent) => {
      const dragState = libraryPointerDragRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      libraryPointerDragRef.current = null;
      setLibraryPointerDrag(null);

      if (dragState.isDragging && isOverReactFlowCanvas(event.clientX, event.clientY)) {
        addLibraryKpiToActiveLens(dragState.nodeId, {
          x: event.clientX,
          y: event.clientY,
        });
      }
    };

    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [addLibraryKpiToActiveLens]);

  const onRemoveFromActiveLens = React.useCallback(
    (nodeId: string) => {
      if (!canManageWorkspace) {
        setLoadError("You need workspace manager or admin access to change the strategy map.");
        return;
      }
      const targetNode = allNodes.find((node) => node.id === nodeId);
      if (!targetNode) return;

      setActiveKpiId(null);

      const becomesUnattached = targetNode.data.lenses.length <= 1;
      const nextEdges = becomesUnattached
        ? allEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
        : allEdges.map((edge) =>
            edge.source === nodeId || edge.target === nodeId
              ? setEdgeLensMembership(edge, activeLens, false)
              : edge
          );

      setAllEdges(nextEdges);

      setAllNodes((previous) =>
        layoutVisibleLensGraph(
          applyContextAndRecalculate(
            previous.map((node) => {
              if (node.id !== nodeId) return node;

              const nextPositionsByLens = { ...node.data.positionsByLens };
              delete nextPositionsByLens[activeLens];

              return {
                ...node,
                data: {
                  ...node.data,
                  lenses: node.data.lenses.filter((lens) => lens !== activeLens),
                  positionsByLens: nextPositionsByLens,
                },
              };
            }),
            nextEdges
          ),
          activeLens,
          nextEdges
        )
      );
    },
    [activeLens, allEdges, allNodes, applyContextAndRecalculate, canManageWorkspace, layoutVisibleLensGraph]
  );

  const onDeleteKpi = React.useCallback(
    (nodeId: string) => {
      if (!canDeleteKpiNode(nodeId)) {
        setLoadError("You can only delete KPIs inside a branch you own.");
        return;
      }
      setActiveKpiId((current) => (current === nodeId ? null : current));
      const nextEdges = allEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
      setAllEdges(nextEdges);
      setAllNodes((previous) =>
        layoutVisibleLensGraph(
          applyContextAndRecalculate(
            previous.filter((node) => node.id !== nodeId),
            nextEdges
          ),
          activeLens,
          nextEdges
        )
      );
    },
    [activeLens, allEdges, applyContextAndRecalculate, canDeleteKpiNode, layoutVisibleLensGraph]
  );

  const collapseAllChildrenForActiveLens = React.useCallback(() => {
    setKpiContextMenu(null);
    setMapContextMenu(null);

    const nodeMap = new Map(allNodes.map((node) => [node.id, node]));
    const parentIds = new Set<string>();
    allEdges.forEach((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);
      if (
        sourceNode?.data.lenses.includes(activeLens) &&
        targetNode?.data.lenses.includes(activeLens) &&
        edgeBelongsToLens(edge, activeLens, nodeMap)
      ) {
        parentIds.add(edge.target);
      }
    });

    if (parentIds.size === 0) return;

    setAllNodes((previous) => {
      const collapsedNodes = previous.map((node) => {
        if (!parentIds.has(node.id) || node.data.collapsedInLenses.includes(activeLens)) {
          return node;
        }

        return {
          ...node,
          data: {
            ...node.data,
            collapsedInLenses: [...node.data.collapsedInLenses, activeLens],
          },
        };
      });

      return layoutVisibleLensGraph(
        applyContextAndRecalculate(collapsedNodes, allEdges),
        activeLens,
        allEdges
      );
    });
  }, [
    activeLens,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    layoutVisibleLensGraph,
  ]);

  const onToggleChildren = React.useCallback(
    (nodeId: string) => {
      const node = allNodes.find((item) => item.id === nodeId);
      const isCollapsed = node?.data.collapsedInLenses.includes(activeLens) ?? false;

      setAllNodes((previous) => {
        const toggledNodes = previous.map((currentNode) => {
          if (currentNode.id !== nodeId) return currentNode;

          return {
            ...currentNode,
            data: {
              ...currentNode.data,
              collapsedInLenses: isCollapsed
                ? currentNode.data.collapsedInLenses.filter((lens) => lens !== activeLens)
                : [...currentNode.data.collapsedInLenses, activeLens],
            },
          };
        });

        return layoutVisibleLensGraph(
          applyContextAndRecalculate(toggledNodes, allEdges),
          activeLens,
          allEdges
        );
      });
    },
    [activeLens, allEdges, allNodes, applyContextAndRecalculate, layoutVisibleLensGraph]
  );

  function wireHandlers(nodes: Node<KpiNodeData>[]): Node<KpiNodeData>[] {
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        lensLabels: node.data.lenses.map((lensId) => lensConfigs[lensId]?.name ?? lensId),
        onLabelChange,
        onMetricChange,
        onConfigChange,
        onDetailChange,
        onApiConfigChange,
        onRestrictedFieldsChange,
        onToggleRollup,
        onAddChild,
        onToggleChildren,
        onOpenDetails,
        onSelectNode,
        onToggleInitiativeRail,
      },
    }));
  }

  const lensNodes = React.useMemo(
    () => allNodes.filter((node) => node.data.lenses.includes(activeLens)),
    [activeLens, allNodes]
  );

  const lensEdges = React.useMemo(
    () => {
      const lensNodeIds = new Set(lensNodes.map((node) => node.id));
      const lensNodeMap = new Map(lensNodes.map((node) => [node.id, node]));
      return allEdges.filter(
        (edge) =>
          lensNodeIds.has(edge.source) &&
          lensNodeIds.has(edge.target) &&
          edgeBelongsToLens(edge, activeLens, lensNodeMap)
      );
    },
    [activeLens, allEdges, lensNodes]
  );

  const visibilityMeta = React.useMemo(() => {
    const childIdsByParent = new Map<string, string[]>();
    const parentIdsByChild = new Map<string, string[]>();

    lensEdges.forEach((edge) => {
      pushUniqueMapValue(childIdsByParent, edge.target, edge.source);
      pushUniqueMapValue(parentIdsByChild, edge.source, edge.target);
    });

    const topLevelNodeIds = lensNodes
      .filter((node) => !parentIdsByChild.has(node.id))
      .map((node) => node.id);

    const visibleNodeIds = new Set<string>();
    const queue =
      topLevelNodeIds.length > 0
        ? [...topLevelNodeIds]
        : lensNodes.map((node) => node.id);

    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      if (visibleNodeIds.has(nodeId)) continue;
      visibleNodeIds.add(nodeId);

      const node = lensNodes.find((item) => item.id === nodeId);
      if (!node || node.data.collapsedInLenses.includes(activeLens)) {
        continue;
      }

      (childIdsByParent.get(nodeId) ?? []).forEach((childId) => {
        if (!visibleNodeIds.has(childId)) {
          queue.push(childId);
        }
      });
    }

    return { visibleNodeIds, childIdsByParent };
  }, [activeLens, lensEdges, lensNodes]);

  const displayedNodesWithoutHandlers = React.useMemo(
    () => {
      const visibleNodes = lensNodes.filter((node) => visibilityMeta.visibleNodeIds.has(node.id));
      const activeRailNode =
        activeInitiativeRailNodeId && visibilityMeta.visibleNodeIds.has(activeInitiativeRailNodeId)
          ? visibleNodes.find((node) => node.id === activeInitiativeRailNodeId)
          : null;
      const activeRailPosition = activeRailNode
        ? activeRailNode.data.positionsByLens[activeLens] ?? activeRailNode.position
        : null;

      return visibleNodes.map((node) => {
          const basePosition = node.data.positionsByLens[activeLens] ?? node.position;
          const shouldShiftForRail =
            activeRailPosition &&
            node.id !== activeInitiativeRailNodeId &&
            basePosition.x > activeRailPosition.x &&
            basePosition.y < activeRailPosition.y + 380 &&
            basePosition.y + KPI_NODE_HEIGHT > activeRailPosition.y - 36;

          return {
            ...node,
            position: {
              ...basePosition,
              x: basePosition.x + (shouldShiftForRail ? 320 : 0),
            },
            data: {
              ...node.data,
              childCount: (visibilityMeta.childIdsByParent.get(node.id) ?? []).length,
              childrenCollapsed: node.data.collapsedInLenses.includes(activeLens),
              isInitiativeRailOpen: node.id === activeInitiativeRailNodeId,
            },
          };
        });
    },
    [activeInitiativeRailNodeId, activeLens, lensNodes, visibilityMeta]
  );
  const displayedNodes = wireHandlers(displayedNodesWithoutHandlers);

  const displayedEdges = React.useMemo(() => {
    const nodeMap = new Map(allNodes.map((node) => [node.id, node]));
    return lensEdges
      .filter(
        (edge) =>
          visibilityMeta.visibleNodeIds.has(edge.source) &&
          visibilityMeta.visibleNodeIds.has(edge.target)
      )
      .map((edge) => {
        const targetNode = nodeMap.get(edge.target);
        if (
          targetNode?.data.calculationMode !== "calculated_values" &&
          targetNode?.data.calculationMode !== "formula_percentage"
        ) return edge;

        return {
          ...edge,
          label: undefined,
          labelStyle: { display: "none" },
          labelBgStyle: { fillOpacity: 0 },
        };
      });
  }, [allNodes, lensEdges, visibilityMeta]);

  const activeKpiNode = allNodes.find((node) => node.id === activeKpiId);
  // The editor uses the same callback-bearing node data as the rendered map.
  // Persisted allNodes intentionally contains data only, so reading the active
  // KPI directly from it leaves otherwise-enabled editor controls disconnected.
  const activeKpi = activeKpiNode ? wireHandlers([activeKpiNode])[0] : undefined;
  const activeKpiCanEdit = activeKpi ? canEditKpiNode(activeKpi.id) : false;
  const activeKpiTargetInputValue = React.useMemo(() => {
    if (!activeKpi) return "";
    if (activeKpi.data.calculationMode === "calculated_values") {
      return activeKpi.data.target ?? "";
    }
    return (
      getValueForContext(
        activeKpi.data.targetHistory,
        getKpiReportingDate(referenceDate, activeKpi.data.frequency, getEffectiveKpiApiConfig(activeKpi)),
        activeKpi.data.frequency,
        activeKpi.data.scope,
        campus,
        activeKpi.data.valueType,
        "current",
        activeKpi.data.metricPersistence
      ) ?? ""
    );
  }, [activeKpi, campus, referenceDate]);
  const activeKpiActualInputValue = React.useMemo(() => {
    if (!activeKpi) return "";
    if (
      activeKpi.data.calculationMode === "calculated_values" ||
      activeKpi.data.calculationMode === "formula_percentage" ||
      activeKpi.data.dataSource === "Poll"
    ) {
      return activeKpi.data.actual ?? "";
    }
    return (
      getValueForContext(
        verifiedSnapshotHistory(activeKpi.data.actualHistory, activeKpi.data.apiActualEvidence, getEffectiveKpiApiConfig(activeKpi), activeKpi.data.frequency),
        getKpiReportingDate(referenceDate, activeKpi.data.frequency, getEffectiveKpiApiConfig(activeKpi)),
        activeKpi.data.frequency,
        activeKpi.data.scope,
        campus,
        activeKpi.data.valueType,
        "current",
        activeKpi.data.metricPersistence
      ) ?? ""
    );
  }, [activeKpi, campus, referenceDate]);
  const activeKpiTargetReadOnly =
    !activeKpiCanEdit || activeKpi?.data.calculationMode === "calculated_values";
  const activeKpiActualReadOnly =
    !activeKpiCanEdit ||
    activeKpi?.data.calculationMode === "calculated_values" ||
    activeKpi?.data.calculationMode === "formula_percentage" ||
    activeKpi?.data.dataSource === "Poll";
  React.useEffect(() => {
    if (!activeKpi || !isKpiInfoOpen || searchParams.get("focus") !== "actual") {
      return;
    }
    const focusTimer = window.setTimeout(() => {
      actualInputRef.current?.focus();
      actualInputRef.current?.select();
    }, 150);
    return () => window.clearTimeout(focusTimer);
  }, [activeKpi, isKpiInfoOpen, searchParams]);
  const activeLensConfig = lensConfigs[activeLens];
  const activeLensDetail = activeLensDetailId ? lensConfigs[activeLensDetailId] : null;
  const selectedEnterprise =
    activeEnterpriseId === null
      ? null
      : enterprises.find((enterprise) => enterprise.id === activeEnterpriseId) ?? null;
  React.useEffect(() => {
    const nextWeekStartsOn = normalizeWeekStartsOn(selectedEnterprise?.week_starts_on);
    setWeekStartsOn((current) => (current === nextWeekStartsOn ? current : nextWeekStartsOn));
    setReferenceDate((current) => getReportingWeekStart(current, nextWeekStartsOn));
  }, [selectedEnterprise?.week_starts_on]);
  const activeKpiInLens = activeKpi ? activeKpi.data.lenses.includes(activeLens) : false;
  const activeKpiLineageLabels = React.useMemo(() => {
    if (!activeKpi) return [];

    const nodesById = new Map(allNodes.map((node) => [node.id, node]));
    const labels: string[] = [];
    const visited = new Set<string>([activeKpi.id]);
    let currentId = activeKpi.id;

    for (let depth = 0; depth < 12; depth += 1) {
      let parentEdge: Edge | undefined;
      for (const edge of lensEdges) {
        if (edge.source === currentId && !visited.has(edge.target)) {
          parentEdge = edge;
          break;
        }
      }
      if (!parentEdge) break;

      const parentNode = nodesById.get(parentEdge.target);
      if (!parentNode) break;

      labels.push(parentNode.data.label || "Untitled KPI");
      visited.add(parentEdge.target);
      currentId = parentEdge.target;
    }

    return labels.reverse();
  }, [activeKpi, allNodes, lensEdges]);
  const pollEditorKpi = React.useMemo(() => {
    if (pollEditorKpiId === null) return null;
    return allNodes.find((node) => node.id === pollEditorKpiId) ?? null;
  }, [allNodes, pollEditorKpiId]);

  const pollEditorResults = React.useMemo(() => {
    const pollConfig = pollEditorKpi?.data.pollConfig;
    if (!pollConfig) return null;

    const responses = [...pollConfig.responses].sort((left, right) => {
      const leftTime = left.submittedAt ? new Date(left.submittedAt).getTime() : 0;
      const rightTime = right.submittedAt ? new Date(right.submittedAt).getTime() : 0;
      return rightTime - leftTime;
    });
    const numericValues = responses
      .map((response) =>
        typeof response.value === "number" ? response.value : Number(response.value)
      )
      .filter((value) => !Number.isNaN(value));
    const average =
      numericValues.length > 0
        ? numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length
        : null;
    const commentTexts = responses
      .map((response) => response.comment ?? "")
      .filter((comment) => comment.trim().length > 0);
    const commentCount = commentTexts.length;
    const commentSummary = summarizePollComments(commentTexts);

    return {
      responseCount: responses.length,
      commentCount,
      completedInviteCount: (pollConfig.invites ?? []).filter((invite) => Boolean(invite.completedAt))
        .length,
      pendingInviteCount: (pollConfig.invites ?? []).filter((invite) => !invite.completedAt).length,
      respondentLabels: Array.from(
        new Set(
          responses
            .map((response) => response.respondentLabel?.trim())
            .filter((label): label is string => Boolean(label))
        )
      ),
      headline: average === null ? "No average yet" : `Average ${average.toFixed(1)}`,
      detail:
        responses.length > 0
          ? `${numericValues.length} ratings submitted${commentCount > 0 ? `, ${commentCount} comments` : ""}`
          : "No responses yet",
      commentSummaryShort: commentSummary?.shortSummary ?? null,
      commentSummaryLong: commentSummary?.longSummary ?? null,
      responses,
    };
  }, [pollEditorKpi]);

  React.useEffect(() => {
    setIsPollSummaryExpanded(false);
    setPollCommentsCopyMessage(null);
  }, [pollEditorKpiId, pollEditorResults?.responseCount]);

  const pollEditorRecipients = React.useMemo(() => {
    const pollConfig = pollEditorKpi?.data.pollConfig;
    if (!pollConfig) return [];

    const selectedGroups =
      pollConfig.targetGroups.length > 0
        ? pollConfig.targetGroups
        : pollConfig.targetGroup
          ? [pollConfig.targetGroup]
          : [];

    if (selectedGroups.length === 0) return [];

    const seenEmails = new Set<string>();
    return pollRecipients.filter((recipient) => {
      const normalizedEmail = recipient.email.trim().toLowerCase();
      const inGroup = recipient.groups.some((group) => selectedGroups.includes(group));
      if (!inGroup || normalizedEmail.length === 0 || seenEmails.has(normalizedEmail)) {
        return false;
      }
      seenEmails.add(normalizedEmail);
      return true;
    });
  }, [pollEditorKpi, pollRecipients]);

  const pollEditorLinkedKpiOptions = React.useMemo(() => {
    if (!pollEditorKpi) return [];

    return allNodes
      .filter((node) => node.id !== pollEditorKpi.id)
      .filter((node) => {
        if (!pollKpiSearch.trim()) return true;
        return matchesSearch(
          [
            node.data.label,
            node.data.owner,
            node.data.definition,
            node.data.measurement,
            formatDataSourceLabel(node.data.dataSource),
            formatValueTypeLabel(node.data.valueType),
          ]
            .filter(Boolean)
            .join(" "),
          pollKpiSearch
        );
      })
      .map((node) => ({
        id: node.id,
        label: node.data.label || "Untitled KPI",
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [allNodes, pollEditorKpi, pollKpiSearch]);
  const pollCampaignSetup = React.useMemo(() => {
    if (!pollEditorKpi) return null;
    return getPollCampaignSetup(
      allNodes,
      pollEditorKpi.id,
      pollEditorKpi.data.pollConfig,
      referenceDate,
      campus
    );
  }, [allNodes, campus, pollEditorKpi, referenceDate]);
  const pollEditorSelectedGroups = React.useMemo(() => {
    const pollConfig = pollEditorKpi?.data.pollConfig;
    if (!pollConfig) return [];

    return pollConfig.targetGroups.length > 0
      ? pollConfig.targetGroups
      : pollConfig.targetGroup
        ? [pollConfig.targetGroup]
        : [];
  }, [pollEditorKpi]);
  const pollPublishContext = React.useMemo(() => {
    if (!pollEditorKpi) return null;
    return getPollPublishContext(pollEditorKpi.data.scope, campus);
  }, [campus, pollEditorKpi]);
  const isPollViewMode = pollEditorMode === "view";
  const isPollEditMode = pollEditorMode === "edit";

  const getPollInviteUrl = React.useCallback(
    (inviteToken: string) => {
      if (!activeEnterpriseId) return "";
      return `${window.location.origin}/polls/respond?enterpriseId=${encodeURIComponent(
        String(activeEnterpriseId)
      )}&inviteToken=${encodeURIComponent(inviteToken)}`;
    },
    [activeEnterpriseId]
  );

  const emailPollInvitation = React.useCallback(
    (invite: PollInvite) => {
      if (!pollEditorKpi?.data.pollConfig) return;

      const selectedGroups =
        pollEditorKpi.data.pollConfig!.targetGroups.length > 0
          ? pollEditorKpi.data.pollConfig!.targetGroups
          : pollEditorKpi.data.pollConfig!.targetGroup
            ? [pollEditorKpi.data.pollConfig!.targetGroup]
            : [];
      const inviteUrl = getPollInviteUrl(invite.token);
      const subject = encodeURIComponent(`${pollEditorKpi.data.pollConfig!.title} - Poll Invitation`);
      const body = encodeURIComponent(
        [
          `Hi ${invite.recipientName || "there"},`,
          "",
          "You are invited to complete a poll.",
          "",
          `KPI: ${pollEditorKpi.data.label || "Untitled KPI"}`,
          `Poll: ${pollEditorKpi.data.pollConfig!.title}`,
          selectedGroups.length > 0 ? `Audience groups: ${selectedGroups.join(", ")}` : "",
          "",
          pollEditorKpi.data.pollConfig!.description || "",
          "",
          `Question: ${pollEditorKpi.data.pollConfig!.questionPrompt}`,
          "",
          "Poll link:",
          inviteUrl,
          "",
          "If the link does not auto-open, copy and paste it into your browser.",
        ]
          .filter(Boolean)
          .join("\n")
      );

      window.location.href = `mailto:${encodeURIComponent(
        invite.recipientEmail
      )}?subject=${subject}&body=${body}`;
    },
    [getPollInviteUrl, pollEditorKpi]
  );

  const openPollEmailComposer = React.useCallback(() => {
    if (!pollEditorKpi?.data.pollConfig) return;
    const pollTitle = pollEditorKpi.data.pollConfig.title || "KPI Poll";
    const question = pollEditorKpi.data.pollConfig.questionPrompt || "Please complete this poll.";
    setPollEmailSubject(`${pollTitle} | ${pollEditorKpi.data.label}`);
    setPollEmailGreeting("Hi {first_name},");
    setPollEmailBody(
      `Please take a moment to respond to this poll for ${pollEditorKpi.data.label}.\n\n${question}`
    );
    setPollEmailClosing("Thank you,\nLenses CPM");
    setPollEmailStatusMessage(null);
    setIsPollEmailComposerOpen(true);
  }, [pollEditorKpi]);

  const sendPollEmailCampaign = React.useCallback(async () => {
    if (!pollEditorKpi?.data.pollConfig) return;

    const recipients = (pollEditorKpi.data.pollConfig.invites ?? [])
      .filter((invite) => invite.recipientEmail?.trim())
      .map((invite) => {
        const name = invite.recipientName?.trim() || "";
        return {
          name,
          first_name: name.split(/\s+/).filter(Boolean)[0] ?? "",
          email: invite.recipientEmail.trim(),
          invite_url: getPollInviteUrl(invite.token),
        };
      });

    if (recipients.length === 0) {
      setPollEmailStatusMessage("No tracked email recipients are available for this poll.");
      return;
    }

    setIsSendingPollEmails(true);
    setPollEmailStatusMessage(null);

    try {
      const response = await api.post("/polls/send-email-campaign", {
        enterprise_id: activeEnterpriseId,
        subject: pollEmailSubject,
        greeting: pollEmailGreeting,
        body: pollEmailBody,
        closing: pollEmailClosing,
        kpi_label: pollEditorKpi.data.label || "Untitled KPI",
        poll_title: pollEditorKpi.data.pollConfig.title || "KPI Poll",
        recipients,
      });
      const result = response.data as { sent_count: number; failed_count: number };
      setPollEmailStatusMessage(
        result.failed_count > 0
          ? `Sent ${result.sent_count} emails. ${result.failed_count} failed.`
          : `Sent ${result.sent_count} poll emails.`
      );
    } catch (error: unknown) {
      console.error("Failed to send poll email campaign", error);
      setPollEmailStatusMessage("Could not send poll emails.");
    } finally {
      setIsSendingPollEmails(false);
    }
  }, [
    activeEnterpriseId,
    getPollInviteUrl,
    pollEditorKpi,
    pollEmailBody,
    pollEmailClosing,
    pollEmailGreeting,
    pollEmailSubject,
  ]);

  const sendInitiativeUpdateRequests = React.useCallback(
    async (initiative: InitiativeItem) => {
      if (activeEnterpriseId === null) {
        setKpiWorkEmailStatusMessage("Select an enterprise before sending update requests.");
        return;
      }
      const taskIds = Array.from(
        new Set(
          initiative.tasks
            .filter((task) => task.status !== "done" && Boolean(task.id) && Boolean(task.owner))
            .map((task) => task.id)
        )
      );
      if (taskIds.length === 0) {
        setKpiWorkEmailStatusMessage("No assigned open tasks are available for this initiative.");
        return;
      }

      setIsSendingKpiWorkUpdateEmails(true);
      setKpiWorkEmailStatusMessage(null);
      try {
        const response = await api.post(
          `/enterprises/${activeEnterpriseId}/tasks/send-update-requests`,
          {
            task_ids: taskIds,
            dry_run: false,
          }
        );
        const result = response.data as TaskEmailResult;
        const skippedText =
          result.skipped_count > 0
            ? ` ${result.skipped_count} skipped because no matching email was found.`
            : "";
        const failedText = result.failed_count > 0 ? ` ${result.failed_count} failed.` : "";
        const errorText = result.errors?.length ? ` ${result.errors.slice(0, 2).join(" ")}` : "";
        setKpiWorkEmailStatusMessage(
          `Sent ${result.sent_count} update request email${result.sent_count === 1 ? "" : "s"} for ${initiative.title || "this initiative"}.${skippedText}${failedText}${errorText}`
        );
      } catch (error) {
        console.error("Failed to send initiative update requests", error);
        setKpiWorkEmailStatusMessage("Could not send update requests for this initiative.");
      } finally {
        setIsSendingKpiWorkUpdateEmails(false);
      }
    },
    [activeEnterpriseId]
  );

  const selectedNodeIds = React.useMemo(
    () =>
      allNodes
        .filter((node) => node.data.lenses.includes(activeLens) && node.selected)
        .map((node) => node.id),
    [activeLens, allNodes]
  );

  const duplicateSelectedKpis = React.useCallback(() => {
    if (!canCreateKpis) {
      setLoadError("You need workspace manager or admin access to duplicate KPIs.");
      return;
    }
    if (selectedNodeIds.length === 0) return;
    const snapshot = JSON.stringify({
      availableLenses,
      lensConfigs,
      activeLens,
      referenceDate: referenceDate.toISOString(),
      aggregationMode,
      comparisonMode,
      campus: defaultCampus,
      ownerList,
      pollAudienceGroups,
      pollRecipients,
      isLensLibraryCollapsed,
      isReferenceLibraryCollapsed,
      isLibraryCollapsed,
      panelOffsets,
      panelSizes,
      allNodes: allNodes.map((node) => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: stripNodeDataForPersistence(node.data),
      })),
      allEdges,
    });
    if (undoHistoryRef.current[undoHistoryRef.current.length - 1] !== snapshot) {
      undoHistoryRef.current = [
        ...undoHistoryRef.current.slice(-(UNDO_HISTORY_LIMIT - 1)),
        snapshot,
      ];
    }
    immediateUndoSnapshotRef.current = snapshot;
    immediateUndoStateRef.current = {
      nodes: cloneNodesForUndo(allNodes),
      edges: cloneEdgesForUndo(allEdges),
    };
    lastWorkspaceSnapshotRef.current = snapshot;

    const selectedSet = new Set(selectedNodeIds);
    const nodeMap = new Map(allNodes.map((node) => [node.id, node]));
    const duplicatedIds = new Map<string, string>();

    const duplicatedNodes = allNodes
      .filter((node) => selectedSet.has(node.id))
      .map((node) => {
        const duplicateId = uuidv4();
        duplicatedIds.set(node.id, duplicateId);
        const sourcePosition = node.data.positionsByLens[activeLens] ?? node.position;
        const nextPosition = {
          x: sourcePosition.x + 60,
          y: sourcePosition.y + 60,
        };

        return {
          ...node,
          id: duplicateId,
          selected: true,
          position: nextPosition,
          data: {
            ...node.data,
            label: getUniqueKpiLabel(`${node.data.label} Copy`),
            positionsByLens: {
              ...node.data.positionsByLens,
              [activeLens]: nextPosition,
            },
            initiatives: node.data.initiatives.map(cloneInitiative),
            tasks: node.data.tasks.map(cloneTask),
            pollConfig: clonePollConfig(node.data.pollConfig),
          },
        } satisfies Node<KpiNodeData>;
      });

    const duplicatedEdges = allEdges
      .filter(
        (edge) =>
          selectedSet.has(edge.source) &&
          selectedSet.has(edge.target) &&
          edgeBelongsToLens(edge, activeLens, nodeMap)
      )
      .map((edge) =>
        makeEdge(
          `e-${duplicatedIds.get(edge.source)}-${duplicatedIds.get(edge.target)}-${uuidv4()}`,
          duplicatedIds.get(edge.source)!,
          duplicatedIds.get(edge.target)!,
          typeof edge.data?.influence === "number" ? edge.data.influence : undefined,
          [activeLens]
        )
      );

    const nextEdges = [...allEdges, ...duplicatedEdges];
    setAllEdges(nextEdges);
    setAllNodes((previous) =>
      layoutVisibleLensGraph(
        applyContextAndRecalculate(
          [
            ...previous.map((node) => ({
              ...node,
              selected: false,
            })),
            ...duplicatedNodes,
          ],
          nextEdges
        ),
        activeLens,
        nextEdges
      )
    );
  }, [
    activeLens,
    aggregationMode,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    availableLenses,
    comparisonMode,
    isLensLibraryCollapsed,
    isLibraryCollapsed,
    isReferenceLibraryCollapsed,
    layoutVisibleLensGraph,
    lensConfigs,
    ownerList,
    panelOffsets,
    panelSizes,
    pollAudienceGroups,
    pollRecipients,
    referenceDate,
    getUniqueKpiLabel,
    canCreateKpis,
    selectedNodeIds,
  ]);

  const onCompactFitView = React.useCallback(() => {
    const relaidOutNodes = layoutVisibleLensGraph(allNodes, activeLens, allEdges, {
      compact: true,
    });
    setAllNodes(relaidOutNodes);

    window.requestAnimationFrame(() => {
      reactFlowInstance?.fitView({
        padding: 0.2,
        duration: 280,
      });
    });
  }, [activeLens, allEdges, allNodes, layoutVisibleLensGraph, reactFlowInstance]);

  const libraryNodes = React.useMemo(
    () =>
      allNodes
        .filter((node) => {
          if (kpiLibraryFilter === allKpisFilterValue) return true;
          if (kpiLibraryFilter === unattachedKpisFilterValue) {
            return node.data.lenses.length === 0;
          }
          return node.data.lenses.includes(kpiLibraryFilter);
        })
        .filter((node) => {
          if (!kpiLibrarySearch.trim()) return true;
          return matchesSearch(
            [
              node.data.label,
              node.data.owner,
              node.data.definition,
              node.data.measurement,
              formatDataSourceLabel(node.data.dataSource),
              formatValueTypeLabel(node.data.valueType),
            ]
              .filter(Boolean)
              .join(" "),
            kpiLibrarySearch
          );
        })
        .slice()
        .sort((left, right) => left.data.label.localeCompare(right.data.label)),
    [allKpisFilterValue, allNodes, kpiLibraryFilter, kpiLibrarySearch, unattachedKpisFilterValue]
  );

  const lensSummaries = React.useMemo(
    () =>
      availableLenses.map((lens) => ({
        lens,
        name: lensConfigs[lens]?.name ?? lens,
        scopeType: lensConfigs[lens]?.scopeType,
        scopeId: lensConfigs[lens]?.scopeId,
        kpiCount: allNodes.filter((node) => node.data.lenses.includes(lens)).length,
        description: lensConfigs[lens]?.description ?? "",
      })),
    [allNodes, availableLenses, lensConfigs]
  );

  const activeEnterpriseName = selectedEnterprise?.name ?? defaultEnterpriseName;
  const activeEnterpriseWebsiteUrl = selectedEnterprise?.website_url ?? "";

  const buildAiWorkspaceContext = React.useCallback(
    () => ({
      enterprise_name: activeEnterpriseName,
      enterprise_website_url: activeEnterpriseWebsiteUrl,
      existing_lenses: lensSummaries.map((lens) => ({
        name: lens.name,
        description: lens.description,
        kpi_count: lens.kpiCount,
      })),
      existing_kpis: allNodes.slice(0, 80).map((node) => ({
        label: node.data.label,
        lens_names: node.data.lenses.map((lens) => lensConfigs[lens]?.name ?? lens),
        owner: node.data.owner,
        value_type: node.data.valueType,
        calculation_mode: node.data.calculationMode,
        status: node.data.status,
        target: node.data.target,
        actual: node.data.actual,
      })),
    }),
    [activeEnterpriseName, activeEnterpriseWebsiteUrl, allNodes, lensConfigs, lensSummaries]
  );

  const getAiErrorMessage = React.useCallback((error: any, fallback: string) => {
    const detail = error?.response?.data?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : typeof detail?.message === "string"
          ? detail.message
          : typeof error?.response?.data === "string"
            ? error.response.data
            : error?.message;

    return typeof message === "string" && message.trim() ? message : fallback;
  }, []);

  const getFirstAiKpiDraft = React.useCallback((draft: AiStrategyRecommendationResponse) => {
    for (const lens of draft.lenses) {
      const kpi = lens.kpis?.[0];
      if (kpi) return kpi;
    }
    return null;
  }, []);

  const getFirstAiInitiativeDraft = React.useCallback((draft: AiStrategyRecommendationResponse) => {
    for (const lens of draft.lenses) {
      for (const kpi of lens.kpis ?? []) {
        const initiative = kpi.initiatives?.[0];
        if (initiative) return initiative;
      }
    }
    return null;
  }, []);

  const getFirstAiTaskDraft = React.useCallback((draft: AiStrategyRecommendationResponse) => {
    for (const lens of draft.lenses) {
      for (const kpi of lens.kpis ?? []) {
        for (const initiative of kpi.initiatives ?? []) {
          const task = initiative.tasks?.[0];
          if (task) return task;
        }
      }
    }
    return null;
  }, []);

  const onGenerateStrategyDraft = React.useCallback(async () => {
    const trimmedPrompt = aiStrategyPrompt.trim();
    if (!trimmedPrompt) {
      setAiStrategyMessage("Add a little context before generating recommendations.");
      return;
    }

    setIsGeneratingStrategyDraft(true);
    setAiStrategyMessage(null);
    try {
      const response = await api.post("/ai/strategy-recommendations", {
        ...buildAiWorkspaceContext(),
        prompt: trimmedPrompt,
      }, {
        timeout: 180000,
      });
      const draft = response.data as AiStrategyRecommendationResponse;
      setAiStrategyDraft(draft);
      setSelectedAiLensIndexes(draft.lenses.map((_, index) => index));
      setAiStrategyMessage(`Generated ${draft.lenses.length} draft lens recommendation${draft.lenses.length === 1 ? "" : "s"}. Review before applying.`);
      setIsAiStrategySectionCollapsed(false);
    } catch (error: any) {
      console.error("Failed to generate strategy recommendations", error);
      setAiStrategyMessage(getAiErrorMessage(error, "Could not generate strategy recommendations. Check the backend AI configuration."));
    } finally {
      setIsGeneratingStrategyDraft(false);
    }
  }, [aiStrategyPrompt, buildAiWorkspaceContext, getAiErrorMessage]);

  const onToggleAiLensSelection = React.useCallback((index: number) => {
    setSelectedAiLensIndexes((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : [...current, index].sort((left, right) => left - right)
    );
  }, []);

  const onApplySelectedStrategyDraft = React.useCallback(() => {
    if (!aiStrategyDraft || selectedAiLensIndexes.length === 0) return;

    const selectedSet = new Set(selectedAiLensIndexes);
    const selectedLensDrafts = aiStrategyDraft.lenses.filter((_, index) => selectedSet.has(index));
    if (selectedLensDrafts.length === 0) return;

    const nextLensConfigs: LensConfigs = { ...lensConfigs };
    const nextAvailableLenses = [...availableLenses];
    const nextLensNameDrafts = { ...lensNameDrafts };
    const nextNodes: Node<KpiNodeData>[] = [...allNodes];
    const nextEdges: Edge[] = [...allEdges];
    let firstAppliedLens: Lens | null = null;

    const createTaskFromDraft = (task: AiStrategyTaskRecommendation): TaskItem => ({
      id: uuidv4(),
      title: task.title?.trim() || "New task",
      owner: task.owner_role?.trim() || "Assign an owner",
      dueDate: "",
      status: "not-started",
      priority: normalizeAiPriority(task.priority),
      effort: normalizeAiEffort(task.effort),
      notes: "",
    });

    const createInitiativeFromDraft = (initiative: AiStrategyInitiativeRecommendation): InitiativeItem => ({
      id: uuidv4(),
      title: initiative.title?.trim() || "New initiative",
      owner: initiative.owner_role?.trim() || "Assign an owner",
      dueDate: "",
      status: "not-started",
      priority: normalizeAiPriority(initiative.priority),
      effort: normalizeAiEffort(initiative.effort),
      summary: initiative.summary?.trim() || "",
      tasks: (initiative.tasks ?? []).slice(0, 8).map(createTaskFromDraft),
    });

    const appendKpiTree = (
      lensId: Lens,
      draft: AiStrategyKpiRecommendation,
      parentId: string | null,
      depth: number,
      siblingIndex: number,
      siblingCount: number,
      rootIndex: number
    ) => {
      const nodeId = uuidv4();
      const children = (draft.children ?? []).slice(0, 8);
      const label = draft.label?.trim() || "Recommended KPI";
      const position = {
        x: 120 + rootIndex * 380 + (siblingIndex - (siblingCount - 1) / 2) * 280,
        y: 120 + depth * 520,
      };
      const calculationMode =
        children.length > 0 && normalizeAiCalculationMode(draft.calculation_mode) === "direct"
          ? "influenced_by_children"
          : normalizeAiCalculationMode(draft.calculation_mode);
      const dataSource = dataSourceOptions.includes(draft.data_source as typeof dataSourceOptions[number])
        ? draft.data_source!
        : "Manual";

      const node: Node<KpiNodeData> = {
        id: nodeId,
        type: "kpi",
        position,
        data: createNodeData(
          {
            label,
            definition: draft.definition?.trim() || "Describe what this KPI is intended to measure.",
            measurement: draft.measurement?.trim() || "Describe exactly how this KPI is counted or calculated.",
            owner: draft.owner_role?.trim() || "Assign an owner",
            valueType: normalizeAiValueType(draft.value_type),
            dataSource,
            frequency: normalizeAiFrequency(draft.frequency),
            calculationMode,
            initiatives: (draft.initiatives ?? []).slice(0, 6).map(createInitiativeFromDraft),
            lenses: [lensId],
            lensLabels: [nextLensConfigs[lensId]?.name ?? lensId],
            positionsByLens: { [lensId]: position },
            metricsEditable: calculationMode === "direct",
          },
          position
        ),
      };

      nextNodes.push(node);
      if (parentId) {
        const influence = siblingCount > 0 ? Number((100 / siblingCount).toFixed(2)) : undefined;
        nextEdges.push(
          makeEdge(
            `e-${nodeId}-${parentId}-${uuidv4()}`,
            nodeId,
            parentId,
            influence,
            [lensId]
          )
        );
      }

      children.forEach((child, index) =>
        appendKpiTree(lensId, child, nodeId, depth + 1, index, children.length, rootIndex)
      );
    };

    selectedLensDrafts.forEach((lensDraft, lensIndex) => {
      const lensId = uuidv4();
      const lensName = lensDraft.name?.trim() || `AI Lens ${lensIndex + 1}`;
      const scopeType = normalizeAiScopeType(lensDraft.scope_type);
      const scopeId = lensDraft.scope_id?.trim() || getDefaultScopeId(scopeType);

      if (!firstAppliedLens) firstAppliedLens = lensId;
      nextAvailableLenses.push(lensId);
      nextLensConfigs[lensId] = {
        name: lensName,
        scopeType,
        scopeId,
        description:
          lensDraft.description?.trim() ||
          lensDraft.rationale?.trim() ||
          "AI-recommended lens awaiting refinement.",
      };
      nextLensNameDrafts[lensId] = lensName;

      (lensDraft.kpis ?? []).slice(0, 8).forEach((kpi, index, kpis) =>
        appendKpiTree(lensId, kpi, null, 0, index, kpis.length, index)
      );
    });

    setAvailableLenses(nextAvailableLenses);
    setLensConfigs(nextLensConfigs);
    setLensNameDrafts(nextLensNameDrafts);
    setAllEdges(nextEdges);
    const nextActiveLens = firstAppliedLens ?? activeLens;
    setActiveLens(nextActiveLens);
    setAllNodes(
      layoutVisibleLensGraph(
        applyContextAndRecalculate(nextNodes, nextEdges, referenceDate, campus, nextActiveLens),
        nextActiveLens,
        nextEdges,
        { compact: true }
      )
    );
    setActiveLensDetailId(firstAppliedLens);
    setAiStrategyDraft(null);
    setSelectedAiLensIndexes([]);
    setAiStrategyPrompt("");
    setAiStrategyMessage(`Applied ${selectedLensDrafts.length} approved lens draft${selectedLensDrafts.length === 1 ? "" : "s"} to the workspace.`);
  }, [
    activeLens,
    aiStrategyDraft,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    availableLenses,
    campus,
    createNodeData,
    layoutVisibleLensGraph,
    lensConfigs,
    lensNameDrafts,
    referenceDate,
    selectedAiLensIndexes,
  ]);

  const onGenerateContextualAiDraft = React.useCallback(
    async (kind: AiAddKind, options: { kpiId?: string; initiativeId?: string } = {}) => {
      const kpi = options.kpiId
        ? allNodes.find((node) => node.id === options.kpiId)
        : activeKpiId
          ? allNodes.find((node) => node.id === activeKpiId)
          : null;
      const initiative =
        kpi && options.initiativeId
          ? kpi.data.initiatives.find((item) => item.id === options.initiativeId)
          : null;
      const activeLensName = lensConfigs[activeLens]?.name ?? activeLens;
      const kpiContext = kpi
        ? [
            `KPI: ${kpi.data.label}`,
            `Status: ${kpi.data.status ?? "neutral"}`,
            `Target: ${kpi.data.target ?? "not set"}`,
            `Actual: ${kpi.data.actual ?? "not set"}`,
            `Definition: ${kpi.data.definition}`,
            `Measurement: ${kpi.data.measurement}`,
            `Owner: ${kpi.data.owner}`,
          ].join("\n")
        : "";
      const initiativeContext = initiative
        ? [
            `Initiative: ${initiative.title}`,
            `Summary: ${initiative.summary}`,
            `Owner: ${initiative.owner}`,
            `Status: ${initiative.status}`,
          ].join("\n")
        : "";
      const promptByKind: Record<AiAddKind, string> = {
        lens:
          `Recommend exactly one new strategic lens for ${activeEnterpriseName}. Return one lens with a short description and 1-2 starter KPIs. Avoid duplicating existing lenses.`,
        kpi:
          `Recommend exactly one new KPI to add to the "${activeLensName}" lens. Return one lens containing one KPI only. Make it measurable, practical, and not duplicative of existing KPIs.`,
        initiative:
          `Recommend exactly one initiative to improve this KPI, including 2-4 concrete starter tasks. Return one lens containing one KPI with one initiative.\n\n${kpiContext}`,
        kpiTask:
          `Recommend exactly one direct task for this KPI. Return one lens containing one KPI with one initiative and one task; the task should stand alone as a useful direct KPI task.\n\n${kpiContext}`,
        initiativeTask:
          `Recommend exactly one next task for this initiative. Return one lens containing one KPI with one initiative and one task.\n\n${kpiContext}\n\n${initiativeContext}`,
      };

      setAiAddLoadingKind(kind);
      setAiAddDraft(null);
      setAiAddMessage(null);
      setAiAddMessageTarget({ kind, kpiId: options.kpiId, initiativeId: options.initiativeId });
      try {
        const response = await api.post(
          "/ai/strategy-recommendations",
          {
            ...buildAiWorkspaceContext(),
            prompt: promptByKind[kind],
          },
          { timeout: 120000 }
        );
        const responseDraft = response.data as AiStrategyRecommendationResponse;
        const nextDraft: AiAddDraft = {
          kind,
          kpiId: options.kpiId,
          initiativeId: options.initiativeId,
          summary: responseDraft.summary,
        };

        if (kind === "lens") {
          nextDraft.lens = responseDraft.lenses[0];
        } else if (kind === "kpi") {
          nextDraft.kpi = getFirstAiKpiDraft(responseDraft) ?? undefined;
        } else if (kind === "initiative") {
          nextDraft.initiative = getFirstAiInitiativeDraft(responseDraft) ?? undefined;
        } else {
          nextDraft.task = getFirstAiTaskDraft(responseDraft) ?? undefined;
        }

        const hasRecommendation =
          Boolean(nextDraft.lens) ||
          Boolean(nextDraft.kpi) ||
          Boolean(nextDraft.initiative) ||
          Boolean(nextDraft.task);
        if (!hasRecommendation) {
          setAiAddMessage("AI did not return a usable recommendation. Try again or add one manually.");
          return;
        }

        setAiAddDraft(nextDraft);
        setAiAddMessage("Review the AI recommendation, then accept it, ask for another one, or add manually.");
      } catch (error: any) {
        console.error("Failed to generate contextual AI recommendation", error);
        setAiAddMessage(getAiErrorMessage(error, "Could not generate an AI recommendation."));
      } finally {
        setAiAddLoadingKind(null);
      }
    },
    [
      activeEnterpriseName,
      activeKpiId,
      activeLens,
      allNodes,
      buildAiWorkspaceContext,
      getAiErrorMessage,
      getFirstAiInitiativeDraft,
      getFirstAiKpiDraft,
      getFirstAiTaskDraft,
      lensConfigs,
    ]
  );

  const onClearAiAddDraft = React.useCallback(() => {
    setAiAddDraft(null);
    setAiAddMessage(null);
    setAiAddMessageTarget(null);
  }, []);

  const onAcceptAiAddDraft = React.useCallback(() => {
    if (!aiAddDraft) return;

    if (aiAddDraft.kind === "lens" && aiAddDraft.lens) {
      const lensId = uuidv4();
      const lensName = aiAddDraft.lens.name?.trim() || "AI recommended lens";
      const scopeType = normalizeAiScopeType(aiAddDraft.lens.scope_type);
      const scopeId = aiAddDraft.lens.scope_id?.trim() || getDefaultScopeId(scopeType);

      setAvailableLenses((previous) => [...previous, lensId]);
      setLensConfigs((previous) => ({
        ...previous,
        [lensId]: {
          name: lensName,
          scopeType,
          scopeId,
          description:
            aiAddDraft.lens?.description?.trim() ||
            aiAddDraft.lens?.rationale?.trim() ||
            "AI-recommended lens awaiting refinement.",
        },
      }));
      setLensNameDrafts((previous) => ({ ...previous, [lensId]: lensName }));
      setActiveLens(lensId);
      setActiveLensDetailId(lensId);
      setAiAddDraft(null);
      setAiAddMessage(null);
      setAiAddMessageTarget(null);
      return;
    }

    if (aiAddDraft.kind === "kpi" && aiAddDraft.kpi) {
      const lensNodes = allNodes.filter((node) => node.data.lenses.includes(activeLens));
      const lensNodeIds = new Set(lensNodes.map((node) => node.id));
      const lensEdges = allEdges.filter(
        (edge) => lensNodeIds.has(edge.source) && lensNodeIds.has(edge.target)
      );
      const rootCount = lensNodes.filter(
        (node) => !lensEdges.some((edge) => edge.target === node.id)
      ).length;
      const nodeId = uuidv4();
      const viewportCenter = reactFlowInstance
        ? reactFlowInstance.screenToFlowPosition({
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
          })
        : null;
      const position = {
        x: viewportCenter?.x ?? 180 + rootCount * 340,
        y: viewportCenter?.y ?? 120,
      };
      const calculationMode = normalizeAiCalculationMode(aiAddDraft.kpi.calculation_mode);
      const dataSource = dataSourceOptions.includes(aiAddDraft.kpi.data_source as typeof dataSourceOptions[number])
        ? aiAddDraft.kpi.data_source!
        : "Manual";
      const newNode: Node<KpiNodeData> = {
        id: nodeId,
        type: "kpi",
        position,
        data: createNodeData(
          {
            label: aiAddDraft.kpi.label?.trim() || "Recommended KPI",
            definition:
              aiAddDraft.kpi.definition?.trim() ||
              "Describe what this KPI is intended to measure.",
            measurement:
              aiAddDraft.kpi.measurement?.trim() ||
              "Describe exactly how this KPI is counted or calculated.",
            owner: aiAddDraft.kpi.owner_role?.trim() || "Assign an owner",
            valueType: normalizeAiValueType(aiAddDraft.kpi.value_type),
            dataSource,
            frequency: normalizeAiFrequency(aiAddDraft.kpi.frequency),
            calculationMode,
            initiatives: (aiAddDraft.kpi.initiatives ?? [])
              .slice(0, 4)
              .map(createInitiativeFromAiDraft),
            metricsEditable: calculationMode === "direct",
          },
          position
        ),
      };

      setAllNodes((previous) =>
        layoutVisibleLensGraph(
          applyContextAndRecalculate([...previous, newNode], allEdges),
          activeLens,
          allEdges
        )
      );
      setActiveKpiId(nodeId);
      setAiAddDraft(null);
      setAiAddMessage(null);
      setAiAddMessageTarget(null);
      return;
    }

    if (aiAddDraft.kind === "initiative" && aiAddDraft.initiative && aiAddDraft.kpiId) {
      const initiative = createInitiativeFromAiDraft(aiAddDraft.initiative);
      updateNodeById(aiAddDraft.kpiId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: [...node.data.initiatives, initiative],
        },
      }));
      setAiAddDraft(null);
      setAiAddMessage(null);
      setAiAddMessageTarget(null);
      return;
    }

    if (aiAddDraft.kind === "kpiTask" && aiAddDraft.task && aiAddDraft.kpiId) {
      const task = createTaskFromAiDraft(aiAddDraft.task);
      updateNodeById(aiAddDraft.kpiId, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: [...node.data.tasks, task],
        },
      }));
      setAiAddDraft(null);
      setAiAddMessage(null);
      setAiAddMessageTarget(null);
      return;
    }

    if (
      aiAddDraft.kind === "initiativeTask" &&
      aiAddDraft.task &&
      aiAddDraft.kpiId &&
      aiAddDraft.initiativeId
    ) {
      const task = createTaskFromAiDraft(aiAddDraft.task);
      updateNodeById(aiAddDraft.kpiId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === aiAddDraft.initiativeId
              ? { ...initiative, tasks: [...initiative.tasks, task] }
              : initiative
          ),
        },
      }));
      setAiAddDraft(null);
      setAiAddMessage(null);
      setAiAddMessageTarget(null);
    }
  }, [
    activeLens,
    aiAddDraft,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    createNodeData,
    layoutVisibleLensGraph,
    reactFlowInstance,
    updateNodeById,
  ]);

  const panelAnchors = React.useMemo(() => {
    return {
      kpi: 16,
      reference: 16,
      lens: 16,
    };
  }, []);

  const serializeWorkspace = React.useCallback(
    () => ({
      ...extraWorkspaceFieldsRef.current,
      enterpriseIdentity: getWorkspaceEnterpriseIdentity(activeEnterpriseId, selectedEnterprise),
      availableLenses,
      lensConfigs,
      activeLens,
      referenceDate: referenceDate.toISOString(),
      aggregationMode,
      comparisonMode,
      campus: defaultCampus,
      ownerList,
      pollAudienceGroups,
      pollRecipients,
      isLensLibraryCollapsed,
      isReferenceLibraryCollapsed,
      isLibraryCollapsed,
      panelOffsets,
      panelSizes,
      allNodes: allNodes.map((node) => ({
        ...node,
        data: stripNodeDataForPersistence(node.data),
      })),
      allEdges,
    }),
    [
      activeLens,
      activeEnterpriseId,
      allEdges,
      allNodes,
      availableLenses,
      isLensLibraryCollapsed,
      isLibraryCollapsed,
      isReferenceLibraryCollapsed,
      aggregationMode,
      comparisonMode,
      lensConfigs,
      ownerList,
      pollAudienceGroups,
      pollRecipients,
      panelOffsets,
      panelSizes,
      referenceDate,
      selectedEnterprise,
    ]
  );

  React.useEffect(() => {
    serializeWorkspaceRef.current = serializeWorkspace;
  }, [serializeWorkspace]);

  const closeKpiContextMenu = React.useCallback(() => {
    setKpiContextMenu(null);
    setMapContextMenu(null);
  }, []);

  const openKpiInfoPanel = React.useCallback((nodeId: string) => {
    setActiveKpiId(nodeId);
    setIsKpiInfoOpen(true);
    setIsKpiWorkOpen(false);
    closeKpiContextMenu();
  }, [closeKpiContextMenu]);

  const openKpiWorkPanel = React.useCallback((nodeId: string) => {
    setActiveKpiId(nodeId);
    setIsKpiInfoOpen(false);
    setIsKpiWorkOpen(true);
    closeKpiContextMenu();
  }, [closeKpiContextMenu]);

  const openKpiPollPanel = React.useCallback((nodeId: string) => {
    onCreatePoll(nodeId);
    setActiveKpiId(nodeId);
    setPollEditorKpiId(nodeId);
    setPollEditorMode("edit");
    closeKpiContextMenu();
  }, [closeKpiContextMenu, onCreatePoll]);

  const emailKpiDataOwner = React.useCallback(
    (node: Node<KpiNodeData>) => {
      const owner = (node.data.dataOwner || node.data.owner).trim();
      if (!owner) {
        setLoadError("This KPI does not have a data owner or owner to email yet.");
        closeKpiContextMenu();
        return;
      }

      const email = owner.includes("@") ? owner : ownerEmailByKey.get(owner.toLowerCase());
      if (!email) {
        setLoadError(`No email address was found for ${owner}. Add them as a user or use their email as the data owner.`);
        closeKpiContextMenu();
        return;
      }

      const updateUrl =
        activeEnterpriseId === null
          ? ""
          : `${window.location.origin}/?${new URLSearchParams({
              enterpriseId: String(activeEnterpriseId),
              editKpiId: node.id,
              lens: activeLens,
              panel: "actuals",
              focus: "actual",
            }).toString()}`;
      const subject = encodeURIComponent(`Actual update requested: ${node.data.label || "Untitled KPI"}`);
      const body = encodeURIComponent(
        [
          `Hi ${owner},`,
          "",
          `Please update the actual value for this KPI in Lenses CPM: ${node.data.label || "Untitled KPI"}.`,
          "",
          `Current actual: ${formatMetricValue(node.data.actual, node.data.valueType)}`,
          `Target: ${formatMetricValue(node.data.target, node.data.valueType)}`,
          updateUrl ? `Update actual: ${updateUrl}` : "",
          "",
          "Thanks,",
        ].filter((line) => line !== "").join("\n")
      );
      window.location.href = `mailto:${encodeURIComponent(email)}?subject=${subject}&body=${body}`;
      closeKpiContextMenu();
    },
    [activeEnterpriseId, activeLens, closeKpiContextMenu, ownerEmailByKey]
  );

  const requestKpiTaskUpdates = React.useCallback(
    async (node: Node<KpiNodeData>) => {
      closeKpiContextMenu();
      setActiveKpiId(node.id);
      setIsKpiInfoOpen(false);
      setIsKpiWorkOpen(true);

      if (activeEnterpriseId === null) {
        setKpiWorkEmailStatusMessage("Select an enterprise before sending update requests.");
        return;
      }

      const taskIds = Array.from(
        new Set(
          [
            ...node.data.tasks,
            ...node.data.initiatives.flatMap((initiative) => initiative.tasks),
          ]
            .filter((task) => task.status !== "done" && Boolean(task.id) && Boolean(task.owner))
            .map((task) => task.id)
        )
      );

      if (taskIds.length === 0) {
        setKpiWorkEmailStatusMessage("No assigned open tasks are available for this KPI.");
        return;
      }

      setIsSendingKpiWorkUpdateEmails(true);
      setKpiWorkEmailStatusMessage(null);
      try {
        const response = await api.post(
          `/enterprises/${activeEnterpriseId}/tasks/send-update-requests`,
          {
            task_ids: taskIds,
            dry_run: false,
          }
        );
        const result = response.data as TaskEmailResult;
        const skippedText =
          result.skipped_count > 0
            ? ` ${result.skipped_count} skipped because no matching email was found.`
            : "";
        const failedText = result.failed_count > 0 ? ` ${result.failed_count} failed.` : "";
        const errorText = result.errors?.length ? ` ${result.errors.slice(0, 2).join(" ")}` : "";
        setKpiWorkEmailStatusMessage(
          `Sent ${result.sent_count} update request email${result.sent_count === 1 ? "" : "s"} for ${node.data.label || "this KPI"}.${skippedText}${failedText}${errorText}`
        );
      } catch (error) {
        console.error("Failed to send KPI update requests", error);
        setKpiWorkEmailStatusMessage("Could not send update requests for this KPI.");
      } finally {
        setIsSendingKpiWorkUpdateEmails(false);
      }
    },
    [activeEnterpriseId, closeKpiContextMenu]
  );

  const trackKpiOnDashboard = React.useCallback(
    async (node: Node<KpiNodeData>) => {
      closeKpiContextMenu();

      if (activeEnterpriseId === null) {
        setLoadError("Select an enterprise before tracking a KPI on the dashboard.");
        return;
      }

      const owner = node.data.owner.trim();
      if (!owner) {
        setLoadError("Assign this KPI to an owner before tracking it on a dashboard.");
        return;
      }

      const existingDashboardConfigs =
        extraWorkspaceFieldsRef.current.dashboardConfigs &&
        typeof extraWorkspaceFieldsRef.current.dashboardConfigs === "object" &&
        !Array.isArray(extraWorkspaceFieldsRef.current.dashboardConfigs)
          ? (extraWorkspaceFieldsRef.current.dashboardConfigs as Record<string, DashboardConfig>)
          : {};
      const currentConfig = existingDashboardConfigs[owner] ?? {
        trackedKpiIds: allNodes
          .filter((item) => item.data.owner === owner)
          .map((item) => item.id),
        trackedKpiLabels: allNodes
          .filter((item) => item.data.owner === owner)
          .map((item) => item.data.label)
          .filter(Boolean),
      };
      const nextTrackedKpiIds = currentConfig.trackedKpiIds.includes(node.id)
        ? currentConfig.trackedKpiIds
        : [...currentConfig.trackedKpiIds, node.id];
      const nextTrackedKpiLabels = nextTrackedKpiIds
        .map((id) => allNodes.find((item) => item.id === id)?.data.label)
        .filter((label): label is string => typeof label === "string" && label.trim().length > 0);

      extraWorkspaceFieldsRef.current = {
        ...extraWorkspaceFieldsRef.current,
        dashboardConfigs: {
          ...existingDashboardConfigs,
          [owner]: {
            trackedKpiIds: nextTrackedKpiIds,
            trackedKpiLabels: nextTrackedKpiLabels,
          },
        },
      };

      const serializedWorkspace = serializeWorkspace();
      window.localStorage.setItem(
        getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise),
        JSON.stringify(serializedWorkspace)
      );
      try {
        await api.put(`/enterprises/${activeEnterpriseId}/workspace`, {
          workspace: serializedWorkspace,
        });
        notifyWorkspaceChanged(activeEnterpriseId, "workspace-save");
        setLoadError(`${node.data.label || "This KPI"} is tracked on ${owner}'s dashboard.`);
      } catch (error) {
        console.error("Failed to save dashboard tracking", error);
        setLoadError("Could not save dashboard tracking for this KPI.");
      }
    },
    [activeEnterpriseId, allNodes, closeKpiContextMenu, selectedEnterprise, serializeWorkspace]
  );

  const onConnectQuickBooks = React.useCallback(async () => {
    if (activeEnterpriseId === null) {
      setQuickBooksMessage("Select an enterprise before connecting QuickBooks.");
      return;
    }
    if (quickBooksStatus?.configured === false) {
      setQuickBooksMessage(
        "QuickBooks needs backend setup before it can connect. Set QUICKBOOKS_CLIENT_ID, QUICKBOOKS_CLIENT_SECRET, and QUICKBOOKS_ENCRYPTION_KEY on the backend service."
      );
      return;
    }
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    try {
      const response = await api.get("/integrations/quickbooks/connect", {
        params: { enterprise_id: activeEnterpriseId },
      });
      const authorizeUrl = response.data?.authorize_url;
      if (typeof authorizeUrl !== "string" || !authorizeUrl) {
        throw new Error("QuickBooks authorize URL was not returned.");
      }
      window.location.href = authorizeUrl;
    } catch (error) {
      console.error("Failed to start QuickBooks connection", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not start the QuickBooks connection. Check the server configuration."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage, quickBooksStatus?.configured]);

  const onConnectPushpay = React.useCallback(async () => {
    if (activeEnterpriseId === null) {
      setPushpayMessage("Select an enterprise before connecting Pushpay ChMS.");
      return;
    }
    if (pushpayStatus?.configured === false) {
      setPushpayMessage(
        "Pushpay ChMS needs backend setup before it can connect. Set PUSHPAY_CHMS_CLIENT_ID, PUSHPAY_CHMS_CLIENT_SECRET, and PUSHPAY_CHMS_ENCRYPTION_KEY, or share QUICKBOOKS_ENCRYPTION_KEY."
      );
      return;
    }
    setIsPushpayLoading(true);
    setPushpayMessage(null);
    try {
      const response = await api.get("/integrations/pushpay/connect", {
        params: { enterprise_id: activeEnterpriseId },
      });
      const authorizeUrl = response.data?.authorize_url;
      if (typeof authorizeUrl !== "string" || !authorizeUrl) {
        throw new Error("Pushpay authorize URL was not returned.");
      }
      window.location.href = authorizeUrl;
    } catch (error) {
      console.error("Failed to start Pushpay ChMS connection", error);
      setPushpayMessage(getAiErrorMessage(error, "Could not start the Pushpay ChMS connection."));
    } finally {
      setIsPushpayLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage, pushpayStatus?.configured]);

  const onTestPushpayIndividuals = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsPushpayLoading(true);
    setPushpayMessage(null);
    try {
      const response = await api.get("/integrations/pushpay/individuals-preview", {
        params: { enterprise_id: activeEnterpriseId },
      });
      const preview = response.data;
      const recordCount = Array.isArray(preview)
        ? preview.length
        : Array.isArray(preview?.items)
          ? preview.items.length
          : 0;
      setPushpayMessage(`Pushpay ChMS connected. Preview returned ${recordCount} individual record${recordCount === 1 ? "" : "s"}.`);
    } catch (error) {
      console.error("Failed to test Pushpay ChMS individuals", error);
      setPushpayMessage(getAiErrorMessage(error, "Pushpay ChMS is connected, but the individuals preview failed."));
    } finally {
      setIsPushpayLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage]);

  const onPreviewKpiApiSource = React.useCallback(
    async (nodeId: string) => {
      if (activeEnterpriseId === null) return;
      const targetNode = allNodes.find((node) => node.id === nodeId);
      if (!targetNode) {
        setKpiApiMessage("Choose a valid KPI before checking API data.");
        return;
      }
      const config = getEffectiveKpiApiConfig(targetNode);
      if (!config) {
        setKpiApiMessage("Choose an API connector for this KPI before checking API data.");
        return;
      }
      if (config.provider === "quickbooks") {
        setKpiApiMessage("QuickBooks finance KPIs sync through the smart append writer. Click Sync KPI Actuals to refresh this KPI.");
        return;
      }
      if (!["weekly", "monthly", "bimonthly", "quarterly", "yearly"].includes(targetNode.data.frequency)) {
        setKpiApiMessage("API preview supports weekly, monthly, bimonthly, quarterly, or yearly KPI frequencies.");
        return;
      }

      setIsKpiApiLoading(true);
      setKpiApiMessage(null);
      try {
        const isEventAttendance = config.resource === "event_attendance";
        const isCampusWeekendAttendance = config.resource === "campus_weekend_attendance";
        const isProcessQueueCount = config.resource === "process_queue_count";
        const isScheduleVolunteerCheckins = config.resource === "schedule_volunteer_checkins";
        const response = await api.get(
          isProcessQueueCount
            ? "/integrations/ccb/process-queue-preview"
            : isScheduleVolunteerCheckins
            ? "/integrations/pushpay/schedule-volunteer-checkins-preview"
            : isCampusWeekendAttendance
            ? "/integrations/pushpay/campus-weekend-attendance-preview"
            : isEventAttendance
            ? "/integrations/pushpay/event-attendance-preview"
            : "/integrations/pushpay/group-attendance-preview",
          {
            params: {
              enterprise_id: activeEnterpriseId,
              start_date: kpiApiStartDate,
              end_date: kpiApiEndDate,
              period: targetNode.data.frequency,
              ...(isCampusWeekendAttendance
                ? { campus_key: config.campusAttendancePreset }
                : isScheduleVolunteerCheckins
                ? { schedule_search: config.scheduleSearch }
                : isProcessQueueCount
                ? {
                    process_search: config.processSearch,
                    queue_search: config.queueSearch,
                    status: config.queueStatus,
                  }
                : isEventAttendance
                ? {
                    event_search: config.eventSearch,
                    event_id: config.eventId,
                    calendar_search: config.calendarSearch,
                  }
                : { group_search: config.groupSearch }),
              metric: config.metric,
            },
            timeout: isProcessQueueCount ? 180000 : undefined,
          }
        );
        const preview = response.data as PushpayDgroupAttendancePreview;
        const itemNames = preview.groups
          .map((group) => String(group.name || group.id || "").trim())
          .filter(Boolean);
        const sampleText = itemNames.length > 0 ? `: ${itemNames.slice(0, 12).join(", ")}` : ".";
        const remainingCount = Math.max(0, preview.group_count - itemNames.slice(0, 12).length);
        const campusLabel =
          pushpayCampusAttendanceOptions.find((option) => option.value === config.campusAttendancePreset)?.label ||
          config.campusAttendancePreset;
        const filterLabel = isCampusWeekendAttendance
          ? campusLabel
          : isScheduleVolunteerCheckins
            ? config.scheduleSearch || "all schedules"
          : isProcessQueueCount
            ? [config.processSearch, config.queueSearch, config.queueStatus].filter(Boolean).join(" / ") || "all visible process queues"
          : isEventAttendance
            ? config.eventId.trim() || [config.eventSearch, config.calendarSearch].filter(Boolean).join(" / ") || "all visible events"
            : config.groupSearch.trim() || "all visible groups";
        const itemLabel = isProcessQueueCount
          ? "process queue"
          : isScheduleVolunteerCheckins
            ? "schedule"
          : isCampusWeekendAttendance
            ? "campus weekend event"
            : isEventAttendance
              ? "event"
              : "group";
        const sourceLabel = getKpiApiSourceLabel(config);
        if (isScheduleVolunteerCheckins) {
          const periodTotals = preview.periods
            .map((item) => `${item.period_key}: ${item.value}`)
            .join(", ");
          setKpiApiMessage(
            `Found ${preview.group_count} matching Schedules & Plans categor${
              preview.group_count === 1 ? "y" : "ies"
            } and ${preview.record_count} schedule${preview.record_count === 1 ? "" : "s"} for ${filterLabel}. ${
              periodTotals
                ? `Checked-in volunteers by ${preview.period}: ${periodTotals}.`
                : "No checked-in volunteers were reported in the selected date range."
            }`
          );
          return;
        }
        setKpiApiMessage(
          `Found ${preview.group_count} matching ${sourceLabel} ${itemLabel}${
            preview.group_count === 1 ? "" : "s"
          } for ${filterLabel}${sampleText}${
            remainingCount > 0 ? `, plus ${remainingCount} more.` : ""
          }`
        );
      } catch (error) {
        console.error("Failed to preview KPI API source", error);
        setKpiApiMessage(getAiErrorMessage(error, "Could not check matching API data."));
      } finally {
        setIsKpiApiLoading(false);
      }
    },
    [activeEnterpriseId, allNodes, getAiErrorMessage, kpiApiEndDate, kpiApiStartDate]
  );

  const persistLatestWorkspace = React.useCallback(() => {
    const save = async () => {
      if (activeEnterpriseId === null || !canPersistWorkspace || !hasLoadedEnterpriseRef.current) {
        throw new Error("Workspace changes have not been saved. Resolve the workspace warning before syncing.");
      }
      const workspace = serializeWorkspaceRef.current();
      const fingerprint = getWorkspacePersistenceFingerprint(workspace);
      if (fingerprint === lastWorkspacePersistenceFingerprintRef.current) return;
      window.localStorage.setItem(getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise), JSON.stringify(workspace));
      window.localStorage.setItem(getWorkspacePendingSaveKey(activeEnterpriseId, selectedEnterprise), "true");
      const response = await api.put(`/enterprises/${activeEnterpriseId}/workspace`, {
        workspace, expected_updated_at: workspaceUpdatedAtRef.current,
      });
      workspaceUpdatedAtRef.current = typeof response.data.updated_at === "string" ? response.data.updated_at : workspaceUpdatedAtRef.current;
      lastWorkspacePersistenceFingerprintRef.current = fingerprint;
      window.localStorage.removeItem(getWorkspacePendingSaveKey(activeEnterpriseId, selectedEnterprise));
      notifyWorkspaceChanged(activeEnterpriseId, "workspace-save");
    };
    // Debounced autosave, page exit, and Sync share one sequence. Sync cannot
    // overtake an in-flight mapping save or issue a competing stale save.
    const operation = workspaceSavePromiseRef.current.catch(() => undefined).then(save);
    workspaceSavePromiseRef.current = operation;
    return operation;
  }, [activeEnterpriseId, canPersistWorkspace, selectedEnterprise]);

  const onSyncKpiApiSource = React.useCallback(
    async (nodeId: string) => {
      if (activeEnterpriseId === null) return;
      const targetNode = allNodes.find((node) => node.id === nodeId);
      if (!targetNode) {
        setKpiApiMessage("Choose a valid KPI before syncing.");
        return;
      }
      if (!canEditKpiNode(targetNode.id) && !currentUserHasLegacyAdminRole) {
        setKpiApiMessage("You do not have permission to update that KPI.");
        return;
      }
      const config = getEffectiveKpiApiConfig(targetNode);
      if (!config) {
        setKpiApiMessage("Choose an API connector for this KPI before syncing.");
        return;
      }
      if (!["weekly", "monthly", "bimonthly", "quarterly", "yearly"].includes(targetNode.data.frequency)) {
        setKpiApiMessage("API sync supports weekly, monthly, bimonthly, quarterly, or yearly KPI frequencies.");
        return;
      }

      setIsKpiApiLoading(true);
      setKpiApiMessage(null);
      try {
        await persistLatestWorkspace();
        const response = await api.post(
          `/enterprises/${activeEnterpriseId}/kpis/sync-api-actuals`,
          {
            node_ids: [targetNode.id],
            start_date: kpiApiStartDate,
            end_date: kpiApiEndDate,
            background: true,
          },
          { timeout: 30000 }
        );
        const result = await waitForSyncRun(activeEnterpriseId, response.data, setKpiApiMessage);
        setKpiApiMessage(syncRunMessage(result));
      } catch (error) {
        console.error("Failed to sync KPI API source", error);
        setKpiApiMessage(getAiErrorMessage(error, "Could not sync this KPI from its API source."));
      } finally {
        setIsKpiApiLoading(false);
      }
    },
    [
      activeEnterpriseId,
      allNodes,
      canEditKpiNode,
      currentUserHasLegacyAdminRole,
      getAiErrorMessage,
      kpiApiEndDate,
      kpiApiStartDate,
      persistLatestWorkspace,
    ]
  );

  const onSyncAllPushpayApiKpis = React.useCallback(async () => {
    if (activeEnterpriseId === null) {
      setPushpayMessage("Select an enterprise before syncing API KPIs.");
      return;
    }
    const baseCandidates = allNodes
      .map((node) => ({ node, config: getEffectiveKpiApiConfig(node) }))
      .filter(
        (item): item is { node: Node<KpiNodeData>; config: KpiApiConfig } =>
          Boolean(item.config) &&
          ["weekly", "monthly", "bimonthly", "quarterly", "yearly"].includes(item.node.data.frequency) &&
          (canEditKpiNode(item.node.id) || currentUserHasLegacyAdminRole)
      );
    const candidates = baseCandidates.filter((item) =>
      isKpiApiSourceAvailable(item.config, pushpayStatus?.connected, quickBooksStatus?.connected)
    );
    if (candidates.length === 0) {
      const pushpayBlockedCount = baseCandidates.filter((item) =>
        kpiApiSourceRequiresPushpayConnection(item.config) && !pushpayStatus?.connected
      ).length;
      const quickBooksBlockedCount = baseCandidates.filter((item) =>
        kpiApiSourceRequiresQuickBooksConnection(item.config) && !quickBooksStatus?.connected
      ).length;
      setPushpayMessage(
        pushpayBlockedCount > 0
          ? "Connect Pushpay ChMS to sync group attendance KPIs. Campus, event, and process queue KPIs can sync through CCB API credentials."
          : quickBooksBlockedCount > 0
            ? "Connect QuickBooks to sync mapped finance KPIs."
            : "No editable KPIs with API sources were found."
      );
      return;
    }

    setIsPushpayLoading(true);
    setPushpayMessage(null);
    try {
      await persistLatestWorkspace();
      const response = await api.post(
        `/enterprises/${activeEnterpriseId}/kpis/sync-api-actuals`,
        { node_ids: candidates.map((item) => item.node.id), background: true },
        { timeout: 30000 }
      );
      const result = await waitForSyncRun(activeEnterpriseId, response.data, setPushpayMessage);
      setPushpayMessage(syncRunMessage(result));
    } catch (error) {
      console.error("Failed to sync API KPIs", error);
      setPushpayMessage(getAiErrorMessage(error, "Could not sync all mapped API KPI sources."));
    } finally {
      setIsPushpayLoading(false);
    }
  }, [
    activeEnterpriseId,
    allNodes,
    canEditKpiNode,
    currentUserHasLegacyAdminRole,
    getAiErrorMessage,
    quickBooksStatus?.connected,
    pushpayStatus?.connected,
    persistLatestWorkspace,
  ]);

  const onDisconnectPushpay = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    const confirmed = window.confirm("Disconnect Pushpay ChMS from this enterprise?");
    if (!confirmed) return;
    setIsPushpayLoading(true);
    setPushpayMessage(null);
    try {
      await api.delete("/integrations/pushpay/disconnect", {
        params: { enterprise_id: activeEnterpriseId },
      });
      setPushpayStatus({
        configured: pushpayStatus?.configured ?? true,
        connected: false,
        enterprise_id: activeEnterpriseId,
      });
      setPushpayMessage("Pushpay ChMS disconnected.");
    } catch (error) {
      console.error("Failed to disconnect Pushpay ChMS", error);
      setPushpayMessage(getAiErrorMessage(error, "Could not disconnect Pushpay ChMS."));
    } finally {
      setIsPushpayLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage, pushpayStatus?.configured]);

  const onTestQuickBooksAccounts = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    try {
      const response = await api.get("/integrations/quickbooks/accounts", {
        params: { enterprise_id: activeEnterpriseId },
      });
      const accounts = Array.isArray(response.data) ? response.data : [];
      setQuickBooksAccounts(accounts);
      setQuickBooksSelectedAccountIds((previous) =>
        previous.length > 0
          ? previous
          : accounts
              .filter(isLikelyQuickBooksGivingAccount)
              .slice(0, 6)
              .map((account: QuickBooksAccount) => account.id)
      );
      setQuickBooksMessage(`QuickBooks connected. Found ${accounts.length} accounts.`);
    } catch (error) {
      console.error("Failed to load QuickBooks accounts", error);
      setQuickBooksMessage(
        getAiErrorMessage(error, "QuickBooks is connected, but account lookup failed.")
      );
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage]);

  const onCopyQuickBooksAccounts = React.useCallback(async () => {
    const accountsToCopy =
      quickBooksFilteredAccounts.length > 0 ? quickBooksFilteredAccounts : quickBooksAccounts;
    if (accountsToCopy.length === 0) {
      setQuickBooksCopyMessage("Load accounts first.");
      return;
    }
    const compiled = [
      `QuickBooks account query: ${quickBooksAccountQuery.trim() || "all accounts"}`,
      `Showing ${accountsToCopy.length} of ${quickBooksAccounts.length} accounts`,
      "",
      ...accountsToCopy.map(formatQuickBooksAccountLine),
    ].join("\n");

    try {
      await navigator.clipboard.writeText(compiled);
      setQuickBooksCopyMessage(`Copied ${accountsToCopy.length} account${accountsToCopy.length === 1 ? "" : "s"}.`);
    } catch (error) {
      console.error("Failed to copy QuickBooks accounts", error);
      setQuickBooksCopyMessage("Could not copy accounts.");
    }
  }, [quickBooksAccountQuery, quickBooksAccounts, quickBooksFilteredAccounts]);

  const onCopyQuickBooksImportDiagnostics = React.useCallback(async () => {
    if (quickBooksAccounts.length === 0) {
      setQuickBooksCopyMessage("Load accounts first.");
      return;
    }
    const selectedAccounts = quickBooksAccounts.filter((account) =>
      quickBooksSelectedAccountIds.includes(account.id)
    );
    const sortedAccounts = quickBooksAccounts
      .slice()
      .sort((left, right) =>
        (left.fully_qualified_name || left.name).localeCompare(right.fully_qualified_name || right.name)
      );
    const incomeAccounts = sortedAccounts.filter(isQuickBooksIncomeAccount);
    const likelyGivingAccounts = sortedAccounts.filter(isLikelyQuickBooksGivingAccount);
    const expenseAccounts = sortedAccounts.filter(isQuickBooksExpenseAccount);
    const bankAccounts = sortedAccounts.filter(isQuickBooksBankAccount);
    const savingsAccounts = sortedAccounts.filter(isQuickBooksSavingsAccount);
    const targetNode = quickBooksTargetKpiId
      ? allNodes.find((node) => node.id === quickBooksTargetKpiId)
      : null;
    const previewLines = [
      quickBooksGivingPreview
        ? [
            "",
            "Giving preview",
            `matched_account_ids=${quickBooksGivingPreview.matched_account_ids.join(", ") || "none"}`,
            ...quickBooksGivingPreview.periods.map(
              (item) =>
                `${item.period_key} | ${item.start_date} to ${item.end_date} | ${formatMetricValue(
                  item.total,
                  "currency"
                )}`
            ),
          ]
        : [],
      quickBooksExpensePreview
        ? [
            "",
            "Expense preview",
            `matched_account_ids=${quickBooksExpensePreview.matched_account_ids.join(", ") || "none"}`,
            ...quickBooksExpensePreview.periods.map(
              (item) =>
                `${item.period_key} | ${item.start_date} to ${item.end_date} | ${formatMetricValue(
                  item.total,
                  "currency"
                )}`
            ),
          ]
        : [],
      quickBooksExpenseBudgetPreview
        ? [
            "",
            "Expense budget preview",
            `matched_account_ids=${quickBooksExpenseBudgetPreview.matched_account_ids.join(", ") || "none"}`,
            `budget_count=${quickBooksExpenseBudgetPreview.budget_count}`,
            `budget_detail_count=${quickBooksExpenseBudgetPreview.budget_detail_count}`,
            ...quickBooksExpenseBudgetPreview.periods.map(
              (item) =>
                `${item.period_key} target | ${item.start_date} to ${item.end_date} | ${formatMetricValue(
                  item.total,
                  "currency"
                )}`
            ),
          ]
        : [],
      quickBooksAccountBalancePreview
        ? [
            "",
            "Account balance preview",
            `matched_account_ids=${quickBooksAccountBalancePreview.matched_account_ids.join(", ") || "none"}`,
            ...quickBooksAccountBalancePreview.periods.map(
              (item) =>
                `${item.period_key} balance | ${item.start_date} to ${item.end_date} | ${formatMetricValue(
                  item.total,
                  "currency"
                )}`
            ),
          ]
        : [],
    ].flat();
    const compiled = [
      "QuickBooks import diagnostics",
      `date_range=${quickBooksStartDate} to ${quickBooksEndDate}`,
      `period=${quickBooksPeriod}`,
      `target_kpi=${targetNode ? `${targetNode.data.label} (${targetNode.id})` : "none selected"}`,
      `target_kpi_frequency=${targetNode?.data.frequency ?? "none"}`,
      `target_kpi_value_type=${targetNode?.data.valueType ?? "none"}`,
      `loaded_accounts=${quickBooksAccounts.length}`,
      `selected_accounts=${selectedAccounts.length}`,
      "",
      "Account type counts",
      ...countQuickBooksAccountsBy(quickBooksAccounts, (account) => account.account_type),
      "",
      "Account subtype counts",
      ...countQuickBooksAccountsBy(quickBooksAccounts, (account) => account.account_sub_type),
      "",
      "Account classification counts",
      ...countQuickBooksAccountsBy(quickBooksAccounts, (account) => account.classification),
      ...formatQuickBooksAccountSection("Selected accounts", selectedAccounts, "None selected"),
      ...formatQuickBooksAccountSection("Bank accounts", bankAccounts),
      ...formatQuickBooksAccountSection("Savings/reserve candidates", savingsAccounts),
      ...formatQuickBooksAccountSection("Likely giving accounts", likelyGivingAccounts),
      ...formatQuickBooksAccountSection("Income accounts", incomeAccounts),
      ...formatQuickBooksAccountSection("Expense accounts", expenseAccounts),
      ...formatQuickBooksAccountSection("All loaded accounts", sortedAccounts),
      ...previewLines,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(compiled);
      setQuickBooksCopyMessage("Copied QuickBooks import diagnostics.");
    } catch (error) {
      console.error("Failed to copy QuickBooks import diagnostics", error);
      setQuickBooksCopyMessage("Could not copy import diagnostics.");
    }
  }, [
    allNodes,
    quickBooksAccountBalancePreview,
    quickBooksAccounts,
    quickBooksEndDate,
    quickBooksExpenseBudgetPreview,
    quickBooksExpensePreview,
    quickBooksGivingPreview,
    quickBooksPeriod,
    quickBooksSelectedAccountIds,
    quickBooksStartDate,
    quickBooksTargetKpiId,
  ]);

  const onCheckQuickBooksDimensions = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    try {
      const response = await api.get("/integrations/quickbooks/dimensions", {
        params: { enterprise_id: activeEnterpriseId },
      });
      const dimensions = response.data as QuickBooksDimensions;
      setQuickBooksDimensions(dimensions);
      setQuickBooksMessage(
        `QuickBooks dimensions found: ${dimensions.classes.length} classes and ${dimensions.locations.length} locations.`
      );
    } catch (error) {
      console.error("Failed to load QuickBooks dimensions", error);
      setQuickBooksMessage(
        getAiErrorMessage(error, "QuickBooks is connected, but dimensions lookup failed.")
      );
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage]);

  const onCopyQuickBooksDimensions = React.useCallback(async () => {
    if (!quickBooksDimensions) {
      setQuickBooksCopyMessage("Check dimensions first.");
      return;
    }
    const compiled = [
      "QuickBooks dimensions check",
      `Classes: ${quickBooksDimensions.classes.length}`,
      `Locations: ${quickBooksDimensions.locations.length}`,
      "",
      "Classes",
      ...(quickBooksDimensions.classes.length > 0
        ? quickBooksDimensions.classes.map(formatQuickBooksDimensionLine)
        : ["None found"]),
      "",
      "Locations",
      ...(quickBooksDimensions.locations.length > 0
        ? quickBooksDimensions.locations.map(formatQuickBooksDimensionLine)
        : ["None found"]),
    ].join("\n");

    try {
      await navigator.clipboard.writeText(compiled);
      setQuickBooksCopyMessage("Copied QuickBooks dimensions.");
    } catch (error) {
      console.error("Failed to copy QuickBooks dimensions", error);
      setQuickBooksCopyMessage("Could not copy dimensions.");
    }
  }, [quickBooksDimensions]);

  const onAnswerQuickBooksGivingByDimension = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    const dimensionName = quickBooksFinanceDimensionName.trim();
    if (!dimensionName) {
      setQuickBooksMessage("Enter a QuickBooks location or class name first.");
      return;
    }
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksGivingDimensionAnswer(null);
    try {
      const params: Record<string, string | number> = {
        enterprise_id: activeEnterpriseId,
        start_date: quickBooksStartDate,
        end_date: quickBooksEndDate,
        dimension_type: quickBooksFinanceDimensionType,
        dimension_name: dimensionName,
      };
      if (quickBooksFinanceAccountingMethod) {
        params.accounting_method = quickBooksFinanceAccountingMethod;
      }
      if (quickBooksSelectedAccountIds.length > 0) {
        params.account_ids = quickBooksSelectedAccountIds.join(",");
      }
      const response = await api.get("/integrations/quickbooks/giving-by-dimension", { params });
      const answer = response.data as QuickBooksGivingDimensionAnswer;
      setQuickBooksGivingDimensionAnswer(answer);
      setQuickBooksMessage(
        answer.matched_dimension
          ? `${answer.matched_dimension} giving from ${answer.start_date} to ${
              answer.end_date
            }: ${formatMetricValue(answer.total, "currency")} from ${answer.matched_account_ids.length} matched account${
              answer.matched_account_ids.length === 1 ? "" : "s"
            }.`
          : `No ${quickBooksFinanceDimensionType} matched "${dimensionName}". Available: ${answer.available_dimensions
              .slice(0, 12)
              .join(", ")}${answer.available_dimensions.length > 12 ? ", ..." : ""}`
      );
    } catch (error) {
      console.error("Failed to answer QuickBooks giving question", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not answer the QuickBooks giving question."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [
    activeEnterpriseId,
    getAiErrorMessage,
    quickBooksEndDate,
    quickBooksFinanceAccountingMethod,
    quickBooksFinanceDimensionName,
    quickBooksFinanceDimensionType,
    quickBooksSelectedAccountIds,
    quickBooksStartDate,
  ]);

  const onCopyQuickBooksGivingDimensionAnswer = React.useCallback(async () => {
    if (!quickBooksGivingDimensionAnswer) {
      setQuickBooksCopyMessage("Run a giving question first.");
      return;
    }
    const answer = quickBooksGivingDimensionAnswer;
    const compiled = [
      "QuickBooks giving answer",
      `date_range=${answer.start_date} to ${answer.end_date}`,
      `dimension_type=${answer.dimension_type}`,
      `requested_dimension=${answer.requested_dimension}`,
      `matched_dimension=${answer.matched_dimension ?? "none"}`,
      `accounting_method=${answer.accounting_method ?? "QuickBooks default"}`,
      `total=${formatMetricValue(answer.total, "currency")}`,
      `account_ids=${answer.account_ids.join(", ") || "none"}`,
      `matched_account_ids=${answer.matched_account_ids.join(", ") || "none"}`,
      `available_dimensions=${answer.available_dimensions.join(", ") || "none"}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(compiled);
      setQuickBooksCopyMessage("Copied QuickBooks giving answer.");
    } catch (error) {
      console.error("Failed to copy QuickBooks giving answer", error);
      setQuickBooksCopyMessage("Could not copy giving answer.");
    }
  }, [quickBooksGivingDimensionAnswer]);

  const onPreviewQuickBooksProfitAndLossByLocation = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksProfitAndLossByLocation(null);
    try {
      const response = await api.get("/integrations/quickbooks/profit-and-loss-by-location", {
        params: {
          enterprise_id: activeEnterpriseId,
          start_date: quickBooksStartDate,
          end_date: quickBooksEndDate,
        },
      });
      const preview = response.data as QuickBooksProfitAndLossByLocation;
      setQuickBooksProfitAndLossByLocation(preview);
      setQuickBooksMessage(`QuickBooks P&L by location found ${preview.locations.length} columns.`);
    } catch (error) {
      console.error("Failed to preview QuickBooks P&L by location", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not preview QuickBooks P&L by location."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage, quickBooksEndDate, quickBooksStartDate]);

  const onCopyQuickBooksProfitAndLossByLocation = React.useCallback(async () => {
    if (!quickBooksProfitAndLossByLocation) {
      setQuickBooksCopyMessage("Preview P&L by location first.");
      return;
    }
    const compiled = [
      "QuickBooks P&L by location",
      `Date range: ${quickBooksProfitAndLossByLocation.start_date} to ${quickBooksProfitAndLossByLocation.end_date}`,
      `Locations: ${quickBooksProfitAndLossByLocation.locations.length}`,
      "",
      ...quickBooksProfitAndLossByLocation.locations.map(formatQuickBooksPnLLocationLine),
    ].join("\n");

    try {
      await navigator.clipboard.writeText(compiled);
      setQuickBooksCopyMessage("Copied QuickBooks P&L by location.");
    } catch (error) {
      console.error("Failed to copy QuickBooks P&L by location", error);
      setQuickBooksCopyMessage("Could not copy P&L by location.");
    }
  }, [quickBooksProfitAndLossByLocation]);

  const onPreviewQuickBooksProfitAndLossByClass = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksProfitAndLossByClass(null);
    try {
      const response = await api.get("/integrations/quickbooks/profit-and-loss-by-class", {
        params: {
          enterprise_id: activeEnterpriseId,
          start_date: quickBooksStartDate,
          end_date: quickBooksEndDate,
        },
      });
      const preview = response.data as QuickBooksProfitAndLossByClass;
      setQuickBooksProfitAndLossByClass(preview);
      setQuickBooksMessage(`QuickBooks P&L by class found ${preview.classes.length} columns.`);
    } catch (error) {
      console.error("Failed to preview QuickBooks P&L by class", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not preview QuickBooks P&L by class."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage, quickBooksEndDate, quickBooksStartDate]);

  const onCopyQuickBooksProfitAndLossByClass = React.useCallback(async () => {
    if (!quickBooksProfitAndLossByClass) {
      setQuickBooksCopyMessage("Preview P&L by class first.");
      return;
    }
    const compiled = [
      "QuickBooks P&L by class",
      `Date range: ${quickBooksProfitAndLossByClass.start_date} to ${quickBooksProfitAndLossByClass.end_date}`,
      `Classes: ${quickBooksProfitAndLossByClass.classes.length}`,
      "",
      ...quickBooksProfitAndLossByClass.classes.map(formatQuickBooksPnLClassLine),
    ].join("\n");

    try {
      await navigator.clipboard.writeText(compiled);
      setQuickBooksCopyMessage("Copied QuickBooks P&L by class.");
    } catch (error) {
      console.error("Failed to copy QuickBooks P&L by class", error);
      setQuickBooksCopyMessage("Could not copy P&L by class.");
    }
  }, [quickBooksProfitAndLossByClass]);

  const onPreviewQuickBooksGiving = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    if (quickBooksSelectedAccountIds.length === 0) {
      setQuickBooksMessage("Choose at least one QuickBooks income account first.");
      return;
    }
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksGivingPreview(null);
    setQuickBooksExpensePreview(null);
    setQuickBooksExpenseBudgetPreview(null);
    setQuickBooksAccountBalancePreview(null);
    try {
      const response = await api.get("/integrations/quickbooks/giving-preview", {
        params: {
          enterprise_id: activeEnterpriseId,
          account_ids: quickBooksSelectedAccountIds.join(","),
          start_date: quickBooksStartDate,
          end_date: quickBooksEndDate,
          period: quickBooksPeriod,
        },
      });
      const preview = response.data as QuickBooksGivingPreview;
      setQuickBooksGivingPreview(preview);
      const total = preview.periods.reduce((sum, item) => sum + item.total, 0);
      setQuickBooksMessage(
        `Preview ready: ${formatMetricValue(total, "currency")} across ${preview.periods.length} ${quickBooksPeriod} periods.`
      );
    } catch (error) {
      console.error("Failed to preview QuickBooks giving", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not preview QuickBooks giving."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [
    activeEnterpriseId,
    getAiErrorMessage,
    quickBooksEndDate,
    quickBooksPeriod,
    quickBooksSelectedAccountIds,
    quickBooksStartDate,
  ]);

  const onPreviewQuickBooksExpenses = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksExpensePreview(null);
    setQuickBooksExpenseBudgetPreview(null);
    setQuickBooksAccountBalancePreview(null);
    try {
      const params: Record<string, string | number> = {
        enterprise_id: activeEnterpriseId,
        start_date: quickBooksStartDate,
        end_date: quickBooksEndDate,
        period: quickBooksPeriod,
      };
      const response = await api.get("/integrations/quickbooks/expense-preview", {
        params,
      });
      const preview = response.data as QuickBooksExpensePreview;
      setQuickBooksExpensePreview(preview);
      const total = preview.periods.reduce((sum, item) => sum + item.total, 0);
      setQuickBooksMessage(
        `P&L expense preview ready: ${formatMetricValue(total, "currency")} across ${
          preview.periods.length
        } ${quickBooksPeriod} periods from total QuickBooks Expenditures.`
      );
    } catch (error) {
      console.error("Failed to preview QuickBooks expenses", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not preview QuickBooks expenses."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [
    activeEnterpriseId,
    getAiErrorMessage,
    quickBooksEndDate,
    quickBooksPeriod,
    quickBooksStartDate,
  ]);

  const onInspectQuickBooksBudgets = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    try {
      const response = await api.get("/integrations/quickbooks/budget-diagnostics", {
        params: {
          enterprise_id: activeEnterpriseId,
          start_date: quickBooksStartDate,
          end_date: quickBooksEndDate,
        },
      });
      const diagnostics = response.data as QuickBooksBudgetDiagnostics;
      const monthLines = diagnostics.months
        .map(
          (item) =>
            `${item.month}: expense ${formatMetricValue(item.expense_budget, "currency")} from ${
              item.matched_expense_lines
            }/${item.total_lines} lines`
        )
        .join("; ");
      const emptyMonthSummary = diagnostics.months.every(
        (item) => item.total_lines === 0 && item.expense_budget === 0 && item.total_budget === 0
      )
        ? " No budget lines were found in the selected date range."
        : "";
      const accountLines = diagnostics.top_accounts
        .slice(0, 5)
        .map(
          (item) =>
            `${item.account_name || item.account_id} ${formatMetricValue(item.total, "currency")} (${item.line_count})`
        )
        .join("; ");
      setQuickBooksMessage(
        `Budget diagnostics: ${diagnostics.message} ${diagnostics.budget_count} budget record${
          diagnostics.budget_count === 1 ? "" : "s"
        }, ${diagnostics.budget_detail_count} detail line${
          diagnostics.budget_detail_count === 1 ? "" : "s"
        }, ${diagnostics.matched_expense_account_count}/${diagnostics.expense_account_count} expense accounts matched.${emptyMonthSummary} ${monthLines}${
          accountLines ? ` Top accounts: ${accountLines}.` : ""
        }`
      );
    } catch (error) {
      console.error("Failed to inspect QuickBooks budgets", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not inspect QuickBooks budgets."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage, quickBooksEndDate, quickBooksStartDate]);

  const onApplyQuickBooksGivingPreview = React.useCallback(() => {
    if (!quickBooksGivingPreview || !quickBooksTargetKpiId) {
      setQuickBooksMessage("Choose a KPI and preview giving before applying.");
      return;
    }
    const targetNode = allNodes.find((node) => node.id === quickBooksTargetKpiId);
    if (!targetNode) {
      setQuickBooksMessage("Choose a valid KPI before applying.");
      return;
    }
    if (targetNode.data.frequency !== quickBooksGivingPreview.period) {
      setQuickBooksMessage("The selected KPI frequency must match the QuickBooks preview period.");
      return;
    }
    if (!canEditKpiNode(targetNode.id) && !currentUserHasLegacyAdminRole) {
      setQuickBooksMessage("You do not have permission to update that KPI.");
      return;
    }

    setAllNodes((previous) =>
      applyContextAndRecalculate(
        previous.map((node) => {
          if (node.id !== targetNode.id) return node;
          const actualHistory = { ...node.data.actualHistory };
          quickBooksGivingPreview.periods.forEach((item) => {
            const periodDate = new Date(`${item.start_date}T00:00:00`);
            actualHistory[getMetricKey(periodDate, node.data.frequency, node.data.scope, campus)] = item.total;
          });
          return {
            ...node,
            data: {
              ...node.data,
              actualHistory,
            },
          };
        }),
        allEdges
      )
    );
    setQuickBooksMessage(
      `Applied ${quickBooksGivingPreview.periods.length} QuickBooks periods to ${targetNode.data.label}. Changes save automatically.`
    );
  }, [
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    campus,
    canEditKpiNode,
    currentUserHasLegacyAdminRole,
    quickBooksGivingPreview,
    quickBooksTargetKpiId,
  ]);

  const onApplyQuickBooksExpensePreview = React.useCallback(() => {
    if (!quickBooksExpensePreview || !quickBooksTargetKpiId) {
      setQuickBooksMessage("Choose a KPI and preview expenses before applying.");
      return;
    }
    const targetNode = allNodes.find((node) => node.id === quickBooksTargetKpiId);
    if (!targetNode) {
      setQuickBooksMessage("Choose a valid KPI before applying.");
      return;
    }
    if (targetNode.data.frequency !== quickBooksExpensePreview.period) {
      setQuickBooksMessage("The selected KPI frequency must match the QuickBooks expense preview period.");
      return;
    }
    if (!canEditKpiNode(targetNode.id) && !currentUserHasLegacyAdminRole) {
      setQuickBooksMessage("You do not have permission to update that KPI.");
      return;
    }

    setAllNodes((previous) =>
      applyContextAndRecalculate(
        previous.map((node) => {
          if (node.id !== targetNode.id) return node;
          const actualHistory = { ...node.data.actualHistory };
          quickBooksExpensePreview.periods.forEach((item) => {
            const periodDate = new Date(`${item.start_date}T00:00:00`);
            actualHistory[getMetricKey(periodDate, node.data.frequency, node.data.scope, campus)] = item.total;
          });
          return {
            ...node,
            data: {
              ...node.data,
              actualHistory,
              valueType: "currency",
            },
          };
        }),
        allEdges
      )
    );
    setQuickBooksMessage(
      `Applied ${quickBooksExpensePreview.periods.length} QuickBooks expense periods to ${targetNode.data.label}. Use the KPI target field for the budget.`
    );
  }, [
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    campus,
    canEditKpiNode,
    currentUserHasLegacyAdminRole,
    quickBooksExpensePreview,
    quickBooksTargetKpiId,
  ]);

  const onPreviewQuickBooksAccountBalances = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    if (quickBooksBalanceAccountIds.length === 0) {
      setQuickBooksMessage("Choose at least one QuickBooks balance sheet account first.");
      return;
    }
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksGivingPreview(null);
    setQuickBooksExpensePreview(null);
    setQuickBooksExpenseBudgetPreview(null);
    setQuickBooksAccountBalancePreview(null);
    try {
      const response = await api.get("/integrations/quickbooks/account-balance-preview", {
        params: {
          enterprise_id: activeEnterpriseId,
          account_ids: quickBooksBalanceAccountIds.join(","),
          start_date: quickBooksStartDate,
          end_date: quickBooksEndDate,
          period: quickBooksPeriod,
        },
      });
      const preview = response.data as QuickBooksAccountBalancePreview;
      setQuickBooksAccountBalancePreview(preview);
      const latest = preview.periods[preview.periods.length - 1];
      const accountScope = `${preview.matched_account_ids.length} selected/matched account${
        preview.matched_account_ids.length === 1 ? "" : "s"
      }`;
      setQuickBooksMessage(
        `Balance preview ready: ${
          latest ? formatMetricValue(latest.total, "currency") : formatMetricValue(0, "currency")
        } as of ${
          latest?.end_date ?? quickBooksEndDate
        }, across ${preview.periods.length} ${quickBooksPeriod} periods from ${accountScope}.`
      );
    } catch (error) {
      console.error("Failed to preview QuickBooks account balances", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not preview QuickBooks account balances."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [
    activeEnterpriseId,
    getAiErrorMessage,
    quickBooksBalanceAccountIds,
    quickBooksEndDate,
    quickBooksPeriod,
    quickBooksStartDate,
  ]);

  const onApplyQuickBooksAccountBalancePreview = React.useCallback(() => {
    if (!quickBooksAccountBalancePreview || !quickBooksTargetKpiId) {
      setQuickBooksMessage("Choose a KPI and preview account balances before applying.");
      return;
    }
    const targetNode = allNodes.find((node) => node.id === quickBooksTargetKpiId);
    if (!targetNode) {
      setQuickBooksMessage("Choose a valid KPI before applying.");
      return;
    }
    if (targetNode.data.frequency !== quickBooksAccountBalancePreview.period) {
      setQuickBooksMessage("The selected KPI frequency must match the QuickBooks balance preview period.");
      return;
    }
    if (!canEditKpiNode(targetNode.id) && !currentUserHasLegacyAdminRole) {
      setQuickBooksMessage("You do not have permission to update that KPI.");
      return;
    }

    setAllNodes((previous) =>
      applyContextAndRecalculate(
        previous.map((node) => {
          if (node.id !== targetNode.id) return node;
          const actualHistory = { ...node.data.actualHistory };
          quickBooksAccountBalancePreview.periods.forEach((item) => {
            const periodDate = new Date(`${item.start_date}T00:00:00`);
            actualHistory[getMetricKey(periodDate, node.data.frequency, node.data.scope, campus)] = item.total;
          });
          return {
            ...node,
            data: {
              ...node.data,
              actualHistory,
              valueType: "currency",
            },
          };
        }),
        allEdges
      )
    );
    setQuickBooksMessage(
      `Applied ${quickBooksAccountBalancePreview.periods.length} QuickBooks balance period${
        quickBooksAccountBalancePreview.periods.length === 1 ? "" : "s"
      } to ${targetNode.data.label}. Changes save automatically.`
    );
  }, [
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    campus,
    canEditKpiNode,
    currentUserHasLegacyAdminRole,
    quickBooksAccountBalancePreview,
    quickBooksTargetKpiId,
  ]);

  const onSyncQuickBooksAccountBalancesToKpi = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    if (!quickBooksTargetKpiId) {
      setQuickBooksMessage("Choose a savings KPI before syncing QuickBooks balances.");
      return;
    }
    if (quickBooksBalanceAccountIds.length === 0) {
      setQuickBooksMessage("Choose the QuickBooks savings accounts before syncing balances.");
      return;
    }
    const targetNode = allNodes.find((node) => node.id === quickBooksTargetKpiId);
    if (!targetNode) {
      setQuickBooksMessage("Choose a valid KPI before syncing QuickBooks balances.");
      return;
    }
    if (!canEditKpiNode(targetNode.id) && !currentUserHasLegacyAdminRole) {
      setQuickBooksMessage("You do not have permission to update that KPI.");
      return;
    }
    if (targetNode.data.frequency !== quickBooksPeriod) {
      setQuickBooksMessage("The selected KPI frequency must match the QuickBooks period.");
      return;
    }

    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksGivingPreview(null);
    setQuickBooksExpensePreview(null);
    setQuickBooksExpenseBudgetPreview(null);
    setQuickBooksAccountBalancePreview(null);
    try {
      const response = await api.get("/integrations/quickbooks/account-balance-preview", {
        params: {
          enterprise_id: activeEnterpriseId,
          account_ids: quickBooksBalanceAccountIds.join(","),
          start_date: quickBooksStartDate,
          end_date: quickBooksEndDate,
          period: quickBooksPeriod,
        },
      });
      const preview = response.data as QuickBooksAccountBalancePreview;
      setQuickBooksAccountBalancePreview(preview);

      setAllNodes((previous) =>
        applyContextAndRecalculate(
          previous.map((node) => {
            if (node.id !== targetNode.id) return node;
            const actualHistory = { ...node.data.actualHistory };
            preview.periods.forEach((item) => {
              const periodDate = new Date(`${item.start_date}T00:00:00`);
              const historyKey = getMetricKey(periodDate, node.data.frequency, node.data.scope, campus);
              actualHistory[historyKey] = item.total;
            });
            return {
              ...node,
              data: {
                ...node.data,
                actualHistory,
                valueType: "currency",
              },
            };
          }),
          allEdges
        )
      );

      const latest = preview.periods[preview.periods.length - 1];
      setQuickBooksMessage(
        `Synced ${preview.periods.length} QuickBooks balance period${
          preview.periods.length === 1 ? "" : "s"
        } into ${targetNode.data.label}: latest balance ${
          latest ? formatMetricValue(latest.total, "currency") : formatMetricValue(0, "currency")
        } as of ${latest?.end_date ?? quickBooksEndDate} from ${preview.matched_account_ids.length} matched account${
          preview.matched_account_ids.length === 1 ? "" : "s"
        }.`
      );
    } catch (error) {
      console.error("Failed to sync QuickBooks account balances", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not sync QuickBooks account balances."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [
    activeEnterpriseId,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    campus,
    canEditKpiNode,
    currentUserHasLegacyAdminRole,
    getAiErrorMessage,
    quickBooksBalanceAccountIds,
    quickBooksEndDate,
    quickBooksPeriod,
    quickBooksStartDate,
    quickBooksTargetKpiId,
  ]);

  const onSyncQuickBooksExpensesToKpi = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    if (!quickBooksTargetKpiId) {
      setQuickBooksMessage("Choose an expenses KPI before syncing QuickBooks.");
      return;
    }
    const targetNode = allNodes.find((node) => node.id === quickBooksTargetKpiId);
    if (!targetNode) {
      setQuickBooksMessage("Choose a valid KPI before syncing QuickBooks.");
      return;
    }
    if (!canEditKpiNode(targetNode.id) && !currentUserHasLegacyAdminRole) {
      setQuickBooksMessage("You do not have permission to update that KPI.");
      return;
    }
    if (targetNode.data.frequency !== quickBooksPeriod) {
      setQuickBooksMessage("The selected KPI frequency must match the QuickBooks period.");
      return;
    }

    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksExpensePreview(null);
    setQuickBooksExpenseBudgetPreview(null);
    setQuickBooksAccountBalancePreview(null);
    try {
      const expenseParams: Record<string, string | number> = {
        enterprise_id: activeEnterpriseId,
        start_date: quickBooksStartDate,
        end_date: quickBooksEndDate,
        period: quickBooksPeriod,
      };
      const selectedLocation = quickBooksExpenseLocationName.trim();
      if (selectedLocation) {
        expenseParams.location_name = selectedLocation;
      }
      const expenseResponse = await api.get("/integrations/quickbooks/expense-preview", {
        params: expenseParams,
      });
      const preview = expenseResponse.data as QuickBooksExpensePreview;
      setQuickBooksExpensePreview(preview);
      setQuickBooksExpenseBudgetPreview(null);

      setAllNodes((previous) =>
        applyContextAndRecalculate(
          previous.map((node) => {
            if (node.id !== targetNode.id) return node;
            const actualHistory = { ...node.data.actualHistory };
            preview.periods.forEach((item) => {
              const periodDate = new Date(`${item.start_date}T00:00:00`);
              const historyKey = getMetricKey(periodDate, node.data.frequency, node.data.scope, campus);
              actualHistory[historyKey] = item.total;
            });
            return {
              ...node,
              data: {
                ...node.data,
                actualHistory,
                valueType: "currency",
              },
            };
          }),
          allEdges
        )
      );

      const total = preview.periods.reduce((sum, item) => sum + item.total, 0);
      const actualWriteCount = preview.periods.length;
      setQuickBooksMessage(
        `Imported ${preview.periods.length} QuickBooks P&L expense period${
          preview.periods.length === 1 ? "" : "s"
        }${selectedLocation ? ` for ${selectedLocation}` : ""} into ${
          targetNode.data.label
        }: wrote ${actualWriteCount} actual value${
          actualWriteCount === 1 ? "" : "s"
        } (${formatMetricValue(total, "currency")}) from ${quickBooksStartDate} to ${quickBooksEndDate}.`
      );
    } catch (error) {
      console.error("Failed to import QuickBooks P&L expenses", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not import QuickBooks P&L expenses."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [
    activeEnterpriseId,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    campus,
    canEditKpiNode,
    currentUserHasLegacyAdminRole,
    getAiErrorMessage,
    quickBooksEndDate,
    quickBooksExpenseLocationName,
    quickBooksPeriod,
    quickBooksStartDate,
    quickBooksTargetKpiId,
  ]);

  const onSyncCcbGivingToKpi = React.useCallback(async () => {
    const selectedCampus = ccbGivingCampusName.trim();
    const targetNode = quickBooksTargetKpiId
      ? allNodes.find((node) => node.id === quickBooksTargetKpiId)
      : null;
    const isSingleCampusImport = Boolean(selectedCampus);
    const mappedCampusGivingNodes = allNodes.filter((node) => {
      const campusGroup = getCcbGivingCampusGroupForKpiLabel(node.data.label);
      return Boolean(
        campusGroup &&
          (canEditKpiNode(node.id) || currentUserHasLegacyAdminRole)
      );
    });
    let givingImportPeriod: "weekly" | "monthly";

    if (isSingleCampusImport) {
      if (!targetNode) {
        setQuickBooksMessage("Choose a KPI before importing one CCB campus.");
        return;
      }
      if (!canEditKpiNode(targetNode.id) && !currentUserHasLegacyAdminRole) {
        setQuickBooksMessage("You do not have permission to update that KPI.");
        return;
      }
      if (!isSupportedCcbGivingPeriod(targetNode.data.frequency)) {
        setQuickBooksMessage("CCB giving import currently supports weekly or monthly KPIs.");
        return;
      }
      givingImportPeriod = targetNode.data.frequency;
    } else {
      if (mappedCampusGivingNodes.length === 0) {
        setQuickBooksMessage("No editable campus giving KPIs were found to update.");
        return;
      }
      const mappedFrequencies = Array.from(
        new Set(mappedCampusGivingNodes.map((node) => node.data.frequency))
      );
      if (mappedFrequencies.length !== 1 || !isSupportedCcbGivingPeriod(mappedFrequencies[0])) {
        setQuickBooksMessage("Mapped campus giving KPIs must all be weekly or all be monthly.");
        return;
      }
      givingImportPeriod = mappedFrequencies[0];
    }

    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    setQuickBooksGivingPreview(null);
    setCcbGivingPreview(null);
    try {
      const params: Record<string, string> = {
        enterprise_id: String(activeEnterpriseId),
        start_date: quickBooksStartDate,
        end_date: quickBooksEndDate,
        period: givingImportPeriod,
        date_basis: "batch",
      };
      if (selectedCampus) {
        params.campus_name = selectedCampus;
      }
      const response = await api.get("/integrations/ccb/giving-preview", { params });
      const preview = response.data as CcbGivingPreview;
      setCcbGivingPreview(preview);

      const expectedUpdatedNodeCount =
        isSingleCampusImport
          ? targetNode
            ? 1
            : 0
          : mappedCampusGivingNodes.length;
      const unmatchedCampuses = new Set<string>();
      setAllNodes((previous) =>
        applyContextAndRecalculate(
          previous.map((node) => {
            let periodTotals: { start_date: string; total: number }[] = [];
            if (isSingleCampusImport) {
              if (node.id !== targetNode?.id) return node;
              periodTotals = preview.periods.map((item) => ({
                start_date: item.start_date,
                total: item.total,
              }));
            } else {
              const campusGroup = getCcbGivingCampusGroupForKpiLabel(node.data.label);
              if (!campusGroup) return node;
              if (node.data.frequency !== givingImportPeriod) return node;
              if (!canEditKpiNode(node.id) && !currentUserHasLegacyAdminRole) return node;
              periodTotals = preview.periods.map((item) => ({
                start_date: item.start_date,
                total: Object.entries(item.campus_totals).reduce(
                  (sum, [campusName, amount]) =>
                    isCcbCampusInGivingGroup(campusName, campusGroup) ? sum + amount : sum,
                  0
                ),
              }));
              Object.keys(preview.periods[0]?.campus_totals ?? {}).forEach((campusName) => {
                const hasAnyNode = previous.some((candidateNode) => {
                  const candidateGroup = getCcbGivingCampusGroupForKpiLabel(candidateNode.data.label);
                  return Boolean(candidateGroup && isCcbCampusInGivingGroup(campusName, candidateGroup));
                });
                if (!hasAnyNode) unmatchedCampuses.add(campusName);
              });
            }
            const actualHistory = clearMetricHistoryRange(
              node.data.actualHistory,
              preview.start_date,
              preview.end_date
            );
            periodTotals.forEach((item) => {
              const periodDate = new Date(`${item.start_date}T00:00:00`);
              const historyKey = getMetricKey(periodDate, node.data.frequency, node.data.scope, campus);
              actualHistory[historyKey] = item.total;
            });
            return {
              ...node,
              data: {
                ...node.data,
                actualHistory,
                valueType: "currency",
                dataSource: "CCB",
              },
            };
          }),
          allEdges
        )
      );

      const total = preview.periods.reduce((sum, item) => sum + item.total, 0);
      const transactionCount = preview.periods.reduce((sum, item) => sum + item.transaction_count, 0);
      const importedLabel =
        isSingleCampusImport
          ? `${selectedCampus ? `${selectedCampus} ` : ""}into ${targetNode?.data.label ?? "the selected KPI"}`
          : `into ${expectedUpdatedNodeCount} campus giving KPI${expectedUpdatedNodeCount === 1 ? "" : "s"}`;
      setQuickBooksMessage(
        `Imported ${preview.periods.length} ${givingImportPeriod} CCB giving period${
          preview.periods.length === 1 ? "" : "s"
        } ${importedLabel}: ${formatMetricValue(
          total,
          "currency"
        )} from ${transactionCount} transaction detail${transactionCount === 1 ? "" : "s"}.${
          unmatchedCampuses.size > 0 ? ` Unmatched campuses: ${Array.from(unmatchedCampuses).join(", ")}.` : ""
        }`
      );
    } catch (error) {
      console.error("Failed to import CCB giving", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not import CCB giving."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [
    activeEnterpriseId,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    campus,
    canEditKpiNode,
    ccbGivingCampusName,
    currentUserHasLegacyAdminRole,
    getAiErrorMessage,
    quickBooksEndDate,
    quickBooksStartDate,
    quickBooksTargetKpiId,
  ]);

  React.useEffect(() => {
    if (!showQuickBooksDiagnostics) return;
    void quickBooksExpenseAccounts;
    void onCopyQuickBooksImportDiagnostics;
    void onPreviewQuickBooksGiving;
    void onPreviewQuickBooksExpenses;
    void onInspectQuickBooksBudgets;
    void onApplyQuickBooksGivingPreview;
    void onApplyQuickBooksExpensePreview;
    void onPreviewQuickBooksAccountBalances;
    void onApplyQuickBooksAccountBalancePreview;
    void onSyncQuickBooksAccountBalancesToKpi;
  }, [
    onApplyQuickBooksAccountBalancePreview,
    onApplyQuickBooksExpensePreview,
    onApplyQuickBooksGivingPreview,
    onCopyQuickBooksImportDiagnostics,
    onInspectQuickBooksBudgets,
    onPreviewQuickBooksAccountBalances,
    onPreviewQuickBooksExpenses,
    onPreviewQuickBooksGiving,
    onSyncQuickBooksAccountBalancesToKpi,
    quickBooksExpenseAccounts,
    showQuickBooksDiagnostics,
  ]);

  const onDisconnectQuickBooks = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    const confirmed = window.confirm("Disconnect QuickBooks from this enterprise?");
    if (!confirmed) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    try {
      const response = await api.delete("/integrations/quickbooks/disconnect", {
        params: {
          enterprise_id: activeEnterpriseId,
        },
      });
      setQuickBooksStatus({
        configured: quickBooksStatus?.configured ?? true,
        connected: false,
        enterprise_id: activeEnterpriseId,
      });
      setQuickBooksAccounts([]);
      setQuickBooksDimensions(null);
      setQuickBooksProfitAndLossByLocation(null);
      setQuickBooksProfitAndLossByClass(null);
      setQuickBooksSelectedAccountIds([]);
      setQuickBooksGivingPreview(null);
      setQuickBooksExpensePreview(null);
      setQuickBooksExpenseBudgetPreview(null);
      setQuickBooksAccountBalancePreview(null);
      setQuickBooksTargetKpiId("");
      setQuickBooksMessage(response.data?.ok ? "QuickBooks disconnected." : "QuickBooks connection removed.");
    } catch (error) {
      console.error("Failed to disconnect QuickBooks", error);
      setQuickBooksMessage("Could not disconnect QuickBooks.");
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, quickBooksStatus?.configured]);

  const onCreateQuickBooksSupportSession = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;
    setIsQuickBooksLoading(true);
    setQuickBooksMessage(null);
    try {
      const response = await api.post("/integrations/quickbooks/support-session", null, {
        params: {
          enterprise_id: activeEnterpriseId,
          minutes: 30,
        },
      });
      const session = response.data as QuickBooksSupportSession;
      setQuickBooksSupportSession(session);
      const instructions = buildQuickBooksSupportInstructions(session);
      try {
        await navigator.clipboard.writeText(instructions);
        setQuickBooksCopyMessage("Copied 30-minute finance support token.");
      } catch {
        setQuickBooksCopyMessage("Clipboard was blocked. Copy the finance support token below.");
      }
      setQuickBooksMessage("Created a 30-minute read-only QuickBooks finance support token.");
    } catch (error) {
      console.error("Failed to create QuickBooks support session", error);
      setQuickBooksMessage(getAiErrorMessage(error, "Could not create a QuickBooks finance support token."));
    } finally {
      setIsQuickBooksLoading(false);
    }
  }, [activeEnterpriseId, getAiErrorMessage]);

  const onCopyQuickBooksSupportInstructions = React.useCallback(async () => {
    if (!quickBooksSupportInstructions) return;
    try {
      await navigator.clipboard.writeText(quickBooksSupportInstructions);
      setQuickBooksCopyMessage("Copied 30-minute finance support token.");
    } catch {
      setQuickBooksCopyMessage("Clipboard was blocked. Select and copy the finance support token below.");
    }
  }, [quickBooksSupportInstructions]);

  const viewKpiLineage = React.useCallback(
    (nodeId: string) => {
      closeKpiContextMenu();
      const relatedIds = new Set<string>([nodeId]);
      lensEdges.forEach((edge) => {
        if (edge.source === nodeId) relatedIds.add(edge.target);
        if (edge.target === nodeId) relatedIds.add(edge.source);
      });
      setAllNodes((previous) =>
        previous.map((node) =>
          node.data.lenses.includes(activeLens)
            ? {
                ...node,
                selected: relatedIds.has(node.id),
              }
            : node
        )
      );
      window.setTimeout(() => {
        reactFlowInstance?.fitView({ padding: 0.25, duration: 350 });
      }, 0);
    },
    [activeLens, closeKpiContextMenu, lensEdges, reactFlowInstance]
  );

  const kpiContextMenuNode = kpiContextMenu
    ? allNodes.find((node) => node.id === kpiContextMenu.nodeId) ?? null
    : null;
  const kpiContextMenuItems = kpiContextMenuNode
    ? [
        {
          label: "Edit KPI",
          detail: "Open KPI info",
          disabled: !canEditKpiNode(kpiContextMenuNode.id),
          action: () => openKpiInfoPanel(kpiContextMenuNode.id),
        },
        {
          label: "Manage initiatives and tasks",
          detail: `${kpiContextMenuNode.data.initiatives.length} initiatives, ${kpiContextMenuNode.data.tasks.length} direct tasks`,
          disabled: !canEditKpiNode(kpiContextMenuNode.id),
          action: () => openKpiWorkPanel(kpiContextMenuNode.id),
        },
        {
          label: "Request update",
          detail: "Email assigned open task owners",
          disabled:
            isSendingKpiWorkUpdateEmails ||
            [
              ...kpiContextMenuNode.data.tasks,
              ...kpiContextMenuNode.data.initiatives.flatMap((initiative) => initiative.tasks),
            ].filter((task) => task.status !== "done" && task.owner).length === 0,
          action: () => void requestKpiTaskUpdates(kpiContextMenuNode),
        },
        {
          label: "Create poll",
          detail: kpiContextMenuNode.data.pollConfig ? "Open existing poll" : "Start KPI feedback",
          disabled: !canEditKpiNode(kpiContextMenuNode.id),
          action: () => openKpiPollPanel(kpiContextMenuNode.id),
        },
        {
          label: "Email data owner",
          detail: kpiContextMenuNode.data.dataOwner || kpiContextMenuNode.data.owner || "No data owner assigned",
          disabled: !(kpiContextMenuNode.data.dataOwner || kpiContextMenuNode.data.owner).trim(),
          action: () => emailKpiDataOwner(kpiContextMenuNode),
        },
        {
          label: "Track on dashboard",
          detail: kpiContextMenuNode.data.owner
            ? `${kpiContextMenuNode.data.owner}'s dashboard`
            : "Assign owner first",
          disabled: !kpiContextMenuNode.data.owner.trim(),
          action: () => void trackKpiOnDashboard(kpiContextMenuNode),
        },
        {
          label: "Add child KPI",
          detail: "Create a measured child",
          disabled: !canCreateKpis && !canEditKpiNode(kpiContextMenuNode.id),
          action: () => {
            closeKpiContextMenu();
            onAddChild(kpiContextMenuNode.id);
          },
        },
        {
          label: "View lineage",
          detail: "Select immediate parents and children",
          disabled: false,
          action: () => viewKpiLineage(kpiContextMenuNode.id),
        },
        {
          label: "Delete KPI",
          detail: "Remove from workspace",
          disabled: !canDeleteKpiNode(kpiContextMenuNode.id),
          tone: "danger" as const,
          action: () => {
            closeKpiContextMenu();
            if (window.confirm(`Delete "${kpiContextMenuNode.data.label || "this KPI"}"?`)) {
              onDeleteKpi(kpiContextMenuNode.id);
            }
          },
        },
      ]
    : [];

  const applyWorkspace = React.useCallback(
    (
      workspace?: Record<string, unknown>,
      options?: {
        resetUndoHistory?: boolean;
        preserveView?: boolean;
      }
    ) => {
      const resetUndoHistory = options?.resetUndoHistory ?? true;
      const preserveView = options?.preserveView ?? false;
      const nextWorkspace = workspace ?? {};
      const {
        availableLenses: _availableLenses,
        lensConfigs: _lensConfigs,
        activeLens: _activeLens,
        referenceDate: _referenceDate,
        aggregationMode: _aggregationMode,
        comparisonMode: _comparisonMode,
        campus: _campus,
        ownerList: _ownerList,
        pollAudienceGroups: _pollAudienceGroups,
        pollRecipients: _pollRecipients,
        isLensLibraryCollapsed: _isLensLibraryCollapsed,
        isReferenceLibraryCollapsed: _isReferenceLibraryCollapsed,
        isLibraryCollapsed: _isLibraryCollapsed,
        panelOffsets: _panelOffsets,
        panelSizes: _panelSizes,
        allNodes: _allNodes,
        allEdges: _allEdges,
        ...extraWorkspaceFields
      } = nextWorkspace;
      extraWorkspaceFieldsRef.current = extraWorkspaceFields;
      const nextAvailableLenses =
        Array.isArray(nextWorkspace.availableLenses) && nextWorkspace.availableLenses.length > 0
          ? (nextWorkspace.availableLenses as Lens[])
          : initialLenses;
      const nextLensConfigs =
        nextWorkspace.lensConfigs &&
        typeof nextWorkspace.lensConfigs === "object" &&
        !Array.isArray(nextWorkspace.lensConfigs)
          ? (nextWorkspace.lensConfigs as LensConfigs)
          : initialLensConfigs;
      const nextActiveLens =
        preserveView && nextAvailableLenses.includes(activeLens) ? activeLens :
        typeof nextWorkspace.activeLens === "string" &&
        nextAvailableLenses.includes(nextWorkspace.activeLens as Lens)
          ? (nextWorkspace.activeLens as Lens)
          : nextAvailableLenses[0];
      const nextWeekStartsOn = normalizeWeekStartsOn(selectedEnterprise?.week_starts_on);
      const nextReferenceDate = preserveView ? referenceDate : getReportingWeekStart(getInitialReferenceDate(nextWeekStartsOn), nextWeekStartsOn);
      const resolvedReferenceDate = Number.isNaN(nextReferenceDate.getTime())
        ? getInitialReferenceDate(nextWeekStartsOn)
        : nextReferenceDate;
      const nextCampus = preserveView ? campus : defaultCampus;

      const nextNodes =
        Array.isArray(nextWorkspace.allNodes)
          ? (nextWorkspace.allNodes as Node<Partial<KpiNodeData>>[]).map((node) => {
              const hydratedData = hydrateNodeDataFromPersistence(node.data ?? {});
              return {
                ...node,
                data:
                  hydratedData.dataSource === "Poll"
                    ? {
                        ...hydratedData,
                        actualHistory: derivePollActualHistory(
                          hydratedData.pollConfig,
                          hydratedData.frequency,
                          hydratedData.scope,
                          nextCampus
                        ),
                      }
                    : hydratedData,
              };
            })
          : defaultNodes;
      const nextEdges =
        Array.isArray(nextWorkspace.allEdges)
          ? (nextWorkspace.allEdges as Edge[]).map((edge) => sanitizePersistedEdge(edge))
          : defaultEdges;

      setAvailableLenses(nextAvailableLenses);
      setLensConfigs(nextLensConfigs);
      setLensNameDrafts(
        Object.fromEntries(
          nextAvailableLenses.map((lens) => [lens, nextLensConfigs[lens]?.name ?? lens])
        )
      );
      setActiveLens(nextActiveLens);
      setReferenceDate(resolvedReferenceDate);
      setWeekStartsOn(nextWeekStartsOn);
      if (!preserveView) {
      setAggregationMode(
        nextWorkspace.aggregationMode === "mtd" ||
          nextWorkspace.aggregationMode === "qtd" ||
          nextWorkspace.aggregationMode === "ytd" ||
          nextWorkspace.aggregationMode === "current"
          ? (nextWorkspace.aggregationMode as AggregationMode)
          : Boolean(nextWorkspace.isCumulativeView)
            ? "ytd"
            : "current"
      );
      setComparisonMode(
        nextWorkspace.comparisonMode === "previous_period" ||
          nextWorkspace.comparisonMode === "same_period_last_year" ||
          nextWorkspace.comparisonMode === "target"
          ? (nextWorkspace.comparisonMode as ComparisonMode)
          : "target"
      );
      }
      setCampus(nextCampus);
      setOwnerList(
        Array.isArray(nextWorkspace.ownerList) && nextWorkspace.ownerList.length > 0
          ? (nextWorkspace.ownerList as string[])
          : initialOwners
      );
      setPollAudienceGroups(
        Array.isArray(nextWorkspace.pollAudienceGroups) && nextWorkspace.pollAudienceGroups.length > 0
          ? (nextWorkspace.pollAudienceGroups as string[])
          : initialPollAudienceGroups
      );
      setPollRecipients(
        Array.isArray(nextWorkspace.pollRecipients)
          ? (nextWorkspace.pollRecipients as PollRecipient[]).map((recipient) => ({
              id: recipient.id ?? uuidv4(),
              firstName:
                recipient.firstName ??
                recipient.name?.trim().split(/\s+/).filter(Boolean)[0] ??
                "",
              lastName:
                recipient.lastName ??
                recipient.name?.trim().split(/\s+/).filter(Boolean).slice(1).join(" ") ??
                "",
              email: recipient.email ?? "",
              groups: Array.isArray(recipient.groups) ? recipient.groups : [],
              name: recipient.name,
            }))
          : initialPollRecipients
      );
      if (!preserveView) {
      setIsLensLibraryCollapsed(true);
      setIsReferenceLibraryCollapsed(true);
      setIsLibraryCollapsed(true);
      setPanelOffsets(
        nextWorkspace.panelOffsets &&
          typeof nextWorkspace.panelOffsets === "object" &&
          !Array.isArray(nextWorkspace.panelOffsets)
          ? ({
              ...(nextWorkspace.panelOffsets as Record<FloatingPanelKey, { x: number; y: number }>),
              lens: { x: 0, y: 0 },
              reference: { x: 0, y: 0 },
              kpi: { x: 0, y: 0 },
            } as Record<FloatingPanelKey, { x: number; y: number }>)
          : { lens: { x: 0, y: 0 }, reference: { x: 0, y: 0 }, kpi: { x: 0, y: 0 } }
      );
      setPanelSizes(
        nextWorkspace.panelSizes &&
          typeof nextWorkspace.panelSizes === "object" &&
          !Array.isArray(nextWorkspace.panelSizes)
          ? ({
              lens: {
                width: (nextWorkspace.panelSizes as Record<FloatingPanelKey, PanelSize>).lens?.width ?? 380,
                height: (nextWorkspace.panelSizes as Record<FloatingPanelKey, PanelSize>).lens?.height ?? 720,
              },
              reference: {
                width:
                  (nextWorkspace.panelSizes as Record<FloatingPanelKey, PanelSize>).reference?.width ?? 280,
                height:
                  (nextWorkspace.panelSizes as Record<FloatingPanelKey, PanelSize>).reference?.height ?? 720,
              },
              kpi: {
                width: (nextWorkspace.panelSizes as Record<FloatingPanelKey, PanelSize>).kpi?.width ?? 320,
                height: (nextWorkspace.panelSizes as Record<FloatingPanelKey, PanelSize>).kpi?.height ?? 720,
              },
            } as Record<FloatingPanelKey, PanelSize>)
          : {
              lens: { width: 380, height: 720 },
              reference: { width: 280, height: 720 },
              kpi: { width: 320, height: 720 },
          }
      );
      }
      const repairedNodes = applyContextAndRecalculate(
        nextNodes,
        nextEdges,
        resolvedReferenceDate,
        nextCampus,
        nextActiveLens
      );
      setAllNodes(layoutVisibleLensGraph(repairedNodes, nextActiveLens, nextEdges));
      setAllEdges(nextEdges);
      if (preserveView) {
        setActiveKpiId((id) => nextNodes.some((node) => node.id === id) ? id : null);
      } else {
        setActiveKpiId(null);
        setActiveLensDetailId(null);
      }

      if (resetUndoHistory) {
        undoHistoryRef.current = [];
        lastWorkspaceSnapshotRef.current = null;
      }
    },
    [activeLens, campus, referenceDate, applyContextAndRecalculate, defaultEdges, defaultNodes, layoutVisibleLensGraph, selectedEnterprise?.week_starts_on]
  );

  React.useEffect(() => {
    applyWorkspaceRef.current = applyWorkspace;
  }, [applyWorkspace]);

  React.useEffect(() => {
    if (!isWorkspaceAgentLoading) {
      setWorkspaceAgentElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    setWorkspaceAgentElapsedSeconds(0);
    const intervalId = window.setInterval(() => {
      setWorkspaceAgentElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isWorkspaceAgentLoading]);

  const askWorkspaceAgent = React.useCallback(async () => {
    const prompt = workspaceAgentPrompt.trim();
    if (!prompt) {
      setWorkspaceAgentResponse({
        intent: "help",
        message: "Ask me something like: what were Beth Emanuel event expenses in Q1 2026, or how many late tasks do I have?",
      });
      return;
    }
    if (activeEnterpriseId === null) {
      setWorkspaceAgentResponse({
        intent: "error",
        message: "Select an enterprise before asking the agent.",
      });
      return;
    }

    setWorkspaceAgentOperation("answer");
    setIsWorkspaceAgentLoading(true);
    try {
      const response = await api.post(
        `/enterprises/${activeEnterpriseId}/${askLensesQueryPath}`,
        {
          message: prompt,
          active_lens: activeLens,
          conversation_id: workspaceAgentConversationId,
        },
        // Broad connector questions can require pagination, rate-limit backoff,
        // and a grounded answer pass. Keep the normal API timeout elsewhere,
        // but allow Ask Lenses enough time to finish those bounded operations.
        { timeout: 180000 }
      );
      const result = response.data as WorkspaceAgentResponse;
      const displayedMessage = result.freshness_warnings?.length
        ? `Data freshness warning: ${result.freshness_warnings.join(" ")}\n\n${result.message}`
        : result.message;
      setWorkspaceAgentConversationId(result.conversation_id ?? workspaceAgentConversationId);
      setWorkspaceAgentResponse({ ...result, message: displayedMessage });
      setWorkspaceAgentHistory((previous) => [
        ...previous.slice(-8),
        { role: "user", content: prompt },
        { role: "assistant", content: displayedMessage },
      ]);
      setWorkspaceAgentPrompt("");
    } catch (error) {
      console.error("Failed to ask workspace agent", error);
      const errorResponse = {
        intent: "error",
        message: getAiErrorMessage(error, "The agent could not answer that yet."),
      };
      setWorkspaceAgentResponse(errorResponse);
      setWorkspaceAgentHistory((previous) => [
        ...previous.slice(-8),
        { role: "user", content: prompt },
        { role: "assistant", content: errorResponse.message },
      ]);
    } finally {
      setIsWorkspaceAgentLoading(false);
    }
  }, [activeEnterpriseId, activeLens, getAiErrorMessage, workspaceAgentConversationId, workspaceAgentPrompt]);

  const handleWorkspaceAgentPromptKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter" || event.shiftKey) return;
      event.preventDefault();
      if (isWorkspaceAgentLoading) return;
      void askWorkspaceAgent();
    },
    [askWorkspaceAgent, isWorkspaceAgentLoading]
  );

  const executeWorkspaceAgentAction = React.useCallback(
    async (action: WorkspaceAgentAction) => {
      if (activeEnterpriseId === null) return;
      if (!window.confirm(action.confirmation)) return;

      setWorkspaceAgentOperation("action");
      setIsWorkspaceAgentLoading(true);
      try {
        const response = await api.post(`/enterprises/${activeEnterpriseId}/agent-v2/execute`, {
          action,
        });
        const result = response.data as WorkspaceAgentResponse;
        setWorkspaceAgentResponse(result);
        setWorkspaceAgentHistory((previous) => [
          ...previous.slice(-8),
          { role: "assistant", content: result.message },
        ]);
        if (result.updated_workspace) {
          window.localStorage.setItem(
            getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise),
            JSON.stringify(result.updated_workspace)
          );
          skipNextWorkspaceAutosaveRef.current = true;
          applyWorkspaceRef.current(result.updated_workspace);
          hasLoadedEnterpriseRef.current = true;
          notifyWorkspaceChanged(activeEnterpriseId, "workspace-agent-action");
        }
      } catch (error) {
        console.error("Failed to execute workspace agent action", error);
        setWorkspaceAgentResponse({
          intent: "error",
          message: getAiErrorMessage(error, "The agent could not complete that action."),
        });
      } finally {
        setIsWorkspaceAgentLoading(false);
      }
    },
    [activeEnterpriseId, getAiErrorMessage, selectedEnterprise]
  );

  React.useEffect(() => {
    if (activeKpiId) {
      setIsKpiInfoOpen(false);
      setIsKpiWorkOpen(false);
    }
  }, [activeKpiId]);

  React.useEffect(() => {
    setWorkspaceAgentConversationId(null);
    setWorkspaceAgentHistory([]);
    setWorkspaceAgentResponse(null);
  }, [activeEnterpriseId]);

  React.useEffect(() => {
    const requestedKpiId = searchParams.get("editKpiId");
    if (!requestedKpiId || !allNodes.some((node) => node.id === requestedKpiId)) {
      return;
    }

    const requestedLens = searchParams.get("lens");
    if (requestedLens && availableLenses.includes(requestedLens) && activeLens !== requestedLens) {
      setActiveLens(requestedLens);
    }

    setActiveKpiId(requestedKpiId);
    const requestedPanel = searchParams.get("panel");
    const shouldOpenWork = requestedPanel === "work" || Boolean(searchParams.get("taskId") || searchParams.get("initiativeId"));
    setIsKpiInfoOpen(!shouldOpenWork);
    setIsKpiWorkOpen(shouldOpenWork);

    const nextSearchParams = new URLSearchParams(searchParams);
    ["editKpiId", "panel", "taskId", "initiativeId", "lens"].forEach((paramName) => {
      nextSearchParams.delete(paramName);
    });
    setSearchParams(nextSearchParams, { replace: true });
  }, [activeLens, allNodes, availableLenses, searchParams, setSearchParams]);

  const onCreateEnterprise = React.useCallback(async () => {
    const nameInput = window.prompt("Enterprise name?", "");
    const trimmedName = nameInput?.trim();
    if (!trimmedName) return;
    const websiteInput = window.prompt("Website URL? Optional, but it helps AI understand the enterprise.", "");
    const trimmedWebsiteUrl = websiteInput?.trim() ?? "";

    try {
      const response = await api.post("/enterprises/", {
        name: trimmedName,
        website_url: trimmedWebsiteUrl,
      });
      const nextEnterprise = response.data as EnterpriseSummary;
      setEnterprises((previous) =>
        [...previous, nextEnterprise].sort((left, right) => left.name.localeCompare(right.name))
      );
      hasLoadedEnterpriseRef.current = false;
      setActiveEnterpriseId(nextEnterprise.id);
      applyWorkspace({});
    } catch (error) {
      console.error("Failed to create enterprise", error);
    }
  }, [applyWorkspace]);

  const onUpdateEnterpriseWebsite = React.useCallback(async (enterprise: EnterpriseSummary) => {
    const websiteInput = window.prompt(
      `Website URL for ${enterprise.name}?`,
      enterprise.website_url ?? ""
    );
    if (websiteInput === null) return;

    try {
      const response = await api.patch(`/enterprises/${enterprise.id}`, {
        website_url: websiteInput.trim(),
      });
      const updatedEnterprise = response.data as EnterpriseSummary;
      setEnterprises((previous) =>
        previous.map((item) => (item.id === updatedEnterprise.id ? updatedEnterprise : item))
      );
    } catch (error) {
      console.error("Failed to update enterprise website", error);
    }
  }, []);

  const onUpdateEnterpriseWeekStart = React.useCallback(async (enterprise: EnterpriseSummary, nextValue: WeekStartsOn) => {
    const nextWeekStartsOn = normalizeWeekStartsOn(nextValue);

    try {
      const response = await api.patch(`/enterprises/${enterprise.id}`, {
        week_starts_on: nextWeekStartsOn,
      });
      const updatedEnterprise = response.data as EnterpriseSummary;
      setEnterprises((previous) =>
        previous.map((item) => (item.id === updatedEnterprise.id ? updatedEnterprise : item))
      );
      if (enterprise.id === activeEnterpriseId) {
        const nextReferenceDate = getReportingWeekStart(referenceDate, nextWeekStartsOn);
        setWeekStartsOn(nextWeekStartsOn);
        setReferenceDate(nextReferenceDate);
        setAllNodes((previous) =>
          applyContextAndRecalculate(previous, allEdges, nextReferenceDate, campus)
        );
      }
    } catch (error) {
      console.error("Failed to update enterprise week start", error);
    }
  }, [activeEnterpriseId, allEdges, applyContextAndRecalculate, campus, referenceDate]);

  const onDeleteEnterprise = React.useCallback(
    async (enterprise: EnterpriseSummary) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete ${enterprise.name}? All lenses, KPIs, and saved data associated with this enterprise will be lost.`
      );
      if (!confirmed) return;

      try {
        await api.delete(`/enterprises/${enterprise.id}`);
        setEnterprises((previous) => {
          const nextEnterprises = previous.filter((item) => item.id !== enterprise.id);

          if (activeEnterpriseId === enterprise.id) {
            hasLoadedEnterpriseRef.current = false;
            if (nextEnterprises.length > 0) {
              setActiveEnterpriseId(nextEnterprises[0].id);
            } else {
              setActiveEnterpriseId(null);
            }
          }

          return nextEnterprises;
        });
      } catch (error) {
        console.error("Failed to delete enterprise", error);
        window.alert(getAiErrorMessage(error, "Could not delete this enterprise."));
      }
    },
    [activeEnterpriseId, getAiErrorMessage]
  );

  React.useEffect(() => {
    const loadEnterprises = async () => {
      setIsEnterpriseLoading(true);
      setLoadError(null);

      try {
        let response = await api.get("/enterprises/");
        let nextEnterprises = (response.data as EnterpriseSummary[]).slice().sort((left, right) => {
          const leftUpdatedAt = left.updated_at ? new Date(left.updated_at).getTime() : 0;
          const rightUpdatedAt = right.updated_at ? new Date(right.updated_at).getTime() : 0;
          return rightUpdatedAt - leftUpdatedAt;
        });

        if (nextEnterprises.length === 0) {
          const createResponse = await api.post("/enterprises/", {
            name: defaultEnterpriseName,
          });
          nextEnterprises = [createResponse.data as EnterpriseSummary];
        }

        window.localStorage.setItem(enterprisesCacheKey, JSON.stringify(nextEnterprises));
        setEnterprises(nextEnterprises);
        hasLoadedEnterpriseRef.current = false;
        const queryEnterpriseId = Number(searchParams.get("enterpriseId"));
        const storedEnterpriseId = window.localStorage.getItem(activeEnterpriseStorageKey);
        const parsedStoredEnterpriseId =
          storedEnterpriseId === null ? Number.NaN : Number(storedEnterpriseId);
        const queryEnterprise = nextEnterprises.find((enterprise) => enterprise.id === queryEnterpriseId);
        const preferredEnterprise =
          queryEnterprise ??
          nextEnterprises.find((enterprise) => enterprise.id === parsedStoredEnterpriseId) ??
          nextEnterprises[0];
        setActiveEnterpriseId((previous) => {
          if (queryEnterprise) return queryEnterprise.id;
          return previous !== null && nextEnterprises.some((enterprise) => enterprise.id === previous)
            ? previous
            : preferredEnterprise.id;
        });
      } catch (error) {
        console.error("Failed to load enterprises", error);
        setLoadError(
          "Could not reach the backend. Start the API on port 8000 or set REACT_APP_API_URL to the correct server."
        );
      } finally {
        setIsEnterpriseLoading(false);
      }
    };

    void loadEnterprises();
  }, [searchParams]);

  React.useEffect(() => {
    if (activeEnterpriseId === null) return;
    window.localStorage.setItem(activeEnterpriseStorageKey, String(activeEnterpriseId));
  }, [activeEnterpriseId]);

  React.useEffect(() => {
    if (activeEnterpriseId === null) {
      setWorkspaceCapabilities(defaultWorkspaceCapabilities);
      return;
    }

    let isActive = true;
    const loadCapabilities = async () => {
      try {
        const response = await api.get("/auth/capabilities", {
          params: { enterprise_id: activeEnterpriseId },
        });
        if (isActive) {
          setWorkspaceCapabilities(response.data as WorkspaceCapabilities);
        }
      } catch (error) {
        console.error("Failed to load workspace capabilities", error);
        if (isActive) {
          setWorkspaceCapabilities(defaultWorkspaceCapabilities);
        }
      }
    };

    void loadCapabilities();

    return () => {
      isActive = false;
    };
  }, [activeEnterpriseId]);

  React.useEffect(() => {
    if (activeEnterpriseId === null) {
      setQuickBooksStatus(null);
      setPushpayStatus(null);
      setQuickBooksAccounts([]);
      setQuickBooksDimensions(null);
      setQuickBooksProfitAndLossByLocation(null);
      setQuickBooksProfitAndLossByClass(null);
      setQuickBooksSelectedAccountIds([]);
      setQuickBooksGivingPreview(null);
      setQuickBooksExpensePreview(null);
      setQuickBooksExpenseBudgetPreview(null);
      setQuickBooksAccountBalancePreview(null);
      setQuickBooksTargetKpiId("");
      return;
    }

    let isActive = true;
    const loadQuickBooksStatus = async () => {
      try {
        const response = await api.get("/integrations/quickbooks/status", {
          params: { enterprise_id: activeEnterpriseId },
        });
        if (isActive) {
          const status = response.data as QuickBooksStatus;
          setQuickBooksStatus(status);
          if (!status.connected) {
            setQuickBooksAccounts([]);
            setQuickBooksDimensions(null);
            setQuickBooksProfitAndLossByLocation(null);
            setQuickBooksProfitAndLossByClass(null);
            setQuickBooksSelectedAccountIds([]);
            setQuickBooksGivingPreview(null);
            setQuickBooksExpensePreview(null);
            setQuickBooksExpenseBudgetPreview(null);
            setQuickBooksAccountBalancePreview(null);
            setQuickBooksTargetKpiId("");
          }
        }
      } catch (error) {
        console.error("Failed to load QuickBooks status", error);
        if (isActive) {
          setQuickBooksStatus(null);
        }
      }
    };

    void loadQuickBooksStatus();

    return () => {
      isActive = false;
    };
  }, [activeEnterpriseId]);

  React.useEffect(() => {
    if (activeEnterpriseId === null) {
      setPushpayStatus(null);
      return;
    }

    let isActive = true;
    const loadPushpayStatus = async () => {
      try {
        const response = await api.get("/integrations/pushpay/status", {
          params: { enterprise_id: activeEnterpriseId },
        });
        if (isActive) {
          setPushpayStatus(response.data as PushpayChmsStatus);
        }
      } catch (error) {
        console.error("Failed to load Pushpay ChMS status", error);
        if (isActive) {
          setPushpayStatus(null);
        }
      }
    };

    void loadPushpayStatus();

    return () => {
      isActive = false;
    };
  }, [activeEnterpriseId]);

  React.useEffect(() => {
    if (signedInUser) {
      setCurrentUser({
        ...signedInUser,
        is_active: signedInUser.is_active ?? true,
      });
    }
  }, [signedInUser]);

  React.useEffect(() => {
    if (signedInUser) return;
    let isActive = true;
    const loadCurrentUser = async () => {
      try {
        const response = await api.get("/auth/me");
        if (isActive) {
          setCurrentUser(response.data as AppUser);
        }
      } catch (error) {
        console.error("Failed to load current user for ownership checks", error);
        if (isActive) {
          setCurrentUser(null);
        }
      }
    };

    void loadCurrentUser();

    return () => {
      isActive = false;
    };
  }, [signedInUser]);

  React.useEffect(() => {
    if (activeEnterpriseId === null) {
      setWorkspaceUsers([]);
      setHasLoadedWorkspaceUsers(false);
      return;
    }

    let isActive = true;
    setHasLoadedWorkspaceUsers(false);
    const loadUsers = async () => {
      try {
        const response = await api.get("/users/");
        if (isActive) {
          setWorkspaceUsers(response.data as AppUser[]);
          setHasLoadedWorkspaceUsers(true);
        }
      } catch (error) {
        console.error("Failed to load users for owner list", error);
        if (isActive) {
          setWorkspaceUsers([]);
        }
      }
    };

    void loadUsers();

    return () => {
      isActive = false;
    };
  }, [activeEnterpriseId]);

  React.useEffect(() => {
    if (!hasLoadedWorkspaceUsers || isWorkspaceLoading) {
      return;
    }

    const current = ownerList.join("\n");
    const next = userOwnerOptions.join("\n");
    if (current !== next) {
      setOwnerList(userOwnerOptions);
    }
  }, [hasLoadedWorkspaceUsers, isWorkspaceLoading, ownerList, userOwnerOptions]);

  React.useEffect(() => {
    if (!hasLoadedWorkspaceUsers || isWorkspaceLoading) {
      return;
    }

    const cleanOwner = (owner: string) =>
      userOwnerSet.has(owner.trim().toLowerCase()) ? owner : "";

    setAllNodes((previous) => {
      let changed = false;
      const nextNodes = previous.map((node) => {
        const nextOwner = cleanOwner(node.data.owner);
        const nextDataOwner = cleanOwner(node.data.dataOwner);
        let tasksChanged = false;
        const nextTasks = node.data.tasks.map((task) => {
          const nextTaskOwner = cleanOwner(task.owner);
          if (nextTaskOwner === task.owner) return task;
          tasksChanged = true;
          return { ...task, owner: nextTaskOwner };
        });
        let initiativesChanged = false;
        const nextInitiatives = node.data.initiatives.map((initiative) => {
          const nextInitiativeOwner = cleanOwner(initiative.owner);
          let initiativeTasksChanged = false;
          const nextInitiativeTasks = initiative.tasks.map((task) => {
            const nextTaskOwner = cleanOwner(task.owner);
            if (nextTaskOwner === task.owner) return task;
            initiativeTasksChanged = true;
            return { ...task, owner: nextTaskOwner };
          });
          if (
            nextInitiativeOwner === initiative.owner &&
            !initiativeTasksChanged
          ) {
            return initiative;
          }
          initiativesChanged = true;
          return {
            ...initiative,
            owner: nextInitiativeOwner,
            tasks: nextInitiativeTasks,
          };
        });

        if (
          nextOwner === node.data.owner &&
          nextDataOwner === node.data.dataOwner &&
          !tasksChanged &&
          !initiativesChanged
        ) {
          return node;
        }

        changed = true;
        return {
          ...node,
          data: {
            ...node.data,
            owner: nextOwner,
            dataOwner: nextDataOwner,
            tasks: nextTasks,
            initiatives: nextInitiatives,
          },
        };
      });

      return changed ? nextNodes : previous;
    });
  }, [hasLoadedWorkspaceUsers, isWorkspaceLoading, userOwnerSet]);

  React.useEffect(() => {
    if (activeEnterpriseId === null) return;

    const loadWorkspace = async () => {
      setIsWorkspaceLoading(true);
      hasLoadedEnterpriseRef.current = false;
      setLoadError(null);

      try {
        const response = await api.get(`/enterprises/${activeEnterpriseId}/workspace`);
        skipNextWorkspaceAutosaveRef.current = true;
        window.localStorage.setItem(
          getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise),
          JSON.stringify(response.data.workspace ?? {})
        );
        window.localStorage.removeItem(getWorkspacePendingSaveKey(activeEnterpriseId, selectedEnterprise));
        workspaceUpdatedAtRef.current =
          typeof response.data.updated_at === "string" ? response.data.updated_at : null;
        applyWorkspaceRef.current(response.data.workspace);
        hasLoadedEnterpriseRef.current = true;
      } catch (error) {
        console.error("Failed to load workspace", error);
        setLoadError(
          "Saved workspace could not be loaded from the backend. Check that the API is running and reachable."
        );
      } finally {
        setIsWorkspaceLoading(false);
      }
    };

    void loadWorkspace();
  }, [activeEnterpriseId, selectedEnterprise]);

  React.useEffect(() => {
    if (!hasLoadedEnterpriseRef.current || activeEnterpriseId === null || !canPersistWorkspace) {
      return;
    }

    const currentSnapshot = JSON.stringify(serializeWorkspace());
    const currentPersistenceFingerprint = getWorkspacePersistenceFingerprint(serializeWorkspace());
    if (skipNextWorkspaceAutosaveRef.current) {
      skipNextWorkspaceAutosaveRef.current = false;
      lastWorkspaceSnapshotRef.current = currentSnapshot;
      lastWorkspacePersistenceFingerprintRef.current = currentPersistenceFingerprint;
      immediateUndoSnapshotRef.current = null;
      return;
    }

    if (lastWorkspaceSnapshotRef.current === null) {
      lastWorkspaceSnapshotRef.current = currentSnapshot;
    } else if (currentSnapshot !== lastWorkspaceSnapshotRef.current) {
      undoHistoryRef.current = [
        ...undoHistoryRef.current.slice(-(UNDO_HISTORY_LIMIT - 1)),
        lastWorkspaceSnapshotRef.current,
      ];
      lastWorkspaceSnapshotRef.current = currentSnapshot;
    }
    immediateUndoSnapshotRef.current = null;

    const pendingWorkspace = serializeWorkspace();
    const pendingPersistenceFingerprint = getWorkspacePersistenceFingerprint(pendingWorkspace);
    if (pendingPersistenceFingerprint === lastWorkspacePersistenceFingerprintRef.current) {
      return;
    }

    window.localStorage.setItem(
      getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise),
      JSON.stringify(pendingWorkspace)
    );
    window.localStorage.setItem(getWorkspacePendingSaveKey(activeEnterpriseId, selectedEnterprise), "true");

    const timeoutId = window.setTimeout(async () => {
      const serializedWorkspace = serializeWorkspace();
      const serializedPersistenceFingerprint = getWorkspacePersistenceFingerprint(serializedWorkspace);
      if (serializedPersistenceFingerprint === lastWorkspacePersistenceFingerprintRef.current) {
        return;
      }

      window.localStorage.setItem(
        getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise),
        JSON.stringify(serializedWorkspace)
      );

      try {
        await persistLatestWorkspace();
      } catch (error) {
        console.error("Failed to save workspace", error);
        const apiError = error as { response?: { status?: number; data?: { detail?: string } } };
        if (apiError.response?.status === 409) {
          hasLoadedEnterpriseRef.current = false;
          setLoadError(
            "Your changes are preserved on this device, but another user updated this workspace first. Reload to review their changes before saving again."
          );
        } else {
          const detail = apiError.response?.data?.detail;
          setLoadError(
            typeof detail === "string" && detail.trim()
              ? `Workspace changes could not be saved: ${detail}`
              : "Workspace changes could not be saved. Check your connection and try again."
          );
        }
      }
    }, 700);

    return () => window.clearTimeout(timeoutId);
  }, [
    activeEnterpriseId,
    allEdges,
    allNodes,
    availableLenses,
    campus,
    comparisonMode,
    isLensLibraryCollapsed,
    isLibraryCollapsed,
    isReferenceLibraryCollapsed,
    lensConfigs,
    ownerList,
    pollAudienceGroups,
    pollRecipients,
    panelOffsets,
    panelSizes,
    referenceDate,
    selectedEnterprise,
    serializeWorkspace,
    canPersistWorkspace,
    persistLatestWorkspace,
  ]);

  React.useEffect(() => {
    if (activeEnterpriseId === null || !canPersistWorkspace) return;

    const flushPendingWorkspace = () => {
      if (!hasLoadedEnterpriseRef.current) return;
      const serializedWorkspace = serializeWorkspaceRef.current();
      const serializedPersistenceFingerprint = getWorkspacePersistenceFingerprint(serializedWorkspace);
      if (serializedPersistenceFingerprint === lastWorkspacePersistenceFingerprintRef.current) return;

      window.localStorage.setItem(
        getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise),
        JSON.stringify(serializedWorkspace)
      );
      window.localStorage.setItem(getWorkspacePendingSaveKey(activeEnterpriseId, selectedEnterprise), "true");
      void persistLatestWorkspace()
        .catch((error) => {
          console.error("Failed to flush workspace before leaving page", error);
          if ((error as { response?: { status?: number } })?.response?.status === 409) {
            hasLoadedEnterpriseRef.current = false;
            setLoadError(
              "Another user updated this workspace first. Your local changes were preserved and were not allowed to overwrite theirs."
            );
          }
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingWorkspace();
      }
    };

    window.addEventListener("pagehide", flushPendingWorkspace);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flushPendingWorkspace);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeEnterpriseId, canPersistWorkspace, selectedEnterprise, persistLatestWorkspace]);

  React.useEffect(() => {
    setAllNodes((previous) =>
      applyContextAndRecalculate(previous, allEdges, referenceDate, campus, activeLens)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setAllNodes((previous) =>
      applyContextAndRecalculate(previous, allEdges, referenceDate, campus, activeLens)
    );
  }, [
    activeLens,
    allEdges,
    applyContextAndRecalculate,
    campus,
    aggregationMode,
    referenceDate,
  ]);

  React.useEffect(() => {
    setLensNameDrafts((previous) => {
      const nextDrafts: Record<string, string> = {};
      availableLenses.forEach((lens) => {
        nextDrafts[lens] = previous[lens] ?? lensConfigs[lens]?.name ?? lens;
      });
      return nextDrafts;
    });
  }, [availableLenses, lensConfigs]);

  const undoLastAction = React.useCallback(() => {
    if (immediateUndoStateRef.current) {
      const snapshot = immediateUndoStateRef.current;
      immediateUndoStateRef.current = null;
      immediateUndoSnapshotRef.current = null;
      setAllEdges(cloneEdgesForUndo(snapshot.edges));
      setAllNodes(cloneNodesForUndo(snapshot.nodes));
      return;
    }

    const previousSnapshot = immediateUndoSnapshotRef.current ?? undoHistoryRef.current.pop();
    if (!previousSnapshot) return;

    immediateUndoSnapshotRef.current = null;
    lastWorkspaceSnapshotRef.current = previousSnapshot;
    applyWorkspaceRef.current(JSON.parse(previousSnapshot) as Record<string, unknown>, {
      resetUndoHistory: false,
    });
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && (kpiContextMenu || mapContextMenu)) {
        event.preventDefault();
        event.stopPropagation();
        closeKpiContextMenu();
        return;
      }

      const normalizedKey = event.key.toLowerCase();
      const isUndoShortcut =
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        (normalizedKey === "z" || event.code === "KeyZ");
      const isDuplicateShortcut =
        (event.metaKey || event.ctrlKey) &&
        (normalizedKey === "d" || event.code === "KeyD");

      if (isUndoShortcut) {
        if (isTypingTarget(event.target)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        undoLastAction();
        return;
      }

      if (isDuplicateShortcut) {
        if (selectedNodeIds.length === 0 || isTypingTarget(event.target)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        duplicateSelectedKpis();
        return;
      }

      if (
        event.key !== "Delete" &&
        event.key !== "Backspace"
      ) {
        return;
      }

      if (selectedNodeIds.length === 0 || isTypingTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      selectedNodeIds.forEach((nodeId) => {
        onRemoveFromActiveLens(nodeId);
      });
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [
    closeKpiContextMenu,
    duplicateSelectedKpis,
    kpiContextMenu,
    mapContextMenu,
    onRemoveFromActiveLens,
    selectedNodeIds,
    undoLastAction,
  ]);

  React.useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const dragState = panelDragRef.current;
      const resizeState = panelResizeRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
      } else {
        setPanelOffsets((previous) => ({
          ...previous,
          [dragState.panel]: {
            x: dragState.originX + (event.clientX - dragState.startX),
            y: dragState.originY + (event.clientY - dragState.startY),
          },
        }));
      }
      if (!resizeState || resizeState.pointerId !== event.pointerId) {
        return;
      }

      setPanelSizes((previous) => ({
        ...previous,
        [resizeState.panel]: {
          width: Math.max(
            resizeState.panel === "reference" ? 260 : 280,
            resizeState.originWidth - (event.clientX - resizeState.startX)
          ),
          height: Math.max(320, resizeState.originHeight + (event.clientY - resizeState.startY)),
        },
      }));
    };

    const endDrag = (event: PointerEvent) => {
      if (panelDragRef.current?.pointerId === event.pointerId) {
        panelDragRef.current = null;
        setDraggingPanel(null);
      }
      if (panelResizeRef.current?.pointerId === event.pointerId) {
        panelResizeRef.current = null;
        setResizingPanel(null);
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, []);

  const onPanelDragStart = React.useCallback(
    (panel: FloatingPanelKey, event: React.PointerEvent<HTMLDivElement>) => {
      stopCanvasDrag(event);
      panelDragRef.current = {
        panel,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: panelOffsets[panel].x,
        originY: panelOffsets[panel].y,
      };
      setDraggingPanel(panel);
    },
    [panelOffsets]
  );

  const onPanelResizeStart = React.useCallback(
    (panel: FloatingPanelKey, event: React.PointerEvent<HTMLDivElement>) => {
      stopCanvasDrag(event);
      panelResizeRef.current = {
        panel,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originWidth: panelSizes[panel].width,
        originHeight: panelSizes[panel].height,
      };
      setResizingPanel(panel);
    },
    [panelSizes]
  );

  const getPanelTransform = React.useCallback(
    (panel: FloatingPanelKey, collapsed: boolean) =>
      `${collapsed ? "translateX(calc(100% + 16px)) " : ""}translate(${panelOffsets[panel].x}px, ${panelOffsets[panel].y}px)`,
    [panelOffsets]
  );

  const refreshWorkspaceForPoll = React.useCallback(async () => {
    if (activeEnterpriseId === null) return;

    const requestedAtMutationVersion = localWorkspaceMutationVersionRef.current;
    const requestedWorkspaceFingerprint = getWorkspacePersistenceFingerprint(
      serializeWorkspaceRef.current()
    );
    try {
      const response = await api.get(`/enterprises/${activeEnterpriseId}/workspace`);
      const hasPendingLocalSave =
        window.localStorage.getItem(getWorkspacePendingSaveKey(activeEnterpriseId, selectedEnterprise)) === "true";
      const currentWorkspaceFingerprint = getWorkspacePersistenceFingerprint(
        serializeWorkspaceRef.current()
      );
      if (
        hasPendingLocalSave ||
        localWorkspaceMutationVersionRef.current !== requestedAtMutationVersion ||
        currentWorkspaceFingerprint !== requestedWorkspaceFingerprint ||
        currentWorkspaceFingerprint !== lastWorkspacePersistenceFingerprintRef.current
      ) {
        return;
      }
      window.localStorage.setItem(
        getWorkspaceCacheKey(activeEnterpriseId, selectedEnterprise),
        JSON.stringify(response.data.workspace ?? {})
      );
      skipNextWorkspaceAutosaveRef.current = true;
      workspaceUpdatedAtRef.current = typeof response.data.updated_at === "string" ? response.data.updated_at : workspaceUpdatedAtRef.current;
      applyWorkspaceRef.current(response.data.workspace, { resetUndoHistory: false, preserveView: true });
      hasLoadedEnterpriseRef.current = true;
    } catch (error) {
      console.error("Failed to refresh poll results", error);
    }
  }, [activeEnterpriseId, selectedEnterprise]);

  React.useEffect(
    () => subscribeToWorkspaceChanged(activeEnterpriseId, () => void refreshWorkspaceForPoll()),
    [activeEnterpriseId, refreshWorkspaceForPoll]
  );

  React.useEffect(() => {
    if (activeEnterpriseId === null) return;

    const refreshWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      void refreshWorkspaceForPoll();
    };

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [activeEnterpriseId, refreshWorkspaceForPoll]);

  const copyPollComments = React.useCallback(async () => {
    if (!pollEditorKpi || !pollEditorResults) return;

    const commentResponses = pollEditorResults.responses.filter(
      (response) => (response.comment ?? "").trim().length > 0
    );

    if (commentResponses.length === 0) {
      setPollCommentsCopyMessage("No comments to copy yet.");
      return;
    }

    const compiled = [
      `KPI: ${pollEditorKpi.data.label}`,
      `Poll: ${pollEditorKpi.data.pollConfig?.title ?? "Untitled Poll"}`,
      pollEditorKpi.data.pollConfig?.questionPrompt
        ? `Question: ${pollEditorKpi.data.pollConfig.questionPrompt}`
        : null,
      "",
      "Comments",
      ...commentResponses.map((response, index) => {
        const rating =
          typeof response.value === "number" || !Number.isNaN(Number(response.value))
            ? `Rating: ${Number(response.value)}/10`
            : null;
        const respondent = response.respondentLabel?.trim()
          ? response.respondentLabel.trim()
          : response.respondentGroup?.trim()
            ? response.respondentGroup.trim()
            : "Anonymous";

        return [
          `${index + 1}. ${respondent}`,
          rating,
          `Comment: ${(response.comment ?? "").trim()}`,
        ]
          .filter(Boolean)
          .join("\n");
      }),
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(compiled);
      setPollCommentsCopyMessage("Comments copied.");
    } catch (error) {
      console.error("Failed to copy poll comments", error);
      setPollCommentsCopyMessage("Could not copy comments.");
    }
  }, [pollEditorKpi, pollEditorResults]);

  const reportHref = activeEnterpriseId ? `/reports?enterpriseId=${activeEnterpriseId}` : "/reports";
  const dashboardHref = activeEnterpriseId
    ? `/dashboard?enterpriseId=${activeEnterpriseId}`
    : "/dashboard";
  const assimilationHref = activeEnterpriseId
    ? `/assimilation?enterpriseId=${activeEnterpriseId}`
    : "/assimilation";
  const supportEmail = "support@lensescpm.com";
  const supportHref = `mailto:${supportEmail}?subject=${encodeURIComponent("Lenses CPM support request")}`;
  const workspaceAgentProgressLabel = workspaceAgentOperation === "action"
    ? workspaceAgentElapsedSeconds < 3
      ? "Sending the approved action"
      : workspaceAgentElapsedSeconds < 12
        ? "Applying the requested changes"
        : "Waiting for the changes to finish"
    : workspaceAgentElapsedSeconds < 3
      ? "Sending your question"
      : workspaceAgentElapsedSeconds < 10
        ? "Planning the answer"
        : workspaceAgentElapsedSeconds < 30
          ? "Gathering and analyzing your data"
          : "Building the final response";
  const workspaceAgentElapsedLabel = workspaceAgentElapsedSeconds < 60
    ? `${workspaceAgentElapsedSeconds}s elapsed`
    : `${Math.floor(workspaceAgentElapsedSeconds / 60)}m ${workspaceAgentElapsedSeconds % 60}s elapsed`;
  const chromeButtonBase: React.CSSProperties = {
    boxSizing: "border-box",
    height: 34,
    border: "1px solid rgba(148,163,184,0.26)",
    borderRadius: 8,
    padding: "0 12px",
    color: "#cbd5e1",
    background: "rgba(15,23,42,0.72)",
    fontSize: 13,
    fontWeight: 800,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    cursor: "pointer",
  };
  const activeChromeButton: React.CSSProperties = {
    ...chromeButtonBase,
    borderColor: "rgba(34,197,94,0.55)",
    color: "#f8fafc",
    background: "rgba(34,197,94,0.18)",
    boxShadow: "0 0 22px rgba(34,197,94,0.18), inset 0 1px 0 rgba(255,255,255,0.05)",
  };
  const workspaceChromeNav = (
    <div
      className="nodrag nopan"
      onMouseDown={stopCanvasDrag}
      onPointerDown={stopCanvasDrag}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginLeft: "auto",
        flex: "0 0 auto",
      }}
    >
      <button
        type="button"
        className="nodrag nopan ask-lenses-trigger"
        onClick={() => setIsWorkspaceAgentOpen((previous) => !previous)}
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          ...chromeButtonBase,
          borderColor: isWorkspaceAgentOpen ? "rgba(34,197,94,0.55)" : "rgba(148,163,184,0.26)",
          color: isWorkspaceAgentOpen ? "#f8fafc" : "#cbd5e1",
          background: isWorkspaceAgentOpen ? "rgba(34,197,94,0.18)" : "rgba(15,23,42,0.72)",
        }}
      >
        <AskLensesBrand compact />
      </button>
      <button
        type="button"
        className="nodrag nopan"
        onClick={() => {
          setIsLensLibraryCollapsed((previous) => !previous);
          setIsReferenceLibraryCollapsed(true);
          setIsLibraryCollapsed(true);
        }}
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          ...chromeButtonBase,
          borderColor: isLensLibraryCollapsed ? "rgba(148,163,184,0.26)" : "rgba(96,165,250,0.52)",
          color: isLensLibraryCollapsed ? "#cbd5e1" : "#bfdbfe",
          background: isLensLibraryCollapsed ? "rgba(15,23,42,0.72)" : "rgba(37,99,235,0.18)",
        }}
      >
        Settings
      </button>
      <a
        href={supportHref}
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={chromeButtonBase}
      >
        Support
      </a>
      {currentUser ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          aria-label="Signed in account"
          style={{
            minHeight: 34,
            display: "flex",
            alignItems: "center",
            gap: 8,
            maxWidth: 300,
            minWidth: 0,
            border: "1px solid rgba(148,163,184,0.26)",
            borderRadius: 10,
            padding: "5px 6px 5px 10px",
            background: "rgba(8,13,20,0.82)",
            color: "#f8fafc",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                maxWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 12,
                fontWeight: 900,
                lineHeight: 1.1,
              }}
            >
              {currentUser.name || "Signed in"}
            </div>
            <div
              style={{
                maxWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 10,
                color: "#9ca3af",
                lineHeight: 1.1,
              }}
            >
              {currentUser.email || currentUser.role || ""}
            </div>
          </div>
          {onSignOut ? (
            <button
              type="button"
              className="nodrag nopan"
              onClick={onSignOut}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "1px solid rgba(148,163,184,0.38)",
                borderRadius: 8,
                background: "rgba(15,23,42,0.82)",
                color: "#e5e7eb",
                padding: "6px 8px",
                fontSize: 12,
                fontWeight: 900,
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          ) : null}
        </div>
      ) : null}
      <div style={{ display: "grid", gap: 4 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "#9ca3af",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          Views
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            padding: 3,
            borderRadius: 10,
            border: "1px solid rgba(148,163,184,0.26)",
            background: "rgba(8,13,20,0.82)",
          }}
          aria-label="Workspace views"
        >
          <span style={activeChromeButton}>Map</span>
          <a
            href={reportHref}
            className="nodrag nopan"
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{ ...chromeButtonBase, borderColor: "transparent", background: "transparent", color: "#e5e7eb" }}
          >
            Summary
          </a>
          <a
            href={dashboardHref}
            className="nodrag nopan"
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{ ...chromeButtonBase, borderColor: "transparent", background: "transparent", color: "#e5e7eb" }}
          >
            Dashboard
          </a>
          <a
            href={assimilationHref}
            className="nodrag nopan"
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{ ...chromeButtonBase, borderColor: "transparent", background: "transparent", color: "#e5e7eb" }}
          >
            Assimilation
          </a>
        </div>
      </div>
    </div>
  );

  const renderAiAddReview = (
    kind: AiAddKind,
    options: { kpiId?: string; initiativeId?: string } = {}
  ) => {
    const isSameDraft =
      aiAddDraft?.kind === kind &&
      aiAddDraft.kpiId === options.kpiId &&
      aiAddDraft.initiativeId === options.initiativeId;
    const isSameMessage =
      aiAddMessageTarget?.kind === kind &&
      aiAddMessageTarget.kpiId === options.kpiId &&
      aiAddMessageTarget.initiativeId === options.initiativeId;
    const isLoading = aiAddLoadingKind === kind && isSameMessage;

    if (!isSameDraft && !isSameMessage && !isLoading) return null;

    const title =
      aiAddDraft?.lens?.name ||
      aiAddDraft?.kpi?.label ||
      aiAddDraft?.initiative?.title ||
      aiAddDraft?.task?.title ||
      (isLoading ? "Thinking through a recommendation..." : "AI recommendation");
    const description =
      aiAddDraft?.lens?.description ||
      aiAddDraft?.lens?.rationale ||
      aiAddDraft?.kpi?.definition ||
      aiAddDraft?.initiative?.summary ||
      aiAddDraft?.summary ||
      aiAddMessage ||
      "";
    const taskCount = aiAddDraft?.initiative?.tasks?.length ?? 0;

    return (
      <div
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          border: "1px solid #bfdbfe",
          borderRadius: 12,
          padding: 12,
          background: "#eff6ff",
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: "#1d4ed8", textTransform: "uppercase" }}>
          AI recommendation
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#111827", lineHeight: 1.35 }}>
          {title}
        </div>
        {description ? (
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{description}</div>
        ) : null}
        {taskCount > 0 ? (
          <div style={{ display: "grid", gap: 4 }}>
            {(aiAddDraft?.initiative?.tasks ?? []).slice(0, 4).map((task) => (
              <div key={task.title} style={{ fontSize: 12, color: "#374151", lineHeight: 1.35 }}>
                - {task.title}
              </div>
            ))}
          </div>
        ) : null}
        {aiAddDraft?.task?.owner_role ? (
          <div style={{ fontSize: 12, color: "#4b5563" }}>Owner: {aiAddDraft.task.owner_role}</div>
        ) : null}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="nodrag nopan"
            disabled={!isSameDraft || isLoading}
            onClick={onAcceptAiAddDraft}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              border: "1px solid #16a34a",
              borderRadius: 10,
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 800,
              background: !isSameDraft || isLoading ? "#f9fafb" : "#f0fdf4",
              color: !isSameDraft || isLoading ? "#9ca3af" : "#166534",
              cursor: !isSameDraft || isLoading ? "not-allowed" : "pointer",
            }}
          >
            Accept
          </button>
          <button
            type="button"
            className="nodrag nopan"
            disabled={isLoading}
            onClick={() => onGenerateContextualAiDraft(kind, options)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              border: "1px solid #93c5fd",
              borderRadius: 10,
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 800,
              background: "#fff",
              color: "#1d4ed8",
              cursor: isLoading ? "wait" : "pointer",
            }}
          >
            {isLoading ? "Generating..." : "Try Again"}
          </button>
          <button
            type="button"
            className="nodrag nopan"
            onClick={() => {
              if (kind === "lens") {
                onAddLens();
              } else if (kind === "kpi") {
                onAddRootKpi();
              } else if (kind === "initiative" && options.kpiId) {
                onAddInitiative(options.kpiId);
              } else if (kind === "kpiTask" && options.kpiId) {
                onAddKpiTask(options.kpiId);
              } else if (kind === "initiativeTask" && options.kpiId && options.initiativeId) {
                onAddInitiativeTask(options.kpiId, options.initiativeId);
              }
              onClearAiAddDraft();
            }}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 10,
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 800,
              background: "#fff",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            Add Manually
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        background: "#000",
        position: "relative",
      }}
    >
      {isPollRecipientsOpen ? (
        <PollRecipientsEditor
          recipients={pollRecipients}
          pollAudienceGroups={pollAudienceGroups}
          onChange={setPollRecipients}
          onClose={() => setIsPollRecipientsOpen(false)}
        />
      ) : null}
      {pollEditorKpi?.data.pollConfig ? (
        <div
          onClick={() => setPollEditorKpiId(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.48)",
            display: "grid",
            placeItems: "center",
            padding: 24,
            zIndex: 1100,
          }}
        >
          <div
            className="nodrag nopan"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "min(760px, 100%)",
              maxHeight: "min(84vh, 880px)",
              overflow: "auto",
              background: "#fff",
              border: "2px solid #1d4ed8",
              borderRadius: 20,
              padding: 22,
              display: "grid",
              gap: 16,
              boxShadow: "0 30px 70px rgba(0,0,0,0.24)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>
                  {isPollViewMode ? "Polling Data" : "Edit Poll"}
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                  {isPollViewMode
                    ? "Review responses, comments, and respondent coverage for this poll."
                    : "Configure a fixed 1-10 rating poll with optional comments."}
                </div>
                <div style={{ fontSize: 13, color: "#1f2937", marginTop: 8 }}>
                  KPI: {pollEditorKpi.data.label || "Untitled KPI"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {pollEditorKpi.data.pollConfig ? (
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => setPollEditorMode(isPollViewMode ? "edit" : "view")}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 12,
                      padding: "10px 14px",
                      background: "#fff",
                      color: "#111827",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {isPollViewMode ? "Edit Poll" : "View Polling Data"}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="nodrag nopan"
                  onClick={() => setPollEditorKpiId(null)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "10px 14px",
                    background: "#fff",
                    color: "#111827",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            {isPollEditMode && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Poll Title
                </span>
                <input
                  value={pollEditorKpi.data.pollConfig!.title}
                  onChange={(event) =>
                    onPollConfigChange(pollEditorKpi.id, "title", event.target.value)
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                  }}
                />
              </label>
              <div
                style={{
                  display: "grid",
                  gap: 6,
                  alignContent: "start",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: "10px 12px",
                  background: "#f9fafb",
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Publish Status
                </span>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                  {formatEnumLabel(pollEditorKpi.data.pollConfig!.status)}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {pollEditorKpi.data.pollConfig!.publishedAt
                    ? `Published ${new Date(pollEditorKpi.data.pollConfig!.publishedAt).toLocaleString("en-US")}`
                    : "This poll is still in draft mode."}
                </div>
              </div>
              <label style={{ display: "grid", gap: 6, gridColumn: "1 / -1" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Poll Description
                </span>
                <textarea
                  value={pollEditorKpi.data.pollConfig!.description}
                  onChange={(event) =>
                    onPollConfigChange(pollEditorKpi.id, "description", event.target.value)
                  }
                  rows={3}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    resize: "vertical",
                  }}
                />
              </label>
              <label style={{ display: "grid", gap: 6, gridColumn: "1 / -1" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Question Prompt
                </span>
                <textarea
                  value={pollEditorKpi.data.pollConfig!.questionPrompt}
                  onChange={(event) =>
                    onPollConfigChange(pollEditorKpi.id, "questionPrompt", event.target.value)
                  }
                  rows={3}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    resize: "vertical",
                  }}
                />
              </label>
              <div
                style={{
                  gridColumn: "1 / -1",
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: "14px 16px",
                  background: "#f9fafb",
                  display: "grid",
                  gap: 10,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                    Included KPIs
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                    Send one poll that asks about multiple KPIs in the same response flow.
                  </div>
                </div>
                <div
                  style={{
                    border: "1px solid #dbe2ea",
                    borderRadius: 12,
                    padding: "10px 12px",
                    background: "#fff",
                    fontSize: 13,
                    color: "#111827",
                  }}
                >
                  This KPI is always included: {pollEditorKpi.data.label || "Untitled KPI"}
                </div>
                <input
                  className="nodrag nopan"
                  type="search"
                  value={pollKpiSearch}
                  placeholder="Search KPIs to add to this poll..."
                  onChange={(e) => setPollKpiSearch(e.target.value)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "10px 12px",
                    fontSize: 13,
                    background: "#fff",
                    color: "#111827",
                  }}
                />
                {pollEditorLinkedKpiOptions.length === 0 ? (
                  <div style={{ fontSize: 13, color: "#6b7280" }}>
                    {pollKpiSearch.trim().length > 0
                      ? "No KPIs match your search."
                      : "No other KPIs are available to add yet."}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {pollEditorLinkedKpiOptions.map((linkedKpi) => {
                      const isSelected = (pollEditorKpi.data.pollConfig?.linkedKpiIds ?? []).includes(
                        linkedKpi.id
                      );

                      return (
                        <label
                          key={linkedKpi.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            border: isSelected ? "2px solid #2563eb" : "1px solid #d1d5db",
                            borderRadius: 12,
                            padding: "12px 14px",
                            background: isSelected ? "#eff6ff" : "#fff",
                            color: "#111827",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onTogglePollLinkedKpi(pollEditorKpi.id, linkedKpi.id)}
                          />
                          {linkedKpi.label}
                        </label>
                      );
                    })}
                  </div>
                )}
                {pollCampaignSetup ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    {pollCampaignSetup.autoConvertKpis.length > 0 ? (
                      <div
                        style={{
                          borderRadius: 12,
                          padding: "12px 14px",
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          display: "grid",
                          gap: 4,
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#1d4ed8" }}>
                          These KPIs will switch to Poll when you publish.
                        </div>
                        <div style={{ fontSize: 12, lineHeight: 1.5, color: "#1e3a8a" }}>
                          {pollCampaignSetup.autoConvertKpis
                            .map((node) => node.data.label || "Untitled KPI")
                            .join(", ")}{" "}
                          will be normalized to Data Source: Poll and Value Type: Rating (1-10).
                        </div>
                      </div>
                    ) : null}
                    {pollCampaignSetup.missingTargetKpis.length > 0 ? (
                      <div
                        style={{
                          borderRadius: 12,
                          padding: "12px 14px",
                          background: "#fff7ed",
                          border: "1px solid #fdba74",
                          display: "grid",
                          gap: 4,
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#c2410c" }}>
                          Some KPIs do not have a target for this polling context.
                        </div>
                        <div style={{ fontSize: 12, lineHeight: 1.5, color: "#9a3412" }}>
                          Add targets before publishing if you want results to evaluate cleanly:{" "}
                          {pollCampaignSetup.missingTargetKpis
                            .map((node) => node.data.label || "Untitled KPI")
                            .join(", ")}
                          .
                        </div>
                      </div>
                    ) : null}
                    {pollCampaignSetup.mismatchedFrequencies ? (
                      <div
                        style={{
                          borderRadius: 12,
                          padding: "12px 14px",
                          background: "#fefce8",
                          border: "1px solid #fde68a",
                          display: "grid",
                          gap: 4,
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#a16207" }}>
                          Included KPIs use different reporting periods.
                        </div>
                        <div style={{ fontSize: 12, lineHeight: 1.5, color: "#854d0e" }}>
                          {Object.entries(pollCampaignSetup.frequencyGroups)
                            .map(([frequency, labels]) => `${formatEnumLabel(frequency)}: ${labels.join(", ")}`)
                            .join(" | ")}
                          . The poll can still run, but answers will write back into different reporting buckets.
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div
                style={{
                  display: "grid",
                  gap: 6,
                  alignContent: "start",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: "10px 12px",
                  background: "#f9fafb",
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Response Format
                </span>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                  1-10 rating
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Respondents can also leave comments with evidence or examples behind their rating.
                </div>
              </div>
              <div style={{ display: "grid", gap: 8, alignContent: "start" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Audience Groups
                </span>
                <div style={{ fontSize: 13, color: "#6b7280" }}>
                  Choose one or more groups to publish this poll to.
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 10,
                  gridColumn: "1 / -1",
                }}
              >
                {pollAudienceGroups.map((group) => {
                  const selectedGroups =
                    pollEditorKpi.data.pollConfig!.targetGroups.length > 0
                      ? pollEditorKpi.data.pollConfig!.targetGroups
                      : pollEditorKpi.data.pollConfig!.targetGroup
                        ? [pollEditorKpi.data.pollConfig!.targetGroup]
                        : [];
                  const isSelected = selectedGroups.includes(group);

                  return (
                    <label
                      key={group}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        border: isSelected ? "2px solid #2563eb" : "1px solid #d1d5db",
                        borderRadius: 12,
                        padding: "12px 14px",
                        background: isSelected ? "#eff6ff" : "#fff",
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onTogglePollAudienceGroup(pollEditorKpi.id, group)}
                      />
                      {group}
                    </label>
                  );
                })}
              </div>
              <div
                style={{
                  gridColumn: "1 / -1",
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: "14px 16px",
                  background: "#f9fafb",
                  display: "grid",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>Recipients</div>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                      People in the selected groups who will receive tracked invitation links.
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: "#374151" }}>
                    {(pollEditorKpi.data.pollConfig!.invites ?? []).length} tracked invite
                    {(pollEditorKpi.data.pollConfig!.invites ?? []).length === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#374151" }}>
                  {pollEditorRecipients.length} matched recipient
                  {pollEditorRecipients.length === 1 ? "" : "s"}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={openPollEmailComposer}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    disabled={(pollEditorKpi.data.pollConfig!.invites ?? []).filter((invite) => invite.recipientEmail?.trim()).length === 0}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "8px 10px",
                      background: "#fff",
                      color:
                        (pollEditorKpi.data.pollConfig!.invites ?? []).filter((invite) => invite.recipientEmail?.trim()).length === 0
                          ? "#9ca3af"
                          : "#111827",
                      fontWeight: 700,
                      cursor:
                        (pollEditorKpi.data.pollConfig!.invites ?? []).filter((invite) => invite.recipientEmail?.trim()).length === 0
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    Send Poll Emails
                  </button>
                </div>
                {isPollEmailComposerOpen ? (
                  <div
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 12,
                      padding: "12px 14px",
                      background: "#fff",
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
                      Poll Email Campaign
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Use placeholders like {"{first_name}"}, {"{name}"}, and {"{invite_url}"}.
                    </div>
                    <input
                      value={pollEmailSubject}
                      onChange={(event) => setPollEmailSubject(event.target.value)}
                      placeholder="Subject"
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14 }}
                    />
                    <textarea
                      value={pollEmailGreeting}
                      onChange={(event) => setPollEmailGreeting(event.target.value)}
                      rows={2}
                      placeholder="Greeting"
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                    />
                    <textarea
                      value={pollEmailBody}
                      onChange={(event) => setPollEmailBody(event.target.value)}
                      rows={5}
                      placeholder="Body"
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                    />
                    <textarea
                      value={pollEmailClosing}
                      onChange={(event) => setPollEmailClosing(event.target.value)}
                      rows={3}
                      placeholder="Closing"
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                    />
                    {pollEmailStatusMessage ? (
                      <div style={{ fontSize: 12, color: "#374151" }}>{pollEmailStatusMessage}</div>
                    ) : null}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="nodrag nopan"
                        onClick={() => setIsPollEmailComposerOpen(false)}
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "8px 10px",
                          background: "#fff",
                          color: "#111827",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Close Composer
                      </button>
                      <button
                        type="button"
                        className="nodrag nopan"
                        onClick={() => void sendPollEmailCampaign()}
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        disabled={isSendingPollEmails || pollEmailSubject.trim().length === 0}
                        style={{
                          border: "none",
                          borderRadius: 10,
                          padding: "8px 10px",
                          background: isSendingPollEmails ? "#9ca3af" : "#1d4ed8",
                          color: "#fff",
                          fontWeight: 800,
                          cursor: isSendingPollEmails ? "wait" : "pointer",
                        }}
                      >
                        {isSendingPollEmails ? "Sending..." : "Send to All Recipients"}
                      </button>
                    </div>
                  </div>
                ) : null}
                {(pollEditorKpi.data.pollConfig!.invites ?? []).length === 0 ? (
                  <div style={{ fontSize: 13, color: "#6b7280" }}>
                    No recipients are assigned to the selected groups yet. Add people in the Poll
                    Recipients section of Settings & Libraries.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {(pollEditorKpi.data.pollConfig!.invites ?? []).slice(0, 12).map((invite) => (
                      <div
                        key={invite.id}
                        style={{
                          border: invite.completedAt ? "2px solid #16a34a" : "1px solid #d1d5db",
                          borderRadius: 12,
                          padding: "10px 12px",
                          background: invite.completedAt ? "#f0fdf4" : "#fff",
                          display: "grid",
                          gap: 8,
                          boxShadow: invite.completedAt
                            ? "0 0 0 2px rgba(22, 163, 74, 0.08)"
                            : "none",
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
                          {invite.recipientName || "Unnamed recipient"}
                        </div>
                        <div style={{ fontSize: 12, color: "#4b5563" }}>{invite.recipientEmail}</div>
                        <div
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            background: "#f9fafb",
                            fontSize: 12,
                            color: "#111827",
                            wordBreak: "break-all",
                          }}
                        >
                          {getPollInviteUrl(invite.token)}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {invite.recipientEmail ? (
                            <button
                              type="button"
                              className="nodrag nopan"
                              onClick={() => emailPollInvitation(invite)}
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                border: "none",
                                borderRadius: 10,
                                padding: "8px 10px",
                                background: "#111827",
                                color: "#fff",
                                fontWeight: 800,
                                cursor: "pointer",
                              }}
                            >
                              Email Invite
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="nodrag nopan"
                            onClick={() =>
                              void navigator.clipboard.writeText(getPollInviteUrl(invite.token))
                            }
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 10px",
                              background: "#fff",
                              color: "#111827",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Copy Link
                          </button>
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          {invite.completedAt
                            ? `Responded ${new Date(invite.completedAt).toLocaleString("en-US")}`
                            : invite.recipientEmail
                              ? "No response yet"
                              : "Share this link directly with your audience"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  gridColumn: "1 / -1",
                  fontSize: 14,
                  color: "#111827",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: "12px 14px",
                  background: "#f9fafb",
                }}
              >
                <input
                  type="checkbox"
                  checked={pollEditorKpi.data.pollConfig!.isAnonymous}
                  onChange={(event) =>
                    onPollConfigChange(pollEditorKpi.id, "isAnonymous", event.target.checked)
                  }
                />
                Anonymous responses
              </label>
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: 12,
                }}
              >
                <div style={{ fontSize: 13, color: "#6b7280" }}>
                  {pollEditorSelectedGroups.length} audience groups selected
                </div>
                {pollPublishContext ? (
                  <div
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      padding: "12px 14px",
                      background:
                        pollPublishContext.tone === "warning" ? "#fef2f2" : "#eff6ff",
                      border:
                        pollPublishContext.tone === "warning"
                          ? "1px solid #fecaca"
                          : "1px solid #bfdbfe",
                      display: "grid",
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color:
                          pollPublishContext.tone === "warning" ? "#b91c1c" : "#1d4ed8",
                      }}
                    >
                      {pollPublishContext.summary}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        color:
                          pollPublishContext.tone === "warning" ? "#7f1d1d" : "#1e3a8a",
                      }}
                    >
                      {pollPublishContext.detail}
                    </div>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={
                    pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                  }
                  onClick={() => onPublishPoll(pollEditorKpi.id)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "10px 14px",
                    background:
                      pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                        ? "#9ca3af"
                        : "#1d4ed8",
                    color: "#fff",
                    fontWeight: 800,
                    cursor:
                      pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
	                  Publish Poll
	                </button>
                  {pollEditorKpi.data.pollConfig?.status === "published" ||
                  (pollEditorKpi.data.pollConfig?.responses.length ?? 0) > 0 ||
                  (pollEditorKpi.data.pollConfig?.invites.length ?? 0) > 0 ? (
                    <button
                      type="button"
                      className="nodrag nopan"
                      disabled={
                        pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                      }
                      onClick={() => onRerunPoll(pollEditorKpi.id)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 12,
                        padding: "10px 14px",
                        background: "#fff",
                        color:
                          pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                            ? "#9ca3af"
                            : "#111827",
                        fontWeight: 800,
                        cursor:
                          pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      Re-run Poll
                    </button>
                  ) : null}
	              </div>
	            </div>
	            )}
	              <div
                style={{
                  gridColumn: "1 / -1",
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: 16,
                  display: "grid",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800 }}>Poll Results</div>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                      Review how many responses have come in and what people are saying.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <div
                      style={{
                        border: "1px solid #dbeafe",
                        borderRadius: 999,
                        padding: "6px 10px",
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {pollEditorResults?.responseCount ?? 0} responses received
                    </div>
                    <button
                      type="button"
                      className="nodrag nopan"
                      onClick={() => void refreshWorkspaceForPoll()}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 10px",
                        background: "#fff",
                        color: "#111827",
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Refresh Results
                    </button>
                    <button
                      type="button"
                      className="nodrag nopan"
                      onClick={() => void copyPollComments()}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 10px",
                        background: "#fff",
                        color: "#111827",
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Copy Comments
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: "14px 16px",
                      background: "#fff",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                      Total Responses
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, marginTop: 8 }}>
                      {pollEditorResults?.responseCount ?? 0}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: "14px 16px",
                      background: "#fff",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                      Results Summary
                    </div>
                          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 8 }}>
                            {pollEditorResults?.headline ?? "No data yet"}
                          </div>
                          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                            {pollEditorResults?.detail ?? "Publish the poll and collect responses."}
                          </div>
                          {pollCommentsCopyMessage ? (
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                              {pollCommentsCopyMessage}
                            </div>
                          ) : null}
                          {pollEditorResults?.commentSummaryShort ? (
                            <div
                              style={{
                                marginTop: 10,
                                paddingTop: 10,
                                borderTop: "1px solid #e5e7eb",
                                fontSize: 13,
                                color: "#374151",
                                lineHeight: 1.5,
                                whiteSpace: "pre-line",
                              }}
                            >
                              <span style={{ fontWeight: 800, color: "#111827" }}>AI Summary:</span>{" "}
                              {isPollSummaryExpanded && pollEditorResults.commentSummaryLong
                                ? pollEditorResults.commentSummaryLong
                                : pollEditorResults.commentSummaryShort}
                              {pollEditorResults.commentSummaryLong &&
                              pollEditorResults.commentSummaryLong !== pollEditorResults.commentSummaryShort ? (
                                <div style={{ marginTop: 8 }}>
                                  <button
                                    type="button"
                                    className="nodrag nopan"
                                    onClick={() => setIsPollSummaryExpanded((current) => !current)}
                                    onMouseDown={stopCanvasDrag}
                                    onPointerDown={stopCanvasDrag}
                                    style={{
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "6px 10px",
                                      background: "#fff",
                                      color: "#111827",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: "pointer",
                                    }}
                                  >
                                    {isPollSummaryExpanded ? "Show Less" : "Show Full Summary"}
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>

                {isPollViewMode ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        padding: "14px 16px",
                        background: "#fff",
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Comments Received
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 800, marginTop: 8 }}>
                        {pollEditorResults?.commentCount ?? 0}
                      </div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                        Responses with written feedback
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        padding: "14px 16px",
                        background: "#fff",
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Response Coverage
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, marginTop: 8 }}>
                        {pollEditorResults?.completedInviteCount ?? 0} responded
                      </div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                        {pollEditorResults?.pendingInviteCount ?? 0} still pending
                      </div>
                    </div>
                  </div>
                ) : null}

                {isPollViewMode ? (
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: "14px 16px",
                      background: "#fff",
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                      Question
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.5 }}>
                      {pollEditorKpi.data.pollConfig!.questionPrompt}
                    </div>
                  </div>
                ) : null}

                {(pollEditorResults?.responses.length ?? 0) === 0 ? (
                  <div
                    style={{
                      border: "1px dashed #d1d5db",
                      borderRadius: 14,
                      padding: "18px 16px",
                      background: "#f9fafb",
                      color: "#6b7280",
                      fontSize: 14,
                    }}
                  >
                    No poll responses have been received yet. Once people submit answers, their
                    results will appear here.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>Recent Responses</div>
                    {pollEditorResults?.responses.slice(0, 6).map((response) => (
                      <div
                        key={response.id}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 14,
                          padding: "12px 14px",
                          background: "#fff",
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                            {pollEditorKpi.data.pollConfig!.isAnonymous
                              ? "Anonymous response"
                              : response.respondentLabel || "Unnamed respondent"}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {response.submittedAt
                              ? new Date(response.submittedAt).toLocaleString("en-US")
                              : "No timestamp"}
                          </div>
                        </div>
                        <div style={{ fontSize: 14, color: "#1f2937" }}>
                          {formatPollResponseValue(response.value)}
                        </div>
                        {(response.comment ?? "").trim().length > 0 ? (
                          <div
                            style={{
                              borderTop: "1px solid #e5e7eb",
                              paddingTop: 8,
                              fontSize: 13,
                              color: "#374151",
                              lineHeight: 1.5,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            Comment: {response.comment}
                          </div>
                        ) : null}
                        {response.respondentGroup ? (
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Group: {response.respondentGroup}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
	                )}
	              </div>
	            </div>
	        </div>
	      ) : null}
      <div
        className="map-top-toolbar"
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          right: 16,
          zIndex: 10,
          display: "grid",
          gap: 12,
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(8,13,20,0.94) 100%)",
          borderRadius: 24,
          border: "1px solid rgba(148,163,184,0.24)",
          padding: 16,
          boxShadow:
            "0 18px 44px rgba(0,0,0,0.36), 0 0 46px rgba(34,197,94,0.08), inset 0 1px 0 rgba(255,255,255,0.06)",
          backdropFilter: "blur(10px)",
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "2px 4px 2px 0",
            justifyContent: "space-between",
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <img
            src={brandLogoUrl}
            alt="Lenses logo"
            style={{
              width: 60,
              height: 60,
              objectFit: "contain",
              clipPath: "inset(3px 0 0 0)",
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: "#22c55e",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                lineHeight: 1.1,
              }}
            >
              Lenses
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: "#f8fafc",
                lineHeight: 1.15,
                whiteSpace: "nowrap",
              }}
            >
              Map View
            </div>
          </div>
          {workspaceChromeNav}
        </div>
        <div
          className="map-filter-toolbar"
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <div className="map-filter-control" style={{ display: "grid", gap: 4, justifyItems: "center" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
              }}
            >
              Enterprise
            </div>
            <select
              className="nodrag nopan map-filter-select"
              value={selectedEnterprise ? String(selectedEnterprise.id) : ""}
              onChange={(e) => {
                hasLoadedEnterpriseRef.current = false;
                setActiveEnterpriseId(Number(e.target.value));
              }}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              disabled={isEnterpriseLoading || enterprises.length === 0}
              style={{
                boxSizing: "border-box",
                border: "1px solid rgba(148,163,184,0.28)",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 14,
                background: "rgba(8,13,20,0.92)",
                color: "#f8fafc",
                minWidth: 180,
                maxWidth: 220,
              }}
            >
              <option value="" disabled>
                Enterprise
              </option>
              {enterprises.map((enterprise) => (
                <option key={enterprise.id} value={String(enterprise.id)}>
                  {enterprise.name}
                </option>
              ))}
            </select>
          </div>
          <div className="map-filter-control" style={{ display: "grid", gap: 4, justifyItems: "center" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
              }}
            >
              Lens
            </div>
            <select
              className="nodrag nopan map-filter-select"
              value={activeLens}
              onChange={(e) => onLensChange(e.target.value)}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                boxSizing: "border-box",
                border: "1px solid rgba(148,163,184,0.28)",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 14,
                background: "rgba(8,13,20,0.92)",
                color: "#f8fafc",
                minWidth: 160,
                maxWidth: 220,
              }}
            >
              {availableLenses.map((lens) => (
                <option key={lens} value={lens}>
                  {lensConfigs[lens]?.name ?? lens}
                </option>
              ))}
            </select>
          </div>

          <div className="map-filter-control" style={{ display: "grid", gap: 4, justifyItems: "center" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
              }}
            >
              Reporting Week
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "40px 1fr 40px",
                gap: 8,
                alignItems: "center",
                minWidth: 210,
              }}
            >
              <button
                className="nodrag nopan map-period-step"
                type="button"
                onClick={() => onReferenceDateStep("previous")}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid rgba(148,163,184,0.28)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 14,
                  background: "rgba(8,13,20,0.92)",
                  color: "#f8fafc",
                  cursor: "pointer",
                }}
              >
                {"<"}
              </button>
              <div
                className="map-period-value"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid rgba(148,163,184,0.28)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                  textAlign: "center",
                  fontWeight: 700,
                  color: "#f8fafc",
                  background: "rgba(15,23,42,0.86)",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {`Wk ${displayedWeekParts.week}, ${displayedWeekParts.year} | ${formatReferenceDate(displayedWeekStartDate)}`}
              </div>
              <button
                className="nodrag nopan map-period-step"
                type="button"
                onClick={() => onReferenceDateStep("next")}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid rgba(148,163,184,0.28)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 14,
                  background: "rgba(8,13,20,0.92)",
                  color: "#f8fafc",
                  cursor: "pointer",
                }}
              >
                {">"}
              </button>
            </div>
          </div>

          <div
            className="map-filter-control"
            style={{
              display: "grid",
              gap: 4,
              justifyItems: "center",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
              }}
            >
              Aggregation
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <select
                className="nodrag nopan map-filter-select"
                value={aggregationMode}
                onChange={(e) => onAggregationModeChange(e.target.value as AggregationMode)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  boxSizing: "border-box",
                  border: "1px solid rgba(148,163,184,0.28)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                  background: "rgba(8,13,20,0.92)",
                  color: "#f8fafc",
                  minWidth: 130,
                }}
              >
                {aggregationModeOptions.map((option) => (
                  <option key={option} value={option}>
                    {formatAggregationLabel(option)}
                  </option>
                ))}
              </select>
              <InlineHelp text="Aggregation controls the time window used for the selected date. Counts and currency sum across the window, while ratings and percentages average. Example: if weekly attendance is 120, 140, and 160, MTD shows 420. If satisfaction scores are 8 and 9, MTD shows 8.5." />
            </div>
          </div>
          <div className="map-filter-control" style={{ display: "grid", gap: 4, justifyItems: "center" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
              }}
            >
              Comparison
            </div>
            <select
              className="nodrag nopan map-filter-select"
              value={comparisonMode}
              onChange={(e) => onComparisonModeChange(e.target.value as ComparisonMode)}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                boxSizing: "border-box",
                border: "1px solid rgba(148,163,184,0.28)",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 13,
                background: "rgba(8,13,20,0.92)",
                color: "#f8fafc",
                minWidth: 150,
              }}
            >
              {comparisonModeOptions.map((option) => (
                <option key={option} value={option}>
                  {formatComparisonLabel(option)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isWorkspaceLoading ? (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#1d4ed8",
              padding: "0 4px",
            }}
          >
            Loading saved workspace...
          </div>
        ) : null}
        {loadError ? (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontSize: 12,
              fontWeight: 700,
              color: "#b91c1c",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              padding: "8px 10px",
              maxWidth: 420,
            }}
          >
            <span style={{ flex: 1 }}>{loadError}</span>
            <button
              type="button"
              className="nodrag nopan"
              onClick={() => setLoadError(null)}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              aria-label="Dismiss notification"
              style={{
                border: "none",
                background: "transparent",
                color: "#991b1b",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 900,
                lineHeight: 1,
                padding: "2px 0 2px 6px",
              }}
            >
              Dismiss
            </button>
          </div>
        ) : null}
      </div>

      {isWorkspaceAgentOpen ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "fixed",
            right: 24,
            bottom: 24,
            zIndex: 900,
            width: "min(420px, calc(100vw - 48px))",
            borderRadius: 18,
            border: "1px solid rgba(148,163,184,0.28)",
            background:
              "linear-gradient(180deg, rgba(15,23,42,0.97) 0%, rgba(8,13,20,0.97) 100%)",
            boxShadow:
              "0 24px 70px rgba(0,0,0,0.42), 0 0 44px rgba(34,197,94,0.10), inset 0 1px 0 rgba(255,255,255,0.06)",
            color: "#f8fafc",
            padding: 16,
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <AskLensesBrand />
              <div style={{ fontSize: 17, fontWeight: 900, lineHeight: 1.2 }}>
                Ask about KPIs, work, and finance
              </div>
            </div>
            <button
              type="button"
              className="nodrag nopan"
              onClick={() => setIsWorkspaceAgentOpen(false)}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              aria-label="Close agent"
              style={{
                border: "1px solid rgba(148,163,184,0.28)",
                borderRadius: 8,
                background: "rgba(15,23,42,0.72)",
                color: "#cbd5e1",
                width: 32,
                height: 32,
                cursor: "pointer",
                fontWeight: 900,
              }}
            >
              X
            </button>
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void askWorkspaceAgent();
            }}
            style={{ display: "grid", gap: 10 }}
          >
            <textarea
              className="nodrag nopan"
              value={workspaceAgentPrompt}
              onChange={(event) => setWorkspaceAgentPrompt(event.target.value)}
              onKeyDown={handleWorkspaceAgentPromptKeyDown}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              placeholder="Example: What were Beth Emanuel event expenses in Q1 2026?"
              rows={3}
              style={{
                width: "100%",
                boxSizing: "border-box",
                resize: "vertical",
                minHeight: 78,
                border: "1px solid rgba(148,163,184,0.28)",
                borderRadius: 12,
                background: "rgba(8,13,20,0.92)",
                color: "#f8fafc",
                padding: 10,
                fontSize: 14,
                lineHeight: 1.45,
              }}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="submit"
                className="nodrag nopan"
                disabled={isWorkspaceAgentLoading}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid rgba(34,197,94,0.55)",
                  borderRadius: 10,
                  background: isWorkspaceAgentLoading ? "rgba(75,85,99,0.7)" : "rgba(34,197,94,0.2)",
                  color: "#f8fafc",
                  padding: "9px 12px",
                  fontSize: 13,
                  fontWeight: 900,
                  cursor: isWorkspaceAgentLoading ? "wait" : "pointer",
                }}
              >
                {isWorkspaceAgentLoading ? "Working..." : <AskLensesBrand compact />}
              </button>
            </div>
            {isWorkspaceAgentLoading ? (
              <div
                className="ask-lenses-progress"
                role="status"
                aria-live="polite"
                aria-label={`${workspaceAgentProgressLabel}. ${workspaceAgentElapsedLabel}.`}
              >
                <div className="ask-lenses-progress-copy">
                  <span>{workspaceAgentProgressLabel}</span>
                  <span>{workspaceAgentElapsedLabel}</span>
                </div>
                <div
                  className="ask-lenses-progress-track"
                  role="progressbar"
                  aria-label="Ask Lenses request in progress"
                  aria-valuetext="In progress; completion percentage is not available"
                >
                  <span />
                </div>
                {workspaceAgentElapsedSeconds >= 10 ? (
                  <div className="ask-lenses-progress-note">
                    This request is taking a little longer because it may need to check multiple data sources.
                    You can leave this panel open while it finishes.
                  </div>
                ) : null}
              </div>
            ) : null}
          </form>
          {workspaceAgentHistory.length > 0 ? (
            <div
              style={{
                display: "grid",
                gap: 8,
                maxHeight: 220,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {workspaceAgentHistory.slice(-6).map((message, index) => (
                <div
                  key={`${message.role}-${index}-${message.content.slice(0, 16)}`}
                  style={{
                    justifySelf: message.role === "user" ? "end" : "start",
                    maxWidth: "92%",
                    border: "1px solid rgba(148,163,184,0.22)",
                    borderRadius: 12,
                    background:
                      message.role === "user"
                        ? "rgba(34,197,94,0.14)"
                        : "rgba(15,23,42,0.76)",
                    color: "#e5e7eb",
                    padding: "8px 10px",
                    fontSize: 13,
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {message.content}
                </div>
              ))}
            </div>
          ) : null}
          {workspaceAgentResponse?.visualization ? (
            <AgentVisualization data={workspaceAgentResponse.visualization} />
          ) : null}
          {workspaceAgentResponse &&
          ((workspaceAgentResponse.links ?? []).length > 0 || workspaceAgentResponse.action) ? (
            <div style={{ display: "grid", gap: 10 }}>
              {(workspaceAgentResponse.links ?? []).length > 0 ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {(workspaceAgentResponse.links ?? []).map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      className="nodrag nopan"
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid rgba(96,165,250,0.55)",
                        borderRadius: 10,
                        background: "rgba(37,99,235,0.18)",
                        color: "#bfdbfe",
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 900,
                        textDecoration: "none",
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              ) : null}
              {workspaceAgentResponse.action ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={isWorkspaceAgentLoading || workspaceAgentResponse.action.target_count === 0}
                  onClick={() => void executeWorkspaceAgentAction(workspaceAgentResponse.action!)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid rgba(248,113,113,0.6)",
                    borderRadius: 10,
                    background:
                      isWorkspaceAgentLoading || workspaceAgentResponse.action.target_count === 0
                        ? "rgba(75,85,99,0.62)"
                        : "rgba(127,29,29,0.34)",
                    color: "#fecaca",
                    padding: "9px 12px",
                    fontSize: 13,
                    fontWeight: 900,
                    cursor:
                      isWorkspaceAgentLoading || workspaceAgentResponse.action.target_count === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {workspaceAgentResponse.action.label}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!isLensLibraryCollapsed ? (
        <div
          className="nodrag nopan"
          onClick={() => setIsLensLibraryCollapsed(true)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.52)",
            zIndex: 999,
          }}
        />
      ) : null}

      <div
        className="nodrag nopan settings-library-surface"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          width: "min(920px, calc(100vw - 48px))",
          maxHeight: "min(82vh, 780px)",
          zIndex: 1000,
          background: "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(8,13,20,0.94) 100%)",
          borderRadius: 18,
          border: "1px solid rgba(148,163,184,0.24)",
          boxShadow:
            "0 24px 70px rgba(0,0,0,0.48), 0 0 46px rgba(34,197,94,0.08), inset 0 1px 0 rgba(255,255,255,0.06)",
          padding: 22,
          overflowY: "auto",
          color: "#f8fafc",
          backdropFilter: "blur(14px)",
          transform: isLensLibraryCollapsed
            ? "translate(-50%, -50%) scale(0.98)"
            : "translate(-50%, -50%)",
          opacity: isLensLibraryCollapsed ? 0 : 1,
          pointerEvents: isLensLibraryCollapsed ? "none" : "auto",
          transition: "transform 180ms ease, opacity 180ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            margin: "-22px -22px 16px",
            padding: "22px 22px 16px",
            position: "sticky",
            top: -22,
            background: "rgba(8,13,20,0.92)",
            borderBottom: "1px solid rgba(148,163,184,0.22)",
            zIndex: 2,
            backdropFilter: "blur(12px)",
          }}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#f8fafc",
            }}
          >
            Settings & Libraries
          </div>
          <button
            type="button"
            className="nodrag nopan"
            onClick={() => setIsLensLibraryCollapsed(true)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              border: "1px solid rgba(148,163,184,0.34)",
              borderRadius: 10,
              padding: "8px 12px",
              background: "rgba(15,23,42,0.82)",
              color: "#e5e7eb",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#cbd5e1",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          Open only the areas you want to work in.
        </div>

        <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
        <button
          type="button"
          className="nodrag nopan"
          onClick={() => setIsApiSettingsCollapsed((previous) => !previous)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            border: "1px solid rgba(148,163,184,0.28)",
            borderRadius: 12,
            padding: "12px 14px",
            fontSize: 14,
            fontWeight: 900,
            background: isApiSettingsCollapsed ? "rgba(15,23,42,0.78)" : "rgba(34,197,94,0.14)",
            color: "#f8fafc",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
            boxShadow: isApiSettingsCollapsed ? "none" : "0 0 22px rgba(34,197,94,0.12)",
          }}
        >
          <span>API Integrations</span>
          <span style={{ color: "#cbd5e1" }}>{isApiSettingsCollapsed ? "Show" : "Hide"}</span>
        </button>

        <div
          style={{
            display: isApiSettingsCollapsed ? "none" : "grid",
            gap: 10,
            marginBottom: 18,
            border: "1px solid rgba(148,163,184,0.22)",
            borderRadius: 16,
            padding: 12,
            background: "rgba(8,13,20,0.58)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <div
            style={{
              border: "1px solid rgba(34,197,94,0.28)",
              borderRadius: 12,
              background: "rgba(22,101,52,0.12)",
              padding: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "grid", gap: 3 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#dcfce7" }}>
                Mapped KPI data sync
              </div>
              <div style={{ fontSize: 12, color: "#bbf7d0", lineHeight: 1.35 }}>
                Updates all mapped Pushpay, CCB, and QuickBooks KPI actuals.
              </div>
            </div>
            <button
              type="button"
              className="nodrag nopan"
              disabled={isPushpayLoading}
              onClick={() => void onSyncAllPushpayApiKpis()}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "1px solid #16a34a",
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: 900,
                background: !isPushpayLoading ? "#16a34a" : "#e5e7eb",
                color: !isPushpayLoading ? "#fff" : "#6b7280",
                cursor: !isPushpayLoading ? "pointer" : "not-allowed",
              }}
            >
              {isPushpayLoading ? "Syncing..." : "Sync KPI Data"}
            </button>
          </div>
          {currentUserHasLegacyAdminRole && activeEnterpriseId ? (
            <SyncHealthPanel enterpriseId={activeEnterpriseId} collapsible beforeAction={persistLatestWorkspace} />
          ) : null}
          <button
            type="button"
            className="nodrag nopan"
            onClick={() => {
              if (pushpayStatus?.configured === false) {
                setPushpayMessage(
                  "Pushpay ChMS needs backend setup before it can connect. Set PUSHPAY_CHMS_CLIENT_ID, PUSHPAY_CHMS_CLIENT_SECRET, and PUSHPAY_CHMS_ENCRYPTION_KEY, or share QUICKBOOKS_ENCRYPTION_KEY."
                );
              }
            }}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              border: "1px solid rgba(148,163,184,0.28)",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 14,
              fontWeight: 800,
              background: "rgba(15,23,42,0.82)",
              color: "#f8fafc",
              cursor: pushpayStatus?.configured === false ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Pushpay / CCB ChMS</span>
            <ApiConnectionStatusBadge
              connected={pushpayStatus?.connected}
              configured={pushpayStatus?.configured}
            />
          </button>
          <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
            <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.5 }}>
              Connect this enterprise to ChMS data for people, groups, attendance, giving, and CCB process queue KPI workflows.
            </div>
            <div
              style={{
                border: "1px solid rgba(59,130,246,0.28)",
                borderRadius: 10,
                background: "rgba(30,64,175,0.12)",
                padding: 10,
                display: "grid",
                gap: 5,
                fontSize: 11,
                color: "#dbeafe",
                lineHeight: 1.35,
              }}
            >
              <div>
                KPI sources: campus weekend attendance, event attendance, group attendance, and People &gt; Processes queue counts.
              </div>
              <div>
                Process queue counts support process filter, queue filter, and queue status: any, waiting, in process, done, or not started.
              </div>
            </div>
            {pushpayStatus?.connected ? (
              <div style={{ fontSize: 12, color: "#e5e7eb", lineHeight: 1.5 }}>
                Token expires:{" "}
                <strong>
                  {pushpayStatus.token_expires_at
                    ? new Date(pushpayStatus.token_expires_at).toLocaleString()
                    : "Unknown"}
                </strong>
              </div>
            ) : null}
            {pushpayMessage ? (
              <div style={{ fontSize: 12, fontWeight: 700, color: "#93c5fd", lineHeight: 1.4 }}>
                {pushpayMessage}
              </div>
            ) : null}
            {pushpayStatus ? (
              <div
                style={{
                  border: "1px solid rgba(148,163,184,0.24)",
                  borderRadius: 10,
                  background: "rgba(15,23,42,0.72)",
                  padding: 10,
                  display: "grid",
                  gap: 4,
                  fontSize: 11,
                  color: "#cbd5e1",
                  lineHeight: 1.35,
                }}
              >
                <div>
                  CCB subdomain: <strong>{pushpayStatus.subdomain || "not set"}</strong>
                </div>
                <div>
                  Redirect: <strong>{pushpayStatus.redirect_uri || "not set"}</strong>
                </div>
                <div>
                  Client ID: <strong>{pushpayStatus.client_id_preview || "not set"}</strong>
                </div>
                <div>
                  Requested scopes: <strong>{pushpayStatus.scope_preview || "not set"}</strong>
                </div>
                {pushpayStatus.connected ? (
                  <>
                    <div>
                      Granted scopes: <strong>{pushpayStatus.granted_scope || "not reported"}</strong>
                    </div>
                    <div>
                      read:groups:{" "}
                      <strong
                        style={{
                          color: pushpayStatus.granted_scope?.split(/\s+/).includes("read:groups")
                            ? "#86efac"
                            : "#fca5a5",
                        }}
                      >
                        {pushpayStatus.granted_scope?.split(/\s+/).includes("read:groups")
                          ? "available"
                          : "missing - reconnect Pushpay"}
                      </strong>
                    </div>
                    <div>
                      read:scheduling:{" "}
                      <strong
                        style={{
                          color: pushpayStatus.granted_scope?.split(/\s+/).includes("read:scheduling")
                            ? "#86efac"
                            : "#fca5a5",
                        }}
                      >
                        {pushpayStatus.granted_scope?.split(/\s+/).includes("read:scheduling")
                          ? "available"
                          : "missing - reconnect Pushpay"}
                      </strong>
                    </div>
                  </>
                ) : null}
                {pushpayStatus.authorization_url ? (
                  <div style={{ overflowWrap: "anywhere" }}>
                    Auth URL: <strong>{pushpayStatus.authorization_url}</strong>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <button
                type="button"
                className="nodrag nopan"
                disabled={isPushpayLoading}
                onClick={onConnectPushpay}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #111827",
                  borderRadius: 10,
                  padding: "9px 12px",
                  fontSize: 12,
                  fontWeight: 800,
                  background: isPushpayLoading ? "#e5e7eb" : "#111827",
                  color: isPushpayLoading ? "#6b7280" : "#fff",
                  cursor: isPushpayLoading ? "not-allowed" : "pointer",
                }}
              >
                {pushpayStatus?.connected
                  ? "Connect Pushpay"
                  : pushpayStatus?.configured === false
                    ? "Setup Pushpay"
                    : "Connect Pushpay"}
              </button>
              <button
                type="button"
                className="nodrag nopan"
                disabled={isPushpayLoading || !pushpayStatus?.connected}
                onClick={onTestPushpayIndividuals}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  padding: "9px 12px",
                  fontSize: 12,
                  fontWeight: 800,
                  background: "#fff",
                  color: pushpayStatus?.connected ? "#111827" : "#9ca3af",
                  cursor: pushpayStatus?.connected ? "pointer" : "not-allowed",
                }}
              >
                Test people
              </button>
              {pushpayStatus?.connected ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={isPushpayLoading}
                  onClick={onDisconnectPushpay}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #fecaca",
                    borderRadius: 10,
                    padding: "9px 12px",
                    fontSize: 12,
                    fontWeight: 800,
                    background: "#fff",
                    color: "#b91c1c",
                    cursor: isPushpayLoading ? "not-allowed" : "pointer",
                  }}
                >
                  Disconnect
                </button>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="nodrag nopan"
            onClick={() => {
              if (quickBooksStatus?.configured === false) {
                setQuickBooksMessage(
                  "QuickBooks needs backend setup before it can connect. Set QUICKBOOKS_CLIENT_ID, QUICKBOOKS_CLIENT_SECRET, and QUICKBOOKS_ENCRYPTION_KEY on the backend service."
                );
              }
            }}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 14,
              fontWeight: 800,
              background: "rgba(15,23,42,0.82)",
              color: "#f8fafc",
              cursor: quickBooksStatus?.configured === false ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>QuickBooks</span>
            <ApiConnectionStatusBadge
              connected={quickBooksStatus?.connected}
              configured={quickBooksStatus?.configured}
            />
          </button>
          <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
            <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.5 }}>
              Connect this enterprise to QuickBooks Online. Detailed finance import tools stay in support diagnostics
              while mapped KPI syncs use saved API presets.
            </div>
            {quickBooksStatus?.connected ? (
              <div style={{ fontSize: 12, color: "#e5e7eb", lineHeight: 1.5 }}>
                Company realm: <strong>{quickBooksStatus.realm_id}</strong>
              </div>
            ) : null}
            {quickBooksMessage ? (
              <div style={{ fontSize: 12, fontWeight: 700, color: "#93c5fd", lineHeight: 1.4 }}>
                {quickBooksMessage}
              </div>
            ) : null}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <button
                type="button"
                className="nodrag nopan"
                disabled={isQuickBooksLoading}
                onClick={onConnectQuickBooks}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #111827",
                  borderRadius: 10,
                  padding: "9px 12px",
                  fontSize: 12,
                  fontWeight: 800,
                  background: isQuickBooksLoading ? "#e5e7eb" : "#111827",
                  color: isQuickBooksLoading ? "#6b7280" : "#fff",
                  cursor: isQuickBooksLoading ? "not-allowed" : "pointer",
                }}
              >
                {quickBooksStatus?.connected
                  ? "Connect QuickBooks"
                  : quickBooksStatus?.configured === false
                    ? "Setup QuickBooks"
                    : "Connect QuickBooks"}
              </button>
              {showQuickBooksDiagnostics ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={isQuickBooksLoading || !quickBooksStatus?.connected}
                  onClick={onTestQuickBooksAccounts}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "9px 12px",
                    fontSize: 12,
                    fontWeight: 800,
                    background: "#fff",
                    color: quickBooksStatus?.connected ? "#111827" : "#9ca3af",
                    cursor: quickBooksStatus?.connected ? "pointer" : "not-allowed",
                  }}
                >
                  Test accounts
                </button>
              ) : null}
              {quickBooksStatus?.connected ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={isQuickBooksLoading}
                  onClick={onDisconnectQuickBooks}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #fecaca",
                    borderRadius: 10,
                    padding: "9px 12px",
                    fontSize: 12,
                    fontWeight: 800,
                    background: "#fff",
                    color: "#b91c1c",
                    cursor: isQuickBooksLoading ? "not-allowed" : "pointer",
                  }}
                >
                  Disconnect
                </button>
              ) : null}
              {quickBooksStatus?.connected ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={isQuickBooksLoading}
                  onClick={onCreateQuickBooksSupportSession}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #0ea5e9",
                    borderRadius: 10,
                    padding: "9px 12px",
                    fontSize: 12,
                    fontWeight: 800,
                    background: isQuickBooksLoading ? "#e5e7eb" : "#e0f2fe",
                    color: isQuickBooksLoading ? "#6b7280" : "#075985",
                    cursor: isQuickBooksLoading ? "not-allowed" : "pointer",
                  }}
                >
                  Create chat finance token
                </button>
              ) : null}
            </div>
            {quickBooksSupportSession ? (
              <div
                style={{
                  border: "1px solid #bae6fd",
                  borderRadius: 10,
                  background: "#f0f9ff",
                  padding: 10,
                  display: "grid",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                  <div style={{ fontSize: 11, color: "#075985", lineHeight: 1.4, fontWeight: 800 }}>
                    Finance support token expires at{" "}
                    <strong>{new Date(quickBooksSupportSession.expires_at).toLocaleString()}</strong>.
                  </div>
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={onCopyQuickBooksSupportInstructions}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #0ea5e9",
                      borderRadius: 8,
                      padding: "7px 9px",
                      fontSize: 11,
                      fontWeight: 900,
                      background: "#fff",
                      color: "#075985",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Copy token
                  </button>
                </div>
                <textarea
                  className="nodrag nopan"
                  readOnly
                  value={quickBooksSupportInstructions}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    minHeight: 126,
                    border: "1px solid #bae6fd",
                    borderRadius: 8,
                    padding: 8,
                    fontSize: 11,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    color: "#0f172a",
                    background: "#fff",
                    resize: "vertical",
                  }}
                />
              </div>
            ) : null}
            {showQuickBooksDiagnostics && quickBooksStatus?.connected ? (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                  display: "grid",
                  gap: 10,
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                      Account query
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
                      Search loaded QuickBooks accounts and copy the results for review.
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>
                    {quickBooksAccounts.length > 0
                      ? `${quickBooksFilteredAccounts.length} of ${quickBooksAccounts.length}`
                      : "Load accounts first"}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <input
                    type="search"
                    className="nodrag nopan"
                    value={quickBooksAccountQuery}
                    onChange={(event) => setQuickBooksAccountQuery(event.target.value)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    placeholder="Search tithe, giving, offering, income..."
                    style={{
                      flex: "1 1 280px",
                      boxSizing: "border-box",
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "9px 10px",
                      fontSize: 12,
                      color: "#111827",
                    }}
                  />
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={quickBooksLikelyGivingAccounts.length === 0}
                    onClick={() => {
                      setQuickBooksSelectedAccountIds(
                        quickBooksLikelyGivingAccounts.slice(0, 12).map((account) => account.id)
                      );
                      setQuickBooksCopyMessage(
                        `Selected ${Math.min(quickBooksLikelyGivingAccounts.length, 12)} likely giving account${
                          Math.min(quickBooksLikelyGivingAccounts.length, 12) === 1 ? "" : "s"
                        }.`
                      );
                    }}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #bbf7d0",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: quickBooksLikelyGivingAccounts.length > 0 ? "#f0fdf4" : "#f3f4f6",
                      color: quickBooksLikelyGivingAccounts.length > 0 ? "#166534" : "#9ca3af",
                      cursor: quickBooksLikelyGivingAccounts.length > 0 ? "pointer" : "not-allowed",
                    }}
                  >
                    Select likely giving
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={quickBooksAccounts.length === 0}
                    onClick={onCopyQuickBooksAccounts}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: "#fff",
                      color: quickBooksAccounts.length > 0 ? "#111827" : "#9ca3af",
                      cursor: quickBooksAccounts.length > 0 ? "pointer" : "not-allowed",
                    }}
                  >
                    Copy results
                  </button>
                </div>
                {quickBooksCopyMessage ? (
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#1d4ed8" }}>
                    {quickBooksCopyMessage}
                  </div>
                ) : null}
                {quickBooksAccounts.length > 0 ? (
                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 10,
                      overflow: "hidden",
                      maxHeight: 170,
                      overflowY: "auto",
                    }}
                  >
                    {quickBooksFilteredAccounts.slice(0, 20).map((account) => (
                      <button
                        key={account.id}
                        type="button"
                        className="nodrag nopan"
                        onClick={() =>
                          setQuickBooksSelectedAccountIds((previous) =>
                            previous.includes(account.id)
                              ? previous.filter((id) => id !== account.id)
                              : [...previous, account.id]
                          )
                        }
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          width: "100%",
                          border: 0,
                          borderBottom: "1px solid #f3f4f6",
                          padding: "8px 10px",
                          display: "grid",
                          gap: 2,
                          textAlign: "left",
                          background: quickBooksSelectedAccountIds.includes(account.id) ? "#ecfdf5" : "#fff",
                          color: "#111827",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 900 }}>
                          {account.fully_qualified_name || account.name}
                        </span>
                        <span style={{ fontSize: 11, color: "#6b7280" }}>
                          {[account.account_type, account.account_sub_type, account.classification]
                            .filter(Boolean)
                            .join(" · ") || "No account type"}
                        </span>
                      </button>
                    ))}
                    {quickBooksFilteredAccounts.length > 20 ? (
                      <div style={{ padding: "8px 10px", fontSize: 11, color: "#6b7280" }}>
                        Showing first 20 matches. Use Copy results for the full filtered list.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {showQuickBooksDiagnostics && quickBooksStatus?.connected ? (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                  display: "grid",
                  gap: 10,
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                      Financial dimensions
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
                      Check whether QuickBooks has classes or locations that could represent campuses.
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>
                    {quickBooksDimensions
                      ? `${quickBooksDimensions?.classes.length ?? 0} classes · ${
                          quickBooksDimensions?.locations.length ?? 0
                        } locations`
                      : "Not checked"}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={isQuickBooksLoading || !quickBooksStatus?.connected}
                    onClick={onCheckQuickBooksDimensions}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: "#fff",
                      color: quickBooksStatus?.connected ? "#111827" : "#9ca3af",
                      cursor: quickBooksStatus?.connected ? "pointer" : "not-allowed",
                    }}
                  >
                    Check dimensions
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={!quickBooksDimensions}
                    onClick={onCopyQuickBooksDimensions}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: "#fff",
                      color: quickBooksDimensions ? "#111827" : "#9ca3af",
                      cursor: quickBooksDimensions ? "pointer" : "not-allowed",
                    }}
                  >
                    Copy dimensions
                  </button>
                </div>
                {quickBooksDimensions ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid #f3f4f6",
                        borderRadius: 10,
                        padding: 10,
                        display: "grid",
                        gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 900, color: "#6b7280" }}>
                        Classes
                      </span>
                      <span style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>
                        {quickBooksDimensions?.classes.length ?? 0}
                      </span>
                    </div>
                    <div
                      style={{
                        border: "1px solid #f3f4f6",
                        borderRadius: 10,
                        padding: 10,
                        display: "grid",
                        gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 900, color: "#6b7280" }}>
                        Locations
                      </span>
                      <span style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>
                        {quickBooksDimensions?.locations.length ?? 0}
                      </span>
                    </div>
                  </div>
                ) : null}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={isQuickBooksLoading || !quickBooksStatus?.connected}
                    onClick={onPreviewQuickBooksProfitAndLossByLocation}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #111827",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: isQuickBooksLoading ? "#e5e7eb" : "#111827",
                      color: isQuickBooksLoading ? "#6b7280" : "#fff",
                      cursor: isQuickBooksLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    Preview P&amp;L by location
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={!quickBooksProfitAndLossByLocation}
                    onClick={onCopyQuickBooksProfitAndLossByLocation}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: "#fff",
                      color: quickBooksProfitAndLossByLocation ? "#111827" : "#9ca3af",
                      cursor: quickBooksProfitAndLossByLocation ? "pointer" : "not-allowed",
                    }}
                  >
                    Copy P&amp;L
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={isQuickBooksLoading || !quickBooksStatus?.connected}
                    onClick={onPreviewQuickBooksProfitAndLossByClass}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #111827",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: isQuickBooksLoading ? "#e5e7eb" : "#111827",
                      color: isQuickBooksLoading ? "#6b7280" : "#fff",
                      cursor: isQuickBooksLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    Preview P&amp;L by class
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={!quickBooksProfitAndLossByClass}
                    onClick={onCopyQuickBooksProfitAndLossByClass}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: "#fff",
                      color: quickBooksProfitAndLossByClass ? "#111827" : "#9ca3af",
                      cursor: quickBooksProfitAndLossByClass ? "pointer" : "not-allowed",
                    }}
                  >
                    Copy class P&amp;L
                  </button>
                </div>
                {quickBooksProfitAndLossByLocation ? (
                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 10,
                      overflow: "hidden",
                      maxHeight: 210,
                      overflowY: "auto",
                    }}
                  >
                    {(quickBooksProfitAndLossByLocation?.locations ?? []).map((location) => (
                      <div
                        key={location.name}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          padding: "8px 10px",
                          display: "grid",
                          gridTemplateColumns: "minmax(120px, 1.2fr) repeat(3, minmax(90px, 0.8fr))",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 900, color: "#111827" }}>
                          {location.name}
                        </span>
                        <span style={{ fontSize: 11, color: "#166534", fontWeight: 800 }}>
                          {formatMetricValue(location.income, "currency")}
                        </span>
                        <span style={{ fontSize: 11, color: "#991b1b", fontWeight: 800 }}>
                          {formatMetricValue(location.expenses, "currency")}
                        </span>
                        <span style={{ fontSize: 11, color: "#374151", fontWeight: 800 }}>
                          {formatMetricValue(location.net_income, "currency")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
                {quickBooksProfitAndLossByClass ? (
                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 10,
                      overflow: "hidden",
                      maxHeight: 210,
                      overflowY: "auto",
                    }}
                  >
                    {(quickBooksProfitAndLossByClass?.classes ?? []).map((quickBooksClass) => (
                      <div
                        key={quickBooksClass.name}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          padding: "8px 10px",
                          display: "grid",
                          gridTemplateColumns: "minmax(160px, 1.2fr) repeat(3, minmax(90px, 0.8fr))",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 900, color: "#111827" }}>
                          {quickBooksClass.name}
                        </span>
                        <span style={{ fontSize: 11, color: "#166534", fontWeight: 800 }}>
                          {formatMetricValue(quickBooksClass.income, "currency")}
                        </span>
                        <span style={{ fontSize: 11, color: "#991b1b", fontWeight: 800 }}>
                          {formatMetricValue(quickBooksClass.expenses, "currency")}
                        </span>
                        <span style={{ fontSize: 11, color: "#374151", fontWeight: 800 }}>
                          {formatMetricValue(quickBooksClass.net_income, "currency")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div
                  style={{
                    borderTop: "1px solid #f3f4f6",
                    paddingTop: 10,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                    Giving question
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 8,
                    }}
                  >
                    <label style={{ display: "grid", gap: 5, fontSize: 11, fontWeight: 800, color: "#6b7280" }}>
                      Dimension
                      <select
                        className="nodrag nopan"
                        value={quickBooksFinanceDimensionType}
                        onChange={(event) =>
                          setQuickBooksFinanceDimensionType(event.target.value === "class" ? "class" : "location")
                        }
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "9px 10px",
                          fontSize: 12,
                          color: "#111827",
                          background: "#fff",
                        }}
                      >
                        <option value="location">Location</option>
                        <option value="class">Class</option>
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 5, fontSize: 11, fontWeight: 800, color: "#6b7280" }}>
                      Name
                      <input
                        className="nodrag nopan"
                        value={quickBooksFinanceDimensionName}
                        onChange={(event) => setQuickBooksFinanceDimensionName(event.target.value)}
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        placeholder="Bethel SonRise"
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "9px 10px",
                          fontSize: 12,
                          color: "#111827",
                        }}
                      />
                    </label>
                    <label style={{ display: "grid", gap: 5, fontSize: 11, fontWeight: 800, color: "#6b7280" }}>
                      Basis
                      <select
                        className="nodrag nopan"
                        value={quickBooksFinanceAccountingMethod}
                        onChange={(event) =>
                          setQuickBooksFinanceAccountingMethod(
                            event.target.value === "Cash"
                              ? "Cash"
                              : event.target.value === "Accrual"
                                ? "Accrual"
                                : ""
                          )
                        }
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "9px 10px",
                          fontSize: 12,
                          color: "#111827",
                          background: "#fff",
                        }}
                      >
                        <option value="">QuickBooks default</option>
                        <option value="Cash">Cash</option>
                        <option value="Accrual">Accrual</option>
                      </select>
                    </label>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button
                      type="button"
                      className="nodrag nopan"
                      disabled={isQuickBooksLoading || !quickBooksStatus?.connected}
                      onClick={onAnswerQuickBooksGivingByDimension}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #111827",
                        borderRadius: 10,
                        padding: "9px 12px",
                        fontSize: 12,
                        fontWeight: 800,
                        background: isQuickBooksLoading ? "#e5e7eb" : "#111827",
                        color: isQuickBooksLoading ? "#6b7280" : "#fff",
                        cursor: isQuickBooksLoading ? "not-allowed" : "pointer",
                      }}
                    >
                      Answer giving question
                    </button>
                    <button
                      type="button"
                      className="nodrag nopan"
                      disabled={!quickBooksGivingDimensionAnswer}
                      onClick={onCopyQuickBooksGivingDimensionAnswer}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "9px 12px",
                        fontSize: 12,
                        fontWeight: 800,
                        background: "#fff",
                        color: quickBooksGivingDimensionAnswer ? "#111827" : "#9ca3af",
                        cursor: quickBooksGivingDimensionAnswer ? "pointer" : "not-allowed",
                      }}
                    >
                      Copy answer
                    </button>
                  </div>
                  {quickBooksGivingDimensionAnswer ? (
                    <div
                      style={{
                        border: "1px solid #e0f2fe",
                        borderRadius: 10,
                        padding: "10px 12px",
                        background: "#f0f9ff",
                        display: "grid",
                        gap: 4,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 900, color: "#075985" }}>
                        {quickBooksGivingDimensionAnswer?.matched_dimension || "No matching dimension"}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: "#0f172a" }}>
                        {formatMetricValue(quickBooksGivingDimensionAnswer?.total ?? 0, "currency")}
                      </div>
                      <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.4 }}>
                        {quickBooksGivingDimensionAnswer?.start_date} to {quickBooksGivingDimensionAnswer?.end_date} ·{" "}
                        {quickBooksGivingDimensionAnswer?.matched_account_ids.length ?? 0} matched account
                        {(quickBooksGivingDimensionAnswer?.matched_account_ids.length ?? 0) === 1 ? "" : "s"}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {showQuickBooksDiagnostics && quickBooksStatus?.connected ? (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                  display: "grid",
                  gap: 10,
                  background: "#fff",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                  Finance actuals
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
                  Import CCB giving actuals or QuickBooks operating expense actuals into the selected KPI.
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: 12,
                    alignItems: "start",
                  }}
                >
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      minWidth: 0,
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#6b7280",
                    }}
                  >
                    Start
                    <input
                      type="date"
                      className="nodrag nopan"
                      value={quickBooksStartDate}
                      onChange={(event) => setQuickBooksStartDate(event.target.value)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "9px 10px",
                        fontSize: 12,
                        color: "#111827",
                      }}
                    />
                  </label>
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      minWidth: 0,
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#6b7280",
                    }}
                  >
                    End
                    <input
                      type="date"
                      className="nodrag nopan"
                      value={quickBooksEndDate}
                      onChange={(event) => setQuickBooksEndDate(event.target.value)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "9px 10px",
                        fontSize: 12,
                        color: "#111827",
                      }}
                    />
                  </label>
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      minWidth: 0,
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#6b7280",
                    }}
                  >
                    Period
                    <select
                      className="nodrag nopan"
                      value={quickBooksPeriod}
                      onChange={(event) => {
                        const period = event.target.value === "monthly" ? "monthly" : "weekly";
                        setQuickBooksPeriod(period);
                        setQuickBooksTargetKpiId("");
                        setQuickBooksGivingPreview(null);
                        setQuickBooksExpensePreview(null);
                        setQuickBooksExpenseBudgetPreview(null);
                        setQuickBooksAccountBalancePreview(null);
                      }}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "9px 10px",
                        fontSize: 12,
                        color: "#111827",
                        background: "#fff",
                      }}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </label>
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      minWidth: 0,
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#6b7280",
                    }}
                  >
                    QB expense location
                    <select
                      className="nodrag nopan"
                      value={quickBooksExpenseLocationName}
                      onChange={(event) => setQuickBooksExpenseLocationName(event.target.value)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "9px 10px",
                        fontSize: 12,
                        color: "#111827",
                        background: "#fff",
                      }}
                    >
                      <option value="">All operating expenses</option>
                      {quickBooksExpenseLocations.map((location) => (
                        <option key={location.id} value={location.name}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      minWidth: 0,
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#6b7280",
                    }}
                  >
                    CCB campus
                    <input
                      className="nodrag nopan"
                      value={ccbGivingCampusName}
                      onChange={(event) => setCcbGivingCampusName(event.target.value)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      placeholder="All campuses"
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "9px 10px",
                        fontSize: 12,
                        color: "#111827",
                        background: "#fff",
                      }}
                    />
                  </label>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "end" }}>
                  <label
                    style={{
                      display: "grid",
                      gap: 5,
                      minWidth: 240,
                      flex: "1 1 320px",
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#6b7280",
                    }}
                  >
                    KPI
                    <select
                      className="nodrag nopan"
                      value={quickBooksTargetKpiId}
                      onChange={(event) => setQuickBooksTargetKpiId(event.target.value)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "9px 10px",
                        fontSize: 12,
                        color: "#111827",
                        background: "#fff",
                      }}
                    >
                      <option value="">All campus giving KPIs</option>
                      {quickBooksImportKpis.map((node) => (
                        <option key={node.id} value={node.id}>
                          {node.data.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={isQuickBooksLoading || (ccbGivingCampusName.trim().length > 0 && !quickBooksTargetKpiId)}
                    onClick={onSyncCcbGivingToKpi}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #22c55e",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background:
                        !isQuickBooksLoading && (quickBooksTargetKpiId || ccbGivingCampusName.trim().length === 0)
                          ? "#dcfce7"
                          : "#f3f4f6",
                      color:
                        !isQuickBooksLoading && (quickBooksTargetKpiId || ccbGivingCampusName.trim().length === 0)
                          ? "#166534"
                          : "#9ca3af",
                      cursor:
                        !isQuickBooksLoading && (quickBooksTargetKpiId || ccbGivingCampusName.trim().length === 0)
                          ? "pointer"
                          : "not-allowed",
                    }}
                  >
                    Import CCB giving
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={isQuickBooksLoading || !quickBooksTargetKpiId}
                    onClick={onSyncQuickBooksExpensesToKpi}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #f97316",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: quickBooksTargetKpiId && !isQuickBooksLoading ? "#ffedd5" : "#f3f4f6",
                      color: quickBooksTargetKpiId && !isQuickBooksLoading ? "#9a3412" : "#9ca3af",
                      cursor: quickBooksTargetKpiId && !isQuickBooksLoading ? "pointer" : "not-allowed",
                    }}
                  >
                    Import P&amp;L expenses
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={isQuickBooksLoading || !quickBooksStatus?.connected}
                    onClick={onCheckQuickBooksDimensions}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "9px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      background: "#fff",
                      color: quickBooksStatus?.connected && !isQuickBooksLoading ? "#111827" : "#9ca3af",
                      cursor: quickBooksStatus?.connected && !isQuickBooksLoading ? "pointer" : "not-allowed",
                    }}
                  >
                    Load locations
                  </button>
                </div>
                {ccbGivingPreview ? (
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      overflow: "hidden",
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    {ccbGivingPreview.periods.map((item) => (
                      <div
                        key={`${item.period_key}-${item.start_date}-ccb-giving`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                          padding: "8px 10px",
                          borderBottom: "1px solid #f3f4f6",
                          fontSize: 12,
                          color: "#374151",
                        }}
                      >
                        <span>
                          <strong>{item.period_key}</strong> · {item.start_date} to {item.end_date} ·{" "}
                          {item.transaction_count} detail{item.transaction_count === 1 ? "" : "s"}
                        </span>
                        <span style={{ textAlign: "right", fontWeight: 900, color: "#166534" }}>
                          {formatMetricValue(item.total, "currency")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
                {quickBooksExpensePreview ? (
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      overflow: "hidden",
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    {quickBooksExpensePreview.periods.map((item) => (
                      <div
                        key={`${item.period_key}-${item.start_date}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                          padding: "8px 10px",
                          borderBottom: "1px solid #f3f4f6",
                          fontSize: 12,
                          color: "#374151",
                        }}
                      >
                        <span>
                          <strong>{item.period_key}</strong> · {item.start_date} to {item.end_date}
                        </span>
                        <span style={{ textAlign: "right", fontWeight: 900, color: "#991b1b" }}>
                          {formatMetricValue(item.total, "currency")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

          <button
            type="button"
            className="nodrag nopan"
            onClick={() => setIsAiStrategySectionCollapsed((previous) => !previous)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 14,
              fontWeight: 800,
              background: "#f9fafb",
              color: "#111827",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>AI Strategy Assistant</span>
            <span>{isAiStrategySectionCollapsed ? "Show" : "Hide"}</span>
          </button>

          {isAiStrategySectionCollapsed ? null : (
            <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
              <div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
                Describe the enterprise and the kind of strategic clarity you need. AI drafts recommendations only; you choose what gets applied.
              </div>
              <textarea
                className="nodrag nopan"
                value={aiStrategyPrompt}
                onChange={(event) => setAiStrategyPrompt(event.target.value)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                rows={5}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #d1d5db",
                  borderRadius: 12,
                  padding: "10px 12px",
                  fontSize: 13,
                  lineHeight: 1.45,
                  background: "#fff",
                  color: "#111827",
                  resize: "vertical",
                }}
              />
              <button
                type="button"
                className="nodrag nopan"
                disabled={isGeneratingStrategyDraft}
                onClick={onGenerateStrategyDraft}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  border: "1px solid #111827",
                  borderRadius: 12,
                  padding: "10px 12px",
                  fontSize: 13,
                  fontWeight: 800,
                  background: isGeneratingStrategyDraft ? "#e5e7eb" : "#111827",
                  color: isGeneratingStrategyDraft ? "#6b7280" : "#fff",
                  cursor: isGeneratingStrategyDraft ? "wait" : "pointer",
                }}
              >
                {isGeneratingStrategyDraft ? "Generating Draft..." : "Generate Recommendations"}
              </button>
              {aiStrategyMessage ? (
                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: 10,
                    background: "#fff",
                    color: "#374151",
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  {aiStrategyMessage}
                </div>
              ) : null}
              {aiStrategyDraft ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <div
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 12,
                      padding: 12,
                      background: "#fff",
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
                      Draft Summary
                    </div>
                    <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45 }}>
                      {aiStrategyDraft.summary}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      Model: {aiStrategyDraft.model}
                    </div>
                  </div>
                  {aiStrategyDraft.lenses.map((lensDraft, lensIndex) => {
                    const isSelected = selectedAiLensIndexes.includes(lensIndex);
                    const kpiCount = lensDraft.kpis?.length ?? 0;
                    const initiativeCount = (lensDraft.kpis ?? []).reduce(
                      (total, kpi) => total + (kpi.initiatives?.length ?? 0),
                      0
                    );
                    return (
                      <label
                        key={`${lensDraft.name}-${lensIndex}`}
                        className="nodrag nopan"
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          border: isSelected ? "2px solid #111827" : "1px solid #d1d5db",
                          borderRadius: 14,
                          padding: 12,
                          background: isSelected ? "#f8fafc" : "#fff",
                          display: "grid",
                          gap: 8,
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggleAiLensSelection(lensIndex)}
                            style={{ marginTop: 3 }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 850, color: "#111827" }}>
                              {lensDraft.name}
                            </div>
                            <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4, lineHeight: 1.45 }}>
                              {lensDraft.description || lensDraft.rationale || "No description supplied."}
                            </div>
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                              {kpiCount} KPI{kpiCount === 1 ? "" : "s"} • {initiativeCount} initiative{initiativeCount === 1 ? "" : "s"}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "grid", gap: 6, paddingLeft: 28 }}>
                          {(lensDraft.kpis ?? []).slice(0, 5).map((kpi) => (
                            <div
                              key={kpi.label}
                              style={{
                                borderLeft: "3px solid #d1d5db",
                                paddingLeft: 10,
                                fontSize: 12,
                                color: "#374151",
                                lineHeight: 1.4,
                              }}
                            >
                              <strong>{kpi.label}</strong>
                              {(kpi.children?.length ?? 0) > 0 ? ` • ${kpi.children?.length} child KPI${kpi.children?.length === 1 ? "" : "s"}` : ""}
                              {(kpi.initiatives?.length ?? 0) > 0 ? ` • ${kpi.initiatives?.length} initiative${kpi.initiatives?.length === 1 ? "" : "s"}` : ""}
                            </div>
                          ))}
                        </div>
                      </label>
                    );
                  })}
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={selectedAiLensIndexes.length === 0}
                    onClick={onApplySelectedStrategyDraft}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      width: "100%",
                      border: "1px solid #16a34a",
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontSize: 13,
                      fontWeight: 800,
                      background: selectedAiLensIndexes.length === 0 ? "#f9fafb" : "#f0fdf4",
                      color: selectedAiLensIndexes.length === 0 ? "#9ca3af" : "#166534",
                      cursor: selectedAiLensIndexes.length === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    Apply Selected Drafts
                  </button>
                </div>
              ) : null}
            </div>
          )}

          <button
            type="button"
            className="nodrag nopan"
            onClick={() => setIsLensesSectionCollapsed((previous) => !previous)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 14,
              fontWeight: 800,
              background: "#f9fafb",
              color: "#111827",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Lenses</span>
            <span>{isLensesSectionCollapsed ? "Show" : "Hide"}</span>
          </button>

          {isLensesSectionCollapsed ? null : (
            <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={!canManageLenses}
                  onClick={onAddLens}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 700,
                    background: canManageLenses ? "#111827" : "#f9fafb",
                    color: canManageLenses ? "#fff" : "#9ca3af",
                    cursor: canManageLenses ? "pointer" : "not-allowed",
                  }}
                >
                  + Add Lens
                </button>
                <button
                  type="button"
                  className="nodrag nopan"
                  onClick={() => onGenerateContextualAiDraft("lens")}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #93c5fd",
                    borderRadius: 12,
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 800,
                    background: "#eff6ff",
                    color: "#1d4ed8",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  AI
                </button>
              </div>
              {renderAiAddReview("lens")}

              <div style={{ display: "grid", gap: 10 }}>
                {lensSummaries.map(({ lens, name, kpiCount, description }) => {
                  const isActive = lens === activeLens;

                  return (
                    <div
                      key={lens}
                      style={{
                        border: isActive ? "2px solid #111827" : "1px solid #d1d5db",
                        borderRadius: 14,
                        padding: 12,
                        background: isActive ? "#f3f4f6" : "#fff",
                      }}
                    >
                      <input
                        className="nodrag nopan"
                        value={lensNameDrafts[lens] ?? name}
                        onChange={(e) => onLensDraftChange(lens, e.target.value)}
                        onBlur={() => onRenameLens(lens, lensNameDrafts[lens] ?? name)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            onRenameLens(lens, lensNameDrafts[lens] ?? name);
                            (e.currentTarget as HTMLInputElement).blur();
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            onLensDraftChange(lens, name);
                            (e.currentTarget as HTMLInputElement).blur();
                          }
                        }}
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "10px 12px",
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#111827",
                          background: "#fff",
                          marginBottom: 8,
                        }}
                      />
                      <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 10, lineHeight: 1.45 }}>
                        {kpiCount} KPI{kpiCount === 1 ? "" : "s"} in this lens
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, lineHeight: 1.45 }}>
                        {description || "No lens description yet."}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => onLensChange(lens)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            flex: 1,
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: isActive ? "#111827" : "#fff",
                            color: isActive ? "#fff" : "#111827",
                            cursor: "pointer",
                          }}
                        >
                          {isActive ? "Current Lens" : "Open Lens"}
                        </button>
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => setActiveLensDetailId(lens)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: "#fff",
                            color: "#111827",
                            cursor: "pointer",
                          }}
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          className="nodrag nopan"
                          disabled={availableLenses.length <= 1}
                          onClick={() => onDeleteLens(lens)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            border: "1px solid #dc2626",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: availableLenses.length <= 1 ? "#f9fafb" : "#fff5f5",
                            color: availableLenses.length <= 1 ? "#9ca3af" : "#b91c1c",
                            cursor: availableLenses.length <= 1 ? "not-allowed" : "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="button"
            className="nodrag nopan"
            onClick={() => setIsKpiSectionCollapsed((previous) => !previous)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 14,
              fontWeight: 800,
              background: "#f9fafb",
              color: "#111827",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>KPIs</span>
            <span>{isKpiSectionCollapsed ? "Show" : "Hide"}</span>
          </button>

          {isKpiSectionCollapsed ? null : (
            <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
              <div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
                Drag an existing KPI into the <strong>{activeLensConfig?.name ?? activeLens}</strong> lens, or open it to edit details.
              </div>

              <select
                className="nodrag nopan"
                value={kpiLibraryFilter}
                onChange={(e) => setKpiLibraryFilter(e.target.value)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #d1d5db",
                  borderRadius: 12,
                  padding: "10px 12px",
                  fontSize: 13,
                  background: "#fff",
                  color: "#111827",
                }}
              >
                <option value={allKpisFilterValue}>All KPIs</option>
                <option value={unattachedKpisFilterValue}>Unattached KPIs</option>
                {availableLenses.map((lens) => (
                  <option key={lens} value={lens}>
                    Attached To {lensConfigs[lens]?.name ?? lens}
                  </option>
                ))}
              </select>

              <input
                className="nodrag nopan"
                type="search"
                value={kpiLibrarySearch}
                placeholder="Search KPIs by name, owner, type..."
                onChange={(e) => setKpiLibrarySearch(e.target.value)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #d1d5db",
                  borderRadius: 12,
                  padding: "10px 12px",
                  fontSize: 13,
                  background: "#fff",
                  color: "#111827",
                }}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                <button
                  type="button"
                  className="nodrag nopan"
                  disabled={!canCreateKpis}
                  onClick={onAddRootKpi}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 700,
                    background: canCreateKpis ? "#111827" : "#f9fafb",
                    color: canCreateKpis ? "#fff" : "#9ca3af",
                    cursor: canCreateKpis ? "pointer" : "not-allowed",
                  }}
                >
                  + Add KPI
                </button>
                <button
                  type="button"
                  className="nodrag nopan"
                  onClick={() => onGenerateContextualAiDraft("kpi")}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #93c5fd",
                    borderRadius: 12,
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 800,
                    background: "#eff6ff",
                    color: "#1d4ed8",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  AI
                </button>
              </div>
              {renderAiAddReview("kpi")}

              <div style={{ display: "grid", gap: 10 }}>
                {libraryNodes.map((node) => {
                  const inLens = node.data.lenses.includes(activeLens);

	                  return (
	                    <div
	                      key={node.id}
	                      className="nodrag nopan"
	                      draggable={false}
	                      onPointerDown={(event) => onLibraryPointerDown(event, node, inLens)}
	                      onDoubleClick={() => setActiveKpiId(node.id)}
	                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 14,
                        padding: 12,
                        background: inLens ? "#f3f4f6" : "#fff",
                        cursor: inLens ? "default" : "grab",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                        {node.data.label}
                      </div>
                      <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginBottom: 8 }}>
                        {toTitleCase(node.data.frequency)} |{" "}
                        {formatDataSourceLabel(node.data.dataSource)} |{" "}
                        {formatValueTypeLabel(node.data.valueType)}
                      </div>
                      <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginBottom: 8 }}>
                        Owner: {node.data.owner || "Unassigned"}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: inLens ? "#374151" : "#1d4ed8",
                          marginBottom: 10,
                        }}
                      >
                        {inLens ? "Already on this lens" : "Drag onto the canvas to add"}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => {
                            setIsLensLibraryCollapsed(true);
                            setActiveKpiId(node.id);
                          }}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            flex: 1,
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: "#fff",
                            color: "#111827",
                            cursor: "pointer",
                          }}
                        >
                          Details
                        </button>
                        {inLens ? (
                          <button
                            type="button"
                            className="nodrag nopan"
                            onClick={() => onRemoveFromActiveLens(node.id)}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 10px",
                              fontSize: 13,
                              fontWeight: 700,
                              background: "#fff",
                              color: "#111827",
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => onDeleteKpi(node.id)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            border: "1px solid #dc2626",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: "#fff5f5",
                            color: "#b91c1c",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="button"
            className="nodrag nopan"
            onClick={() => setIsReferenceListsSectionCollapsed((previous) => !previous)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 14,
              fontWeight: 800,
              background: "#f9fafb",
              color: "#111827",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Reference Lists</span>
            <span>{isReferenceListsSectionCollapsed ? "Show" : "Hide"}</span>
          </button>

          {isReferenceListsSectionCollapsed ? null : (
            <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setIsEnterpriseListCollapsed((previous) => !previous)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  background: "#fff",
                  color: "#111827",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ paddingLeft: 10 }}>Enterprises</span>
                <span>{isEnterpriseListCollapsed ? "Show" : "Hide"}</span>
              </button>
              {isEnterpriseListCollapsed ? null : (
                <div style={{ marginLeft: 26, display: "grid", gap: 10 }}>
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => void onCreateEnterprise()}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      width: "100%",
                      border: "1px solid #d1d5db",
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#111827",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    + Add Enterprise
                  </button>
                  <div
                    style={{
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 14,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    {enterprises.map((enterprise) => {
                      const isActive = enterprise.id === activeEnterpriseId;
                      return (
                        <div
                          key={enterprise.id}
                          style={{
                            border: isActive ? "2px solid #111827" : "1px solid #d1d5db",
                            borderRadius: 10,
                            background: isActive ? "#f3f4f6" : "#fff",
                            padding: 8,
                            display: "grid",
                            gap: 8,
                          }}
                        >
                          <button
                            type="button"
                            className="nodrag nopan"
                            onClick={() => {
                              hasLoadedEnterpriseRef.current = false;
                              setActiveEnterpriseId(enterprise.id);
                            }}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              border: "none",
                              borderRadius: 8,
                              padding: "6px 8px",
                              fontSize: 13,
                              fontWeight: 700,
                              background: "transparent",
                              color: "#111827",
                              cursor: "pointer",
                            }}
                          >
                            {enterprise.name}
                          </button>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              padding: "0 8px",
                            }}
                          >
                            <span
                              style={{
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: 12,
                                color: enterprise.website_url ? "#4b5563" : "#9ca3af",
                              }}
                              title={enterprise.website_url || "No website URL set"}
                            >
                              {enterprise.website_url || "No website URL set"}
                            </span>
                            <button
                              type="button"
                              className="nodrag nopan"
                              onClick={() => void onUpdateEnterpriseWebsite(enterprise)}
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: "5px 8px",
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer",
                              }}
                            >
                              Website
                            </button>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              padding: "0 8px",
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#4b5563" }}>
                              Week starts
                            </span>
                            <select
                              className="nodrag nopan"
                              value={normalizeWeekStartsOn(enterprise.week_starts_on)}
                              onChange={(event) =>
                                void onUpdateEnterpriseWeekStart(enterprise, event.target.value as WeekStartsOn)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: "5px 8px",
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#fff",
                                color: "#374151",
                              }}
                            >
                              <option value="saturday">Saturday</option>
                              <option value="sunday">Sunday</option>
                              <option value="monday">Monday</option>
                            </select>
                          </div>
                          <button
                            type="button"
                            className="nodrag nopan"
                            disabled={enterprises.length <= 1}
                            onClick={() => void onDeleteEnterprise(enterprise)}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              border: "1px solid #dc2626",
                              borderRadius: 8,
                              padding: "6px 8px",
                              fontSize: 12,
                              fontWeight: 700,
                              background: enterprises.length <= 1 ? "#f9fafb" : "#fff5f5",
                              color: enterprises.length <= 1 ? "#9ca3af" : "#b91c1c",
                              cursor: enterprises.length <= 1 ? "not-allowed" : "pointer",
                            }}
                          >
                            Delete Enterprise
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setIsOwnerListCollapsed((previous) => !previous)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  background: "#fff",
                  color: "#111827",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ paddingLeft: 10 }}>Owners</span>
                <span>{isOwnerListCollapsed ? "Show" : "Hide"}</span>
              </button>
              {isOwnerListCollapsed ? null : (
                <div style={{ marginLeft: 26 }}>
                  <div
                    style={{
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 14,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
                      Owners now come from Users & Permissions.
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                      Active users for this enterprise appear in KPI and task owner fields.
                    </div>
                    <a href="/users" style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}>
                      Manage users and permissions
                    </a>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setIsPollAudienceGroupsCollapsed((previous) => !previous)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  background: "#fff",
                  color: "#111827",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ paddingLeft: 10 }}>Poll Audience Groups</span>
                <span>{isPollAudienceGroupsCollapsed ? "Show" : "Hide"}</span>
              </button>
              {isPollAudienceGroupsCollapsed ? null : (
                <div style={{ marginLeft: 26 }}>
                  <StringListEditor
                    title="Poll Audience Groups"
                    items={pollAudienceGroups}
                    onChange={setPollAudienceGroups}
                  />
                </div>
              )}

              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setIsPollRecipientsCollapsed((previous) => !previous)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  width: "100%",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  background: "#fff",
                  color: "#111827",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ paddingLeft: 10 }}>Poll Recipients</span>
                <span>{isPollRecipientsCollapsed ? "Show" : "Hide"}</span>
              </button>
              {isPollRecipientsCollapsed ? null : (
                <div style={{ marginLeft: 26 }}>
                  <div
                    style={{
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 14,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                        Poll Recipients
                      </div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                        Manage your recipient directory in a separate window with larger name and email fields.
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: "#374151" }}>
                      {pollRecipients.length} saved recipient{pollRecipients.length === 1 ? "" : "s"}
                    </div>
                    <button
                      type="button"
                      className="nodrag nopan"
                      onClick={() => setIsPollRecipientsOpen(true)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "10px 12px",
                        fontSize: 14,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#111827",
                        cursor: "pointer",
                        justifySelf: "start",
                      }}
                    >
                      Open Poll Recipients
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelResizeStart("lens", event)}
          style={{
            position: "absolute",
            right: 8,
            bottom: 8,
            width: 28,
            height: 28,
            borderRadius: 8,
            background:
              "linear-gradient(135deg, rgba(209,213,219,0.95), rgba(156,163,175,0.95))",
            border: "1px solid #9ca3af",
            boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
            cursor: "nwse-resize",
            touchAction: "none",
            zIndex: 2,
          }}
        >
          <div
            style={{
              position: "absolute",
              right: 5,
              bottom: 5,
              width: 12,
              height: 12,
              borderRight: "2px solid #ffffff",
              borderBottom: "2px solid #ffffff",
              opacity: 0.95,
            }}
          />
        </div>
      </div>

      <div
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          display: "none",
          position: "absolute",
          top: 64,
          right: panelAnchors.reference,
          width: panelSizes.reference.width,
          height: Math.min(panelSizes.reference.height, window.innerHeight - 80),
          zIndex: 10,
          background: "rgba(255,255,255,0.96)",
          borderRadius: 18,
          border: "1px solid #d1d5db",
          boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          padding: 16,
          overflowY: "auto",
          transform: getPanelTransform("reference", isReferenceLibraryCollapsed),
          opacity: isReferenceLibraryCollapsed ? 0 : 1,
          pointerEvents: isReferenceLibraryCollapsed ? "none" : "auto",
          transition:
            draggingPanel === "reference" || resizingPanel === "reference"
              ? "none"
              : "transform 180ms ease, opacity 180ms ease, right 180ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 6,
            cursor: "move",
            userSelect: "none",
            touchAction: "none",
          }}
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelDragStart("reference", event)}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#111827",
            }}
          >
            Lists
          </div>
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#4b5563",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          Maintain the shared lists used for owners and poll setup.
        </div>

        <button
          type="button"
          className="nodrag nopan"
          onClick={() => void onCreateEnterprise()}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            background: "#111827",
            color: "#fff",
            cursor: "pointer",
            marginBottom: 14,
          }}
        >
          + Add Enterprise
        </button>

        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 14,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: "#111827",
              marginBottom: 10,
            }}
          >
            Enterprises
          </div>

          <div
            style={{
              display: "grid",
              gap: 8,
            }}
          >
            {enterprises.map((enterprise) => {
              const isActive = enterprise.id === activeEnterpriseId;

              return (
                <div
                  key={enterprise.id}
                  style={{
                    border: isActive ? "2px solid #111827" : "1px solid #d1d5db",
                    borderRadius: 10,
                    background: isActive ? "#f3f4f6" : "#fff",
                    padding: 8,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => {
                      hasLoadedEnterpriseRef.current = false;
                      setActiveEnterpriseId(enterprise.id);
                    }}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 8px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "transparent",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    {enterprise.name}
                  </button>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "0 8px",
                    }}
                  >
                    <span
                      style={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 12,
                        color: enterprise.website_url ? "#4b5563" : "#9ca3af",
                      }}
                      title={enterprise.website_url || "No website URL set"}
                    >
                      {enterprise.website_url || "No website URL set"}
                    </span>
                    <button
                      type="button"
                      className="nodrag nopan"
                      onClick={() => void onUpdateEnterpriseWebsite(enterprise)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        flexShrink: 0,
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        padding: "5px 8px",
                        fontSize: 12,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#374151",
                        cursor: "pointer",
                      }}
                    >
                      Website
                    </button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "0 8px",
                    }}
                  >
                    <span style={{ fontSize: 12, color: "#4b5563" }}>
                      Week starts
                    </span>
                    <select
                      className="nodrag nopan"
                      value={normalizeWeekStartsOn(enterprise.week_starts_on)}
                      onChange={(event) =>
                        void onUpdateEnterpriseWeekStart(enterprise, event.target.value as WeekStartsOn)
                      }
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        flexShrink: 0,
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        padding: "5px 8px",
                        fontSize: 12,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#374151",
                      }}
                    >
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                      <option value="monday">Monday</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={enterprises.length <= 1}
                    onClick={() => void onDeleteEnterprise(enterprise)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #dc2626",
                      borderRadius: 8,
                      padding: "6px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                      background: enterprises.length <= 1 ? "#f9fafb" : "#fff5f5",
                      color: enterprises.length <= 1 ? "#9ca3af" : "#b91c1c",
                      cursor: enterprises.length <= 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Delete Enterprise
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gap: 16,
          }}
        >
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 14,
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
              Owners are active users.
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
              Use Users & Permissions to add people, assign enterprise access, and change roles.
            </div>
            <a href="/users" style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}>
              Manage users and permissions
            </a>
          </div>
          <StringListEditor
            title="Poll Audience Groups"
            items={pollAudienceGroups}
            onChange={setPollAudienceGroups}
          />
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 14,
              display: "grid",
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                Poll Recipients
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                Manage your recipient directory in a separate window with larger name and email
                fields.
              </div>
            </div>
            <div style={{ fontSize: 13, color: "#374151" }}>
              {pollRecipients.length} saved recipient{pollRecipients.length === 1 ? "" : "s"}
            </div>
            <button
              type="button"
              className="nodrag nopan"
              onClick={() => setIsPollRecipientsOpen(true)}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 14,
                fontWeight: 700,
                background: "#fff",
                color: "#111827",
                cursor: "pointer",
                justifySelf: "start",
              }}
            >
              Open Poll Recipients
            </button>
          </div>
        </div>
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelResizeStart("reference", event)}
          style={{
            position: "absolute",
            right: 8,
            bottom: 8,
            width: 28,
            height: 28,
            borderRadius: 8,
            background:
              "linear-gradient(135deg, rgba(209,213,219,0.95), rgba(156,163,175,0.95))",
            border: "1px solid #9ca3af",
            boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
            cursor: "nwse-resize",
            touchAction: "none",
            zIndex: 2,
          }}
        >
          <div
            style={{
              position: "absolute",
              right: 5,
              bottom: 5,
              width: 12,
              height: 12,
              borderRight: "2px solid #ffffff",
              borderBottom: "2px solid #ffffff",
              opacity: 0.95,
            }}
          />
        </div>
      </div>

      <div
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          display: "none",
          position: "absolute",
          top: 64,
          right: panelAnchors.kpi,
          width: panelSizes.kpi.width,
          height: Math.min(panelSizes.kpi.height, window.innerHeight - 80),
          zIndex: 10,
          background: "rgba(255,255,255,0.96)",
          borderRadius: 18,
          border: "1px solid #d1d5db",
          boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          padding: 16,
          overflowY: "auto",
          transform: getPanelTransform("kpi", isLibraryCollapsed),
          opacity: isLibraryCollapsed ? 0 : 1,
          pointerEvents: isLibraryCollapsed ? "none" : "auto",
          transition:
            draggingPanel === "kpi" || resizingPanel === "kpi"
              ? "none"
              : "transform 180ms ease, opacity 180ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 6,
            cursor: "move",
            userSelect: "none",
            touchAction: "none",
          }}
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelDragStart("kpi", event)}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#111827",
            }}
          >
            KPI Library
          </div>
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#4b5563",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          Drag an existing KPI into the <strong>{activeLensConfig?.name ?? activeLens}</strong>{" "}
          lens, or open it to edit details.
        </div>

        <select
          className="nodrag nopan"
          value={kpiLibraryFilter}
          onChange={(e) => setKpiLibraryFilter(e.target.value)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            background: "#fff",
            color: "#111827",
            marginBottom: 14,
          }}
        >
          <option value={allKpisFilterValue}>All KPIs</option>
          <option value={unattachedKpisFilterValue}>Unattached KPIs</option>
          {availableLenses.map((lens) => (
            <option key={lens} value={lens}>
              Attached To {lensConfigs[lens]?.name ?? lens}
            </option>
          ))}
        </select>

        <input
          className="nodrag nopan"
          type="search"
          value={kpiLibrarySearch}
          placeholder="Search KPIs by name, owner, type..."
          onChange={(e) => setKpiLibrarySearch(e.target.value)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            background: "#fff",
            color: "#111827",
            marginBottom: 14,
          }}
        />

        <button
          type="button"
          className="nodrag nopan"
          disabled={!canCreateKpis}
          onClick={onAddRootKpi}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            background: canCreateKpis ? "#111827" : "#f9fafb",
            color: canCreateKpis ? "#fff" : "#9ca3af",
            cursor: canCreateKpis ? "pointer" : "not-allowed",
            marginBottom: 14,
          }}
        >
          + Add KPI
        </button>

        <div
          style={{
            display: "grid",
            gap: 10,
          }}
        >
          {libraryNodes.map((node) => {
            const inLens = node.data.lenses.includes(activeLens);

	            return (
	              <div
	                key={node.id}
	                className="nodrag nopan"
	                draggable={false}
	                onPointerDown={(event) => onLibraryPointerDown(event, node, inLens)}
	                onDoubleClick={() => setActiveKpiId(node.id)}
	                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 14,
                  padding: 12,
                  background: inLens ? "#f3f4f6" : "#fff",
                  cursor: inLens ? "default" : "grab",
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: 4,
                  }}
                >
                  {node.data.label}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#4b5563",
                    lineHeight: 1.45,
                    marginBottom: 8,
                  }}
                >
                  {toTitleCase(node.data.frequency)} |{" "}
                  {formatDataSourceLabel(node.data.dataSource)} |{" "}
                  {formatValueTypeLabel(node.data.valueType)}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#4b5563",
                    lineHeight: 1.45,
                    marginBottom: 8,
                  }}
                >
                  Owner: {node.data.owner || "Unassigned"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: inLens ? "#374151" : "#1d4ed8",
                    marginBottom: 10,
                  }}
                >
                  {inLens ? "Already on this lens" : "Drag onto the canvas to add"}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => {
                      setIsLensLibraryCollapsed(true);
                      setActiveKpiId(node.id);
                    }}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      flex: 1,
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#fff",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    Details
                  </button>
                  {inLens ? (
                    <button
                      type="button"
                      className="nodrag nopan"
                      onClick={() => onRemoveFromActiveLens(node.id)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => onDeleteKpi(node.id)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #dc2626",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#fff5f5",
                      color: "#b91c1c",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelResizeStart("kpi", event)}
          style={{
            position: "absolute",
            right: 8,
            bottom: 8,
            width: 28,
            height: 28,
            borderRadius: 8,
            background:
              "linear-gradient(135deg, rgba(209,213,219,0.95), rgba(156,163,175,0.95))",
            border: "1px solid #9ca3af",
            boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
            cursor: "nwse-resize",
            touchAction: "none",
            zIndex: 2,
          }}
        >
          <div
            style={{
              position: "absolute",
              right: 5,
              bottom: 5,
              width: 12,
              height: 12,
              borderRight: "2px solid #ffffff",
              borderBottom: "2px solid #ffffff",
              opacity: 0.95,
            }}
          />
        </div>
      </div>

      {displayedNodes.length === 0 ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.96)",
              border: "1px solid #d1d5db",
              borderRadius: 18,
              boxShadow: "0 16px 36px rgba(0,0,0,0.24)",
              padding: 24,
              textAlign: "center",
              maxWidth: 380,
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#111827",
                marginBottom: 8,
              }}
            >
              Start The {activeLensConfig?.name ?? activeLens} Lens
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#4b5563",
                lineHeight: 1.5,
                marginBottom: 16,
              }}
            >
              Add a new root KPI or drag an existing KPI in from the library to build this
              strategy map.
            </div>
            <button
              type="button"
              className="nodrag nopan"
              disabled={!canCreateKpis}
              onClick={onAddRootKpi}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "none",
                borderRadius: 12,
                padding: "10px 16px",
                fontSize: 14,
                fontWeight: 700,
                background: canCreateKpis ? "#111827" : "#f9fafb",
                color: canCreateKpis ? "#fff" : "#9ca3af",
                cursor: canCreateKpis ? "pointer" : "not-allowed",
              }}
            >
              Add First KPI
            </button>
          </div>
        </div>
      ) : null}

      {isCreateLensOpen ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            className="nodrag nopan"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "min(620px, 100%)",
              maxHeight: "min(78vh, 720px)",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 22,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 18,
                position: "sticky",
                top: -22,
                margin: "-22px -22px 18px",
                padding: "22px 22px 16px",
                background: "rgba(255,255,255,0.96)",
                borderBottom: "1px solid #e5e7eb",
                zIndex: 2,
                backdropFilter: "blur(6px)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#111827",
                    marginBottom: 6,
                  }}
                >
                  Create Lens
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#4b5563",
                    lineHeight: 1.5,
                  }}
                >
                  Build a lens by giving it a title and description.
                </div>
              </div>
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setIsCreateLensOpen(false)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#111827",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gap: 16,
              }}
            >
              <DetailField
                label="Lens Title"
                value={newLensForm.name}
                onChange={(value) =>
                  setNewLensForm((previous) => ({
                    ...previous,
                    name: value,
                  }))
                }
              />
              <DetailField
                label="Description"
                value={newLensForm.description}
                multiline
                onChange={(value) =>
                  setNewLensForm((previous) => ({
                    ...previous,
                    description: value,
                  }))
                }
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 18,
              }}
            >
              <button
                type="button"
                className="nodrag nopan"
                onClick={onCreateLens}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "none",
                  borderRadius: 12,
                  padding: "10px 16px",
                  fontSize: 14,
                  fontWeight: 700,
                  background: "#111827",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Create Lens
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activeLensDetailId && activeLensDetail ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            className="nodrag nopan"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "min(620px, 100%)",
              maxHeight: "min(78vh, 720px)",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 22,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 18,
                position: "sticky",
                top: -22,
                margin: "-22px -22px 18px",
                padding: "22px 22px 16px",
                background: "rgba(255,255,255,0.96)",
                borderBottom: "1px solid #e5e7eb",
                zIndex: 2,
                backdropFilter: "blur(6px)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 10 }}>
                  <DetailField
                    label="Lens Name"
                    value={lensNameDrafts[activeLensDetailId] ?? activeLensDetail.name}
                    onChange={(value) => onLensDraftChange(activeLensDetailId, value)}
                    onBlur={() =>
                      onRenameLens(
                        activeLensDetailId,
                        lensNameDrafts[activeLensDetailId] ?? activeLensDetail.name
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onRenameLens(
                          activeLensDetailId,
                          lensNameDrafts[activeLensDetailId] ?? activeLensDetail.name
                        );
                        (
                          e.currentTarget as HTMLInputElement | HTMLTextAreaElement
                        ).blur();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        onLensDraftChange(activeLensDetailId, activeLensDetail.name);
                        (
                          e.currentTarget as HTMLInputElement | HTMLTextAreaElement
                        ).blur();
                      }
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#4b5563",
                    lineHeight: 1.5,
                  }}
                >
                  {lensSummaries.find((item) => item.lens === activeLensDetailId)?.kpiCount ?? 0}{" "}
                  KPI
                  {(lensSummaries.find((item) => item.lens === activeLensDetailId)?.kpiCount ??
                    0) === 1
                    ? ""
                    : "s"}{" "}
                  currently assigned to this lens
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6, fontWeight: 700 }}>
                  Changes save automatically.
                </div>
              </div>
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setActiveLensDetailId(null)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#111827",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <DetailField
              label="Lens Description"
              value={activeLensDetail.description}
              multiline
              onChange={(value) => onLensConfigChange(activeLensDetailId, "description", value)}
            />
          </div>
        </div>
      ) : null}

      {activeKpi ? (
        <div
          className="nodrag nopan kpi-editor-backdrop"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            className="nodrag nopan kpi-editor-surface"
            role="dialog"
            aria-modal="true"
            aria-label={`Edit KPI: ${activeKpi.data.label || "Untitled KPI"}`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "min(860px, calc(100vw - 40px))",
              maxWidth: "calc(100vw - 48px)",
              maxHeight: "calc(100vh - 48px)",
              minWidth: 620,
              minHeight: 520,
              overflow: "auto",
              resize: "both",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 18,
            }}
          >
            <div
              className="kpi-editor-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 18,
                position: "sticky",
                top: -18,
                margin: "-18px -18px 14px",
                padding: "18px 18px 14px",
                background: "rgba(255,255,255,0.96)",
                borderBottom: "1px solid #e5e7eb",
                zIndex: 2,
                backdropFilter: "blur(6px)",
              }}
            >
              <div className="kpi-editor-heading" style={{ flex: 1 }}>
                <div className="kpi-editor-eyebrow">KPI workspace</div>
                <input
                  className="nodrag nopan"
                  value={activeKpi.data.label}
                  aria-label="KPI name"
                  readOnly={!activeKpiCanEdit}
                  onChange={(event) => activeKpi.data.onLabelChange(activeKpi.id, event.target.value)}
                  onBlur={() => onLabelCommit(activeKpi.id)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      (event.currentTarget as HTMLInputElement).blur();
                    }
                    if (event.key === "Escape") {
                      event.preventDefault();
                      (event.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                  style={{
                    width: "100%",
                    border: "1px solid transparent",
                    borderRadius: 10,
                    padding: "2px 0",
                    background: "transparent",
                    color: activeKpiCanEdit ? "#111827" : "#6b7280",
                    fontSize: 24,
                    fontWeight: 850,
                    lineHeight: 1.15,
                    cursor: activeKpiCanEdit ? "text" : "default",
                  }}
                />
                <div
                  className="kpi-editor-lineage"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 6,
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.35,
                    color: "#94a3b8",
                  }}
                >
                  {activeKpiLineageLabels.length > 0 ? (
                    <>
                      <span style={{ color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Lineage
                      </span>
                      {activeKpiLineageLabels.map((label, index) => (
                        <React.Fragment key={`${label}-${index}`}>
                          <span style={{ color: "#64748b" }}>/</span>
                          <span>{label}</span>
                        </React.Fragment>
                      ))}
                      <span style={{ color: "#64748b" }}>/</span>
                      <span style={{ color: "#f8fafc" }}>{activeKpi.data.label || "Untitled KPI"}</span>
                    </>
                  ) : (
                    <>
                      <span style={{ color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Root KPI
                      </span>
                      <span style={{ color: "#64748b" }}>/</span>
                      <span style={{ color: "#f8fafc" }}>{activeKpi.data.label || "Untitled KPI"}</span>
                    </>
                  )}
                </div>
                <div
                  className="kpi-editor-summary"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                    fontSize: 12,
                    color: "#6b7280",
                    lineHeight: 1.4,
                    fontWeight: 700,
                  }}
                >
                  <span>{toTitleCase(activeKpi.data.frequency)}</span>
                  <span style={{ color: "#d1d5db" }}>•</span>
                  <span>
                    {activeKpi.data.performanceDirection === "lower_is_better"
                      ? "Lower is better"
                      : "Higher is better"}
                  </span>
                  <span style={{ color: "#d1d5db" }}>•</span>
                  <span>{formatValueTypeLabel(activeKpi.data.valueType)}</span>
                  <span style={{ color: "#d1d5db" }}>•</span>
                  <span>{activeKpi.data.eventDate ? `Event ${activeKpi.data.eventDate}` : "Ongoing"}</span>
                  <span style={{ color: "#d1d5db" }}>•</span>
                  <span>{formatCalculationModeLabel(activeKpi.data.calculationMode)}</span>
                </div>
                <div className={`kpi-editor-save-status${activeKpiCanEdit ? "" : " is-read-only"}`}>
                  {activeKpiCanEdit
                    ? <><span aria-hidden="true" />All changes save automatically</>
                    : "Read-only: you can only edit KPIs you own or KPIs under one you own."}
                </div>
              </div>
              <button
                type="button"
                className="nodrag nopan kpi-editor-close"
                onClick={() => setActiveKpiId(null)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                aria-label="Close KPI editor"
                title="Close KPI editor"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            <div
              style={{
                marginTop: 6,
                display: "grid",
                gap: 12,
              }}
            >
              <div
                className="kpi-editor-section"
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  background: "#fff",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  className="nodrag nopan kpi-editor-toggle"
                  onClick={() => setIsKpiInfoOpen((current) => !current)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    border: "none",
                    background: "#f9fafb",
                    color: "#111827",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 16, fontWeight: 800 }}>Details &amp; performance</div>
                    <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                      Current results, ownership, reporting, and measurement
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}>
                    {isKpiInfoOpen ? "Collapse" : "Open"}
                  </div>
                </button>

                {isKpiInfoOpen ? (
                  <div
                    className="kpi-editor-section-body"
                    style={{
                      padding: 12,
                      display: "grid",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Reporting Period
                        </div>
                        <select
                          className="nodrag nopan"
                          value={activeKpi.data.frequency}
                          onChange={(e) =>
                            activeKpi.data.onDetailChange(
                              activeKpi.id,
                              "frequency",
                              e.target.value
                            )
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          {frequencyOptions.map((option) => (
                            <option key={option} value={option}>
                              {formatEnumLabel(option)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {activeKpi.data.dataSource === "API" ? (
                        <div
                          style={{
                            background: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 14,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#6b7280",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              marginBottom: 8,
                            }}
                          >
                            API Connection
                          </div>
                          <div style={{ display: "grid", gap: 10 }}>
                            <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                              Connector
                              <select
                                className="nodrag nopan"
                                value={(getEffectiveKpiApiConfig(activeKpi) ?? defaultPushpayGroupAttendanceApiConfig).provider}
                                onChange={(event) =>
                                  activeKpi.data.onApiConfigChange(
                                    activeKpi.id,
                                    event.target.value === "quickbooks"
                                      ? getQuickBooksFinanceConfigForLabel(activeKpi.data.label) ?? defaultQuickBooksExpensesApiConfig
                                      : getCampusAttendanceConfigForLabel(activeKpi.data.label) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig
                                  )
                                }
                                onMouseDown={stopCanvasDrag}
                                onPointerDown={stopCanvasDrag}
                                style={{
                                  width: "100%",
                                  boxSizing: "border-box",
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  color: "#111827",
                                  background: "#fff",
                                }}
                              >
                                <option value="pushpay_chms">Pushpay / CCB ChMS</option>
                                <option value="quickbooks">QuickBooks</option>
                              </select>
                            </label>
                            <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                              API data source
                              <select
                                className="nodrag nopan"
                                value={(getEffectiveKpiApiConfig(activeKpi) ?? defaultPushpayGroupAttendanceApiConfig).resource}
                                onChange={(event) => {
                                  const resource = event.target.value as KpiApiResource;
                                  const currentConfig = normalizeKpiApiConfig(activeKpi.data.apiConfig);
                                  if (resource === "campus_weekend_attendance") {
                                    setKpiApiStartDate("2023-03-01");
                                  }
                                  activeKpi.data.onApiConfigChange(activeKpi.id, {
                                    ...(currentConfig ?? {}),
                                    ...(resource === "quickbooks_giving"
                                      ? getQuickBooksFinanceConfigForLabel(activeKpi.data.label) ?? defaultQuickBooksGivingApiConfig
                                      : resource === "quickbooks_expenses"
                                        ? getQuickBooksFinanceConfigForLabel(activeKpi.data.label) ?? defaultQuickBooksExpensesApiConfig
                                      : resource === "quickbooks_account_balance"
                                        ? getQuickBooksFinanceConfigForLabel(activeKpi.data.label) ?? defaultQuickBooksAccountBalanceApiConfig
                                      : resource === "event_attendance"
                                      ? defaultPushpayEventAttendanceApiConfig
                                      : resource === "campus_weekend_attendance"
                                        ? defaultPushpayCampusAttendanceApiConfig
                                        : resource === "process_queue_count"
                                          ? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig
                                        : defaultPushpayGroupAttendanceApiConfig),
                                    resource,
                                  });
                                }}
                                onMouseDown={stopCanvasDrag}
                                onPointerDown={stopCanvasDrag}
                                style={{
                                  width: "100%",
                                  boxSizing: "border-box",
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  color: "#111827",
                                  background: "#fff",
                                }}
                              >
                                {pushpayResourceOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                                {quickBooksResourceOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            {(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "quickbooks_giving" ||
                            (normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "quickbooks_expenses" ||
                            (normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "quickbooks_account_balance" ? (
                              <div style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                QuickBooks mapping
                                <div
                                  style={{
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 13,
                                    color: "#111827",
                                    background: "#f9fafb",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  Uses auto-detected accounts
                                  {(() => {
                                    const config = normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultQuickBooksExpensesApiConfig;
                                    const dimension = config.resource === "quickbooks_account_balance" ? "" : config.dimensionName || config.locationName || "";
                                    return dimension ? ` and matches ${dimension} by location/department.` : ".";
                                  })()}
                                </div>
                              </div>
                            ) : (normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "campus_weekend_attendance" ? (
                              <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                Campus attendance preset
                                <select
                                  className="nodrag nopan"
                                  value={
                                    (normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayCampusAttendanceApiConfig)
                                      .campusAttendancePreset
                                  }
                                  onChange={(event) =>
                                    activeKpi.data.onApiConfigChange(activeKpi.id, {
                                      ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayCampusAttendanceApiConfig),
                                      campusAttendancePreset: event.target.value as PushpayCampusAttendancePreset,
                                    })
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#fff",
                                  }}
                                >
                                  {pushpayCampusAttendanceOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            ) : (normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "event_attendance" ? (
                              <>
                                <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                  Event ID
                                  <input
                                    className="nodrag nopan"
                                    placeholder="Example: 2090"
                                    value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayEventAttendanceApiConfig).eventId}
                                    onChange={(event) =>
                                      activeKpi.data.onApiConfigChange(activeKpi.id, {
                                        ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayEventAttendanceApiConfig),
                                        eventId: event.target.value,
                                      })
                                    }
                                    onMouseDown={stopCanvasDrag}
                                    onPointerDown={stopCanvasDrag}
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      color: "#111827",
                                      background: "#fff",
                                    }}
                                  />
                                </label>
                                <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                  Event name
                                  <input
                                    className="nodrag nopan"
                                    placeholder="Example: 9:00am Service"
                                    value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayEventAttendanceApiConfig).eventSearch}
                                    onChange={(event) =>
                                      activeKpi.data.onApiConfigChange(activeKpi.id, {
                                        ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayEventAttendanceApiConfig),
                                        eventSearch: event.target.value,
                                      })
                                    }
                                    onMouseDown={stopCanvasDrag}
                                    onPointerDown={stopCanvasDrag}
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      color: "#111827",
                                      background: "#fff",
                                    }}
                                  />
                                </label>
                                <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                  Calendar / campus filter
                                  <input
                                    className="nodrag nopan"
                                    placeholder="Example: North Campus"
                                    value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayEventAttendanceApiConfig).calendarSearch}
                                    onChange={(event) =>
                                      activeKpi.data.onApiConfigChange(activeKpi.id, {
                                        ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? defaultPushpayEventAttendanceApiConfig),
                                        calendarSearch: event.target.value,
                                      })
                                    }
                                    onMouseDown={stopCanvasDrag}
                                    onPointerDown={stopCanvasDrag}
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      color: "#111827",
                                      background: "#fff",
                                    }}
                                  />
                                </label>
                              </>
                            ) : (getEffectiveKpiApiConfig(activeKpi) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "process_queue_count" ? (
                              <>
                                <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                  Process filter
                                  <input
                                    className="nodrag nopan"
                                    placeholder="Example: Pastoral Care, Guest Book, or blank for all processes"
                                    value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig).processSearch}
                                    onChange={(event) =>
                                      activeKpi.data.onApiConfigChange(activeKpi.id, {
                                        ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig),
                                        processSearch: event.target.value,
                                        processId: undefined,
                                        queueId: undefined,
                                      })
                                    }
                                    onMouseDown={stopCanvasDrag}
                                    onPointerDown={stopCanvasDrag}
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      color: "#111827",
                                      background: "#fff",
                                    }}
                                  />
                                </label>
                                <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                  Queue filter
                                  <input
                                    className="nodrag nopan"
                                    placeholder="Example: Pastoral Care Calls, Call, Meeting"
                                    value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig).queueSearch}
                                    onChange={(event) =>
                                      activeKpi.data.onApiConfigChange(activeKpi.id, {
                                        ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig),
                                        queueSearch: event.target.value,
                                        queueId: undefined,
                                      })
                                    }
                                    onMouseDown={stopCanvasDrag}
                                    onPointerDown={stopCanvasDrag}
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      color: "#111827",
                                      background: "#fff",
                                    }}
                                  />
                                </label>
                                <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                  Queue status
                                  <select
                                    className="nodrag nopan"
                                    value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig).queueStatus}
                                    onChange={(event) =>
                                      activeKpi.data.onApiConfigChange(activeKpi.id, {
                                        ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig),
                                        queueStatus: event.target.value as CcbProcessQueueStatus,
                                      })
                                    }
                                    onMouseDown={stopCanvasDrag}
                                    onPointerDown={stopCanvasDrag}
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      color: "#111827",
                                      background: "#fff",
                                    }}
                                  >
                                    {ccbProcessQueueStatusOptions.map((option) => (
                                      <option key={option.value || "any"} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </>
                            ) : (getEffectiveKpiApiConfig(activeKpi) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "schedule_volunteer_checkins" ? (
                              <div style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                Campus category filter
                                <div
                                  style={{
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#f9fafb",
                                  }}
                                >
                                  {(getEffectiveKpiApiConfig(activeKpi) ?? defaultPushpayGroupAttendanceApiConfig).scheduleSearch}
                                </div>
                              </div>
                            ) : (
                              <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                Group filter
                                <input
                                  className="nodrag nopan"
                                  placeholder="Example: Sonrise, Kesher, Discipleship, or blank for all groups"
                                  value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig).groupSearch}
                                  onChange={(event) =>
                                    activeKpi.data.onApiConfigChange(activeKpi.id, {
                                      ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig),
                                      groupSearch: event.target.value,
                                    })
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#fff",
                                  }}
                                />
                              </label>
                            )}
                            {(getEffectiveKpiApiConfig(activeKpi) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "process_queue_count" ? (
                              <div style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                Metric
                                <div
                                  style={{
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#f9fafb",
                                  }}
                                >
                                  {(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getProcessQueueConfigForLabel(activeKpi.data.label) ?? defaultCcbProcessQueueApiConfig).queueStatus === "done"
                                    ? "Completed items by period"
                                    : "Current queue count"}
                                </div>
                              </div>
                            ) : (getEffectiveKpiApiConfig(activeKpi) ?? defaultPushpayGroupAttendanceApiConfig).resource ===
                            "schedule_volunteer_checkins" ? (
                              <div style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                Metric
                                <div
                                  style={{
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#f9fafb",
                                  }}
                                >
                                  Checked-in volunteers
                                </div>
                              </div>
                            ) : (
                              <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                Metric
                                <select
                                  className="nodrag nopan"
                                  value={(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig).metric}
                                  onChange={(event) =>
                                    activeKpi.data.onApiConfigChange(activeKpi.id, {
                                      ...(normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig),
                                      metric: event.target.value as PushpayAttendanceMetric,
                                    })
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#fff",
                                  }}
                                >
                                  {pushpayAttendanceMetricOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            )}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                              <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                Start
                                <input
                                  className="nodrag nopan"
                                  type="date"
                                  value={kpiApiStartDate}
                                  onChange={(event) => setKpiApiStartDate(event.target.value)}
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#fff",
                                  }}
                                />
                              </label>
                              <label style={{ display: "grid", gap: 4, fontSize: 12, fontWeight: 700, color: "#374151" }}>
                                End
                                <input
                                  className="nodrag nopan"
                                  type="date"
                                  value={kpiApiEndDate}
                                  onChange={(event) => setKpiApiEndDate(event.target.value)}
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    color: "#111827",
                                    background: "#fff",
                                  }}
                                />
                              </label>
                            </div>
                            <button
                              type="button"
                              disabled={
                                isKpiApiLoading ||
                                !isKpiApiSourceAvailable(
                                  normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                  pushpayStatus?.connected,
                                  quickBooksStatus?.connected
                                )
                              }
                              onClick={() => void onPreviewKpiApiSource(activeKpi.id)}
                              style={{
                                border: "1px solid #bfdbfe",
                                borderRadius: 10,
                                padding: "10px 12px",
                                fontSize: 14,
                                fontWeight: 800,
                                color:
                                  isKpiApiLoading ||
                                  !isKpiApiSourceAvailable(
                                    normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                    pushpayStatus?.connected,
                                    quickBooksStatus?.connected
                                  )
                                    ? "#6b7280"
                                    : "#1d4ed8",
                                background:
                                  isKpiApiLoading ||
                                  !isKpiApiSourceAvailable(
                                    normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                    pushpayStatus?.connected,
                                    quickBooksStatus?.connected
                                  )
                                    ? "#e5e7eb"
                                    : "#eff6ff",
                                cursor:
                                  isKpiApiLoading ||
                                  !isKpiApiSourceAvailable(
                                    normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                    pushpayStatus?.connected,
                                    quickBooksStatus?.connected
                                  )
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                            >
                              {isKpiApiLoading ? "Checking..." : "Check API Data"}
                            </button>
                            <button
                              type="button"
                              disabled={
                                isKpiApiLoading ||
                                !isKpiApiSourceAvailable(
                                  normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                  pushpayStatus?.connected,
                                  quickBooksStatus?.connected
                                )
                              }
                              onClick={() => void onSyncKpiApiSource(activeKpi.id)}
                              style={{
                                border: "1px solid #16a34a",
                                borderRadius: 10,
                                padding: "10px 12px",
                                fontSize: 14,
                                fontWeight: 800,
                                color:
                                  isKpiApiLoading ||
                                  !isKpiApiSourceAvailable(
                                    normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                    pushpayStatus?.connected,
                                    quickBooksStatus?.connected
                                  )
                                    ? "#6b7280"
                                    : "#fff",
                                background:
                                  isKpiApiLoading ||
                                  !isKpiApiSourceAvailable(
                                    normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                    pushpayStatus?.connected,
                                    quickBooksStatus?.connected
                                  )
                                    ? "#e5e7eb"
                                    : "#16a34a",
                                cursor:
                                  isKpiApiLoading ||
                                  !isKpiApiSourceAvailable(
                                    normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                    pushpayStatus?.connected,
                                    quickBooksStatus?.connected
                                  )
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                            >
                              {isKpiApiLoading ? "Syncing..." : "Sync KPI Actuals"}
                            </button>
                            {kpiApiMessage ? (
                              <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45 }}>
                                {kpiApiMessage}
                              </div>
                            ) : !isKpiApiSourceAvailable(
                                normalizeKpiApiConfig(activeKpi.data.apiConfig) ?? getKpiApiConfigForLabel(activeKpi.data.label) ?? defaultPushpayGroupAttendanceApiConfig,
                                pushpayStatus?.connected,
                                quickBooksStatus?.connected
                              ) ? (
                              <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.45 }}>
                                Connect the required API in Settings before syncing this KPI source.
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          KPI Calculation
                          <InlineHelp
                            text={
                              activeKpi.data.calculationMode === "calculated_values"
                                ? "Target and actual are read-only totals calculated from child KPIs. Relationship percentages are not used."
                                : activeKpi.data.calculationMode === "formula_percentage"
                                  ? "Target is entered directly. Actual is calculated from a numerator KPI divided by a denominator KPI."
                                  : activeKpi.data.calculationMode === "influenced_by_children"
                                    ? "Performance is calculated from child KPI scores using relationship percentages."
                                    : "Target and actual are entered directly for this KPI."
                            }
                          />
                        </div>
                        <select
                          className="nodrag nopan"
                          value={activeKpi.data.calculationMode}
                          onChange={(e) =>
                            activeKpi.data.onConfigChange(
                              activeKpi.id,
                              "calculationMode",
                              e.target.value as KpiCalculationMode
                            )
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          {calculationModeOptions.map((option) => (
                            <option key={option} value={option}>
                              {formatCalculationModeLabel(option)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {activeKpi.data.calculationMode === "formula_percentage" ? (
                        <>
                          <div
                            style={{
                              background: "#f9fafb",
                              border: "1px solid #e5e7eb",
                              borderRadius: 12,
                              padding: 14,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#6b7280",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 4,
                              }}
                            >
                              Numerator KPI
                            </div>
                            <select
                              className="nodrag nopan"
                              value={activeKpi.data.formulaNumeratorKpiId ?? ""}
                              onChange={(e) =>
                                activeKpi.data.onDetailChange(
                                  activeKpi.id,
                                  "formulaNumeratorKpiId",
                                  e.target.value
                                )
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "10px 12px",
                                fontSize: 14,
                                color: "#111827",
                                background: "#fff",
                              }}
                            >
                              <option value="">Select numerator KPI</option>
                              {allNodes
                                .filter((node) => node.id !== activeKpi.id)
                                .map((node) => (
                                  <option key={node.id} value={node.id}>
                                    {node.data.label}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div
                            style={{
                              background: "#f9fafb",
                              border: "1px solid #e5e7eb",
                              borderRadius: 12,
                              padding: 14,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#6b7280",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 4,
                              }}
                            >
                              Denominator KPI
                            </div>
                            <select
                              className="nodrag nopan"
                              value={activeKpi.data.formulaDenominatorKpiId ?? ""}
                              onChange={(e) =>
                                activeKpi.data.onDetailChange(
                                  activeKpi.id,
                                  "formulaDenominatorKpiId",
                                  e.target.value
                                )
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "10px 12px",
                                fontSize: 14,
                                color: "#111827",
                                background: "#fff",
                              }}
                            >
                              <option value="">Select denominator KPI</option>
                              {allNodes
                                .filter((node) => node.id !== activeKpi.id)
                                .map((node) => (
                                  <option key={node.id} value={node.id}>
                                    {node.data.label}
                                  </option>
                                ))}
                            </select>
                          </div>
                        </>
                      ) : null}
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Owner
                        </div>
                        <select
                          className="nodrag nopan"
                          value={userOwnerOptions.includes(activeKpi.data.owner) ? activeKpi.data.owner : ""}
                          onChange={(e) =>
                            activeKpi.data.onDetailChange(activeKpi.id, "owner", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          <option value="">Select owner</option>
                          {userOwnerOptions.map((owner) => (
                            <option key={owner} value={owner}>
                              {owner}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Data owner
                          <InlineHelp text="Data owners receive actual-value update requests. Leave blank to use the KPI owner." />
                        </div>
                        <select
                          className="nodrag nopan"
                          value={userOwnerOptions.includes(activeKpi.data.dataOwner) ? activeKpi.data.dataOwner : ""}
                          onChange={(e) =>
                            activeKpi.data.onDetailChange(activeKpi.id, "dataOwner", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          <option value="">Use KPI owner</option>
                          {userOwnerOptions.map((owner) => (
                            <option key={owner} value={owner}>
                              {owner}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Performance Rule
                        </div>
                        <select
                          className="nodrag nopan"
                          value={activeKpi.data.performanceDirection}
                          onChange={(e) =>
                            activeKpi.data.onConfigChange(
                              activeKpi.id,
                              "performanceDirection",
                              e.target.value as PerformanceDirection
                            )
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          {performanceDirectionOptions.map((option) => (
                            <option key={option} value={option}>
                              {option === "lower_is_better"
                                ? "Lower is better"
                                : "Higher is better"}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Event Date
                          <InlineHelp text="Leave blank for an ongoing KPI. Before this date, the KPI stays neutral and shows as upcoming." />
                        </div>
                        <input
                          className="nodrag nopan"
                          type="date"
                          value={activeKpi.data.eventDate}
                          onChange={(e) =>
                            activeKpi.data.onDetailChange(
                              activeKpi.id,
                              "eventDate",
                              e.target.value
                            )
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color: "#111827",
                            background: "#fff",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Data Source
                        </div>
                        <select
                          className="nodrag nopan"
                          value={
                            dataSourceOptions.includes(
                              activeKpi.data.dataSource as (typeof dataSourceOptions)[number]
                            )
                              ? activeKpi.data.dataSource
                              : "Manual"
                          }
                          onChange={(e) =>
                            activeKpi.data.onDetailChange(activeKpi.id, "dataSource", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          {dataSourceOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Value Type
                          {(activeKpi.data.dataSource === "Poll" ||
                            activeKpi.data.calculationMode === "formula_percentage") ? (
                            <InlineHelp
                              text={
                                activeKpi.data.dataSource === "Poll"
                                  ? "Poll-based KPIs always use a 1-10 rating value."
                                  : "Formula percentage KPIs always use a percentage value type."
                              }
                            />
                          ) : null}
                        </div>
                        <select
                          className="nodrag nopan"
                          value={
                            activeKpi.data.calculationMode === "formula_percentage"
                              ? "percentage"
                              : activeKpi.data.dataSource === "Poll"
                                ? "rating_1_10"
                                : activeKpi.data.valueType
                          }
                          onChange={(e) =>
                            activeKpi.data.onDetailChange(activeKpi.id, "valueType", e.target.value)
                          }
                          disabled={
                            activeKpi.data.dataSource === "Poll" ||
                            activeKpi.data.calculationMode === "formula_percentage"
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 14,
                            color:
                              activeKpi.data.dataSource === "Poll" ||
                              activeKpi.data.calculationMode === "formula_percentage"
                                ? "#6b7280"
                                : "#111827",
                            background:
                              activeKpi.data.dataSource === "Poll" ||
                              activeKpi.data.calculationMode === "formula_percentage"
                                ? "#f3f4f6"
                                : "#fff",
                            cursor:
                              activeKpi.data.dataSource === "Poll" ||
                              activeKpi.data.calculationMode === "formula_percentage"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          <option value="count">Count</option>
                          <option value="currency">Currency</option>
                          <option value="percentage">Percentage</option>
                          <option value="rating_1_10">Rating (1-10)</option>
                        </select>
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Target
                          <InlineHelp
                            text={
                              !activeKpiCanEdit
                                ? "You can only edit KPI targets you own or have permission to manage."
                                : activeKpi.data.calculationMode === "calculated_values"
                                  ? "Calculated from child KPI targets."
                                  : activeKpi.data.calculationMode === "formula_percentage"
                                    ? "Set the desired percentage target for this KPI."
                                    : "Set the desired result for the current reporting period."
                            }
                          />
                        </div>
                        <div style={{ position: "relative" }}>
                          {getMetricInputPrefix(activeKpi.data.valueType) ? (
                            <span
                              style={{
                                position: "absolute",
                                left: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: 14,
                                color: "#6b7280",
                                pointerEvents: "none",
                              }}
                            >
                              {getMetricInputPrefix(activeKpi.data.valueType)}
                            </span>
                          ) : null}
                          <input
                            ref={actualInputRef}
                            className="nodrag nopan"
                            type="number"
                            min={activeKpi.data.valueType === "rating_1_10" ? 1 : undefined}
                            max={activeKpi.data.valueType === "rating_1_10" ? 10 : undefined}
                            step={getMetricInputStep(activeKpi.data.valueType)}
                            disabled={activeKpiTargetReadOnly}
                            value={activeKpiTargetInputValue}
                            onChange={(e) => activeKpi.data.onMetricChange(activeKpi.id, "target", e.target.value)}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              width: "100%",
                              boxSizing: "border-box",
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: `10px ${getMetricInputSuffix(activeKpi.data.valueType) ? 30 : 12}px 10px ${
                                getMetricInputPrefix(activeKpi.data.valueType) ? 28 : 12
                              }px`,
                              fontSize: 14,
                              color: activeKpiTargetReadOnly ? metricInputReadOnlyStyle.color : "#111827",
                              background: activeKpiTargetReadOnly ? metricInputReadOnlyStyle.background : "#fff",
                              cursor: activeKpiTargetReadOnly ? metricInputReadOnlyStyle.cursor : "text",
                            }}
                          />
                          {getMetricInputSuffix(activeKpi.data.valueType) ? (
                            <span
                              style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: 14,
                                color: "#6b7280",
                                pointerEvents: "none",
                              }}
                            >
                              {getMetricInputSuffix(activeKpi.data.valueType)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 4,
                          }}
                        >
                          Actual
                          <InlineHelp
                            text={
                              !activeKpiCanEdit
                                ? "You can only edit KPI actuals you own or have permission to manage."
                                : activeKpi.data.calculationMode === "calculated_values"
                                  ? "Calculated from child KPI actuals."
                                  : activeKpi.data.calculationMode === "formula_percentage"
                                    ? "Calculated from numerator and denominator KPI actuals."
                                    : activeKpi.data.dataSource === "Poll"
                                      ? "Poll KPIs calculate actual performance from submitted responses."
                                      : "Actual reflects the value stored for the current reporting period."
                            }
                          />
                        </div>
                        <div style={{ position: "relative" }}>
                          {getMetricInputPrefix(activeKpi.data.valueType) ? (
                            <span
                              style={{
                                position: "absolute",
                                left: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: 14,
                                color: "#6b7280",
                                pointerEvents: "none",
                              }}
                            >
                              {getMetricInputPrefix(activeKpi.data.valueType)}
                            </span>
                          ) : null}
                          <input
                            className="nodrag nopan"
                            type="number"
                            min={activeKpi.data.valueType === "rating_1_10" ? 1 : undefined}
                            max={activeKpi.data.valueType === "rating_1_10" ? 10 : undefined}
                            step={getMetricInputStep(activeKpi.data.valueType)}
                            disabled={activeKpiActualReadOnly}
                            value={activeKpiActualInputValue}
                            onChange={(e) =>
                              activeKpi.data.onMetricChange(activeKpi.id, "actual", e.target.value)
                            }
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              width: "100%",
                              boxSizing: "border-box",
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: `10px ${getMetricInputSuffix(activeKpi.data.valueType) ? 30 : 12}px 10px ${
                                getMetricInputPrefix(activeKpi.data.valueType) ? 28 : 12
                              }px`,
                              fontSize: 14,
                              color: activeKpiActualReadOnly ? metricInputReadOnlyStyle.color : "#111827",
                              background: activeKpiActualReadOnly ? metricInputReadOnlyStyle.background : "#fff",
                              cursor: activeKpiActualReadOnly ? metricInputReadOnlyStyle.cursor : "text",
                            }}
                          />
                          {getMetricInputSuffix(activeKpi.data.valueType) ? (
                            <span
                              style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: 14,
                                color: "#6b7280",
                                pointerEvents: "none",
                              }}
                            >
                              {getMetricInputSuffix(activeKpi.data.valueType)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gap: 16,
                      }}
                    >
                      <DetailField
                        label="Definition"
                        value={activeKpi.data.definition}
                        multiline
                        onChange={(value) =>
                          activeKpi.data.onDetailChange(activeKpi.id, "definition", value)
                        }
                      />
                      <DetailField
                        label="How It Is Measured"
                        value={activeKpi.data.measurement}
                        multiline
                        onChange={(value) =>
                          activeKpi.data.onDetailChange(activeKpi.id, "measurement", value)
                        }
                      />
                      <DetailField
                        label="Update Notes"
                        value={activeKpi.data.updateNotes}
                        multiline
                        onChange={(value) =>
                          activeKpi.data.onDetailChange(activeKpi.id, "updateNotes", value)
                        }
                      />
                      {canManageWorkspace ? (
                        <div
                          style={{
                            border: "1px solid #263247",
                            borderRadius: 12,
                            padding: 14,
                            background: "#0b111d",
                            display: "grid",
                            gap: 10,
                          }}
                        >
                          <button
                            type="button"
                            className="nodrag nopan"
                            aria-expanded={isRestrictedFieldsOpen}
                            onClick={() => setIsRestrictedFieldsOpen((current) => !current)}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              width: "100%",
                              padding: 0,
                              border: 0,
                              background: "transparent",
                              color: "inherit",
                              textAlign: "left",
                              cursor: "pointer",
                            }}
                          >
                            <span>
                              <span style={{ display: "block", fontSize: 12, fontWeight: 900, textTransform: "uppercase", color: "#8b95a7" }}>
                                Restricted fields
                              </span>
                              <span style={{ display: "block", fontSize: 12, color: "#c8d0de", marginTop: 4, lineHeight: 1.45 }}>
                                Hide sensitive KPI information by access tag.
                              </span>
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 900, color: "#7ee2a8" }}>
                              {isRestrictedFieldsOpen ? "Collapse" : "Open"}
                            </span>
                          </button>
                          {isRestrictedFieldsOpen ? (
                            <div style={{ display: "grid", gap: 8, paddingTop: 4 }}>
                            {restrictedKpiFieldOptions.map((field) => {
                              const currentTags = activeKpi.data.restrictedFields?.[field.key] ?? [];
                              const isRestricted = currentTags.length > 0;
                              const selectedTag = currentTags[0] ?? accessTagOptions[0].key;
                              const setRestrictedFields = (nextTags: string[]) => {
                                const nextRestrictedFields = { ...(activeKpi.data.restrictedFields ?? {}) };
                                if (nextTags.length > 0) {
                                  nextRestrictedFields[field.key] = nextTags;
                                } else {
                                  delete nextRestrictedFields[field.key];
                                }
                                activeKpi.data.onRestrictedFieldsChange(activeKpi.id, nextRestrictedFields);
                              };
                              return (
                                <label
                                  key={field.key}
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(140px, 1fr) 160px",
                                    gap: 10,
                                    alignItems: "center",
                                    color: "#f8fafc",
                                    fontSize: 13,
                                    fontWeight: 800,
                                  }}
                                >
                                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <input
                                      type="checkbox"
                                      checked={isRestricted}
                                      onChange={(event) => setRestrictedFields(event.target.checked ? [selectedTag] : [])}
                                    />
                                    {field.label}
                                  </span>
                                  <select
                                    aria-label={`${field.label} access tag`}
                                    value={selectedTag}
                                    disabled={!isRestricted}
                                    onChange={(event) => setRestrictedFields([event.target.value])}
                                    style={{
                                      border: "1px solid #334155",
                                      borderRadius: 8,
                                      padding: "7px 9px",
                                      background: isRestricted ? "#111827" : "#0f172a",
                                      color: isRestricted ? "#f8fafc" : "#64748b",
                                      fontWeight: 800,
                                    }}
                                  >
                                    {accessTagOptions.map((tag) => (
                                      <option key={tag.key} value={tag.key}>
                                        {tag.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              );
                            })}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={{
                        display: "none",
                        border: "1px solid #e5e7eb",
                        borderRadius: 16,
                        padding: 16,
                        gap: 12,
                        background: "#fafafa",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 800 }}>Polling</div>
                          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                            Create a poll for this KPI so you can collect responses later.
                          </div>
                        </div>
                        {!activeKpi.data.pollConfig ? (
                          <button
                            type="button"
                            className="nodrag nopan"
                            onClick={() => {
                              onCreatePoll(activeKpi.id);
                              setPollEditorMode("edit");
                              setPollEditorKpiId(activeKpi.id);
                            }}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 12px",
                              background: "#fff",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Create Poll
                          </button>
                        ) : (
                          <div style={{ display: "grid", gap: 8 }}>
                            <button
                              type="button"
                              className="nodrag nopan"
                              onClick={() => {
                                setPollEditorMode("view");
                                setPollEditorKpiId(activeKpi.id);
                              }}
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                border: "1px solid #bfdbfe",
                                borderRadius: 10,
                                padding: "8px 12px",
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                fontWeight: 800,
                                cursor: "pointer",
                              }}
                            >
                              View Polling Data
                            </button>
                            <button
                              type="button"
                              className="nodrag nopan"
                              onClick={() => {
                                setPollEditorMode("edit");
                                setPollEditorKpiId(activeKpi.id);
                              }}
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "8px 12px",
                                background: "#fff",
                                color: "#111827",
                                fontWeight: 700,
                                cursor: "pointer",
                              }}
                            >
                              Edit Poll
                            </button>
                          </div>
                        )}
                      </div>

                      {activeKpi.data.pollConfig ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            borderTop: "1px solid #e5e7eb",
                            paddingTop: 12,
                          }}
                        >
                          <div style={{ display: "grid", gap: 4 }}>
                            <div style={{ fontSize: 14, fontWeight: 800 }}>
                              {activeKpi.data.pollConfig.title}
                            </div>
                          <div style={{ fontSize: 13, color: "#6b7280" }}>
                            {activeKpi.data.pollConfig.isAnonymous ? "Anonymous" : "Identified"} rating poll for{" "}
                            {(activeKpi.data.pollConfig.targetGroups?.length ?? 0) > 0
                              ? activeKpi.data.pollConfig.targetGroups.join(", ")
                              : activeKpi.data.pollConfig.targetGroup || "selected groups"}
                          </div>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 800,
                              color:
                                activeKpi.data.pollConfig.status === "published"
                                  ? "#166534"
                                  : "#92400e",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {activeKpi.data.pollConfig.status}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              {(activeKpi.data.dataSource === "Poll" || activeKpi.data.pollConfig) ? <div
                className="kpi-editor-section kpi-polling-section"
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  background: "#fff",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    background: "#f9fafb",
                    color: "#111827",
                  }}
                >
                  <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 16, fontWeight: 800 }}>Polling</div>
                    <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                      Create, publish, and review poll-based KPI responses
                    </div>
                  </div>
                  {!activeKpi.data.pollConfig ? (
                    <button
                      type="button"
                      className="nodrag nopan"
                      onClick={() => {
                        onCreatePoll(activeKpi.id);
                        setPollEditorMode("edit");
                        setPollEditorKpiId(activeKpi.id);
                      }}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 12px",
                        background: "#fff",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Create Poll
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="nodrag nopan"
                        onClick={() => {
                          setPollEditorMode("view");
                          setPollEditorKpiId(activeKpi.id);
                        }}
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          border: "1px solid #bfdbfe",
                          borderRadius: 10,
                          padding: "8px 12px",
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          fontWeight: 800,
                          cursor: "pointer",
                        }}
                      >
                        View Polling Data
                      </button>
                      <button
                        type="button"
                        className="nodrag nopan"
                        onClick={() => {
                          setPollEditorMode("edit");
                          setPollEditorKpiId(activeKpi.id);
                        }}
                        onMouseDown={stopCanvasDrag}
                        onPointerDown={stopCanvasDrag}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "8px 12px",
                          background: "#fff",
                          color: "#111827",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Edit Poll
                      </button>
                    </div>
                  )}
                </div>

                {activeKpi.data.pollConfig ? (
                  <div
                    style={{
                      padding: 16,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "grid", gap: 4 }}>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>
                        {activeKpi.data.pollConfig.title}
                      </div>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>
                        {activeKpi.data.pollConfig.isAnonymous ? "Anonymous" : "Identified"} rating poll for{" "}
                        {(activeKpi.data.pollConfig.targetGroups?.length ?? 0) > 0
                          ? activeKpi.data.pollConfig.targetGroups.join(", ")
                          : activeKpi.data.pollConfig.targetGroup || "selected groups"}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color:
                          activeKpi.data.pollConfig.status === "published"
                            ? "#166534"
                            : "#92400e",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {activeKpi.data.pollConfig.status}
                    </div>
                  </div>
                ) : null}
              </div> : null}

              <div
                className="kpi-work-section"
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  background: "#fff",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  className="nodrag nopan kpi-work-toggle"
                  onClick={() => setIsKpiWorkOpen((current) => !current)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    border: "none",
                    background: "#f9fafb",
                    color: "#111827",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 16, fontWeight: 800 }}>Initiatives &amp; tasks</div>
                    <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                      Manage direct KPI tasks and initiative-based work
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}>
                    {isKpiWorkOpen ? "Collapse" : "Open"}
                  </div>
                </button>

                {isKpiWorkOpen ? (
                  <div
                    style={{
                      padding: 16,
                      display: "grid",
                      gap: 18,
                    }}
                  >
                    {kpiWorkEmailStatusMessage ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          border: "1px solid #bfdbfe",
                          borderRadius: 12,
                          padding: "10px 12px",
                          background: "#eff6ff",
                          color: "#1e3a8a",
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        <span style={{ flex: 1 }}>{kpiWorkEmailStatusMessage}</span>
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => setKpiWorkEmailStatusMessage(null)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#1d4ed8",
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 900,
                            padding: 0,
                          }}
                        >
                          Dismiss
                        </button>
                      </div>
                    ) : null}
              <div
                className="kpi-work-direct-card"
                style={{
                  order: 2,
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: "#111827",
                      }}
                    >
                      KPI Tasks
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#4b5563",
                        lineHeight: 1.45,
                      }}
                    >
                      Assign tasks directly to this KPI outside of a specific initiative.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="nodrag nopan kpi-work-action-button"
                      onClick={() => onAddKpiTask(activeKpi.id)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      + Task
                    </button>
                    <button
                      type="button"
                      className="nodrag nopan kpi-work-ai-button"
                      onClick={() => onGenerateContextualAiDraft("kpiTask", { kpiId: activeKpi.id })}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #93c5fd",
                        borderRadius: 10,
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 800,
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        cursor: "pointer",
                      }}
                    >
                      AI
                    </button>
                  </div>
                </div>
                {renderAiAddReview("kpiTask", { kpiId: activeKpi.id })}

                <div
                  style={{
                    display: "grid",
                    gap: 12,
                  }}
                >
                  {activeKpi.data.tasks.length === 0 ? (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                      }}
                    >
                      No KPI tasks yet.
                    </div>
                  ) : null}

                  {activeKpi.data.tasks.map((task) => (
                    <div
                      key={task.id}
                      className="kpi-work-task-row"
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 12,
                        padding: 12,
                        background: "#fff",
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.4fr 1fr 150px auto",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <WorkItemField
                          value={task.title}
                          placeholder="Task name"
                          onChange={(value) =>
                            onTaskChange(activeKpi.id, task.id, "title", value)
                          }
                        />
                        <select
                          className="nodrag nopan"
                          value={userOwnerOptions.includes(task.owner) ? task.owner : ""}
                          onChange={(e) =>
                            onTaskChange(activeKpi.id, task.id, "owner", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#111827",
                            background: "#f9fafb",
                          }}
                        >
                          <option value="">Unassigned</option>
                          {userOwnerOptions.map((owner) => (
                            <option key={owner} value={owner}>
                              {owner}
                            </option>
                          ))}
                        </select>
                        <input
                          className="nodrag nopan"
                          type="date"
                          value={task.dueDate}
                          onChange={(e) =>
                            onTaskChange(activeKpi.id, task.id, "dueDate", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            color: "#111827",
                            background: "#fff",
                          }}
                        />
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => onDeleteKpiTask(activeKpi.id, task.id)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            border: "1px solid #dc2626",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: "#fff5f5",
                            color: "#b91c1c",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "160px 1fr",
                          gap: 8,
                        }}
                      >
                        <select
                          className="nodrag nopan"
                          value={task.status}
                          onChange={(e) =>
                            onTaskChange(activeKpi.id, task.id, "status", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          {workStatusOptions.map((status) => (
                            <option key={status} value={status}>
                              {toTitleCase(status)}
                            </option>
                          ))}
                        </select>
                        <WorkItemField
                          value={task.notes}
                          placeholder="Notes"
                          onChange={(value) =>
                            onTaskChange(activeKpi.id, task.id, "notes", value)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="kpi-work-initiative-list"
                style={{
                  order: 1,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: "#111827",
                      }}
                    >
                      Initiatives
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#4b5563",
                        lineHeight: 1.45,
                      }}
                    >
                      Group strategic work under initiatives, then assign tasks inside each one.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="nodrag nopan kpi-work-action-button"
                      onClick={() => onAddInitiative(activeKpi.id)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      + Initiative
                    </button>
                    <button
                      type="button"
                      className="nodrag nopan kpi-work-ai-button"
                      onClick={() => onGenerateContextualAiDraft("initiative", { kpiId: activeKpi.id })}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #93c5fd",
                        borderRadius: 10,
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 800,
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        cursor: "pointer",
                      }}
                    >
                      AI
                    </button>
                  </div>
                </div>
                {renderAiAddReview("initiative", { kpiId: activeKpi.id })}

                <div
                  style={{
                    display: "grid",
                    gap: 12,
                  }}
                >
                  {activeKpi.data.initiatives.length === 0 ? (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                      }}
                    >
                      No initiatives yet.
                    </div>
                  ) : null}

                  {activeKpi.data.initiatives.map((initiative) => (
                    <div
                      key={initiative.id}
                      className="kpi-work-initiative-card"
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 16,
                        padding: 18,
                        background: "#fff",
                        display: "grid",
                        gap: 14,
                        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: 12,
                          alignItems: "start",
                        }}
                      >
                        <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
                          <WorkItemField
                            value={initiative.title}
                            placeholder="Initiative name"
                            variant="title"
                            onChange={(value) =>
                              onInitiativeChange(activeKpi.id, initiative.id, "title", value)
                            }
                          />
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                              color: "#6b7280",
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            <select
                              className="nodrag nopan"
                              value={userOwnerOptions.includes(initiative.owner) ? initiative.owner : ""}
                              onChange={(e) =>
                                onInitiativeChange(activeKpi.id, initiative.id, "owner", e.target.value)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                width: 130,
                                minWidth: 100,
                                border: "none",
                                borderBottom: "1px solid #e5e7eb",
                                padding: "3px 0",
                                background: "transparent",
                                color: "#4b5563",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              <option value="">Unassigned</option>
                              {userOwnerOptions.map((owner) => (
                                <option key={owner} value={owner}>
                                  {owner}
                                </option>
                              ))}
                            </select>
                            <span style={{ color: "#d1d5db" }}>•</span>
                            <input
                              className="nodrag nopan"
                              type="date"
                              value={initiative.dueDate}
                              aria-label="Initiative due date"
                              onChange={(e) =>
                                onInitiativeChange(activeKpi.id, initiative.id, "dueDate", e.target.value)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                width: 118,
                                border: "none",
                                borderBottom: "1px solid #e5e7eb",
                                padding: "3px 0",
                                background: "transparent",
                                color: "#6b7280",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                              title={formatWorkDueDate(initiative.dueDate)}
                            />
                            <span style={{ color: "#d1d5db" }}>•</span>
                            <select
                              className="nodrag nopan"
                              value={initiative.status}
                              aria-label="Initiative status"
                              onChange={(e) =>
                                onInitiativeChange(activeKpi.id, initiative.id, "status", e.target.value)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                ...workStatusChipStyle(initiative.status),
                                appearance: "none",
                                cursor: "pointer",
                              }}
                            >
                              {workStatusOptions.map((status) => (
                                <option key={status} value={status}>
                                  {formatWorkStatusLabel(status)}
                                </option>
                              ))}
                            </select>
                            <span style={{ color: "#d1d5db" }}>•</span>
                            <select
                              className="nodrag nopan"
                              value={initiative.priority ?? "normal"}
                              aria-label="Initiative priority"
                              onChange={(e) =>
                                onInitiativeChange(activeKpi.id, initiative.id, "priority", e.target.value)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                ...workPriorityChipStyle(initiative.priority),
                                appearance: "none",
                                cursor: "pointer",
                              }}
                            >
                              {workPriorityOptions.map((priority) => (
                                <option key={priority} value={priority}>
                                  {formatWorkPriorityLabel(priority)}
                                </option>
                              ))}
                            </select>
                            <span style={{ color: "#d1d5db" }}>•</span>
                            <select
                              className="nodrag nopan"
                              value={initiative.effort ?? "medium"}
                              aria-label="Initiative effort"
                              onChange={(e) =>
                                onInitiativeChange(activeKpi.id, initiative.id, "effort", e.target.value)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                ...workEffortChipStyle(initiative.effort),
                                appearance: "none",
                                cursor: "pointer",
                              }}
                            >
                              {workEffortOptions.map((effort) => (
                                <option key={effort} value={effort}>
                                  {formatWorkEffortLabel(effort)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <button
                            type="button"
                            className="nodrag nopan kpi-work-update-button"
                            onClick={() => sendInitiativeUpdateRequests(initiative)}
                            disabled={
                              isSendingKpiWorkUpdateEmails ||
                              initiative.tasks.filter((task) => task.status !== "done" && task.owner).length === 0
                            }
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "6px 10px",
                              fontSize: 12,
                              fontWeight: 800,
                              background:
                                isSendingKpiWorkUpdateEmails ||
                                initiative.tasks.filter((task) => task.status !== "done" && task.owner).length === 0
                                  ? "#f3f4f6"
                                  : "#fff",
                              color:
                                isSendingKpiWorkUpdateEmails ||
                                initiative.tasks.filter((task) => task.status !== "done" && task.owner).length === 0
                                  ? "#9ca3af"
                                  : "#111827",
                              cursor:
                                isSendingKpiWorkUpdateEmails ||
                                initiative.tasks.filter((task) => task.status !== "done" && task.owner).length === 0
                                  ? "not-allowed"
                                  : "pointer",
                              whiteSpace: "nowrap",
                            }}
                            title="Send update request emails for this initiative's assigned open tasks"
                          >
                            Request updates
                          </button>
                          <button
                            type="button"
                            className="nodrag nopan"
                            onClick={() => onDeleteInitiative(activeKpi.id, initiative.id)}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              width: 28,
                              height: 28,
                              border: "1px solid transparent",
                              borderRadius: 999,
                              padding: 0,
                              fontSize: 18,
                              fontWeight: 700,
                              background: "transparent",
                              color: "#9ca3af",
                              cursor: "pointer",
                              lineHeight: 1,
                            }}
                            title="Delete initiative"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 6 }}>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Description
                        </div>
                        <WorkItemField
                          value={initiative.summary}
                          placeholder="Describe what this initiative is meant to accomplish"
                          variant="body"
                          onChange={(value) =>
                            onInitiativeChange(activeKpi.id, initiative.id, "summary", value)
                          }
                        />
                      </div>

                      <div
                        style={{
                          borderTop: "1px solid #e5e7eb",
                          paddingTop: 14,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{ fontSize: 13, fontWeight: 800, color: "#374151" }}
                          >
                            Initiative Tasks
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              className="nodrag nopan kpi-work-action-button"
                              onClick={() => onAddInitiativeTask(activeKpi.id, initiative.id)}
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "6px 10px",
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#fff",
                                color: "#111827",
                                cursor: "pointer",
                              }}
                            >
                              + Task
                            </button>
                            <button
                              type="button"
                              className="nodrag nopan kpi-work-ai-button"
                              onClick={() =>
                                onGenerateContextualAiDraft("initiativeTask", {
                                  kpiId: activeKpi.id,
                                  initiativeId: initiative.id,
                                })
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                border: "1px solid #93c5fd",
                                borderRadius: 10,
                                padding: "6px 10px",
                                fontSize: 12,
                                fontWeight: 800,
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                cursor: "pointer",
                              }}
                            >
                              AI
                            </button>
                          </div>
                        </div>
                        {renderAiAddReview("initiativeTask", {
                          kpiId: activeKpi.id,
                          initiativeId: initiative.id,
                        })}

                        {initiative.tasks.length === 0 ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#6b7280",
                            }}
                          >
                            No initiative tasks yet.
                          </div>
                        ) : null}

                        {initiative.tasks.map((task) => (
                          <div
                            key={task.id}
                            className="kpi-work-task-row kpi-work-nested-task-row"
                            style={{
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 1fr) auto",
                              gap: 10,
                              alignItems: "start",
                              padding: "10px 4px",
                              borderTop: "1px solid #eef2f7",
                            }}
                          >
                            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                              <WorkItemField
                                value={task.title}
                                placeholder="Task name"
                                variant="taskTitle"
                                onChange={(value) =>
                                  onInitiativeTaskChange(
                                    activeKpi.id,
                                    initiative.id,
                                    task.id,
                                    "title",
                                    value
                                  )
                                }
                              />
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                  flexWrap: "wrap",
                                  color: "#6b7280",
                                  fontSize: 12,
                                }}
                              >
                                <select
                                  className="nodrag nopan"
                                  value={userOwnerOptions.includes(task.owner) ? task.owner : ""}
                                  onChange={(e) =>
                                    onInitiativeTaskChange(
                                      activeKpi.id,
                                      initiative.id,
                                      task.id,
                                      "owner",
                                      e.target.value
                                    )
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    width: 120,
                                    border: "1px solid transparent",
                                    borderBottom: "1px solid #e5e7eb",
                                    padding: "3px 0",
                                    background: "transparent",
                                    color: "#4b5563",
                                    fontSize: 12,
                                    fontWeight: 700,
                                  }}
                                >
                                  <option value="">Unassigned</option>
                                  {userOwnerOptions.map((owner) => (
                                    <option key={owner} value={owner}>
                                      {owner}
                                    </option>
                                  ))}
                                </select>
                                <span style={{ color: "#d1d5db" }}>•</span>
                                <input
                                  className="nodrag nopan"
                                  type="date"
                                  value={task.dueDate}
                                  title={formatWorkDueDate(task.dueDate)}
                                  onChange={(e) =>
                                    onInitiativeTaskChange(
                                      activeKpi.id,
                                      initiative.id,
                                      task.id,
                                      "dueDate",
                                      e.target.value
                                    )
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    width: 118,
                                    border: "none",
                                    borderBottom: "1px solid #e5e7eb",
                                    padding: "3px 0",
                                    background: "transparent",
                                    color: "#6b7280",
                                    fontSize: 12,
                                    fontWeight: 700,
                                  }}
                                />
                                <span style={{ color: "#d1d5db" }}>•</span>
                                <select
                                  className="nodrag nopan"
                                  value={task.status}
                                  aria-label="Task status"
                                  onChange={(e) =>
                                    onInitiativeTaskChange(
                                      activeKpi.id,
                                      initiative.id,
                                      task.id,
                                      "status",
                                      e.target.value
                                    )
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    ...workStatusChipStyle(task.status),
                                    appearance: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workStatusOptions.map((status) => (
                                    <option key={status} value={status}>
                                      {formatWorkStatusLabel(status)}
                                    </option>
                                  ))}
                                </select>
                                <span style={{ color: "#d1d5db" }}>•</span>
                                <select
                                  className="nodrag nopan"
                                  value={task.priority ?? "normal"}
                                  aria-label="Task priority"
                                  onChange={(e) =>
                                    onInitiativeTaskChange(
                                      activeKpi.id,
                                      initiative.id,
                                      task.id,
                                      "priority",
                                      e.target.value
                                    )
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    ...workPriorityChipStyle(task.priority),
                                    appearance: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workPriorityOptions.map((priority) => (
                                    <option key={priority} value={priority}>
                                      {formatWorkPriorityLabel(priority)}
                                    </option>
                                  ))}
                                </select>
                                <span style={{ color: "#d1d5db" }}>•</span>
                                <select
                                  className="nodrag nopan"
                                  value={task.effort ?? "medium"}
                                  aria-label="Task effort"
                                  onChange={(e) =>
                                    onInitiativeTaskChange(
                                      activeKpi.id,
                                      initiative.id,
                                      task.id,
                                      "effort",
                                      e.target.value
                                    )
                                  }
                                  onMouseDown={stopCanvasDrag}
                                  onPointerDown={stopCanvasDrag}
                                  style={{
                                    ...workEffortChipStyle(task.effort),
                                    appearance: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workEffortOptions.map((effort) => (
                                    <option key={effort} value={effort}>
                                      {formatWorkEffortLabel(effort)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="nodrag nopan"
                              onClick={() =>
                                onDeleteInitiativeTask(activeKpi.id, initiative.id, task.id)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                width: 26,
                                height: 26,
                                border: "1px solid transparent",
                                borderRadius: 999,
                                padding: 0,
                                fontSize: 16,
                                fontWeight: 700,
                                background: "transparent",
                                color: "#9ca3af",
                                cursor: "pointer",
                                lineHeight: 1,
                              }}
                              title="Delete task"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 18,
                paddingTop: 18,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              {activeKpiInLens ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  onClick={() => onRemoveFromActiveLens(activeKpi.id)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    background: "#fff",
                    color: "#111827",
                    padding: "8px 12px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Remove From {activeLensConfig?.name ?? activeLens}
                </button>
              ) : null}
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => onDeleteKpi(activeKpi.id)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #dc2626",
                  borderRadius: 10,
                  background: "#fff5f5",
                  color: "#b91c1c",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Delete KPI
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <datalist id="owner-options">
        {userOwnerOptions.map((owner) => (
          <option key={owner} value={owner} />
        ))}
      </datalist>

      {childKpiInfluenceDraft ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Set child KPI influence"
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10100,
            display: "grid",
            placeItems: "center",
            background: "rgba(2,6,23,0.56)",
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitChildKpiInfluence();
            }}
            style={{
              width: "min(360px, calc(100vw - 32px))",
              border: "1px solid rgba(148,163,184,0.28)",
              borderRadius: 12,
              padding: 16,
              background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(8,13,20,0.98))",
              boxShadow: "0 24px 80px rgba(0,0,0,0.44)",
              color: "#f8fafc",
            }}
          >
            <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: "#22c55e",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Child KPI Influence
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.3 }}>
                {childKpiInfluenceDraft.parentLabel}
              </div>
            </div>

            <label style={{ display: "grid", gap: 6, marginBottom: 14 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Influence %
              </span>
              <input
                autoFocus
                type="number"
                min={0}
                step="any"
                value={childKpiInfluenceDraft.value}
                onChange={(event) =>
                  setChildKpiInfluenceDraft((draft) =>
                    draft ? { ...draft, value: event.target.value } : draft
                  )
                }
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid rgba(148,163,184,0.32)",
                  borderRadius: 8,
                  padding: "10px 11px",
                  background: "rgba(2,6,23,0.82)",
                  color: "#f8fafc",
                  fontSize: 15,
                  fontWeight: 800,
                }}
              />
            </label>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => setChildKpiInfluenceDraft(null)}
                style={{
                  border: "1px solid rgba(148,163,184,0.28)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  background: "transparent",
                  color: "#cbd5e1",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  border: "1px solid rgba(34,197,94,0.55)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  background: "rgba(34,197,94,0.18)",
                  color: "#f8fafc",
                  fontSize: 13,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Add KPI
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {mapContextMenu ? (
        <div
          role="menu"
          aria-label="Map actions"
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            position: "fixed",
            left: mapContextMenu.x,
            top: mapContextMenu.y,
            zIndex: 10050,
            width: 240,
            border: "1px solid rgba(148,163,184,0.26)",
            borderRadius: 12,
            padding: 6,
            background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(8,13,20,0.98))",
            boxShadow: "0 18px 48px rgba(0,0,0,0.38), 0 0 28px rgba(34,197,94,0.08)",
            color: "#f8fafc",
          }}
        >
          <div
            style={{
              padding: "8px 9px 9px",
              borderBottom: "1px solid rgba(148,163,184,0.16)",
              marginBottom: 4,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: "#22c55e",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Map Actions
            </div>
          </div>

          <button
            type="button"
            role="menuitem"
            disabled={!canCreateKpis}
            onClick={() => {
              if (!canCreateKpis) return;
              const position = mapContextMenu.flowPosition;
              closeKpiContextMenu();
              addRootKpiAtPosition(position);
            }}
            style={{
              width: "100%",
              border: "none",
              borderRadius: 8,
              padding: "8px 9px",
              background: "transparent",
              color: canCreateKpis ? "#e5e7eb" : "#64748b",
              cursor: canCreateKpis ? "pointer" : "not-allowed",
              textAlign: "left",
              display: "grid",
              gap: 2,
            }}
            onMouseEnter={(event) => {
              if (canCreateKpis) {
                event.currentTarget.style.background = "rgba(148,163,184,0.12)";
              }
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.background = "transparent";
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 900 }}>Add KPI here</span>
            <span style={{ fontSize: 11, color: canCreateKpis ? "#94a3b8" : "#475569" }}>
              Create a root KPI on this map
            </span>
          </button>

          <button
            type="button"
            role="menuitem"
            onClick={collapseAllChildrenForActiveLens}
            style={{
              width: "100%",
              border: "none",
              borderRadius: 8,
              padding: "8px 9px",
              background: "transparent",
              color: "#e5e7eb",
              cursor: "pointer",
              textAlign: "left",
              display: "grid",
              gap: 2,
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.background = "rgba(148,163,184,0.12)";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.background = "transparent";
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 900 }}>Collapse all children</span>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              Hide child KPI branches in this lens
            </span>
          </button>
        </div>
      ) : null}

      {kpiContextMenu && kpiContextMenuNode ? (
        <div
          role="menu"
          aria-label={`Actions for ${kpiContextMenuNode.data.label || "KPI"}`}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            position: "fixed",
            left: kpiContextMenu.x,
            top: kpiContextMenu.y,
            zIndex: 10050,
            width: 280,
            maxHeight: "min(520px, calc(100vh - 24px))",
            overflowY: "auto",
            border: "1px solid rgba(148,163,184,0.26)",
            borderRadius: 12,
            padding: 6,
            background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(8,13,20,0.98))",
            boxShadow: "0 18px 48px rgba(0,0,0,0.38), 0 0 28px rgba(34,197,94,0.08)",
            color: "#f8fafc",
          }}
        >
          <div
            style={{
              padding: "8px 9px 9px",
              borderBottom: "1px solid rgba(148,163,184,0.16)",
              marginBottom: 4,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: "#22c55e",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              KPI Actions
            </div>
            <div
              style={{
                marginTop: 3,
                fontSize: 13,
                fontWeight: 850,
                lineHeight: 1.25,
                color: "#f8fafc",
              }}
            >
              {kpiContextMenuNode.data.label || "Untitled KPI"}
            </div>
          </div>

          {kpiContextMenuItems.map((item) => {
            const isDanger = item.tone === "danger";
            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (!item.disabled) {
                    item.action();
                  }
                }}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 9px",
                  background: "transparent",
                  color: item.disabled
                    ? "#64748b"
                    : isDanger
                      ? "#fca5a5"
                      : "#e5e7eb",
                  cursor: item.disabled ? "not-allowed" : "pointer",
                  textAlign: "left",
                  display: "grid",
                  gap: 2,
                }}
                onMouseEnter={(event) => {
                  if (!item.disabled) {
                    event.currentTarget.style.background = isDanger
                      ? "rgba(239,68,68,0.12)"
                      : "rgba(148,163,184,0.12)";
                  }
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "transparent";
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 900 }}>{item.label}</span>
                <span style={{ fontSize: 11, color: item.disabled ? "#475569" : "#94a3b8" }}>
                  {item.detail}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      {libraryPointerDrag ? (
        <div
          style={{
            position: "fixed",
            left: libraryPointerDrag.x + 12,
            top: libraryPointerDrag.y + 12,
            zIndex: 2000,
            pointerEvents: "none",
            border: "1px solid #93c5fd",
            borderRadius: 12,
            padding: "9px 12px",
            background: "rgba(239,246,255,0.96)",
            color: "#1e3a8a",
            boxShadow: "0 12px 28px rgba(15,23,42,0.22)",
            fontSize: 13,
            fontWeight: 900,
            maxWidth: 240,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {libraryPointerDrag.label}
        </div>
      ) : null}

      <ReactFlowProvider>
        <ReactFlow
          nodes={displayedNodes}
          edges={displayedEdges}
          nodeTypes={nodeTypes}
          onInit={setReactFlowInstance}
          onConnect={onConnect}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgeClick={onEdgeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={onPaneClick}
          onPaneContextMenu={onPaneContextMenu}
          onDragOver={onCanvasDragOver}
          onDrop={onCanvasDrop}
          elementsSelectable
          selectionOnDrag
          selectionMode={SelectionMode.Partial}
          multiSelectionKeyCode={["Shift"]}
          minZoom={0.05}
          maxZoom={2.5}
          fitView
        >
          <Controls showInteractive={false}>
            <ControlButton onClick={onCompactFitView} title="Clean up spacing and fit view">
              <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>[]</span>
            </ControlButton>
          </Controls>
          <Background color="#444" gap={24} size={1} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
